import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { RetrofitSurveyData } from '../utils/retrofitSurveyStorage';
import { calculateHLPLength, calculatePWLength, calculateSWLength } from '../utils/floorPlanUtils';
import { PIXELS_PER_METER } from '../constants/floorPlanConstants';
import { generateEnhancedFloorPlanCanvas } from '../utils/exportUtils';
import { generatePropertyElevationCanvas } from './propertyElevationCanvasRenderer';
import { generateExtensionsSection, generateExtensionsPhotoPages, getExtensionPhotoCount } from './retrofitExtensionsSection';
import type { FormTemplate } from '../types/formSchema';
import {
  renderCustomPageForPDF,
  calculateTotalCustomPagesCount,
  getCustomPagesForPDF,
  calculateCustomPageCount,
  getCustomFieldsPDFStyles
} from './customFieldsPDFRenderer_NEW';
import { renderFieldForPDF } from './customFieldsPDFRenderer';

/**
 * Comprehensive Retrofit Survey PDF Export Service
 * 
 * Generates a professional PDF report from retrofit survey data using:
 * - Modern HTML/CSS for layout and styling
 * - jsPDF + html2canvas for client-side PDF generation
 * - Multi-page support with proper pagination
 * - Photo integration with proper sizing
 * - Floor plan image embedding
 */

/**
 * Convert wall type code to readable label
 */
function getWallTypeLabel(code: string): string {
  const wallTypeMap: Record<string, string> = {
    'SG': 'Stone: granite or whinstone',
    'SS': 'Stone: sandstone or limestone',
    'SO': 'Solid Brick',
    'CO': 'Cob',
    'CA': 'Cavity',
    'TI': 'Timber Frame',
    'SY': 'System Build',
    'CW': 'Curtain Wall'
  };
  return wallTypeMap[code] || code;
}

export interface FloorPlanImageData {
  dataUrl: string;
  canvasWidth: number;
  canvasHeight: number;
}

/**
 * Interface for tracking section page numbers in Table of Contents
 */
export interface SectionPageMap {
  propertyOverview: number;
  tableOfContents: number;
  floorPlan: number | null;
  radiatorsWindows: number | null;
  dimensionsCalculations: number | null;
  heatingSystem: number | null;
  measureSpecification: number | null;
  pas2030Questions: number | null;
  propertyFront: number | null;
  propertySide: number | null;
  propertyRear: number | null;
  cellar: number | null;
  rooms: number | null;
  extensions: number | null;
  metersServices: number | null;
  ventilationCondition: number | null;
  occupancy: number | null;
  siteAssessment: number | null;
  signatures: number | null;
  additionalNotes: number | null;
  customPages?: Record<string, number>; // Dynamic custom pages from form builder
}

export interface ElevationImageData {
  dataUrl: string;
  canvasWidth: number;
  canvasHeight: number;
  elevationType: string; // FRONT, REAR, LEFT, RIGHT
  elevationData: {
    dimensions: { height: number; width: number };
    treatableArea: number;
    untreatableArea: number;
    totalArea: number;
    elements: {
      doors: Array<{ id: string; height: number; width: number; area: number; }>;
      windows: Array<{ id: string; height: number; width: number; area: number; }>;
      airVents: Array<{ id: string; }>;
    };
  };
}

export interface RetrofitPDFExportOptions {
  includePhotos?: boolean;
  includeFloorPlan?: boolean;
  floorPlanImages?: FloorPlanImageData[]; // Floor plan images with dimensions
  elevationImages?: ElevationImageData[]; // Wall insulation elevation diagrams
  logoDataUrl?: string; // Optional company logo
  headerColor?: string; // Theme color for headers
  companyName?: string; // Company name for headers
  companyWebsite?: string; // Company website for headers
  companyPhone?: string; // Company phone for headers
  companyEmail?: string; // Company email
  companyAddress?: string; // Company address
  format?: 'pdf' | 'html' | 'both'; // Export format selection
  onProgress?: (progress: number, stage: string) => void; // Real-time progress callback
  formTemplate?: FormTemplate; // Custom form template from form builder
  customFieldsData?: Record<string, any>; // Data for custom fields
}

/**
 * Built-in section IDs for page ordering
 */
export const BUILT_IN_SECTION_IDS = {
  FLOOR_PLAN: 'floor_plan',
  RADIATORS_WINDOWS: 'radiators_windows',
  DIMENSIONS_CALCULATIONS: 'dimensions_calculations',
  HEATING_SYSTEM: 'heating_system',
  MEASURE_SPECIFICATION: 'measure_specification',
  PAS2030_QUESTIONS: 'pas2030_questions',
  PROPERTY_FRONT: 'property_front',
  PROPERTY_SIDE: 'property_side',
  PROPERTY_REAR: 'property_rear',
  CELLAR: 'cellar',
  ROOMS: 'rooms',
  EXTENSIONS: 'extensions',
  METERS_SERVICES: 'meters_services',
  VENTILATION_CONDITION: 'ventilation_condition',
  OCCUPANCY: 'occupancy',
  SITE_ASSESSMENT: 'site_assessment',
  SIGNATURES: 'signatures',
  ADDITIONAL_NOTES: 'additional_notes',
} as const;

/**
 * Legacy section ID mapping for backward compatibility
 * Maps old section IDs from formTemplate.pageOrder to current BUILT_IN_SECTION_IDS
 */
const LEGACY_SECTION_ID_MAP: Record<string, string | null> = {
  'initial_questionnaire': null, // Data section, not a PDF section - skip it
  'room_details': BUILT_IN_SECTION_IDS.ROOMS,
  'meters': BUILT_IN_SECTION_IDS.METERS_SERVICES,
  'main_property_measurements': BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS,
  'occupancy_assessment': BUILT_IN_SECTION_IDS.OCCUPANCY,
  'radiators_windows': BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS,
  'dimensions_calculations': BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS,
  'ventilation_condition': BUILT_IN_SECTION_IDS.VENTILATION_CONDITION,
  'meters_services': BUILT_IN_SECTION_IDS.METERS_SERVICES,
  'export': null, // Ignore 'export' section - not a real section
};

/**
 * Normalize section ID from legacy to current format
 */
function normalizeSectionId(sectionId: string): string | null {
  // Check if it's already a valid BUILT_IN_SECTION_ID
  const builtInIds = Object.values(BUILT_IN_SECTION_IDS) as string[];
  if (builtInIds.includes(sectionId)) {
    return sectionId;
  }
  
  // Check legacy mapping
  if (sectionId in LEGACY_SECTION_ID_MAP) {
    return LEGACY_SECTION_ID_MAP[sectionId];
  }
  
  // Assume any other ID is a custom page ID - keep as-is
  // Custom pages can have any ID format (e.g., UUID, custom_page_xxx, etc.)
  return sectionId;
}

/**
 * Ensure critical PAS sections are included in page order
 * Merges formTemplate.pageOrder with essential sections that must always appear
 */
function ensureCriticalSections(pageOrder: string[]): string[] {
  const critical = {
    // Section ID: [insert after this section, or null for end]
    [BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS]: BUILT_IN_SECTION_IDS.FLOOR_PLAN,
  };
  
  const result = [...pageOrder];
  
  Object.entries(critical).forEach(([sectionId, insertAfter]) => {
    if (!result.includes(sectionId)) {
      if (insertAfter && result.includes(insertAfter)) {
        // Insert after specified section
        const index = result.indexOf(insertAfter);
        result.splice(index + 1, 0, sectionId);
        console.log(`✅ Auto-injected critical section "${sectionId}" after "${insertAfter}"`);
      } else {
        // Insert at end if anchor not found
        result.push(sectionId);
        console.log(`✅ Auto-injected critical section "${sectionId}" at end`);
      }
    }
  });
  
  return result;
}

/**
 * Get default page order when formTemplate.pageOrder is not provided
 * This matches the original PDF structure:
 * 1. Property Overview (cover)
 * 2. TOC
 * 3. Floor Plan
 * 4. Radiators & Windows Summary
 * 5. All Floors Dimensions & Calculations
 * 6. Heating System Assessment
 * 7. Measure Specification
 * 8. PAS 2030 Questions
 * 9. Property Front
 * 10. Property Rear
 * 11. Cellar
 * 12. Rooms
 * 13. Extensions
 * 14. Meters & Services
 * 15. Ventilation & Condition
 * 16. Occupancy Assessment
 * 17. Site Assessment
 * 18. Signatures & Feedback
 * 19. Additional Notes
 * 20. Custom pages (appended after)
 */
function getDefaultPageOrder(): string[] {
  return [
    BUILT_IN_SECTION_IDS.FLOOR_PLAN,                  // 3
    BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS,           // 4
    BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS,     // 5
    BUILT_IN_SECTION_IDS.HEATING_SYSTEM,              // 6
    BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION,       // 7
    BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS,           // 8
    BUILT_IN_SECTION_IDS.PROPERTY_FRONT,              // 9
    BUILT_IN_SECTION_IDS.PROPERTY_REAR,               // 10 (not Property Side)
    BUILT_IN_SECTION_IDS.CELLAR,                      // 11
    BUILT_IN_SECTION_IDS.ROOMS,                       // 12
    BUILT_IN_SECTION_IDS.EXTENSIONS,                  // 13
    BUILT_IN_SECTION_IDS.METERS_SERVICES,             // 14
    BUILT_IN_SECTION_IDS.VENTILATION_CONDITION,       // 15
    BUILT_IN_SECTION_IDS.OCCUPANCY,                   // 16
    BUILT_IN_SECTION_IDS.SITE_ASSESSMENT,             // 17
    BUILT_IN_SECTION_IDS.SIGNATURES,                  // 18
    BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES,            // 19
    // Property Side is not in original - only used if no left/right data
  ];
}

/**
 * Merge page order: Built-in sections use default order, custom pages respect user's order
 * @param formTemplate The form template with pageOrder and customPages
 * @returns Merged page order array
 */
function getMergedPageOrder(formTemplate?: FormTemplate): string[] {
  const defaultOrder = getDefaultPageOrder();
  
  if (!formTemplate) {
    return defaultOrder;
  }
  
  // Get custom page IDs
  const customPages = getCustomPagesForPDF(formTemplate);
  const customPageIds = customPages.map(p => p.id);
  
  // If no pageOrder specified, append custom pages at end
  if (!formTemplate.pageOrder || formTemplate.pageOrder.length === 0) {
    console.log('📄 No pageOrder found, using default order + custom pages at end');
    return [...defaultOrder, ...customPageIds];
  }
  
  // Build result: default order for built-ins + custom pages in user's order
  const result: string[] = [];
  const builtInIdsArray = Object.values(BUILT_IN_SECTION_IDS);
  const isBuiltInSection = (id: string): boolean => builtInIdsArray.includes(id as any);
  
  // First, add all built-in sections in default order
  result.push(...defaultOrder);
  
  // Then, insert custom pages according to their position in pageOrder
  // Find where each custom page should go relative to built-in sections
  const customPagePositions: Array<{ id: string; insertAfter: string | null }> = [];
  
  for (const customPageId of customPageIds) {
    const indexInPageOrder = formTemplate.pageOrder.indexOf(customPageId);
    
    if (indexInPageOrder === -1) {
      // Not in pageOrder, append at end
      customPagePositions.push({ id: customPageId, insertAfter: null });
      continue;
    }
    
    // Find the last built-in section that appears before this custom page in pageOrder
    let insertAfter: string | null = null;
    for (let i = indexInPageOrder - 1; i >= 0; i--) {
      const prevId = formTemplate.pageOrder[i];
      if (isBuiltInSection(prevId)) {
        insertAfter = prevId;
        break;
      }
    }
    
    customPagePositions.push({ id: customPageId, insertAfter });
  }
  
  // Now insert custom pages at their specified positions
  for (const { id, insertAfter } of customPagePositions) {
    if (insertAfter === null) {
      // Append at end
      result.push(id);
    } else {
      // Insert after the specified built-in section
      const insertIndex = result.indexOf(insertAfter);
      if (insertIndex !== -1) {
        result.splice(insertIndex + 1, 0, id);
      } else {
        // Fallback: append at end
        result.push(id);
      }
    }
  }
  
  console.log('📄 Merged page order:', {
    totalSections: result.length,
    builtIn: result.filter(id => isBuiltInSection(id)).length,
    custom: result.filter(id => !isBuiltInSection(id)).length,
    customPageOrder: customPagePositions.map(p => `${p.id} after ${p.insertAfter || 'end'}`)
  });
  
  return result;
}

/**
 * Utility: Convert blob to base64
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result;
      if (typeof result === 'string') {
        resolve(result);
      } else {
        reject(new Error('Failed to convert blob to base64'));
      }
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Utility: Optimize image by resizing and compressing
 * High quality settings: 1600x1200 @ 90% WebP for sharp PDF documentation
 * WebP reduces file size by 70-80% while maintaining excellent visual quality
 */
async function optimizeImageForPDF(
  imageUrl: string,
  maxWidth: number = 1600,
  maxHeight: number = 1200,
  quality: number = 0.90
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        // Calculate new dimensions maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth || height > maxHeight) {
          const widthRatio = maxWidth / width;
          const heightRatio = maxHeight / height;
          const ratio = Math.min(widthRatio, heightRatio);
          
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        
        // Create canvas and resize image
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }
        
        // Use better image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // Draw resized image
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to WebP with compression for better file size (30% smaller than JPEG)
        const optimizedUrl = canvas.toDataURL('image/webp', quality);
        resolve(optimizedUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

/**
 * Utility: Convert HTML to MHTML (Web Archive) format with embedded images
 * MHTML is a single-file format that works offline with full interactivity
 */
async function convertHTMLToMHTML(html: string): Promise<Blob> {
  try {
    console.log('📦 Converting HTML to MHTML format...');
    
    // Extract all image URLs from the HTML
    const imgRegex = /<img[^>]+src="([^">]+)"/g;
    const images: { url: string; base64: string; contentType: string }[] = [];
    let match;
    
    while ((match = imgRegex.exec(html)) !== null) {
      const url = match[1];
      
      // Skip if already processed or if it's already a data URL
      if (url.startsWith('data:') || images.some(img => img.url === url)) {
        continue;
      }
      
      try {
        // Fetch and convert image to base64
        const response = await fetch(url);
        const blob = await response.blob();
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        
        images.push({
          url,
          base64,
          contentType: blob.type || 'image/webp'
        });
      } catch (error) {
        console.warn(`Failed to fetch image: ${url}`, error);
      }
    }
    
    console.log(`📸 Embedded ${images.length} images in MHTML`);
    
    // Generate unique boundary for MIME multipart
    const boundary = `----=_NextPart_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Build MHTML content
    let mhtmlContent = `MIME-Version: 1.0
Content-Type: multipart/related; boundary="${boundary}"

This is a multi-part message in MIME format.

--${boundary}
Content-Type: text/html; charset="utf-8"
Content-Transfer-Encoding: quoted-printable
Content-Location: index.html

${html}

`;
    
    // Add each image as a separate MIME part
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      mhtmlContent += `--${boundary}
Content-Type: ${img.contentType}
Content-Transfer-Encoding: base64
Content-Location: ${img.url}

${img.base64}

`;
    }
    
    // Close the MIME multipart
    mhtmlContent += `--${boundary}--
`;
    
    console.log('✅ MHTML conversion complete');
    
    return new Blob([mhtmlContent], { type: 'application/x-mimearchive' });
  } catch (error) {
    console.error('❌ Error converting HTML to MHTML:', error);
    throw error;
  }
}

/**
 * Add clickable links to TOC pages in the PDF
 */
function addTOCLinksToRetrofitPDF(
  pdf: jsPDF,
  sectionPageMap: SectionPageMap,
  formTemplate?: FormTemplate
): void {
  try {
    console.log('📱 Adding clickable links to Retrofit TOC...');
    
    // Create a map of built-in section IDs to their TOC metadata
    const builtInSectionMap: Record<string, { title: string; key: string }> = {
      'property_overview': { title: 'Property Overview', key: 'propertyOverview' },
      [BUILT_IN_SECTION_IDS.FLOOR_PLAN]: { title: 'Floor Plan', key: 'floorPlan' },
      [BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS]: { title: 'Radiators & Windows Summary', key: 'radiatorsWindows' },
      [BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS]: { title: 'All Floors Dimensions & Calculations', key: 'dimensionsCalculations' },
      [BUILT_IN_SECTION_IDS.HEATING_SYSTEM]: { title: 'Heating System Assessment', key: 'heatingSystem' },
      [BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION]: { title: 'Measure Specification', key: 'measureSpecification' },
      [BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS]: { title: 'PAS 2030 Questions', key: 'pas2030Questions' },
      [BUILT_IN_SECTION_IDS.PROPERTY_FRONT]: { title: 'Property Front', key: 'propertyFront' },
      [BUILT_IN_SECTION_IDS.PROPERTY_SIDE]: { title: 'Property Side', key: 'propertySide' },
      [BUILT_IN_SECTION_IDS.PROPERTY_REAR]: { title: 'Property Rear', key: 'propertyRear' },
      [BUILT_IN_SECTION_IDS.CELLAR]: { title: 'Cellar', key: 'cellar' },
      [BUILT_IN_SECTION_IDS.ROOMS]: { title: 'Rooms', key: 'rooms' },
      [BUILT_IN_SECTION_IDS.EXTENSIONS]: { title: 'Extensions', key: 'extensions' },
      [BUILT_IN_SECTION_IDS.METERS_SERVICES]: { title: 'Meters & Services', key: 'metersServices' },
      [BUILT_IN_SECTION_IDS.VENTILATION_CONDITION]: { title: 'Ventilation & Condition', key: 'ventilationCondition' },
      [BUILT_IN_SECTION_IDS.OCCUPANCY]: { title: 'Occupancy Assessment', key: 'occupancy' },
      [BUILT_IN_SECTION_IDS.SITE_ASSESSMENT]: { title: 'Site Assessment', key: 'siteAssessment' },
      [BUILT_IN_SECTION_IDS.SIGNATURES]: { title: 'Signatures & Feedback', key: 'signatures' },
      [BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES]: { title: 'Additional Notes', key: 'additionalNotes' },
    };
    
    // Get the actual page order (merged order respecting custom page positions)
    const pageOrder = getMergedPageOrder(formTemplate);
    
    // Get custom pages for lookup
    const customPages = formTemplate ? getCustomPagesForPDF(formTemplate) : [];
    const customPageMap = new Map(customPages.map(p => [p.id, p]));
    
    // Build TOC items array following the actual page order
    const allTocItems: Array<{title: string, page: number | null}> = [];
    
    // Always add Property Overview first (it's always page 1)
    if (sectionPageMap.propertyOverview) {
      allTocItems.push({
        title: 'Property Overview',
        page: sectionPageMap.propertyOverview
      });
    }
    
    // Then follow the page order for remaining sections
    for (const sectionId of pageOrder) {
      // Check if it's a built-in section
      if (builtInSectionMap[sectionId]) {
        const { title, key } = builtInSectionMap[sectionId];
        const page = (sectionPageMap as any)[key];
        
        if (page !== null && page !== undefined) {
          allTocItems.push({ title, page });
        }
      } 
      // Check if it's a custom page
      else if (customPageMap.has(sectionId)) {
        const customPage = customPageMap.get(sectionId)!;
        const page = sectionPageMap.customPages?.[sectionId];
        
        if (page !== null && page !== undefined) {
          allTocItems.push({
            title: customPage.title,
            page
          });
        }
      }
    }
    
    console.log(`📱 Built ${allTocItems.length} TOC items in order:`, 
      allTocItems.map((item, i) => `${i + 1}. ${item.title} → pg ${item.page}`));
    
    const tocItems = allTocItems.filter(item => item.page !== null); // Only include sections that exist

    const ITEMS_PER_PAGE = 13;
    const tocStartPage = 2; // TOC starts on page 2
    
    // Measured values from actual PDF (in mm from top of page)
    const firstItemTop = 62; // mm from top to first TOC item (measured: 6.17cm = 61.7mm)
    const itemHeight = 14; // mm between each TOC item top (measured: ~1.4cm spacing)
    const touchTargetHeight = 11.6; // mm tall clickable area per item (measured: 1.16cm)
    
    console.log(`📱 Adding ${tocItems.length} clickable TOC links across ${Math.ceil(tocItems.length / ITEMS_PER_PAGE)} page(s)`);
    
    let clickableCount = 0;
    
    const totalPages = pdf.internal.pages.length - 1; // Subtract 1 for the internal placeholder page
    console.log(`📄 PDF has ${totalPages} total pages (internal count: ${pdf.internal.pages.length})`);
    
    tocItems.forEach((item, index) => {
      const pageIndex = Math.floor(index / ITEMS_PER_PAGE);
      const tocPage = tocStartPage + pageIndex;
      const itemOnPage = index % ITEMS_PER_PAGE;
      
      // Skip if target page doesn't exist or is invalid
      if (!item.page || typeof item.page !== 'number' || item.page < 1 || item.page > totalPages) {
        console.warn(`⚠️ Skipping TOC link for "${item.title}": invalid page ${item.page} (total: ${totalPages})`);
        return;
      }
      
      // Skip if TOC page doesn't exist
      if (tocPage > totalPages) {
        console.warn(`⚠️ Skipping TOC link: TOC page ${tocPage} doesn't exist`);
        return;
      }
      
      try {
        // Calculate vertical position for this item
        const itemTop = firstItemTop + (itemOnPage * itemHeight);
        
        pdf.setPage(tocPage);
        
        // Add large clickable area for reliable tapping
        pdf.link(
          10,  // x: start from left margin
          itemTop, // y: position of this TOC item
          190, // width: full page width
          touchTargetHeight, // height: clickable zone
          { pageNumber: item.page }
        );
        
        clickableCount++;
        
        // Log first and last few for verification
        if (index < 3 || index >= tocItems.length - 3) {
          console.log(`  [${index}] "${item.title}" @ TOC pg ${tocPage} → pg ${item.page}`);
        }
      } catch (linkError) {
        console.warn(`⚠️ Failed to add link for "${item.title}":`, linkError);
      }
    });
    
    console.log(`✅ Added ${clickableCount} clickable links to Retrofit TOC`);
    
  } catch (error) {
    console.error('⚠️ Failed to add TOC links:', error);
    // Don't throw - TOC still readable even without links
  }
}

/**
 * Utility: Convert HTML to PDF Blob using jsPDF + html2canvas
 */
async function convertHTMLToPDFBlob(
  html: string, 
  filename: string,
  sectionPageMap: SectionPageMap,
  formTemplate?: FormTemplate,
  onProgress?: (progress: number, stage: string) => void
): Promise<Blob> {
  try {
    console.log('🎨 Starting retrofit PDF generation...');
    
    // Create temporary container
    onProgress?.(0, 'Setting up document...');
    const container = document.createElement('div');
    container.innerHTML = html;
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '-9999px';
    container.style.width = '210mm';
    container.style.minHeight = '297mm';
    document.body.appendChild(container);

    // Wait for any images to load
    onProgress?.(5, 'Loading images...');
    const images = container.getElementsByTagName('img');
    await Promise.all(Array.from(images).map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise((resolve) => {
        img.onload = () => resolve(void 0);
        img.onerror = () => resolve(void 0); // Don't fail for broken images
        setTimeout(() => resolve(void 0), 5000); // Timeout after 5s
      });
    }));

    // Initialize PDF with high quality settings
    onProgress?.(10, 'Initializing PDF...');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
      compress: true,
      precision: 8
    });

    // Get all pages
    const pages = container.querySelectorAll('.pdf-page');
    console.log(`📄 Found ${pages.length} pages to convert`);

    if (pages.length === 0) {
      throw new Error('No pages found in HTML content');
    }

    // Convert each page to canvas and add to PDF
    // Reserve 10-90% of progress for page conversion
    const conversionStartProgress = 10;
    const conversionEndProgress = 90;
    const progressPerPage = (conversionEndProgress - conversionStartProgress) / pages.length;
    
    for (let i = 0; i < pages.length; i++) {
      const page = pages[i] as HTMLElement;
      const currentProgress = conversionStartProgress + (i * progressPerPage);
      
      onProgress?.(
        Math.round(currentProgress),
        `Converting page ${i + 1} of ${pages.length}...`
      );
      
      console.log(`🖼️ Converting page ${i + 1}/${pages.length}...`);

      try {
        // Generate high-quality canvas at 3.5x scale for crisp text and images
        const canvas = await html2canvas(page, {
          scale: 3.5,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width: page.scrollWidth || page.offsetWidth,
          height: page.scrollHeight || page.offsetHeight,
          logging: false,
          imageTimeout: 10000,
          foreignObjectRendering: false,
          removeContainer: true,
          windowWidth: page.scrollWidth || page.offsetWidth,
          windowHeight: page.scrollHeight || page.offsetHeight
        });

        // Add new page (except for first page)
        if (i > 0) {
          pdf.addPage();
        }

        // Add image to PDF with high-quality JPEG compression
        const imgData = canvas.toDataURL('image/jpeg', 0.95);
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'SLOW');
        
      } catch (pageError) {
        console.warn(`Failed to convert page ${i + 1}:`, pageError);
        // Add blank page to maintain pagination
        if (i > 0) pdf.addPage();
      }
    }

    // Add clickable links to TOC
    onProgress?.(92, 'Adding interactive links...');
    try {
      addTOCLinksToRetrofitPDF(pdf, sectionPageMap, formTemplate);
    } catch (linkError) {
      console.warn('⚠️ Failed to add TOC links:', linkError);
      // Continue without links rather than failing
    }

    // Clean up
    onProgress?.(95, 'Finalizing PDF...');
    document.body.removeChild(container);
    
    // Convert to blob
    try {
      const pdfBlob = pdf.output('blob');
      onProgress?.(100, 'Complete!');
      console.log('✅ Retrofit PDF generated successfully');
      return pdfBlob;
    } catch (outputError) {
      console.error('❌ Failed to generate PDF blob:', outputError);
      const errorMessage = outputError instanceof Error ? outputError.message : String(outputError);
      throw new Error(`PDF generation failed: ${errorMessage}`);
    }
    
  } catch (error) {
    console.error('❌ Failed to generate retrofit PDF:', error);
    throw error;
  }
}

/**
 * Main export function for retrofit survey
 */
export async function exportRetrofitSurveyToPDF(
  surveyData: RetrofitSurveyData,
  options: RetrofitPDFExportOptions = {}
): Promise<void> {
  try {
    console.log('🚀 Starting retrofit survey PDF export...');
    
    // DEBUG: Check what siteNotesData the PDF export receives
    const receivedSiteNotesData = surveyData.floor_plan?.linkedFloorPlanData?.siteNotesData;
    console.log('📊 [PDF Export Entry] Received surveyData with siteNotesData:', {
      hasSurveyData: !!surveyData,
      hasFloorPlan: !!surveyData.floor_plan,
      hasLinkedFloorPlanData: !!surveyData.floor_plan?.linkedFloorPlanData,
      hasSiteNotesData: !!receivedSiteNotesData,
      siteNotesFloorsCount: receivedSiteNotesData?.floors?.length || 0,
      siteNotesFloors: receivedSiteNotesData?.floors?.map((f: any) => ({
        floorLevel: f.floorLevel,
        buildingType: f.buildingType,
        hasHighlighterBreakdown: !!f.highlighterBreakdown
      }))
    });
    
    console.log('🔍 [PDF Export Entry] DETAILED siteNotesData.floors:');
    receivedSiteNotesData?.floors?.forEach((f: any, index: number) => {
      console.log(`  Floor ${index + 1} (${f.buildingType}):`, {
        floorLevel: f.floorLevel,
        buildingType: f.buildingType,
        area: f.floorArea,
        hlp: f.hlp,
        pw: f.pw,
        hasHighlighterBreakdown: !!f.highlighterBreakdown,
        highlighterSegments: f.highlighterBreakdown ? {
          hlp: f.highlighterBreakdown.hlpSegments?.length || 0,
          pw: f.highlighterBreakdown.pwSegments?.length || 0
        } : null
      });
    });
    
    console.log('📊 [PDF Export Entry] OLD LOG FORMAT:', {
      hasSurveyData: !!surveyData,
      hasFloorPlan: !!surveyData.floor_plan,
      hasLinkedFloorPlanData: !!surveyData.floor_plan?.linkedFloorPlanData,
      hasSiteNotesData: !!receivedSiteNotesData,
      siteNotesFloorsCount: receivedSiteNotesData?.floors?.length || 0,
      siteNotesFloors: receivedSiteNotesData?.floors?.map((f: any) => ({
        id: f.id,
        floorLevel: f.floorLevel,
        buildingType: f.buildingType,
        hasRooms: !!f.rooms,
        hasHighlighterBreakdown: !!f.highlighterBreakdown
      })),
      linkedFloorPlanDataKeys: surveyData.floor_plan?.linkedFloorPlanData ? Object.keys(surveyData.floor_plan.linkedFloorPlanData) : []
    });
    
    // Report initial progress
    if (options.onProgress) {
      options.onProgress(5, 'Initializing export...');
    }
    
    const mergedOptions: RetrofitPDFExportOptions = {
      includePhotos: true,
      includeFloorPlan: true,
      ...options
    };

    // Report preparing data
    if (mergedOptions.onProgress) {
      mergedOptions.onProgress(10, 'Preparing survey data...');
    }

    // Generate floor plan images if floor plan data is available
    const floorPlanImages: FloorPlanImageData[] = [];
    if (mergedOptions.includeFloorPlan && surveyData.floor_plan?.linkedFloorPlanData) {
      console.log('🎨 Generating floor plan canvas images...');
      
      if (mergedOptions.onProgress) {
        mergedOptions.onProgress(15, 'Processing floor plan data...');
      }
      try {
        const { generateEnhancedFloorPlanCanvas } = await import('../utils/exportUtils');
        const { calculateRooms } = await import('../utils/floorPlanUtils');
        let floorPlanData = surveyData.floor_plan.linkedFloorPlanData;
        
        // Extract and merge rooms from both sources
        // Priority: allFloorPlans (per-floor data with correct floor numbers) > linkedFloorPlanData.rooms > survey room_details
        let linkedRooms = floorPlanData.rooms || [];
        const surveyRooms = surveyData.room_details?.rooms || [];
        
        // Check if rooms have valid floor numbers (not all floor 1)
        const hasValidFloorNumbers = linkedRooms.length > 0 && 
          new Set(linkedRooms.map((r: any) => r.floor || 1)).size > 1;
        
        console.log('🔍 Room floor number check:', {
          roomCount: linkedRooms.length,
          uniqueFloors: Array.from(new Set(linkedRooms.map((r: any) => r.floor || 1))),
          hasValidFloorNumbers,
          hasAllFloorPlans: !!floorPlanData.allFloorPlans,
          hasFloorPlans: !!floorPlanData.floorPlans,
          willExtractFromMultiFloorData: !hasValidFloorNumbers && (floorPlanData.allFloorPlans || floorPlanData.floorPlans)
        });
        
        // If rooms don't have valid floor numbers, try to extract from multi-floor data
        // Check both allFloorPlans (canvas export) and floorPlans (retrofit save format)
        const multiFloorSource = floorPlanData.allFloorPlans || floorPlanData.floorPlans;
        
        if (!hasValidFloorNumbers && multiFloorSource) {
          console.log('📊 Extracting rooms from multi-floor data structure...');
          
          if (mergedOptions.onProgress) {
            mergedOptions.onProgress(20, 'Analyzing floor plan geometry...');
          }
          
          const roomsFromFloorPlans: any[] = [];
          
          Object.entries(multiFloorSource).forEach(([floorNum, floorData]: [string, any]) => {
            const floorNumInt = parseInt(floorNum);
            
            // Check if floorData has rooms (allFloorPlans format) or is just walls array (floorPlans format)
            if (floorData.rooms && Array.isArray(floorData.rooms)) {
              console.log(`  Floor ${floorNum}: ${floorData.rooms.length} rooms from allFloorPlans`);
              roomsFromFloorPlans.push(...floorData.rooms);
            } else if (Array.isArray(floorData)) {
              // floorPlans format: { 1: [walls], 2: [walls] } - need to calculate rooms
              console.log(`  Floor ${floorNum}: Computing rooms from ${floorData.length} walls`);
              try {
                const computedRooms = calculateRooms(
                  floorData,
                  floorPlanData.roomCustomLabels || {},
                  'geometryUpdate',
                  floorNumInt
                );
                
                // CRITICAL: Manually set floor property on computed rooms
                computedRooms.forEach((room: any) => {
                  room.floor = floorNumInt;
                });
                
                console.log(`    Computed ${computedRooms.length} rooms for floor ${floorNum} (floor property set)`);
                roomsFromFloorPlans.push(...computedRooms);
              } catch (error) {
                console.error(`    Failed to compute rooms for floor ${floorNum}:`, error);
              }
            }
          });
          
          if (roomsFromFloorPlans.length > 0) {
            linkedRooms = roomsFromFloorPlans;
            console.log('✅ Extracted rooms from multi-floor structure:', {
              roomCount: roomsFromFloorPlans.length,
              uniqueFloors: Array.from(new Set(roomsFromFloorPlans.map((r: any) => r.floor || 1))),
              rooms: roomsFromFloorPlans.map((r: any) => ({
                id: r.id || r.stableId,
                name: r.name,
                area: r.area,
                floor: r.floor || 'MISSING'
              }))
            });
          }
        }
        
        // If still no rooms, calculate from walls (LAST RESORT - loses floor info)
        if (linkedRooms.length === 0 && floorPlanData.floorPlans) {
          console.log('⚠️ Computing rooms from merged walls (will lose floor separation)...');
          const allWalls: any[] = [];
          Object.entries(floorPlanData.floorPlans).forEach(([floor, walls]) => {
            if (Array.isArray(walls)) {
              allWalls.push(...walls);
            }
          });
          
          if (allWalls.length > 0) {
            try {
              const computedRooms = calculateRooms(
                allWalls,
                floorPlanData.roomCustomLabels || {},
                'geometryUpdate',
                floorPlanData.currentFloor || 1
              );
              linkedRooms = computedRooms;
              console.log('⚠️ Computed rooms from merged walls (all will be floor 1):', {
                wallCount: allWalls.length,
                computedRoomCount: computedRooms.length,
                rooms: computedRooms.map((r: any) => ({
                  id: r.id || r.stableId,
                  name: r.name,
                  area: r.area,
                  floor: r.floor,
                  walls: r.walls?.length || 0
                }))
              });
            } catch (error) {
              console.error('❌ Failed to compute rooms from walls:', error);
            }
          }
        }
        
        console.log('🏠 Room sources comparison:', {
          linkedRoomsCount: linkedRooms.length,
          linkedRoomsAreas: linkedRooms.map((r: any) => ({ id: r.id || r.stableId, name: r.name, area: r.area, floor: r.floor })),
          surveyRoomsCount: surveyRooms.length,
          surveyRoomsAreas: surveyRooms.map((r: any) => ({ id: r.id, name: r.name, area: r.area, floor: r.floor }))
        });
        
        // Use linkedFloorPlanData rooms as primary source (matches canvas display)
        // Fall back to survey rooms only if linked rooms are empty
        const roomsToUse = linkedRooms.length > 0 ? linkedRooms : surveyRooms;
        
        console.log('🎯 Selected room source:', {
          source: linkedRooms.length > 0 ? 'linkedFloorPlanData.rooms (canvas)' : 'surveyData.room_details.rooms',
          count: roomsToUse.length,
          areas: roomsToUse.map((r: any) => ({ id: r.id || r.stableId, name: r.name, area: r.area, floor: r.floor }))
        });
        
        // CRITICAL: Always update floorPlanData.rooms with extracted rooms (even if transformation doesn't run)
        floorPlanData.rooms = roomsToUse;
        console.log('✅ Updated floorPlanData.rooms with extracted/selected rooms');
        
        // Transform data structure: floorPlans object -> flat arrays
        // The saved data has floorPlans: { 1: [...walls], 2: [...walls] }
        // But export expects currentWalls: [...walls]
        if (floorPlanData.floorPlans && !floorPlanData.currentWalls) {
          console.log('🔄 Transforming floorPlans structure to flat arrays for export');
          const allWalls: any[] = [];
          const allFloors = Object.keys(floorPlanData.floorPlans);
          
          Object.entries(floorPlanData.floorPlans).forEach(([floor, walls]) => {
            if (Array.isArray(walls)) {
              allWalls.push(...walls);
            }
          });
          
          floorPlanData = {
            ...floorPlanData,
            currentWalls: allWalls,
            elements: floorPlanData.elements || [],
            rooms: roomsToUse,
            textBoxes: floorPlanData.textBoxes || [],
            roomLabelPositions: floorPlanData.roomLabelPositions || {},
            roomCustomLabels: floorPlanData.roomCustomLabels || {}
          };
          
          console.log('✅ Transformed data:', {
            originalFloors: allFloors,
            currentWallsCount: allWalls.length,
            elementsCount: floorPlanData.elements?.length || 0,
            roomsCount: floorPlanData.rooms?.length || 0,
            roomLabelPositionsCount: Object.keys(floorPlanData.roomLabelPositions || {}).length,
            roomCustomLabelsCount: Object.keys(floorPlanData.roomCustomLabels || {}).length
          });
          
          console.log('🏷️ Room data details AFTER TRANSFORMATION (with floor numbers):', {
            rooms: floorPlanData.rooms?.map((r: any) => ({
              id: r.id || r.stableId,
              name: r.name,
              floor: r.floor || 'MISSING',
              area: r.area
            })),
            labelPositions: Object.entries(floorPlanData.roomLabelPositions || {}).map(([key, pos]: [string, any]) => ({
              roomKey: key,
              x: pos.x,
              y: pos.y
            })),
            customLabels: floorPlanData.roomCustomLabels
          });
        }
        
        // Build siteNotesData.floors structure if not present but multi-floor rooms detected
        const uniqueFloorNumbers: number[] = Array.from(new Set(roomsToUse.map((r: any) => r.floor || 1)));
        
        // Check if existing floors have valid data
        const hasValidFloorData = floorPlanData.siteNotesData?.floors?.length > 0 && 
          floorPlanData.siteNotesData.floors.some((f: any) => f.id && f.floorArea);
        
        console.log('🔍 Floor data validation:', {
          uniqueFloors: uniqueFloorNumbers,
          hasExistingFloors: !!floorPlanData.siteNotesData?.floors,
          existingFloorCount: floorPlanData.siteNotesData?.floors?.length || 0,
          hasFloorMetrics: !!(floorPlanData as any).floorMetrics,
          hasValidData: hasValidFloorData,
          willRebuild: uniqueFloorNumbers.length > 1 && !hasValidFloorData
        });
        
        // Check if we have floorMetrics from RetrofitFloorPlan (modern approach)
        const hasFloorMetrics = !!(floorPlanData as any).floorMetrics;
        
        if ((uniqueFloorNumbers.length >= 1 && !hasValidFloorData) || hasFloorMetrics) {
          console.log('🏗️ Building siteNotesData.floors structure from room floor numbers:', uniqueFloorNumbers);
          
          const floorLevelMap: Record<number, string> = {
            1: 'ground',
            2: 'first',
            3: 'second',
            4: 'third',
            5: 'fourth'
          };
          
          const allWalls = floorPlanData.currentWalls || [];
          
          floorPlanData.siteNotesData = floorPlanData.siteNotesData || {};
          
          // 🔧 CRITICAL: Preserve existing extension floors before rebuilding
          const existingSiteNotesData = floorPlanData.siteNotesData;
          const existingExtensionFloors = existingSiteNotesData.floors?.filter((f: any) => {
            const buildingType = (f.buildingType || '').toLowerCase();
            return buildingType.includes('ext') || buildingType === 'extension';
          }) || [];
          
          console.log('🔧 [PDF] Preserving extension floors before rebuild:', {
            totalExistingFloors: existingSiteNotesData.floors?.length || 0,
            extensionFloorsToPreserve: existingExtensionFloors.length,
            extensionFloors: existingExtensionFloors.map((f: any) => ({
              floorLevel: f.floorLevel,
              buildingType: f.buildingType,
              hasHighlighterBreakdown: !!f.highlighterBreakdown
            }))
          });
          
          floorPlanData.siteNotesData.floors = uniqueFloorNumbers.sort((a: number, b: number) => a - b).map((floorNum: number) => {
            const floorRooms = roomsToUse.filter((r: any) => r.floor === floorNum);
            const totalArea = floorRooms.reduce((sum: number, r: any) => sum + (r.area || 0), 0);
            
            // Use pre-calculated floorMetrics if available (from RetrofitFloorPlan)
            const savedMetrics = hasFloorMetrics ? (floorPlanData as any).floorMetrics[floorNum] : null;
            
            console.log(`🔍 [Floor ${floorNum}] Metrics lookup:`, {
              hasFloorMetrics,
              hasSavedMetrics: !!savedMetrics,
              floorMetricsKeys: (floorPlanData as any).floorMetrics ? Object.keys((floorPlanData as any).floorMetrics) : 'undefined',
              savedMetrics
            });
            
            let hlpSegments: number[] = [];
            let pwSegments: number[] = [];
            let hlpTotal = 0;
            let pwTotal = 0;
            
            if (savedMetrics) {
              console.log(`✅ Using pre-calculated floorMetrics for floor ${floorNum}:`, savedMetrics);
              hlpSegments = savedMetrics.hlpSegments || [];
              pwSegments = savedMetrics.pwSegments || [];
              hlpTotal = savedMetrics.hlpLength || 0;
              pwTotal = savedMetrics.pwLength || 0;
            } else {
              // Fallback: Calculate HLP and PW from wall sections
              console.log(`🔧 Calculating metrics from walls for floor ${floorNum}`);
              const floorWalls = allWalls.filter((w: any) => (w.floor || 1) === floorNum);
              const PIXELS_PER_METER = 100;
              
              floorWalls.forEach((wall: any) => {
                const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
                
                if (wall.sections && wall.sections.length > 0) {
                  wall.sections.forEach((section: any) => {
                    const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
                    const sectionLengthMeters = sectionPixelLength / PIXELS_PER_METER;
                    
                    if (section.type === 'HLP' || section.type === 'SW') {
                      hlpSegments.push(sectionLengthMeters);
                    } else if (section.type === 'PW') {
                      pwSegments.push(sectionLengthMeters);
                    }
                  });
                } else if (wall.type === 'HLP' || wall.type === 'SW') {
                  hlpSegments.push(wallPixelLength / PIXELS_PER_METER);
                } else if (wall.type === 'PW') {
                  pwSegments.push(wallPixelLength / PIXELS_PER_METER);
                }
              });
              
              hlpTotal = hlpSegments.reduce((sum, seg) => sum + seg, 0);
              pwTotal = pwSegments.reduce((sum, seg) => sum + seg, 0);
            }
            
            // Add length/width to rooms for area breakdown
            // Get room dimensions from survey's room_details
            const surveyRooms = surveyData?.room_details?.rooms || [];
            console.log(`📐 Floor ${floorNum} room dimension lookup:`, {
              floorRoomCount: floorRooms.length,
              floorRoomNames: floorRooms.map((r: any) => r.name),
              surveyRoomCount: surveyRooms.length,
              surveyRoomsForThisFloor: surveyRooms.filter((sr: any) => sr.floor === floorNum).map((sr: any) => ({
                name: sr.name,
                floor: sr.floor,
                length: sr.length,
                width: sr.width
              }))
            });
            
            const roomsWithDimensions = floorRooms.map((room: any) => {
              // Try to find matching room in survey data by name and floor
              const surveyRoom = surveyRooms.find(
                (sr: any) => sr.name === room.name && sr.floor === floorNum
              );
              
              if (surveyRoom && surveyRoom.length && surveyRoom.width) {
                // Use survey room dimensions if available
                console.log(`📐 Using survey dimensions for ${room.name}:`, {
                  length: surveyRoom.length,
                  width: surveyRoom.width
                });
                return {
                  ...room,
                  length: String(surveyRoom.length),
                  width: String(surveyRoom.width),
                  area: room.area || 0
                };
              } else if (room.points && room.points.length > 0) {
                // Fallback: Calculate bounding box to get dimensions
                const xs = room.points.map((p: any) => p.x);
                const ys = room.points.map((p: any) => p.y);
                const width = (Math.max(...xs) - Math.min(...xs)) / 100; // Convert pixels to meters
                const length = (Math.max(...ys) - Math.min(...ys)) / 100;
                
                console.log(`📐 Calculated bounding box for ${room.name}:`, {
                  length: length.toFixed(2),
                  width: width.toFixed(2)
                });
                return {
                  ...room,
                  length: length.toFixed(2),
                  width: width.toFixed(2),
                  area: room.area || 0
                };
              }
              console.log(`⚠️ No dimensions found for ${room.name}`);
              return room;
            });
            
            return {
              id: floorNum,
              floorNumber: floorNum,
              floorLevel: floorLevelMap[floorNum] || `floor ${floorNum}`,
              buildingType: 'main building',
              floorArea: totalArea.toFixed(2),
              hlp: hlpTotal.toFixed(2),
              pw: pwTotal.toFixed(2),
              rooms: roomsWithDimensions,  // Include rooms with dimensions
              highlighterBreakdown: {
                hlpSegments,
                pwSegments
              }
            };
          });
          
          // 🔧 CRITICAL: Re-add preserved extension floors
          if (existingExtensionFloors.length > 0) {
            console.log('🔧 [PDF] Re-adding preserved extension floors:', {
              mainBuildingFloorsCount: floorPlanData.siteNotesData.floors.length,
              extensionFloorsToAdd: existingExtensionFloors.length
            });
            
            floorPlanData.siteNotesData.floors = [
              ...floorPlanData.siteNotesData.floors,
              ...existingExtensionFloors
            ];
            
            console.log('✅ [PDF] Merged floors (main building + extensions):', {
              totalFloors: floorPlanData.siteNotesData.floors.length,
              floors: floorPlanData.siteNotesData.floors.map((f: any) => ({
                floorLevel: f.floorLevel,
                buildingType: f.buildingType,
                hasHighlighterBreakdown: !!f.highlighterBreakdown
              }))
            });
          }
          
          console.log('✅ Built siteNotesData.floors:', floorPlanData.siteNotesData.floors.map((f: any) => ({
            id: f.id,
            floorLevel: f.floorLevel,
            floorArea: f.floorArea,
            hlp: f.hlp,
            pw: f.pw
          })));
        }
        
        // Check if multi-floor data exists
        if (floorPlanData.siteNotesData?.floors && floorPlanData.siteNotesData.floors.length > 0) {
          console.log(`📐 Multi-floor plan detected: ${floorPlanData.siteNotesData.floors.length} floors`);
          
          if (mergedOptions.onProgress) {
            mergedOptions.onProgress(25, `Preparing ${floorPlanData.siteNotesData.floors.length} floor plans...`);
          }
          
          // Pre-calculate universal bounds for consistent scaling across all floors
          let universalMinX = Infinity, universalMinY = Infinity, universalMaxX = -Infinity, universalMaxY = -Infinity;
          
          for (const floor of floorPlanData.siteNotesData.floors) {
            const floorNumber = floor.floorNumber;
            const allWalls = floorPlanData.currentWalls || [];
            const allElements = floorPlanData.elements || [];
            const allTextBoxes = floorPlanData.textBoxes || [];
            
            const floorWalls = allWalls.filter((w: any) => w.floor === floorNumber);
            const floorElements = allElements.filter((e: any) => e.floor === floorNumber);
            const floorTextBoxes = allTextBoxes.filter((t: any) => t.floor === floorNumber);
            
            // Calculate bounds for this floor
            floorWalls.forEach((wall: any) => {
              universalMinX = Math.min(universalMinX, wall.start.x, wall.end.x);
              universalMinY = Math.min(universalMinY, wall.start.y, wall.end.y);
              universalMaxX = Math.max(universalMaxX, wall.start.x, wall.end.x);
              universalMaxY = Math.max(universalMaxY, wall.start.y, wall.end.y);
            });
            
            floorElements.forEach((el: any) => {
              if (el.position) {
                universalMinX = Math.min(universalMinX, el.position.x);
                universalMinY = Math.min(universalMinY, el.position.y);
                universalMaxX = Math.max(universalMaxX, el.position.x);
                universalMaxY = Math.max(universalMaxY, el.position.y);
              }
            });
            
            floorTextBoxes.forEach((box: any) => {
              if (box.position) {
                const boxMaxX = box.position.x + (box.width || 100);
                const boxMaxY = box.position.y + (box.height || 50);
                universalMinX = Math.min(universalMinX, box.position.x);
                universalMinY = Math.min(universalMinY, box.position.y);
                universalMaxX = Math.max(universalMaxX, boxMaxX);
                universalMaxY = Math.max(universalMaxY, boxMaxY);
              }
            });
          }
          
          // Add padding
          const padding = 30;
          universalMinX -= padding;
          universalMinY -= padding;
          universalMaxX += padding;
          universalMaxY += padding;
          
          const universalMaxWidth = universalMaxX - universalMinX;
          const universalMaxHeight = universalMaxY - universalMinY;
          
          const totalFloors = floorPlanData.siteNotesData.floors.length;
          for (let i = 0; i < floorPlanData.siteNotesData.floors.length; i++) {
            const floor = floorPlanData.siteNotesData.floors[i];
            const floorNumber = floor.id; // Use floor.id instead of floor.floorNumber
            
            // Report progress for each floor (25-45% range for multi-floor preparation)
            if (mergedOptions.onProgress) {
              const progressPerFloor = 20 / totalFloors;
              const currentProgress = 25 + (progressPerFloor * (i + 1));
              mergedOptions.onProgress(Math.round(currentProgress), `Rendering floor ${i + 1} of ${totalFloors}...`);
            }
            
            // Filter data for this floor - ensure all arrays exist
            const allWalls = floorPlanData.currentWalls || [];
            const allElements = floorPlanData.elements || [];
            const allRooms = floorPlanData.rooms || [];
            const allTextBoxes = floorPlanData.textBoxes || [];
            
            const floorWalls = allWalls.filter((w: any) => w.floor === floorNumber);
            const floorElements = allElements.filter((e: any) => e.floor === floorNumber);
            const floorRooms = allRooms.filter((r: any) => r.floor === floorNumber);
            const floorTextBoxes = allTextBoxes.filter((t: any) => t.floor === floorNumber);
            
            // Filter room label positions for this floor
            const floorRoomLabelPositions: Record<string, { x: number; y: number }> = {};
            if (floorPlanData.roomLabelPositions) {
              const floorRoomIds = new Set(floorRooms.map((r: any) => r.stableId || r.id));
              Object.entries(floorPlanData.roomLabelPositions).forEach(([key, pos]) => {
                if (floorRoomIds.has(key)) {
                  floorRoomLabelPositions[key] = pos as { x: number; y: number };
                }
              });
            }
            
            console.log(`📊 Floor ${floorNumber} data:`, {
              walls: floorWalls.length,
              elements: floorElements.length,
              rooms: floorRooms.length,
              textBoxes: floorTextBoxes.length
            });
            
            const floorExportData = {
              ...floorPlanData,
              currentWalls: floorWalls,
              elements: floorElements,
              rooms: floorRooms,
              textBoxes: floorTextBoxes,
              currentFloor: floorNumber,
              roomLabelPositions: floorRoomLabelPositions
            };
            
            const canvas = await generateEnhancedFloorPlanCanvas(floorExportData, universalMaxWidth, universalMaxHeight);
            if (canvas) {
              // Use PNG for floor plans to preserve transparency
              const imageDataUrl = canvas.toDataURL('image/png', 1.0);
              floorPlanImages.push({
                dataUrl: imageDataUrl,
                canvasWidth: canvas.width,
                canvasHeight: canvas.height
              });
            }
          }
        } else {
          // Single floor plan - ensure all required arrays exist
          console.log('📐 Single floor plan detected');
          
          if (mergedOptions.onProgress) {
            mergedOptions.onProgress(30, 'Rendering floor plan...');
          }
          
          const sanitizedData = {
            ...floorPlanData,
            currentWalls: floorPlanData.currentWalls || [],
            elements: floorPlanData.elements || [],
            rooms: roomsToUse,
            textBoxes: floorPlanData.textBoxes || [],
            roomLabelPositions: floorPlanData.roomLabelPositions || {},
            roomCustomLabels: floorPlanData.roomCustomLabels || {}
          };
          
          console.log('📊 Single floor data:', {
            walls: sanitizedData.currentWalls.length,
            elements: sanitizedData.elements.length,
            rooms: sanitizedData.rooms.length,
            textBoxes: sanitizedData.textBoxes.length,
            roomLabelPositionsCount: Object.keys(sanitizedData.roomLabelPositions).length,
            roomCustomLabelsCount: Object.keys(sanitizedData.roomCustomLabels).length
          });
          
          console.log('🏷️ Single floor room label data:', {
            rooms: sanitizedData.rooms.map((r: any) => ({
              id: r.id || r.stableId,
              name: r.name,
              area: r.area
            })),
            labelPositions: Object.entries(sanitizedData.roomLabelPositions).map(([key, pos]: [string, any]) => ({
              roomKey: key,
              x: pos?.x,
              y: pos?.y
            })),
            customLabels: sanitizedData.roomCustomLabels
          });
          
          const canvas = await generateEnhancedFloorPlanCanvas(sanitizedData);
          if (canvas) {
            // Use PNG for floor plans to preserve transparency
            const imageDataUrl = canvas.toDataURL('image/png', 1.0);
            floorPlanImages.push({
              dataUrl: imageDataUrl,
              canvasWidth: canvas.width,
              canvasHeight: canvas.height
            });
          }
        }
        
        // IMPORTANT: Save the transformed floorPlanData back to surveyData
        // This ensures computed rooms are available for the radiators & windows summary
        if (floorPlanData.rooms && floorPlanData.rooms.length > 0) {
          console.log('💾 Persisting computed rooms to surveyData:', {
            roomCount: floorPlanData.rooms.length,
            uniqueFloors: Array.from(new Set(floorPlanData.rooms.map((r: any) => r.floor || 1))),
            roomsWithFloors: floorPlanData.rooms.map((r: any) => ({
              id: r.id || r.stableId,
              name: r.name,
              floor: r.floor || 'MISSING',
              area: r.area
            }))
          });
          surveyData.floor_plan.linkedFloorPlanData = floorPlanData;
        }
        
      } catch (error) {
        console.error('❌ Error generating floor plan images:', error);
      }
    }
    
    console.log(`📊 Floor plan image generation complete: ${floorPlanImages.length} canvas(es) generated`);
    
    // Update options with generated images
    mergedOptions.floorPlanImages = floorPlanImages;

    // Generate wall insulation elevation diagram images if Wall Insulation is selected
    const elevationImages: ElevationImageData[] = [];
    const isWallInsulationSelected = surveyData.measure_specification?.suitableMeasures?.includes('Wall Insulation');
    
    console.log('🔍 [PDF] Wall Insulation check:', {
      isSelected: isWallInsulationSelected,
      suitableMeasures: surveyData.measure_specification?.suitableMeasures,
      hasElevations: !!surveyData.measure_specification?.wallInsulationElevations,
      elevationsCount: surveyData.measure_specification?.wallInsulationElevations?.length || 0
    });
    
    if (isWallInsulationSelected &&
        surveyData.measure_specification?.wallInsulationElevations && 
        surveyData.measure_specification.wallInsulationElevations.length > 0) {
      console.log('✅ [PDF] Wall Insulation selected, generating elevation diagrams...');
      
      if (mergedOptions.onProgress) {
        mergedOptions.onProgress(40, 'Processing elevation diagrams...');
      }

      try {
        // Calculate actual floor count from site notes data (main building only, not extensions)
        const dwellingType = surveyData.initial_questionnaire?.dwellingType || 'Detached';
        const isBungalow = dwellingType.toLowerCase().includes('bungalow');
        
        let calculatedFloorCount = 1; // Default to single storey
        if (!isBungalow) {
          // PRIORITY: Use site notes floor data to count only main building floors
          const linkedFloorPlanData = surveyData.floor_plan?.linkedFloorPlanData;
          if (linkedFloorPlanData?.siteNotesData?.floors) {
            const mainBuildingFloors = linkedFloorPlanData.siteNotesData.floors.filter((f: any) => f.buildingType === 'main building');
            calculatedFloorCount = mainBuildingFloors.length || 1;
          } else if (surveyData.room_details?.rooms) {
            // Fallback: Calculate from room data if no site notes data available
            calculatedFloorCount = Math.max(...surveyData.room_details.rooms.map(r => r.floor || 1));
          }
        }
        
        console.log('🏠 [PDF] Building height calculation:', {
          dwellingType,
          isBungalow,
          hasSiteNotesData: !!surveyData.floor_plan?.linkedFloorPlanData?.siteNotesData?.floors,
          totalFloors: surveyData.floor_plan?.linkedFloorPlanData?.siteNotesData?.floors?.length || 0,
          mainBuildingFloors: surveyData.floor_plan?.linkedFloorPlanData?.siteNotesData?.floors?.filter((f: any) => f.buildingType === 'main building').length || 0,
          calculatedFloorCount,
          finalFloorCount: isBungalow ? 1 : calculatedFloorCount
        });
        
        // Extract property data for elevation rendering
        const propertyData = {
          propertyType: dwellingType,
          roofTypeRdSAP: surveyData.ventilation_condition?.roofType || 'Pitched roof',
          groundLevel: 'Ground level', // Default
          detachment: dwellingType,
          floorCount: isBungalow ? 1 : calculatedFloorCount // Bungalows are always single-storey
        };

        for (const elevation of surveyData.measure_specification.wallInsulationElevations) {
          if (elevation.placedElements && elevation.placedElements.length > 0) {
            console.log(`🖼️ Generating elevation diagram for: ${elevation.type}`);
            
            const elevationData = {
              type: elevation.type,
              dimensions: elevation.dimensions || { height: 5, width: 5 },
              placedElements: elevation.placedElements as Array<{ id: string; type: 'window' | 'door' | 'airvent'; x: number; y: number; elevation: string; height?: number; width?: number; }>,
              wallInsulationType: surveyData.measure_specification.wallInsulationType,
              buildingHeight: propertyData.floorCount // Use floorCount as building height
            };

            const canvas = await generatePropertyElevationCanvas(elevationData, propertyData);
            
            if (canvas) {
              const imageDataUrl = canvas.toDataURL('image/png', 1.0);
              // Extract element details for the data table
              const doors = (elevation.elements?.doors || []).map((door, idx) => ({
                id: `D${idx + 1}`,
                height: door.height || 2.0,
                width: door.width || 0.89,
                area: (door.height || 2.0) * (door.width || 0.89)
              }));
              
              const windows = (elevation.elements?.windows || []).map((window, idx) => ({
                id: `W${idx + 1}`,
                height: window.height || 1.0,
                width: window.width || 1.5,
                area: (window.height || 1.0) * (window.width || 1.5)
              }));
              
              const airVents = (elevation.elements?.airVents || []).map((vent, idx) => ({
                id: `V${idx + 1}`
              }));
              
              const totalArea = elevationData.dimensions.height * elevationData.dimensions.width;
              
              elevationImages.push({
                dataUrl: imageDataUrl,
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
                elevationType: elevation.type,
                elevationData: {
                  dimensions: elevationData.dimensions,
                  treatableArea: elevation.calculations?.treatableArea || 0,
                  untreatableArea: elevation.calculations?.totalElementsArea || 0,
                  totalArea: totalArea,
                  elements: {
                    doors,
                    windows,
                    airVents
                  }
                }
              });
              console.log(`✅ Generated ${elevation.type} elevation diagram`);
            }
          }
        }
      } catch (error) {
        console.error('❌ Error generating elevation diagrams:', error);
      }
      
      console.log(`📊 Elevation diagram generation complete: ${elevationImages.length} diagram(s) generated`);
    } else {
      if (!isWallInsulationSelected) {
        console.log('⏭️ [PDF] Skipping wall insulation elevation diagrams (Wall Insulation not selected in measure specification)');
      } else if (!surveyData.measure_specification?.wallInsulationElevations || surveyData.measure_specification.wallInsulationElevations.length === 0) {
        console.log('⏭️ [PDF] Skipping wall insulation elevation diagrams (no elevations data available)');
      }
    }
    
    // Update options with generated elevation images
    mergedOptions.elevationImages = elevationImages;

    console.log('📋 Generating HTML content for retrofit survey...');
    console.log('🖼️  Optimizing photos for PDF (resizing to 1600x1200 and compressing to 90% WebP quality for sharp details)...');
    
    if (mergedOptions.onProgress) {
      mergedOptions.onProgress(45, 'Building report sections...');
    }
    
    console.log('🎬 EXPORT: Calling generateRetrofitSurveyHTML...');
    const { html: htmlContent, sectionPageMap } = await generateRetrofitSurveyHTML(surveyData, mergedOptions);
    console.log('🎬 EXPORT: generateRetrofitSurveyHTML completed, HTML length:', htmlContent.length);
    console.log('✅ Photo optimization complete - WebP format provides 70-80% file size reduction with high quality');

    // Generate filenames using full address
    const { getRetrofitSurveyAddress } = await import('../utils/retrofitSurveyStorage');
    const fullAddress = getRetrofitSurveyAddress(surveyData);
    const sanitizedAddress = fullAddress
      .replace(/[^a-zA-Z0-9\s\-_,]/g, '') // Remove special chars except comma, space, dash, underscore
      .replace(/\s+/g, '-')                // Replace spaces with dashes
      .replace(/-+/g, '-')                 // Replace multiple dashes with single dash
      .substring(0, 100);                  // Limit length
    const baseFilename = sanitizedAddress || `retrofit-survey-${Date.now()}`;
    const pdfFilename = `${baseFilename}.pdf`;
    const htmlFilename = `${baseFilename}.html`;

    // Use format from options or default to 'html'
    const isMobile = Capacitor.isNativePlatform();
    const userChoice = options.format || 'html'; // Use the format selected in the modern modal

    // Generate PDF if needed
    let pdfBlob: Blob | null = null;
    if (userChoice === 'pdf' || userChoice === 'both') {
      console.log('📄 Generating PDF...');
      
      if (options.onProgress) {
        options.onProgress(75, 'Converting to PDF format...');
      }
      
      // Create progress wrapper for PDF conversion
      const pdfProgressCallback = userChoice === 'both' 
        ? (progress: number, stage: string) => {
            // For 'both' mode, PDF conversion is 75-95% (20% of total)
            const adjustedProgress = 75 + (progress * 0.20);
            options.onProgress?.(adjustedProgress, stage);
          }
        : (progress: number, stage: string) => {
            // For PDF-only, PDF conversion is 75-100% (25% of total)
            const adjustedProgress = 75 + (progress * 0.25);
            options.onProgress?.(adjustedProgress, stage);
          };
      
      pdfBlob = await convertHTMLToPDFBlob(htmlContent, pdfFilename, sectionPageMap, options.formTemplate, pdfProgressCallback);
    }

    // Generate HTML if needed
    let htmlBlob: Blob | null = null;
    if (userChoice === 'html' || userChoice === 'both') {
      console.log('🌐 Generating interactive HTML (fully offline with embedded images)...');
      
      if (userChoice === 'both') {
        // For 'both' mode, HTML generation is 95-100%
        options.onProgress?.(95, 'Finalizing HTML...');
      } else {
        // For HTML-only, finalization is 75-100%
        options.onProgress?.(75, 'Finalizing HTML...');
      }
      
      htmlBlob = new Blob([htmlContent], { type: 'text/html' });
      
      // HTML generation is fast, so jump to completion
      const finalProgress = userChoice === 'both' ? 100 : 100;
      options.onProgress?.(finalProgress, 'HTML complete!');
    }

    // Handle mobile vs desktop
    if (isMobile) {
      // Mobile: Save and share
      const filesToShare: string[] = [];
      
      if (pdfBlob) {
        const base64Data = await blobToBase64(pdfBlob);
        const base64WithoutPrefix = base64Data.split(',')[1];
        const result = await Filesystem.writeFile({
          path: pdfFilename,
          data: base64WithoutPrefix,
          directory: Directory.Documents
        });
        filesToShare.push(result.uri);
        console.log('📱 PDF saved on mobile:', result.uri);
      }
      
      if (htmlBlob) {
        const htmlText = await htmlBlob.text();
        const result = await Filesystem.writeFile({
          path: htmlFilename,
          data: htmlText,
          directory: Directory.Documents,
          encoding: undefined
        });
        filesToShare.push(result.uri);
        console.log('📱 HTML saved on mobile:', result.uri);
      }

      // Share the files
      if (filesToShare.length === 1) {
        await Share.share({
          title: 'Retrofit Survey Report',
          text: 'Here is your retrofit survey report',
          url: filesToShare[0],
          dialogTitle: 'Share Retrofit Survey Report'
        });
      } else {
        await Share.share({
          title: 'Retrofit Survey Reports',
          text: 'Here are your retrofit survey reports',
          files: filesToShare,
          dialogTitle: 'Share Retrofit Survey Reports'
        });
      }
    } else {
      // Desktop: Download
      if (pdfBlob) {
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = pdfFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('💻 PDF downloaded:', pdfFilename);
      }
      
      if (htmlBlob) {
        const url = URL.createObjectURL(htmlBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = htmlFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('💻 HTML downloaded:', htmlFilename);
      }
    }

    console.log('✅ Retrofit survey export completed successfully');
  } catch (error) {
    console.error('❌ Error exporting retrofit survey PDF:', error);
    throw error;
  }
}

/**
 * Helper to count photos in a section
 */
function countPhotosInSection(photos: any, photoKeys: string[]): number {
  let count = 0;
  for (const key of photoKeys) {
    const sectionPhotos = photos[key]?.photos || [];
    count += sectionPhotos.length;
  }
  return count;
}

/**
 * Helper to count loft photos in measure_specification
 */
function getLoftPhotoCount(measure_specification: any): number {
  const loftPhotoSections = [
    'mainPropertyInsulationDepthEvidence',
    'mainLoftHatchEvidence',
    'mainPropertyRoofPitchEvidence',
    'mainLoftAreaImages',
    'apexMeasurementEvidence',
    'partyWallEvidence',
    'downlightImage',
    'coldWaterTankPipeworkEvidence',
    'roofVentsImage'
  ];
  
  let count = 0;
  for (const section of loftPhotoSections) {
    const sectionPhotos = measure_specification[section];
    if (Array.isArray(sectionPhotos)) {
      count += sectionPhotos.length;
    }
  }
  return count;
}

/**
 * Helper to count RIR photos in measure_specification
 */
function getRIRPhotoCount(measure_specification: any): number {
  const rirPhotoSections = [
    'rirExteriorElevations',
    'rirInternalRoomPhotos',
    'rirFlatCeilingInsulation',
    'rirSlopingCeilingInsulation',
    'rirStudWallsInsulation',
    'rirDormerPhotos'
  ];
  
  let count = 0;
  for (const section of rirPhotoSections) {
    const sectionPhotos = measure_specification[section];
    if (Array.isArray(sectionPhotos)) {
      count += sectionPhotos.length;
    }
  }
  return count;
}

/**
 * Generate complete HTML content for the retrofit survey
 */
async function generateRetrofitSurveyHTML(
  surveyData: RetrofitSurveyData,
  options: RetrofitPDFExportOptions
): Promise<{ html: string; sectionPageMap: SectionPageMap }> {
  console.log('🎯 generateRetrofitSurveyHTML STARTED');
  
  // Calculate number of photo pages for heating system (6 photos per page)
  const heatingPhotos = surveyData.heating_system?.photos || {};
  const heatingPhotoTypes = ['currentBoilerImage', 'boilerDataBadgeImage', 'fluePipeImage', 'condensatePipeImage', 'oilLpgTankImage', 'renewablesImage', 'cylinderWaterHeatingImage'];
  const heatingPhotoCount = countPhotosInSection(heatingPhotos, heatingPhotoTypes);
  const heatingPhotoPageCount = heatingPhotoCount > 0 ? Math.ceil(heatingPhotoCount / 6) : 0; // 6 photos per page

  // Calculate number of photo pages for Loft insulation (6 photos per page)
  const loftPhotoCount = getLoftPhotoCount(surveyData.measure_specification || {});
  const loftPhotoPageCount = loftPhotoCount > 0 ? Math.ceil(loftPhotoCount / 6) : 0; // 6 photos per page

  // Calculate number of photo pages for RIR insulation (6 photos per page)
  const rirPhotoCount = getRIRPhotoCount(surveyData.measure_specification || {});
  const rirPhotoPageCount = rirPhotoCount > 0 ? Math.ceil(rirPhotoCount / 6) : 0; // 6 photos per page
  
  console.log('📸 [PDF] Photo page counts:', {
    heating: { count: heatingPhotoCount, pages: heatingPhotoPageCount },
    loft: { count: loftPhotoCount, pages: loftPhotoPageCount },
    rir: { count: rirPhotoCount, pages: rirPhotoPageCount }
  });

  // Calculate total pages
  let totalPages = 1; // Cover page
  
  // Table of Contents pages (13 items per page)
  // Count how many sections will be in TOC (max 16 sections)
  let tocSectionCount = 1; // Property Overview always exists
  if (options.includeFloorPlan && options.floorPlanImages && options.floorPlanImages.length > 0) tocSectionCount++;
  // Radiators & Windows (will be checked below)
  // Dimensions, Heating, Measure Spec - always exist
  tocSectionCount += 3;
  // Property sections, Rooms, Extensions, Meters, Ventilation, Occupancy, Site, Notes - will be counted below
  // For now, assume max sections and we'll refine in calculateSectionPages
  const estimatedTocSections = 16; // Maximum possible sections
  const tocPageCount = Math.ceil(estimatedTocSections / 13);
  totalPages += tocPageCount;
  
  if (options.includeFloorPlan && options.floorPlanImages && options.floorPlanImages.length > 0) {
    totalPages += calculateFloorPlanPageCount(options.floorPlanImages);
  }
  // Formula & Working section removed - using All Floors table instead
  // Check if radiators & windows summary should be included
  const hasRadiatorsOrWindows = surveyData.floor_plan?.linkedFloorPlanData?.elements && 
    surveyData.floor_plan.linkedFloorPlanData.elements.length > 0 &&
    surveyData.floor_plan.linkedFloorPlanData.elements.some((el: any) => 
      el.type === 'radiator' || el.type === 'window' || el.type === 'bayWindow'
    );
  if (hasRadiatorsOrWindows) {
    totalPages++; // Radiators & Windows Summary
  }
  totalPages++; // Main property measurements
  totalPages++; // Occupancy assessment
  
  // Site Assessment (1-3 pages depending on data)
  const siteAssessmentHasData = surveyData.site_assessment && Object.values(surveyData.site_assessment).some(v => v !== null && v !== undefined && v !== '');
  totalPages += siteAssessmentHasData ? 3 : 1; // 3 pages if data exists, 1 page if empty
  console.log(`📊 [PDF] Adding ${siteAssessmentHasData ? 3 : 1} Site Assessment page(s)`);
  
  // Signatures & Feedback (4 pages)
  const hasSignaturesData = surveyData.signatures && (surveyData.signatures.customer || surveyData.signatures.surveyor || surveyData.signatures.feedback);
  if (hasSignaturesData) {
    totalPages += 4; // Pre-Install Questions + PAS 2030 Questions + Customer Signatures + Surveyor Signatures
    console.log('📊 [PDF] Adding 4 Signatures & Feedback pages');
  }
  
  totalPages += heatingPhotoPageCount; // Heating system photo pages (6 per page)
  // Measure Specification (only if Wall Insulation has actual content)
  // Note: Loft, RIR, and Heating System measures have their own separate pages
  const hasWallInsulationContent = surveyData.measure_specification?.suitableMeasures?.includes('Wall Insulation')
    && surveyData.measure_specification.wallInsulationType
    && options.elevationImages
    && options.elevationImages.length > 0;
  if (hasWallInsulationContent) {
    totalPages++; // Measure specification page (includes first 2 elevations)
    console.log('📊 [PDF] Adding Measure Specification page (Wall Insulation)');
  }
  // Wall insulation continuation pages (2 elevations per page after the first 2)
  if (options.elevationImages && options.elevationImages.length > 2) {
    const remainingElevations = options.elevationImages.length - 2;
    const continuationPages = Math.ceil(remainingElevations / 2);
    totalPages += continuationPages;
    console.log(`📊 [PDF] Adding ${continuationPages} wall insulation continuation page(s) for ${remainingElevations} additional elevations`);
  }
  // PAS 2030 Measures Questions page (shows after measure specification)
  const hasPAS2030Measures = surveyData.measure_specification?.suitableMeasures?.some(
    m => m === 'Heating System' || m === 'Wall Insulation' || m === 'RIR insulation'
  );
  if (hasPAS2030Measures) {
    totalPages++; // PAS 2030 Measures Questions page
    console.log('📊 [PDF] Adding PAS 2030 Measures Questions page');
  }
  
  // Custom pages from form builder
  if (options.formTemplate && options.customFieldsData) {
    console.log(`📊 [PDF] Checking formTemplate:`, {
      hasCustomPages: !!options.formTemplate.customPages,
      customPagesCount: options.formTemplate.customPages?.length || 0,
      hasPagesArray: !!options.formTemplate.pages,
      pagesCount: options.formTemplate.pages?.length || 0
    });
    
    const customPagesCount = calculateTotalCustomPagesCount(options.formTemplate, options.customFieldsData);
    if (customPagesCount > 0) {
      totalPages += customPagesCount;
      console.log(`📊 [PDF] Adding ${customPagesCount} custom page(s) from form builder`);
    } else {
      console.log(`📊 [PDF] No custom pages to add (count: ${customPagesCount})`);
    }
  }
  
  // Heating System Measure page (separate page, different from general heating system assessment)
  if (surveyData.measure_specification?.suitableMeasures?.includes('Heating System')) {
    totalPages++; // Heating System measure page
    console.log('📊 [PDF] Adding Heating System measure page');
  }
  // Loft Insulation Assessment page (separate page)
  if (surveyData.measure_specification?.suitableMeasures?.includes('Loft insulation')) {
    totalPages++; // Loft insulation assessment page
    console.log('📊 [PDF] Adding Loft Insulation assessment page');
  }
  // Loft Photo Pages (6 photos per page) - only if Loft insulation selected
  if (loftPhotoPageCount > 0 && surveyData.measure_specification?.suitableMeasures?.includes('Loft insulation')) {
    totalPages += loftPhotoPageCount;
    console.log(`📊 [PDF] Adding ${loftPhotoPageCount} loft photo page(s) for ${loftPhotoCount} photos`);
  }
  // RIR Insulation Assessment page (separate from RIR photos)
  if (surveyData.measure_specification?.suitableMeasures?.includes('RIR insulation') && surveyData.measure_specification.roomInRoofData) {
    totalPages++; // RIR insulation assessment page
    console.log('📊 [PDF] Adding RIR Insulation assessment page');
  }
  // RIR Photo Pages (6 photos per page) - only if RIR insulation selected
  if (rirPhotoPageCount > 0 && surveyData.measure_specification?.suitableMeasures?.includes('RIR insulation')) {
    totalPages += rirPhotoPageCount;
    console.log(`📊 [PDF] Adding ${rirPhotoPageCount} RIR photo page(s) for ${rirPhotoCount} photos`);
  }
  // Extensions & Conservatory section
  const extensionPhotoCount = getExtensionPhotoCount(surveyData.extensions);
  const extensionPhotoPageCount = extensionPhotoCount > 0 ? Math.ceil(extensionPhotoCount / 6) : 0;
  const hasExtensionsData = surveyData.extensions && 
    (surveyData.extensions.existingExtensions?.length > 0 || surveyData.extensions.hasConservatory);
  if (hasExtensionsData) {
    totalPages++; // Extensions summary page
    totalPages += extensionPhotoPageCount; // Extensions photo pages (6 per page)
    console.log(`📊 [PDF] Adding 1 extensions summary page + ${extensionPhotoPageCount} photo page(s) for ${extensionPhotoCount} photos`);
  }
  // Room details section
  const roomDetailsPageCount = await calculateRoomDetailsPages(surveyData);
  totalPages += roomDetailsPageCount;
  // Property elevation sections (includes summary + detail pages)
  const propertyFrontPageCount = await calculatePropertyFrontPages(surveyData);
  if (propertyFrontPageCount > 0) totalPages++; // Summary page
  totalPages += propertyFrontPageCount;
  
  const propertyRearPageCount = hasPropertyRearData(surveyData.property_rear) 
    ? await calculatePropertyElevationPages(surveyData.property_rear) 
    : 0;
  if (propertyRearPageCount > 0) totalPages++; // Summary page
  totalPages += propertyRearPageCount;
  
  // Cellar section (calculate pages dynamically based on photos)
  const cellarPageCount = await calculateCellarPages(surveyData);
  totalPages += cellarPageCount;
  
  const propertyLeftPageCount = hasPropertySideData(surveyData.property_left)
    ? await calculatePropertyElevationPages(surveyData.property_left)
    : 0;
  if (propertyLeftPageCount > 0) totalPages++; // Summary page
  totalPages += propertyLeftPageCount;
  
  const propertyRightPageCount = hasPropertySideData(surveyData.property_right)
    ? await calculatePropertyElevationPages(surveyData.property_right)
    : 0;
  if (propertyRightPageCount > 0) totalPages++; // Summary page
  totalPages += propertyRightPageCount;
  
  // Meters & Services section
  const metersServicesPageCount = await calculateMetersServicesPages(surveyData);
  if (surveyData.initial_questionnaire && hasMetersServicesData(surveyData.initial_questionnaire)) {
    totalPages++; // Summary page (always added if data exists)
  }
  totalPages += metersServicesPageCount; // Photo pages
  
  // Ventilation & Condition section
  const ventilationConditionPageCount = await calculateVentilationConditionPages(surveyData);
  totalPages += ventilationConditionPageCount;
  
  if (surveyData.initial_questionnaire?.additionalNotes && surveyData.initial_questionnaire.additionalNotes.trim().length > 0) {
    totalPages++; // Final notes
  }
  
  // Generate optimized photo pages (await async operations)
  // Note: We pass placeholder page numbers (1), they'll be updated during HTML generation
  const heatingSystemPages = await generateHeatingSystemPages(surveyData, 1, totalPages, options);
  
  // Generate Loft photo pages (6 photos per page)
  const loftPhotoPages = await generateLoftPhotoPages(surveyData, 1, totalPages, options);
  console.log(`📸 [PDF] Loft photo pages generated: ${loftPhotoPages.length} pages`);

  // Generate RIR photo pages (6 photos per page)
  const rirPhotoPages = await generateRIRPhotoPages(surveyData, 1, totalPages, options);
  console.log(`📸 [PDF] RIR photo pages generated: ${rirPhotoPages.length} pages`);
  
  // Pre-generate measure specification section (async for image optimization)
  const measureSpecificationSection = await generateMeasureSpecificationSection(surveyData, options.elevationImages);
  console.log(`📋 [PDF] Measure specification section generated: ${measureSpecificationSection.length > 0 ? 'YES' : 'NO'}`);
  
  // Pre-generate Heating System measure section as a separate page
  const heatingSystemMeasureSection = generateHeatingSystemMeasureSection(surveyData);
  console.log(`📋 [PDF] Heating System measure section generated: ${heatingSystemMeasureSection.length > 0 ? 'YES' : 'NO'}`);

  // Pre-generate Loft Insulation section as a separate page
  const loftInsulationSection = generateLoftInsulationSection(surveyData);
  const hasLoftMeasure = surveyData.measure_specification?.suitableMeasures?.includes('Loft insulation');
  console.log(`📋 [PDF] Loft section - Measure selected: ${hasLoftMeasure}, HTML generated: ${loftInsulationSection.length > 0}${loftInsulationSection.length > 0 ? ` (${loftInsulationSection.length} chars)` : ''}`);
  
  // Pre-generate RIR insulation section as a separate page (async for image optimization)
  const rirInsulationSection = await generateRIRInsulationSection(surveyData);
  const hasRIRMeasure = surveyData.measure_specification?.suitableMeasures?.includes('RIR insulation');
  const hasRIRData = !!surveyData.measure_specification?.roomInRoofData;
  console.log(`📋 [PDF] RIR section - Measure selected: ${hasRIRMeasure}, Has data: ${hasRIRData}, HTML generated: ${rirInsulationSection.length > 0}${rirInsulationSection.length > 0 ? ` (${rirInsulationSection.length} chars)` : ''}`);
  
  console.log(`📋 [PDF] Suitable measures:`, surveyData.measure_specification?.suitableMeasures || 'NONE');
  
  // Pre-generate Site Assessment pages (1-2 pages with full questions)
  console.log(`📋 [PDF] Generating site assessment pages...`);
  console.log(`📋 [PDF] site_assessment exists:`, !!surveyData.site_assessment);
  if (surveyData.site_assessment) {
    console.log(`📋 [PDF] site_assessment data:`, Object.keys(surveyData.site_assessment).length, 'keys');
    console.log(`📋 [PDF] site_assessment sample:`, JSON.stringify(surveyData.site_assessment).substring(0, 200));
  }
  const siteAssessmentPages = generateSiteAssessmentPages(surveyData);
  console.log(`📋 [PDF] Site assessment pages generated: ${siteAssessmentPages.length} page(s)`);
  
  // Pre-generate Extensions section
  const extensionsSection = generateExtensionsSection(surveyData);
  console.log(`📋 [PDF] Extensions section generated: ${extensionsSection.length > 0 ? 'YES' : 'NO'}`);
  
  // Generate Extensions photo pages (6 photos per page)
  const extensionsPhotoPages = await generateExtensionsPhotoPages(surveyData, 1, totalPages, options);
  console.log(`📸 [PDF] Extensions photo pages generated: ${extensionsPhotoPages.length} pages`);
  
  console.log('🏠 Calling generateRoomDetailsPages...');
  console.log('🔍 Survey data check before room details:', {
    hasRoomDetails: !!surveyData.room_details,
    roomCount: surveyData.room_details?.rooms?.length || 0,
    firstRoom: surveyData.room_details?.rooms?.[0]
  });
  const roomDetailsPages = await generateRoomDetailsPages(surveyData, 1, totalPages, options);
  console.log(`📊 Room details pages generated: ${roomDetailsPages.length} pages`);
  
  const propertyFrontPages = await generatePropertyFrontPages(surveyData, 1, totalPages, options);
  const propertyRearPages = await generatePropertyElevationPages(surveyData, 'rear', 1, totalPages, options);
  const propertyLeftPages = await generatePropertyElevationPages(surveyData, 'left', 1, totalPages, options);
  const propertyRightPages = await generatePropertyElevationPages(surveyData, 'right', 1, totalPages, options);
  const cellarPages = await generateCellarPages(surveyData, 1, totalPages, options);
  console.log(`📊 Cellar pages generated: ${cellarPages.length} pages`);
  
  console.log('🔥 MAIN: About to call generateMetersServicesPages');
  const metersServicesPages = await generateMetersServicesPages(surveyData, 1, totalPages, options);
  console.log('🔥 MAIN: generateMetersServicesPages returned', metersServicesPages.length, 'pages');
  
  const ventilationConditionPages = await generateVentilationConditionPages(surveyData, 1, totalPages, options);
  console.log('🔥 MAIN: generateVentilationConditionPages returned', ventilationConditionPages.length, 'pages');

  /**
   * Helper: Check if a built-in section should be included in PDF
   * Respects both data availability and hiddenSections configuration
   */
  const shouldIncludeSection = (sectionId: string): boolean => {
    // First check if section is explicitly hidden via form template
    if (options.formTemplate?.hiddenSections) {
      const allHiddenSections = Object.values(options.formTemplate.hiddenSections).flat();
      if (allHiddenSections.includes(sectionId)) {
        console.log(`⚠️ [PDF] Section "${sectionId}" is hidden via form template configuration`);
        return false;
      }
    }

    // Then check if section has data (existing logic)
    switch (sectionId) {
      case BUILT_IN_SECTION_IDS.FLOOR_PLAN:
        // Include floor plan section even without images (will show placeholder)
        return !!options.includeFloorPlan;
      case BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS:
        return hasRadiatorsOrWindows;
      case BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS:
        return true; // Always included
      case BUILT_IN_SECTION_IDS.HEATING_SYSTEM:
        // Always include if heating system data exists
        return !!(surveyData.heating_system);
      case BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION:
        return !!measureSpecificationSection;
      case BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS:
        return !!(surveyData.measure_specification?.suitableMeasures?.some(
          m => m === 'Heating System' || m === 'Wall Insulation' || m === 'RIR insulation'
        ));
      case BUILT_IN_SECTION_IDS.PROPERTY_FRONT:
        return !!(surveyData.property_front && hasPropertyFrontData(surveyData.property_front));
      case BUILT_IN_SECTION_IDS.PROPERTY_SIDE:
        return !!((surveyData.property_left && hasPropertySideData(surveyData.property_left)) || 
                  (surveyData.property_right && hasPropertySideData(surveyData.property_right)));
      case BUILT_IN_SECTION_IDS.PROPERTY_REAR:
        return !!(surveyData.property_rear && hasPropertyRearData(surveyData.property_rear));
      case BUILT_IN_SECTION_IDS.CELLAR:
        // Always include if cellar pages were generated
        return cellarPages.length > 0 || !!(surveyData as any).cellar;
      case BUILT_IN_SECTION_IDS.ROOMS:
        // Always include - will show "No data" message if empty
        return !!(surveyData.room_details);
      case BUILT_IN_SECTION_IDS.EXTENSIONS:
        return !!extensionsSection;
      case BUILT_IN_SECTION_IDS.METERS_SERVICES:
        return !!(surveyData.initial_questionnaire && hasMetersServicesData(surveyData.initial_questionnaire));
      case BUILT_IN_SECTION_IDS.VENTILATION_CONDITION:
        // Always include if ventilation pages or data exists
        return ventilationConditionPages.length > 0 || !!surveyData.initial_questionnaire;
      case BUILT_IN_SECTION_IDS.OCCUPANCY:
        return true; // Always included
      case BUILT_IN_SECTION_IDS.SITE_ASSESSMENT:
        // Always include if site assessment data exists
        return !!(surveyData.site_assessment);
      case BUILT_IN_SECTION_IDS.SIGNATURES:
        return !!hasSignaturesData; // Only include if signature data exists
      case BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES:
        return !!(surveyData.initial_questionnaire?.additionalNotes && 
                  surveyData.initial_questionnaire.additionalNotes.trim().length > 0);
      default:
        return false;
    }
  };

  /**
   * Helper: Get page count for a built-in section
   */
  const getSectionPageCount = (sectionId: string): number => {
    switch (sectionId) {
      case BUILT_IN_SECTION_IDS.FLOOR_PLAN:
        return calculateFloorPlanPageCount(options.floorPlanImages || []);
      case BUILT_IN_SECTION_IDS.HEATING_SYSTEM:
        return heatingSystemPages.length > 0 ? heatingSystemPages.length : (surveyData.heating_system ? 1 : 0);
      case BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION:
        // Includes measure spec page + wall insulation continuation pages + other measure pages
        let count = 1; // Main measure spec page
        // Wall insulation continuation pages
        if (options.elevationImages && options.elevationImages.length > 2) {
          const remainingElevations = options.elevationImages.length - 2;
          count += Math.ceil(remainingElevations / 2);
        }
        // Heating system measure page
        if (heatingSystemMeasureSection) count++;
        // Loft insulation page
        if (loftInsulationSection) count++;
        // Loft photos
        count += loftPhotoPages.length;
        // RIR insulation page
        if (rirInsulationSection) count++;
        // RIR photos
        count += rirPhotoPages.length;
        return count;
      case BUILT_IN_SECTION_IDS.PROPERTY_FRONT:
        return 1 + propertyFrontPages.length; // Summary + details
      case BUILT_IN_SECTION_IDS.PROPERTY_SIDE:
        let sideCount = 0;
        if (surveyData.property_left && hasPropertySideData(surveyData.property_left)) {
          sideCount += 1 + propertyLeftPages.length; // Summary + details
        }
        if (surveyData.property_right && hasPropertySideData(surveyData.property_right)) {
          sideCount += 1 + propertyRightPages.length; // Summary + details
        }
        return sideCount;
      case BUILT_IN_SECTION_IDS.PROPERTY_REAR:
        return 1 + propertyRearPages.length; // Summary + details
      case BUILT_IN_SECTION_IDS.CELLAR:
        return cellarPages.length > 0 ? cellarPages.length : ((surveyData as any).cellar ? 1 : 0);
      case BUILT_IN_SECTION_IDS.ROOMS:
        return roomDetailsPages.length > 0 ? roomDetailsPages.length : (surveyData.room_details ? 1 : 0);
      case BUILT_IN_SECTION_IDS.EXTENSIONS:
        return 1 + extensionsPhotoPages.length; // Summary + photos
      case BUILT_IN_SECTION_IDS.METERS_SERVICES:
        return 1 + metersServicesPages.length; // Summary + details
      case BUILT_IN_SECTION_IDS.VENTILATION_CONDITION:
        return ventilationConditionPages.length > 0 ? ventilationConditionPages.length : (surveyData.initial_questionnaire ? 1 : 0);
      case BUILT_IN_SECTION_IDS.SITE_ASSESSMENT:
        return siteAssessmentPages.length > 0 ? siteAssessmentPages.length : (surveyData.site_assessment ? 1 : 0);
      case BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS:
        return 1; // Single page summary
      case BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS:
        return 1; // Single page
      case BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS:
        return 1; // Single page
      case BUILT_IN_SECTION_IDS.OCCUPANCY:
        return 1; // Single page
      case BUILT_IN_SECTION_IDS.SIGNATURES:
        return hasSignaturesData ? 4 : 0; // 4 pages when signatures exist
      case BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES:
        return 1; // Single page
      default:
        return 1; // Default: single page
    }
  };

  /**
   * Helper: Check if a field should be shown in PDF
   * Checks hiddenBuiltInFields configuration from form template
   * @param sectionId - The section ID (e.g., 'initial_questionnaire', 'property_front')
   * @param fieldName - The field name to check (e.g., 'buildingName', 'flatFloorNumber')
   * @returns true if field should be shown, false if hidden
   */
  const shouldShowField = (sectionId: string, fieldName: string): boolean => {
    if (!options.formTemplate?.hiddenBuiltInFields) {
      return true; // No hidden fields configured, show all
    }

    const hiddenFields = options.formTemplate.hiddenBuiltInFields[sectionId];
    if (!hiddenFields || !Array.isArray(hiddenFields)) {
      return true; // No hidden fields for this section
    }

    const isHidden = hiddenFields.includes(fieldName);
    if (isHidden) {
      console.log(`⚠️ [PDF] Field "${fieldName}" in section "${sectionId}" is hidden via form template configuration`);
    }
    return !isHidden;
  };

  /**
   * Helper: Get custom fields added to a built-in section
   * @param sectionId - The section ID (e.g., 'initial_questionnaire', 'property_front')
   * @returns Array of custom field schemas, or empty array if none
   */
  const getCustomFieldsForSection = (sectionId: string): import('../types/formSchema').FieldSchema[] => {
    if (!options.formTemplate?.customFieldsForBuiltInPages) {
      return [];
    }

    const customFields = options.formTemplate.customFieldsForBuiltInPages[sectionId];
    if (!customFields || !Array.isArray(customFields)) {
      return [];
    }

    console.log(`📋 [PDF] Found ${customFields.length} custom field(s) for section "${sectionId}"`);
    return customFields;
  };

  /**
   * Helper: Render custom fields for a built-in section
   * @param sectionId - The section ID
   * @returns HTML string with rendered custom fields
   */
  const renderCustomFieldsForSection = (sectionId: string): string => {
    const customFields = getCustomFieldsForSection(sectionId);
    if (customFields.length === 0) {
      return '';
    }

    if (!options.customFieldsData) {
      console.log(`⚠️ [PDF] Custom fields defined for "${sectionId}" but no customFieldsData provided`);
      return '';
    }

    // Sort fields by explicit order or default order property
    let sortedFields: import('../types/formSchema').FieldSchema[];
    
    // Check if explicit field order is defined for this section
    const explicitOrder = options.formTemplate?.builtInFieldOrder?.[sectionId];
    if (explicitOrder && Array.isArray(explicitOrder)) {
      console.log(`📋 [PDF] Using explicit field order for "${sectionId}":`, explicitOrder);
      
      // Sort fields according to explicit order array
      const fieldMap = new Map(customFields.map(f => [f.id, f]));
      sortedFields = explicitOrder
        .map(fieldId => fieldMap.get(fieldId))
        .filter((field): field is import('../types/formSchema').FieldSchema => field !== undefined);
      
      // Add any fields not in the explicit order at the end (sorted by order property)
      const orderedIds = new Set(explicitOrder);
      const remainingFields = customFields
        .filter(f => !orderedIds.has(f.id))
        .sort((a, b) => a.order - b.order);
      sortedFields.push(...remainingFields);
    } else {
      // Default: sort by order property
      sortedFields = [...customFields].sort((a, b) => a.order - b.order);
    }

    // Render each field
    const fieldsHTML = sortedFields
      .map(field => {
        const value = options.customFieldsData![field.id];
        return renderFieldForPDF(field, value, options.customFieldsData);
      })
      .filter(html => html.length > 0) // Remove empty fields
      .join('\n');

    if (fieldsHTML.length === 0) {
      return '';
    }

    // Wrap in a section with header
    return `
      <div class="content-section" style="margin-top: 20px;">
        <h3 style="color: #1e40af; font-size: 12pt; font-weight: 600; margin-bottom: 12px; border-bottom: 2px solid #1e40af; padding-bottom: 4px;">
          Additional Information
        </h3>
        ${fieldsHTML}
      </div>
    `;
  };

  // Calculate section page numbers for Table of Contents
  const calculateSectionPages = (): SectionPageMap => {
    let page = 1;
    const sectionPages: SectionPageMap = {
      propertyOverview: page++, // Page 1
      tableOfContents: page, // Page 2 (or 2-3 if TOC spans multiple pages)
      floorPlan: null,
      radiatorsWindows: null,
      dimensionsCalculations: null,
      heatingSystem: null,
      measureSpecification: null,
      pas2030Questions: null,
      propertyFront: null,
      propertySide: null,
      propertyRear: null,
      cellar: null,
      rooms: null,
      extensions: null,
      metersServices: null,
      ventilationCondition: null,
      occupancy: null,
      siteAssessment: null,
      signatures: null,
      additionalNotes: null,
      customPages: {},
    };

    // Count sections that will appear in TOC to calculate TOC page count
    let tocSectionCount = 1; // Property Overview
    if (options.includeFloorPlan && options.floorPlanImages && options.floorPlanImages.length > 0) tocSectionCount++;
    if (hasRadiatorsOrWindows) tocSectionCount++;
    tocSectionCount++; // Dimensions & Calculations (always exists)
    if (heatingSystemPages.length > 0) tocSectionCount++;
    if (measureSpecificationSection) tocSectionCount++;
    // PAS 2030 Measures Questions (if applicable measures selected)
    const hasPAS2030Measures = surveyData.measure_specification?.suitableMeasures?.some(
      m => m === 'Heating System' || m === 'Wall Insulation' || m === 'RIR insulation'
    );
    if (hasPAS2030Measures) tocSectionCount++;
    // Custom pages from form builder
    if (options.formTemplate) {
      const customPages = getCustomPagesForPDF(options.formTemplate);
      tocSectionCount += customPages.length;
    }
    if (surveyData.property_front && hasPropertyFrontData(surveyData.property_front)) tocSectionCount++;
    if ((surveyData.property_left && hasPropertySideData(surveyData.property_left)) || 
        (surveyData.property_right && hasPropertySideData(surveyData.property_right))) tocSectionCount++;
    if (surveyData.property_rear && hasPropertyRearData(surveyData.property_rear)) tocSectionCount++;
    if (cellarPages.length > 0) tocSectionCount++;
    if (roomDetailsPages.length > 0) tocSectionCount++;
    if (extensionsSection) tocSectionCount++;
    if (surveyData.initial_questionnaire && hasMetersServicesData(surveyData.initial_questionnaire)) tocSectionCount++;
    if (ventilationConditionPages.length > 0) tocSectionCount++;
    tocSectionCount++; // Occupancy Assessment (always exists)
    if (siteAssessmentPages.length > 0) tocSectionCount++;
    if (surveyData.initial_questionnaire?.additionalNotes && surveyData.initial_questionnaire.additionalNotes.trim().length > 0) tocSectionCount++;
    
    // Calculate TOC page count (13 items per page)
    const tocPageCount = Math.ceil(tocSectionCount / 13);
    page += tocPageCount; // Advance past all TOC pages
    console.log(`📊 [PDF] TOC will have ${tocSectionCount} sections across ${tocPageCount} page(s)`);

    // ===== NEW: Use dynamic page ordering =====
    // Get merged page order (built-ins in default order + custom pages in user's order)
    const rawPageOrder = getMergedPageOrder(options.formTemplate);
    
    // Normalize section IDs and remove nulls/duplicates
    let pageOrder = Array.from(new Set(
      rawPageOrder
        .map(id => normalizeSectionId(id))
        .filter((id): id is string => id !== null)
    ));
    
    // Ensure critical PAS sections are included (auto-inject if missing)
    pageOrder = ensureCriticalSections(pageOrder);

    // Get converted custom pages (CustomPage -> PageSection)
    const customPagesConverted = options.formTemplate ? getCustomPagesForPDF(options.formTemplate) : [];

    // Iterate through page order and assign page numbers
    for (const sectionId of pageOrder) {
      // Check if it's a custom page
      const customPage = customPagesConverted.find(p => p.id === sectionId);
      
      if (customPage && options.customFieldsData) {
        // Handle custom page
        if (!sectionPages.customPages) sectionPages.customPages = {};
        sectionPages.customPages[customPage.id] = page;
        const pageCount = calculateCustomPageCount(customPage, options.customFieldsData);
        console.log(`📊 [PDF] Storing custom page "${customPage.title}" (ID: "${customPage.id}") at page ${page}, spans ${pageCount} page(s)`);
        console.log(`📊 [PDF] sectionPages.customPages now has keys:`, Object.keys(sectionPages.customPages));
        page += pageCount;
      } else if (shouldIncludeSection(sectionId)) {
        // Handle built-in section
        const pageCount = getSectionPageCount(sectionId);
        
        // Map section ID to section page map property
        switch (sectionId) {
          case BUILT_IN_SECTION_IDS.FLOOR_PLAN:
            sectionPages.floorPlan = page;
            break;
          case BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS:
            sectionPages.radiatorsWindows = page;
            break;
          case BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS:
            sectionPages.dimensionsCalculations = page;
            break;
          case BUILT_IN_SECTION_IDS.HEATING_SYSTEM:
            sectionPages.heatingSystem = page;
            break;
          case BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION:
            sectionPages.measureSpecification = page;
            break;
          case BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS:
            sectionPages.pas2030Questions = page;
            break;
          case BUILT_IN_SECTION_IDS.PROPERTY_FRONT:
            sectionPages.propertyFront = page;
            break;
          case BUILT_IN_SECTION_IDS.PROPERTY_SIDE:
            sectionPages.propertySide = page;
            break;
          case BUILT_IN_SECTION_IDS.PROPERTY_REAR:
            sectionPages.propertyRear = page;
            console.log(`📊 [PDF] Property Rear at page ${page}`);
            break;
          case BUILT_IN_SECTION_IDS.CELLAR:
            sectionPages.cellar = page;
            console.log(`📊 [PDF] Cellar at page ${page} (${pageCount} page(s))`);
            break;
          case BUILT_IN_SECTION_IDS.ROOMS:
            sectionPages.rooms = page;
            console.log(`📊 [PDF] Rooms at page ${page} (${pageCount} page(s))`);
            break;
          case BUILT_IN_SECTION_IDS.EXTENSIONS:
            sectionPages.extensions = page;
            break;
          case BUILT_IN_SECTION_IDS.METERS_SERVICES:
            sectionPages.metersServices = page;
            break;
          case BUILT_IN_SECTION_IDS.VENTILATION_CONDITION:
            sectionPages.ventilationCondition = page;
            break;
          case BUILT_IN_SECTION_IDS.OCCUPANCY:
            sectionPages.occupancy = page;
            break;
          case BUILT_IN_SECTION_IDS.SITE_ASSESSMENT:
            sectionPages.siteAssessment = page;
            break;
          case BUILT_IN_SECTION_IDS.SIGNATURES:
            sectionPages.signatures = page;
            break;
          case BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES:
            sectionPages.additionalNotes = page;
            break;
        }
        
        console.log(`📊 [PDF] ${sectionId} at page ${page}, spans ${pageCount} page(s)`);
        page += pageCount;
      }
    }
    // ===============================================
    
    return sectionPages;
  };

  const sectionPageMap = calculateSectionPages();
  console.log('📊 [PDF] Section page map:', sectionPageMap);

  /**
   * Helper: Render HTML for a specific section based on section ID
   * Returns empty string if section doesn't exist or shouldn't be included
   */
  const renderSectionHTML = (sectionId: string): string => {
    // Check if this section should be included
    if (!shouldIncludeSection(sectionId)) {
      return '';
    }

    // Get the assigned page number for this section from the map
    const getPageNumber = (id: string): number => {
      switch (id) {
        case BUILT_IN_SECTION_IDS.FLOOR_PLAN: return sectionPageMap.floorPlan || 1;
        case BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS: return sectionPageMap.radiatorsWindows || 1;
        case BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS: return sectionPageMap.dimensionsCalculations || 1;
        case BUILT_IN_SECTION_IDS.HEATING_SYSTEM: return sectionPageMap.heatingSystem || 1;
        case BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION: return sectionPageMap.measureSpecification || 1;
        case BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS: return sectionPageMap.pas2030Questions || 1;
        case BUILT_IN_SECTION_IDS.PROPERTY_FRONT: return sectionPageMap.propertyFront || 1;
        case BUILT_IN_SECTION_IDS.PROPERTY_SIDE: return sectionPageMap.propertySide || 1;
        case BUILT_IN_SECTION_IDS.PROPERTY_REAR: return sectionPageMap.propertyRear || 1;
        case BUILT_IN_SECTION_IDS.CELLAR: return sectionPageMap.cellar || 1;
        case BUILT_IN_SECTION_IDS.ROOMS: return sectionPageMap.rooms || 1;
        case BUILT_IN_SECTION_IDS.EXTENSIONS: return sectionPageMap.extensions || 1;
        case BUILT_IN_SECTION_IDS.METERS_SERVICES: return sectionPageMap.metersServices || 1;
        case BUILT_IN_SECTION_IDS.VENTILATION_CONDITION: return sectionPageMap.ventilationCondition || 1;
        case BUILT_IN_SECTION_IDS.OCCUPANCY: return sectionPageMap.occupancy || 1;
        case BUILT_IN_SECTION_IDS.SITE_ASSESSMENT: return sectionPageMap.siteAssessment || 1;
        case BUILT_IN_SECTION_IDS.SIGNATURES: return sectionPageMap.signatures || 1;
        case BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES: return sectionPageMap.additionalNotes || 1;
        default: return 1;
      }
    };

    const shouldInclude = shouldIncludeSection(sectionId);
    let pageNum = getPageNumber(sectionId);
    
    if (!shouldInclude) {
      return '';
    }
    
    let html = '';

    switch (sectionId) {
      case BUILT_IN_SECTION_IDS.FLOOR_PLAN:
        if (options.floorPlanImages && options.floorPlanImages.length > 0) {
          const floorPlanPages = generateFloorPlanPages(surveyData, pageNum, totalPages, options);
          if (floorPlanPages.length > 0) {
            floorPlanPages[0] = floorPlanPages[0].replace('<section class="pdf-page">', '<section class="pdf-page" id="floor-plan">');
          }
          html = floorPlanPages.join('\n');
          console.log(`🎨 [PDF] Rendered ${floorPlanPages.length} floor plan page(s)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS:
        if (hasRadiatorsOrWindows) {
          const section = generateRadiatorsWindowsSummarySection(surveyData, pageNum, totalPages, options);
          html = section.replace('<section class="pdf-page">', '<section class="pdf-page" id="radiators-windows">');
          console.log(`🎨 [PDF] Rendered radiators & windows section`);
        }
        break;

      case BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS:
        html = `
          <section class="pdf-page" id="dimensions-calculations">
            ${generateHeader(surveyData, pageNum, totalPages, options)}
            ${generateMainPropertyMeasurementsSection(surveyData)}
            ${renderCustomFieldsForSection('dimensions_calculations')}
            ${generateFooter(options)}
          </section>
        `;
        console.log(`🎨 [PDF] Rendered dimensions & calculations section`);
        break;

      case BUILT_IN_SECTION_IDS.HEATING_SYSTEM:
        if (heatingSystemPages.length > 0) {
          html = heatingSystemPages.map((pageContent: string, index: number) => {
            return pageContent.replace(
              /Page \d+ of \d+/g,
              'Page ' + (pageNum + index) + ' of ' + totalPages
            );
          }).join('\n');
          console.log(`🎨 [PDF] Rendered ${heatingSystemPages.length} heating system page(s)`);
        } else if (surveyData.heating_system) {
          // No pages generated but data exists - show placeholder
          html = `
            <section class="pdf-page" id="heating-system">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              <div class="content-section">
                <div class="section-heading">Heating System Assessment</div>
                <p style="color: #64748b; font-style: italic; text-align: center; padding: 40px;">No heating system details available</p>
              </div>
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered heating system section (no data)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION:
        if (measureSpecificationSection) {
          html = `
            <section class="pdf-page" id="measure-specification">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              ${measureSpecificationSection}
              ${generateFooter(options)}
            </section>
          `;
          pageNum++;
          
          // Wall Insulation Continuation Pages
          if (options.elevationImages && options.elevationImages.length > 2) {
            const remainingElevations = options.elevationImages.slice(2);
            const continuationPages: string[] = [];
            
            for (let i = 0; i < remainingElevations.length; i += 2) {
              const pageElevations = remainingElevations.slice(i, i + 2);
              const pageContent = generateWallInsulationContinuationPage(pageElevations, surveyData);
              
              continuationPages.push(`
                <section class="pdf-page">
                  ${generateHeader(surveyData, pageNum++, totalPages, options)}
                  ${pageContent}
                  ${generateFooter(options)}
                </section>
              `);
            }
            
            html += '\n' + continuationPages.join('\n');
          }
          
          // Heating System Measure
          if (heatingSystemMeasureSection) {
            html += `
              <section class="pdf-page">
                ${generateHeader(surveyData, pageNum++, totalPages, options)}
                ${heatingSystemMeasureSection}
                ${generateFooter(options)}
              </section>
            `;
          }
          
          // Loft Insulation
          if (loftInsulationSection) {
            console.log(`✅ Adding Loft Insulation page to measure_specification`);
            html += `
              <section class="pdf-page">
                ${generateHeader(surveyData, pageNum++, totalPages, options)}
                ${loftInsulationSection}
                ${generateFooter(options)}
              </section>
            `;
          } else {
            console.log(`⏭️ Skipping Loft Insulation page (no content)`);
          }
          
          // Loft Photos
          if (loftPhotoPages.length > 0) {
            console.log(`✅ Adding ${loftPhotoPages.length} Loft photo page(s) to measure_specification`);
            html += '\n' + loftPhotoPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (pageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
            pageNum += loftPhotoPages.length;
          }
          
          // RIR Insulation
          if (rirInsulationSection) {
            console.log(`✅ Adding RIR Insulation page to measure_specification`);
            html += `
              <section class="pdf-page">
                ${generateHeader(surveyData, pageNum++, totalPages, options)}
                ${rirInsulationSection}
                ${generateFooter(options)}
              </section>
            `;
          } else {
            console.log(`⏭️ Skipping RIR Insulation page (no content)`);
          }
          
          // RIR Photos
          if (rirPhotoPages.length > 0) {
            console.log(`✅ Adding ${rirPhotoPages.length} RIR photo page(s) to measure_specification`);
            html += '\n' + rirPhotoPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (pageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
          }
          
          console.log(`🎨 [PDF] Rendered measure specification with continuation pages`);
        }
        break;

      case BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS:
        const pas2030Content = generatePAS2030MeasuresQuestionsPage(surveyData);
        if (pas2030Content) {
          html = `
            <section class="pdf-page" id="pas2030-measures">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              ${pas2030Content}
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered PAS2030 questions page`);
        }
        break;

      case BUILT_IN_SECTION_IDS.PROPERTY_FRONT:
        if (surveyData.property_front && hasPropertyFrontData(surveyData.property_front)) {
          const summary = generatePropertyFrontSummary(surveyData, pageNum, totalPages, options);
          const customFieldsHTML = renderCustomFieldsForSection('property_front');
          // Inject custom fields before the footer
          html = summary
            .replace('<section class="pdf-page">', '<section class="pdf-page" id="property-front">')
            .replace(/(<\/div>\s*<\/div>\s*<footer)/, `${customFieldsHTML}</div></div><footer`);
          pageNum++;
          
          // Add detail pages with photos
          if (propertyFrontPages.length > 0) {
            html += '\n' + propertyFrontPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (pageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
            console.log(`🎨 [PDF] Rendered property front summary + ${propertyFrontPages.length} detail page(s)`);
          } else {
            console.log(`🎨 [PDF] Rendered property front summary (no detail pages)`);
          }
        }
        break;

      case BUILT_IN_SECTION_IDS.PROPERTY_SIDE:
        let sideHtml = '';
        let sidePageNum = pageNum;
        
        // Left side
        if (surveyData.property_left && hasPropertySideData(surveyData.property_left)) {
          const leftSummary = generatePropertyElevationSummary(surveyData, 'left', sidePageNum, totalPages, options);
          sideHtml += leftSummary.replace('<section class="pdf-page">', '<section class="pdf-page" id="property-side">');
          sidePageNum++;
          
          if (propertyLeftPages.length > 0) {
            sideHtml += '\n' + propertyLeftPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (sidePageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
            sidePageNum += propertyLeftPages.length;
          }
        }
        
        // Right side
        if (surveyData.property_right && hasPropertySideData(surveyData.property_right)) {
          const rightSummary = generatePropertyElevationSummary(surveyData, 'right', sidePageNum, totalPages, options);
          sideHtml += '\n' + rightSummary;
          sidePageNum++;
          
          if (propertyRightPages.length > 0) {
            sideHtml += '\n' + propertyRightPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (sidePageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
          }
        }
        
        html = sideHtml;
        if (sideHtml) {
          console.log(`🎨 [PDF] Rendered property side section(s)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.PROPERTY_REAR:
        if (surveyData.property_rear && hasPropertyRearData(surveyData.property_rear)) {
          const rearSummary = generatePropertyElevationSummary(surveyData, 'rear', pageNum, totalPages, options);
          html = rearSummary.replace('<section class="pdf-page">', '<section class="pdf-page" id="property-rear">');
          pageNum++;
          
          if (propertyRearPages.length > 0) {
            html += '\n' + propertyRearPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (pageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
          }
          console.log(`🎨 [PDF] Rendered property rear section`);
        }
        break;

      case BUILT_IN_SECTION_IDS.CELLAR:
        if (cellarPages.length > 0) {
          html = cellarPages.map((pageContent: string, index: number) => {
            const updatedPage = pageContent.replace(
              /Page \d+ of \d+/g,
              'Page ' + (pageNum + index) + ' of ' + totalPages
            );
            if (index === 0) {
              return updatedPage.replace('<section class="pdf-page">', '<section class="pdf-page" id="cellar">');
            }
            return updatedPage;
          }).join('\n');
          console.log(`🎨 [PDF] Rendered ${cellarPages.length} cellar page(s)`);
        } else if ((surveyData as any).cellar) {
          // No pages generated but data exists - show placeholder
          html = `
            <section class="pdf-page" id="cellar">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              <div class="content-section">
                <div class="section-heading">Cellar</div>
                <p style="color: #64748b; font-style: italic; text-align: center; padding: 40px;">No cellar data available</p>
              </div>
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered cellar section (no data)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.ROOMS:
        if (roomDetailsPages.length > 0) {
          html = roomDetailsPages.map((pageContent: string, index: number) => {
            const updatedPage = pageContent.replace(
              /Page \d+ of \d+/g,
              'Page ' + (pageNum + index) + ' of ' + totalPages
            );
            if (index === 0) {
              return updatedPage.replace('<section class="pdf-page">', '<section class="pdf-page" id="rooms">');
            }
            return updatedPage;
          }).join('\n');
          console.log(`🎨 [PDF] Rendered ${roomDetailsPages.length} room page(s)`);
        } else {
          // No rooms - show placeholder
          html = `
            <section class="pdf-page" id="rooms">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              <div class="content-section">
                <div class="section-heading">Room Details</div>
                <p style="color: #64748b; font-style: italic; text-align: center; padding: 40px;">No room data available</p>
              </div>
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered rooms section (no data)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.EXTENSIONS:
        if (extensionsSection) {
          html = `
            <section class="pdf-page" id="extensions">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              ${extensionsSection}
              ${generateFooter(options)}
            </section>
          `;
          pageNum++;
          
          if (extensionsPhotoPages.length > 0) {
            html += '\n' + extensionsPhotoPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (pageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
          }
          console.log(`🎨 [PDF] Rendered extensions section`);
        }
        break;

      case BUILT_IN_SECTION_IDS.METERS_SERVICES:
        if (surveyData.initial_questionnaire && hasMetersServicesData(surveyData.initial_questionnaire)) {
          const metersSummary = generateMetersServicesSummary(surveyData, pageNum, totalPages, options);
          html = metersSummary.replace('<section class="pdf-page">', '<section class="pdf-page" id="meters-services">');
          pageNum++;
          
          if (metersServicesPages.length > 0) {
            html += '\n' + metersServicesPages.map((pageContent: string, index: number) => {
              return pageContent.replace(
                /Page \d+ of \d+/g,
                'Page ' + (pageNum + index) + ' of ' + totalPages
              );
            }).join('\n');
          }
          console.log(`🎨 [PDF] Rendered meters & services section`);
        }
        break;

      case BUILT_IN_SECTION_IDS.VENTILATION_CONDITION:
        if (ventilationConditionPages.length > 0) {
          html = ventilationConditionPages.map((pageContent: string, index: number) => {
            const updatedPage = pageContent.replace(
              /Page \d+ of \d+/g,
              'Page ' + (pageNum + index) + ' of ' + totalPages
            );
            if (index === 0) {
              return updatedPage.replace('<section class="pdf-page">', '<section class="pdf-page" id="ventilation-condition">');
            }
            return updatedPage;
          }).join('\n');
          console.log(`🎨 [PDF] Rendered ${ventilationConditionPages.length} ventilation page(s)`);
        } else if (surveyData.initial_questionnaire) {
          // No pages generated but data exists - show placeholder
          html = `
            <section class="pdf-page" id="ventilation-condition">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              <div class="content-section">
                <div class="section-heading">Ventilation & Condition</div>
                <p style="color: #64748b; font-style: italic; text-align: center; padding: 40px;">No ventilation data available</p>
              </div>
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered ventilation section (no data)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.OCCUPANCY:
        html = `
          <section class="pdf-page" id="occupancy">
            ${generateHeader(surveyData, pageNum, totalPages, options)}
            ${generateOccupancyAssessmentSection(surveyData)}
            ${renderCustomFieldsForSection('occupancy_assessment')}
            ${generateFooter(options)}
          </section>
        `;
        console.log(`🎨 [PDF] Rendered occupancy assessment section`);
        break;

      case BUILT_IN_SECTION_IDS.SITE_ASSESSMENT:
        console.log(`🔍 [PDF] Site Assessment debug:`, {
          hasSiteAssessmentData: !!surveyData.site_assessment,
          siteAssessmentPagesCount: siteAssessmentPages.length,
          siteAssessmentDataKeys: surveyData.site_assessment ? Object.keys(surveyData.site_assessment) : [],
        });
        
        if (siteAssessmentPages.length > 0) {
          // Wrap each page content in a full page section
          html = siteAssessmentPages.map((pageContent: string, index: number) => {
            const sectionId = index === 0 ? ' id="site-assessment"' : '';
            const customFieldsHTML = index === siteAssessmentPages.length - 1 ? renderCustomFieldsForSection('site_assessment') : '';
            
            return `
              <section class="pdf-page"${sectionId}>
                ${generateHeader(surveyData, pageNum + index, totalPages, options)}
                ${pageContent}
                ${customFieldsHTML}
                ${generateFooter(options)}
              </section>`;
          }).join('\n');
          console.log(`✅ [PDF] Rendered ${siteAssessmentPages.length} site assessment page(s) with proper wrappers`);
        } else if (surveyData.site_assessment) {
          // No pages generated but data exists - show placeholder
          const customFieldsHTML = renderCustomFieldsForSection('site_assessment');
          html = `
            <section class="pdf-page" id="site-assessment">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              <div class="content-section">
                <div class="section-heading">Site Assessment</div>
                <p style="color: #64748b; font-style: italic; text-align: center; padding: 40px;">No site assessment data available</p>
              </div>
              ${customFieldsHTML}
              ${generateFooter(options)}
            </section>
          `;
          console.log(`⚠️ [PDF] Rendered site assessment section (placeholder - pages not generated)`);
        } else {
          console.log(`⏭️ [PDF] Skipping site assessment (no data)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.SIGNATURES:
        if (hasSignaturesData) {
          html = `
            <section class="pdf-page" id="signatures">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              ${generatePreInstallationQuestionsPage(surveyData)}
              ${generateFooter(options)}
            </section>
            <section class="pdf-page">
              ${generateHeader(surveyData, pageNum + 1, totalPages, options)}
              ${generatePAS2030QuestionsPage(surveyData)}
              ${generateFooter(options)}
            </section>
            <section class="pdf-page">
              ${generateHeader(surveyData, pageNum + 2, totalPages, options)}
              ${generateCustomerSignaturesPage(surveyData)}
              ${generateFooter(options)}
            </section>
            <section class="pdf-page">
              ${generateHeader(surveyData, pageNum + 3, totalPages, options)}
              ${generateSurveyorSignaturesPage(surveyData)}
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered signatures section (4 pages)`);
        }
        break;

      case BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES:
        if (surveyData.initial_questionnaire?.additionalNotes && 
            surveyData.initial_questionnaire.additionalNotes.trim().length > 0) {
          html = `
            <section class="pdf-page" id="additional-notes">
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              ${generateFinalNotesSection(surveyData)}
              ${generateFooter(options)}
            </section>
          `;
          console.log(`🎨 [PDF] Rendered additional notes section`);
        }
        break;

      default:
        console.warn(`⚠️ Unknown section ID: ${sectionId}`);
    }
    
    return html;
  };

  // Now generate HTML with correct page tracking
  let currentPage = 1;

  const html = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0, user-scalable=yes">
      <title>Retrofit Survey Report - ${surveyData.name}</title>
      <!-- PDF Generation Libraries -->
      <script src="https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js"></script>
      <script src="https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js"></script>
      ${getCSS(options.headerColor || '#3B82F6')}
    </head>
    <body>
      <!-- Page 1: Cover & Property Details -->
      <section class="pdf-page" id="property-overview">
        ${generateHeader(surveyData, currentPage++, totalPages, options)}
        ${generateCoverSection(surveyData)}
        ${generatePropertyDetailsSection(surveyData)}
        ${generateFooter(options)}
      </section>

      <!-- Page 2+: Table of Contents (may span multiple pages) -->
      ${(() => {
        const tocPages = generateTableOfContents(sectionPageMap, surveyData, options);
        const startPage = currentPage;
        const tocHTML = tocPages.map((tocContent, index) => {
          const pageNum = startPage + index;
          // Add section ID only to the first TOC page
          const sectionId = index === 0 ? ' id="table-of-contents"' : '';
          currentPage++;
          return `
            <section class="pdf-page"${sectionId}>
              ${generateHeader(surveyData, pageNum, totalPages, options)}
              ${tocContent}
              ${generateFooter(options)}
            </section>
          `;
        }).join('\n');
        console.log(`📄 [PDF] Added ${tocPages.length} Table of Contents page(s)`);
        return tocHTML;
      })()}

      <!-- ===== DYNAMIC SECTION RENDERING ===== -->
      ${(() => {
        // Get merged page order (built-ins in default order + custom pages in user's order)
        const rawPageOrder = getMergedPageOrder(options.formTemplate);
        
        const customPagesConverted = options.formTemplate ? getCustomPagesForPDF(options.formTemplate) : [];
        
        console.log(`📄 [PDF] Custom pages available:`, {
          count: customPagesConverted.length,
          pages: customPagesConverted.map(p => ({ id: p.id, title: p.title })),
          hasFormData: !!options.customFieldsData,
        });
        
        // Normalize section IDs and remove nulls/duplicates
        let pageOrder = Array.from(new Set(
          rawPageOrder
            .map(id => normalizeSectionId(id))
            .filter((id): id is string => id !== null)
        ));
        
        // Ensure critical PAS sections are included (auto-inject if missing)
        pageOrder = ensureCriticalSections(pageOrder);
        
        // Log detailed page order comparison
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('📋 PDF PAGE ORDER COMPARISON');
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('');
        console.log('ORIGINAL ORDER (from TOC):');
        const originalOrder = [
          '1. Property Overview (Cover)',
          '2. Table of Contents',
          '3. Floor Plan',
          '4. Radiators & Windows Summary',
          '5. All Floors Dimensions & Calculations',
          '6. Heating System Assessment',
          '7. Measure Specification',
          '8. PAS 2030 Questions',
          '9. Property Front',
          '10. Property Rear',
          '11. Cellar',
          '12. Rooms',
          '13. Extensions',
          '14. Meters & Services',
          '15. Ventilation & Condition',
          '16. Occupancy Assessment',
          '17. Site Assessment',
          '18. Signatures & Feedback',
          '19. Additional Notes',
          '20. Custom Pages (appended)',
        ];
        originalOrder.forEach(item => console.log(`  ${item}`));
        console.log('');
        console.log('CURRENT ORDER (will be rendered):');
        pageOrder.forEach((id, index) => {
          const displayName = id.startsWith('custom_page_') ? 
            `CUSTOM PAGE (${id})` : 
            id.replace(/_/g, ' ').toUpperCase();
          console.log(`  ${index + 3}. ${displayName}`); // +3 because cover=1, TOC=2
        });
        console.log('');
        console.log('SOURCE: formTemplate.pageOrder exists = ' + !!(options.formTemplate?.pageOrder));
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('');

        const renderedSections: string[] = [];
        const skippedSections: string[] = [];
        
        const result = pageOrder.map((sectionId) => {
          // Check if it's a custom page
          const customPage = customPagesConverted.find(p => p.id === sectionId);
          
          if (customPage && options.customFieldsData) {
            // Render custom page
            const pageNumber = sectionPageMap.customPages?.[customPage.id] || 1;
            
            console.log(`🔍 [PDF] Rendering custom page "${customPage.title}":`, {
              pageId: customPage.id,
              pageNumber,
              customPagesMapKeys: Object.keys(sectionPageMap.customPages || {}),
              customPagesMap: sectionPageMap.customPages,
              hasFormData: !!options.customFieldsData,
              hasTitle: !!customPage.title,
            });
            
            const customPageHTMLArray = renderCustomPageForPDF(customPage, {
              pageNumber,
              totalPages,
              generateHeader,
              generateFooter,
              surveyData,
              formData: options.customFieldsData,
              options
            });
            
            const html = customPageHTMLArray.join('\n');
            if (html && html.trim() !== '') {
              console.log(`✅ [PDF] Rendered custom page "${customPage.title}" (${customPageHTMLArray.length} page(s), ${html.length} chars)`);
              renderedSections.push(`CUSTOM: ${customPage.title}`);
              return html;
            } else {
              console.log(`⚠️ [PDF] Custom page "${customPage.title}" generated no HTML`);
            }
            return '';
          } else {
            // Render built-in section
            const html = renderSectionHTML(sectionId);
            if (html && html.trim() !== '') {
              renderedSections.push(sectionId);
              return html;
            } else {
              skippedSections.push(sectionId);
              return '';
            }
          }
        })
        .filter(html => html && html.trim() !== ''); // Remove empty sections
        
        console.log('✅ Rendered sections:', renderedSections);
        console.log('⏭️ Skipped sections:', skippedSections);
        
        return result.join('\n');
      })()}

      <!-- ===== END DYNAMIC SECTION RENDERING ===== -->
      
      <!-- Lightbox Modal -->
      <div id="lightbox-modal" class="lightbox-modal">
        <span class="lightbox-close" onclick="closeLightbox()">&times;</span>
        <img id="lightbox-img" class="lightbox-content" src="" alt="">
        <div id="lightbox-caption" class="lightbox-caption"></div>
      </div>

      <script>
        // Lightbox functionality
        function openLightbox(imgElement) {
          const modal = document.getElementById('lightbox-modal');
          const modalImg = document.getElementById('lightbox-img');
          const caption = document.getElementById('lightbox-caption');
          
          modal.classList.add('active');
          modalImg.src = imgElement.src;
          caption.textContent = imgElement.alt || 'Photo';
        }

        function closeLightbox() {
          const modal = document.getElementById('lightbox-modal');
          modal.classList.remove('active');
        }

        // Add click listeners to all images
        document.addEventListener('DOMContentLoaded', function() {
          const images = document.querySelectorAll('.photo-item img, .property-photo-item img');
          
          images.forEach(function(img) {
            img.addEventListener('click', function(e) {
              e.preventDefault();
              e.stopPropagation();
              openLightbox(this);
            });
            
            img.addEventListener('touchend', function(e) {
              e.preventDefault();
              e.stopPropagation();
              openLightbox(this);
            });
            
            img.style.cursor = 'pointer';
            img.style.webkitTapHighlightColor = 'rgba(0, 0, 0, 0.1)';
            img.style.touchAction = 'manipulation';
          });

          const modal = document.getElementById('lightbox-modal');
          modal.addEventListener('click', function(e) {
            if (e.target === this) {
              closeLightbox();
            }
          });
          
          modal.addEventListener('touchend', function(e) {
            if (e.target === this) {
              closeLightbox();
            }
          });

          document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape') {
              closeLightbox();
            }
          });
        });
      </script>
      
    </body>
    </html>
  `;

  console.log('✅ [PDF] HTML generation complete with dynamic section ordering');

  // Count actual <section class="pdf-page"> elements in the generated HTML
  const pageSectionMatches = html.match(/<section[^>]*class="pdf-page"/g);
  const actualTotalPages = pageSectionMatches ? pageSectionMatches.length : 0;
  
  console.log(`📊 [PDF] Page count: estimated ${totalPages}, actual ${actualTotalPages} (counted from DOM)`);
  
  // If actual page count differs from estimated, update all page numbers in HTML
  if (actualTotalPages !== totalPages) {
    console.log(`⚠️ [PDF] Page count mismatch. Updating all "Page X of Y" references...`);
    // Replace all "Page X of Y" with correct total
    const updatedHtml = html.replace(/Page (\d+) of \d+/g, `Page $1 of ${actualTotalPages}`);
    return { html: updatedHtml, sectionPageMap };
  }
  
  console.log(`✅ [PDF] Page count matches: ${actualTotalPages} pages`);
  return { html, sectionPageMap };
}

/**
 * Generate CSS styles for the PDF with dynamic theme color
 */
function getCSS(themeColor: string = '#3B82F6'): string {
  // Lighten the theme color for gradients (approximate)
  const lighterTheme = themeColor + 'CC'; // Add transparency for lighter effect
  
  return `
    <style>
      :root {
        --theme-color: ${themeColor};
        --theme-color-light: ${lighterTheme};
      }
      
      @page { margin: 0; }
      
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      body {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 10pt;
        line-height: 1.5;
        color: #1E293B;
        background: white;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        padding: 30px 0; /* Padding top and bottom for better viewing */
      }
      
      /* White background for print */
      @media print {
        body {
          background: white;
          padding: 0;
        }
      }

      /* Page structure */
      .pdf-page {
        page-break-after: always;
        min-height: 297mm; /* Minimum A4 height, can expand */
        margin: 0 auto 30px auto; /* 30px gap between pages for screen viewing */
        padding: 5mm;
        padding-bottom: 20mm; /* Extra bottom padding for footer */
        background: white;
        box-sizing: border-box;
        position: relative; /* For absolute footer positioning */
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1); /* Subtle shadow for depth */
      }
      
      /* Remove margin for print */
      @media print {
        .pdf-page {
          margin: 0;
          box-shadow: none;
          padding-bottom: 20mm; /* Maintain footer space in print */
        }
      }

      /* Header with Stacked Logo/Page Number */
      .header {
        background: white;
        border: 0.5pt solid #E5E7EB;
        border-radius: 6pt;
        padding: 6pt 8pt;
        margin-bottom: 8pt;
        box-shadow: 0.3pt 0.3pt 0pt #E5E7EB;
        min-height: 40pt;
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .header-left {
        display: flex;
        flex-direction: column;
        gap: 1pt;
      }

      .header-right {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .header-logo {
        max-height: 28pt;
        max-width: 70pt;
        height: auto;
        width: auto;
        object-fit: contain;
      }

      .page-number {
        font-size: 7pt;
        color: #6B7280;
        font-weight: normal;
        margin: 0;
        white-space: nowrap;
      }

      .property-address {
        font-size: 10pt;
        font-weight: 600;
        color: #111827;
        margin: 0;
        line-height: 1.2;
      }

      .report-type {
        font-size: 8pt;
        color: #374151;
        margin: 0;
        line-height: 1.2;
      }

      /* Footer - 2 Lines, Fixed at Bottom */
      .footer {
        position: absolute;
        bottom: 5mm;
        left: 5mm;
        right: 5mm;
        text-align: center;
        padding-top: 6pt;
        border-top: 0.5pt solid #E5E7EB;
      }

      .footer-line {
        font-size: 7pt;
        color: #6B7280;
        line-height: 1.5;
        margin: 1pt 0;
      }

      .footer-separator {
        margin: 0 6pt;
        color: #9CA3AF;
      }

      /* Professional Section Headings */
      .section-heading {
        font-size: 11pt;
        font-weight: 600;
        color: #0f172a;
        margin: 0 0 12pt 0;
        padding: 8pt 16pt;
        background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
        border-left: 4pt solid var(--theme-color);
        border-radius: 8pt;
        text-transform: uppercase;
        letter-spacing: 0.5pt;
        display: flex;
        align-items: center;
        box-shadow: 0 2pt 4pt rgba(0, 0, 0, 0.05);
        min-height: 32pt;
        height: 32pt;
      }
      
      /* Subsection Headings (for measure specification subsections) */
      .subsection-heading {
        font-size: 9.5pt;
        font-weight: 600;
        color: #334155;
        margin: 0 0 10pt 0;
        padding: 6pt 12pt;
        background: #f8fafc;
        border-left: 3pt solid #94a3b8;
        border-radius: 4pt;
        text-transform: none;
        letter-spacing: 0.3pt;
        box-shadow: 0 1pt 2pt rgba(0, 0, 0, 0.03);
      }
      
      /* Property Cards Grid */
      .property-cards {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12pt;
        margin-bottom: 16pt;
      }
      
      .property-card {
        background: white;
        border: 1pt solid #e2e8f0;
        border-radius: 10pt;
        padding: 12pt;
        box-shadow: 0 2pt 8pt rgba(0, 0, 0, 0.04);
      }
      
      .property-card-title {
        font-size: 9pt;
        font-weight: 600;
        color: #475569;
        text-transform: uppercase;
        letter-spacing: 0.3pt;
        margin-bottom: 10pt;
        padding-bottom: 6pt;
        border-bottom: 1pt solid #e2e8f0;
      }
      
      .property-card-content {
        display: flex;
        flex-direction: column;
        gap: 6pt;
      }
      
      .property-card-row {
        display: grid;
        grid-template-columns: 100pt 1fr;
        gap: 8pt;
        font-size: 8.5pt;
      }
      
      .property-card-label {
        color: #64748b;
        font-weight: 500;
      }
      
      .property-card-value {
        color: #0f172a;
        font-weight: 400;
      }

      /* Summary Section Styles */
      .summary-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8pt;
        margin-bottom: 12pt;
      }

      .summary-card {
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border: 1pt solid #e2e8f0;
        border-radius: 8pt;
        padding: 8pt 10pt;
        box-shadow: 0 1pt 4pt rgba(0, 0, 0, 0.03);
      }

      .summary-card-label {
        font-size: 7pt;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.3pt;
        margin-bottom: 4pt;
      }

      .summary-card-value {
        font-size: 9pt;
        color: #0f172a;
        font-weight: 500;
        line-height: 1.3;
      }

      .summary-section-title {
        font-size: 10pt;
        font-weight: 600;
        color: #1e293b;
        margin: 12pt 0 8pt 0;
        padding: 6pt 10pt;
        background: linear-gradient(90deg, #3b82f6 0%, transparent 100%);
        border-left: 3pt solid #3b82f6;
        border-radius: 4pt;
      }

      /* Professional Tables */
      .info-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: 6pt;
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        border-bottom: 1.5pt solid rgba(0, 0, 0, 0.12);
        border-right: 1pt solid rgba(0, 0, 0, 0.1);
        overflow: hidden;
        margin-bottom: 12pt;
        font-size: 9pt;
      }

      .info-table tr:nth-child(even) {
        background: #F8FAFC;
      }

      .info-table tr:nth-child(odd) {
        background: white;
      }

      .info-table td {
        padding: 6pt 10pt;
        border-bottom: 0.5pt solid #E8EDF3;
        border-right: 0.5pt solid #E8EDF3;
      }

      .info-table td:first-child {
        font-weight: 600;
        color: #374151;
        width: 40%;
        background: linear-gradient(90deg, #F1F5F9 0%, #F8FAFC 100%);
      }

      .info-table td:last-child {
        border-right: none;
      }

      .info-table tr:last-child td {
        border-bottom: none;
      }

      /* Photo Grid - Professional Layout */
      .photo-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8pt;
        margin-top: 8pt;
      }

      .photo-item {
        border: 0.5pt solid #E5E7EB;
        border-radius: 6pt;
        overflow: hidden;
        background: white;
        box-shadow: 0 1pt 3pt rgba(0, 0, 0, 0.05);
      }

      .photo-item img {
        width: 100%;
        height: auto;
        display: block;
        max-height: 110mm;
        object-fit: cover;
      }

      .photo-label {
        padding: 5pt 8pt;
        background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
        font-size: 8pt;
        font-weight: 500;
        text-align: center;
        color: white;
        letter-spacing: 0.2pt;
      }

      /* Property Front Section - Modern Layout */
      .property-section {
        margin-bottom: 16pt;
        border-radius: 8pt;
        background: white;
        border: 0.5pt solid #e5e7eb;
        overflow: hidden;
        box-shadow: 0 1pt 3pt rgba(0, 0, 0, 0.05);
      }

      .property-section-header {
        display: flex;
        align-items: center;
        padding: 10pt 12pt;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border-bottom: 0.5pt solid #e5e7eb;
      }

      .property-icon-container {
        width: 28pt;
        height: 28pt;
        border-radius: 6pt;
        background: linear-gradient(135deg, #64748b 0%, #475569 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 10pt;
        box-shadow: 0 2pt 4pt rgba(0, 0, 0, 0.1);
      }

      .property-icon {
        width: 16pt;
        height: 16pt;
        color: white;
      }

      .property-section-header h3 {
        margin: 0;
        font-size: 11pt;
        font-weight: 600;
        color: #1e293b;
        letter-spacing: -0.1pt;
      }

      .property-section-header h3 .section-parent {
        color: var(--theme-color);
        font-weight: 700;
        margin-right: 6pt;
        font-size: 10pt;
        text-transform: uppercase;
        letter-spacing: 0.5pt;
      }

      .property-photo-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 8pt;
        padding: 12pt;
        background: #fafafa;
      }

      .property-photo-wrapper {
        display: flex;
        flex-direction: column;
        width: 100%;
        min-width: 0;
      }

      .property-photo-item {
        border-radius: 6pt 6pt 0 0;
        overflow: hidden;
        background: white;
        box-shadow: 0 2pt 4pt rgba(0, 0, 0, 0.08);
        border: 0.5pt solid #e5e7eb;
        border-bottom: none;
        height: 85mm;
        width: 100%;
        min-width: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .property-photo-item img {
        max-width: 100%;
        max-height: 100%;
        width: auto;
        height: auto;
        display: block;
        object-fit: contain;
        cursor: pointer;
        transition: opacity 0.2s ease;
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
        touch-action: manipulation;
        user-select: none;
        -webkit-user-select: none;
      }

      .property-photo-item img:hover,
      .property-photo-item img:active {
        opacity: 0.9;
      }

      .photo-item img {
        cursor: pointer;
        transition: opacity 0.2s ease;
      }

      .photo-item img:hover {
        opacity: 0.9;
      }

      /* Lightbox Modal Styles */
      .lightbox-modal {
        display: none;
        position: fixed;
        z-index: 10000;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.95);
        animation: fadeIn 0.2s ease;
      }

      .lightbox-modal.active {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .lightbox-content {
        max-width: 95%;
        max-height: 95%;
        object-fit: contain;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
        animation: zoomIn 0.2s ease;
      }

      .lightbox-close {
        position: absolute;
        top: 20px;
        right: 35px;
        color: white;
        font-size: 40px;
        font-weight: bold;
        cursor: pointer;
        transition: color 0.2s ease;
        z-index: 10001;
      }

      .lightbox-close:hover {
        color: #f87171;
      }

      .lightbox-caption {
        position: absolute;
        bottom: 20px;
        left: 50%;
        transform: translateX(-50%);
        color: white;
        font-size: 14pt;
        padding: 10px 20px;
        background: rgba(0, 0, 0, 0.7);
        border-radius: 6pt;
        max-width: 80%;
        text-align: center;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes zoomIn {
        from { transform: scale(0.8); opacity: 0; }
        to { transform: scale(1); opacity: 1; }
      }

      .property-photo-label {
        padding: 6pt 8pt;
        background: var(--theme-color);
        font-size: 7pt;
        font-weight: 500;
        text-align: center;
        color: white;
        letter-spacing: 0.3pt;
        border-radius: 0 0 6pt 6pt;
        border: 0.5pt solid #e5e7eb;
        border-top: none;
      }

      .property-qa-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 5pt;
        padding: 8pt;
      }

      .property-qa-item {
        background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
        border: 0.5pt solid #e5e7eb;
        border-radius: 4pt;
        padding: 6pt 8pt;
        box-shadow: 0 1pt 2pt rgba(0, 0, 0, 0.03);
      }

      .property-question {
        font-size: 6.5pt;
        font-weight: 600;
        color: #64748b;
        text-transform: uppercase;
        letter-spacing: 0.4pt;
        margin-bottom: 3pt;
      }

      .property-answer {
        font-size: 8.5pt;
        font-weight: 500;
        color: #1e293b;
        line-height: 1.3;
      }

      .property-notes-content {
        padding: 12pt;
        font-size: 9pt;
        line-height: 1.5;
        color: #334155;
        background: #fafafa;
        border-top: 0.5pt solid #e5e7eb;
      }

      /* Modern Minimalist Cover Section */
      .cover-section {
        text-align: center;
        padding: 40pt 32pt;
        margin: 16pt 0 20pt 0;
        border-radius: 20pt;
        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        border: 1pt solid #e2e8f0;
        box-shadow: 0 4pt 16pt rgba(0, 0, 0, 0.04);
        position: relative;
      }

      .cover-title {
        font-size: 24pt;
        font-weight: 600;
        color: #0f172a;
        margin-bottom: 20pt;
        letter-spacing: -0.3pt;
      }

      .cover-address {
        margin-top: 8pt;
        margin-bottom: 20pt;
        padding: 16pt 0;
      }
      
      .cover-address-main {
        font-size: 16pt;
        color: #1e293b;
        font-weight: 600;
        margin-bottom: 8pt;
        letter-spacing: 0.3pt;
        line-height: 1.4;
      }
      
      .cover-address-sub {
        font-size: 13pt;
        color: #475569;
        font-weight: 400;
        letter-spacing: 0.2pt;
        line-height: 1.4;
      }
      
      .cover-meta-row {
        display: flex;
        justify-content: center;
        gap: 16pt;
        margin-top: 24pt;
      }
      
      .cover-meta-item {
        text-align: center;
        padding: 12pt 20pt;
        background: white;
        border: 1pt solid #e2e8f0;
        border-radius: 12pt;
        min-width: 100pt;
        box-shadow: 0 2pt 4pt rgba(0, 0, 0, 0.02);
      }
      
      .cover-meta-label {
        font-size: 7.5pt;
        color: #94a3b8;
        text-transform: uppercase;
        letter-spacing: 0.8pt;
        margin-bottom: 6pt;
        font-weight: 600;
      }
      
      .cover-meta-value {
        font-size: 10.5pt;
        color: #0f172a;
        font-weight: 600;
      }

      /* Floor Plan Section - Matching Offline Export */
      .pdf-page {
        --page-height: 297mm;
        --page-vpad: 10mm;
        --header-est: 22mm;
        --legend-est: 140mm;
        --available-height: calc(var(--page-height) - var(--page-vpad) - var(--header-est) - var(--legend-est));
      }

      /* Content sections */
      .content-section {
        margin-bottom: 16pt;
        padding-top: 0;
      }

      /* Floor plan image styling - matching offline export */
      .floorplan-image {
        max-width: 100%;
        width: auto;
        height: auto;
        display: block;
        object-fit: contain;
        image-rendering: auto;
        margin: 0 auto;
        max-height: min(230mm, var(--available-height)) !important;
      }

      /* Full-page floor plan with space for section heading and Building Fabric table */
      .floorplan-image-fullpage {
        max-width: 200mm !important;
        max-height: 200mm !important;
        height: auto !important;
        width: auto !important;
        display: block;
        margin: 0 auto;
      }

      .floorplan-section {
        display: block;
        margin-bottom: 12pt;
        page-break-inside: avoid;
      }

      /* Element key styling - exact copy from offline export */
      .element-key {
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        border-bottom: 1.5pt solid rgba(0, 0, 0, 0.12);
        border-right: 1pt solid rgba(0, 0, 0, 0.1);
        border-radius: 12pt;
        overflow: hidden;
        margin-top: 16pt;
        background: #FFFFFF;
        page-break-inside: avoid;
        break-inside: avoid;
      }

      .element-key,
      .element-key .ek-row,
      .element-key .ek-cell {
        break-inside: avoid-page;
        page-break-inside: avoid;
      }

      .element-key .ek-header {
        background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
        color: #FFFFFF;
        font-weight: 500;
        text-align: center;
        padding: 7pt 16pt 13pt 16pt;
        font-size: 10pt;
        letter-spacing: 0.5pt;
        text-transform: uppercase;
        border-bottom: none;
        position: relative;
      }

      .element-key .ek-header::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 1.5pt;
        background: linear-gradient(90deg, #3B82F6, #60A5FA);
      }

      .element-key .ek-row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        border-bottom: 1pt solid #E8EDF3;
        transition: background-color 0.2s ease;
      }

      .element-key .ek-row:nth-child(even) {
        background: #FAFBFC;
      }

      .element-key .ek-row:last-child {
        border-bottom: none;
      }

      .element-key .ek-cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8pt;
        padding: 8pt 12pt;
        border-right: 1pt solid #E8EDF3;
        height: 32pt;
        position: relative;
        box-sizing: border-box;
      }

      .element-key .ek-cell:last-child {
        border-right: none;
      }
      
      .element-key .ek-cell:hover {
        background: rgba(59, 130, 246, 0.04);
      }

      .ek-symbol-line {
        display: inline-block;
        width: 14pt;
        height: 0;
        border-top: 1.2pt solid #000;
      }

      .ek-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 1pt 6pt;
        font-size: 7pt;
        font-weight: 500;
        border: 1pt solid rgba(0,0,0,0.1);
        border-radius: 6pt;
        min-width: 20pt;
        height: 18pt;
        flex-shrink: 0;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
      }

      .ek-light-led {
        color: #22c55e;
        font-weight: 500;
        font-size: 9pt;
        line-height: 1;
      }

      .ek-light-standard {
        display: inline-block;
        width: 7pt;
        height: 7pt;
        border: 1.2pt solid #f97316;
        border-radius: 50%;
      }

      .ek-desc {
        font-size: 8pt;
        color: #64748B;
        font-weight: 400;
        line-height: 1.3;
      }

      .ek-desc strong {
        color: #334155;
        font-weight: 500;
      }

      /* Occupancy table styling */
      .occupancy-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: 6pt;
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        overflow: hidden;
        margin-top: 8pt;
        font-size: 9pt;
      }

      .occupancy-table thead tr {
        background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
        color: white;
      }

      .occupancy-table th {
        padding: 6pt 10pt;
        text-align: left;
        font-weight: 500;
        font-size: 9pt;
        border-right: 0.5pt solid rgba(255, 255, 255, 0.1);
      }

      .occupancy-table th:last-child {
        border-right: none;
      }

      .occupancy-table tbody tr:nth-child(even) {
        background: #F8FAFC;
      }

      .occupancy-table tbody tr:nth-child(odd) {
        background: white;
      }

      .occupancy-table td {
        padding: 6pt 10pt;
        border-bottom: 0.5pt solid #E8EDF3;
        border-right: 0.5pt solid #E8EDF3;
        color: #1E293B;
      }

      .occupancy-table td:last-child {
        border-right: none;
      }

      .occupancy-table tbody tr:last-child td {
        border-bottom: none;
      }

      /* Utility classes */
      .text-sm {
        font-size: 8pt;
      }

      .text-muted {
        color: #6B7280;
      }

      .mt-2 {
        margin-top: 8pt;
      }

      .mb-2 {
        margin-bottom: 8pt;
      }

      /* Room details table */
      .room-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: 6pt;
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        overflow: hidden;
        margin-bottom: 10pt;
        font-size: 8.5pt;
      }

      .room-table thead tr {
        background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
        color: white;
      }

      .room-table th {
        padding: 5pt 8pt;
        text-align: left;
        font-weight: 500;
        font-size: 8.5pt;
      }

      .room-table tbody tr:nth-child(even) {
        background: #F8FAFC;
      }

      .room-table td {
        padding: 5pt 8pt;
        border-bottom: 0.5pt solid #E8EDF3;
        border-right: 0.5pt solid #E8EDF3;
      }

      .room-table td:last-child {
        border-right: none;
      }

      .room-table tbody tr:last-child td {
        border-bottom: none;
      }

      /* Element Key Table - Matching Offline Floor Plan Export */
      .element-key {
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        border-bottom: 1.5pt solid rgba(0, 0, 0, 0.12);
        border-right: 1pt solid rgba(0, 0, 0, 0.1);
        border-radius: 12pt;
        overflow: hidden;
        margin-top: 16pt;
        margin-bottom: 20pt; /* Extra bottom margin to prevent cutoff */
        background: #FFFFFF;
      }

      .element-key .ek-header {
        background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
        color: #FFFFFF;
        font-weight: 500;
        text-align: center;
        padding: 7pt 16pt 13pt 16pt;
        font-size: 10pt;
        letter-spacing: 0.5pt;
        text-transform: uppercase;
        border-bottom: none;
        position: relative;
      }
      
      .element-key .ek-header::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2pt;
        background: var(--theme-color);
      }

      .element-key .ek-row {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr 1fr;
        border-bottom: 1pt solid #E8EDF3;
        transition: background-color 0.2s ease;
      }
      
      .element-key .ek-row:nth-child(even) {
        background: #FAFBFC;
      }

      .element-key .ek-row:last-child {
        border-bottom: none;
      }

      .element-key .ek-cell {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8pt;
        padding: 8pt 12pt;
        border-right: 1pt solid #E8EDF3;
        height: 32pt;
        position: relative;
        box-sizing: border-box;
      }

      .element-key .ek-cell:last-child {
        border-right: none;
      }
      
      .element-key .ek-cell:hover {
        background: rgba(59, 130, 246, 0.04);
      }

      .ek-symbol-line {
        display: inline-block;
        width: 14pt;
        height: 0;
        border-top: 1.2pt solid #000;
      }

      .ek-badge {
        display: inline-block;
        text-align: center;
        padding: 1pt 6pt 5pt 6pt;
        font-size: 7pt;
        font-weight: 500;
        line-height: 1;
        border: 1pt solid rgba(0,0,0,0.1);
        border-radius: 6pt;
        min-width: 20pt;
        min-height: 16pt;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
        text-rendering: optimizeLegibility;
      }

      .ek-light-led {
        color: #22c55e;
        font-weight: 500;
        font-size: 9pt;
        line-height: 1;
      }

      .ek-light-standard {
        display: inline-block;
        width: 7pt;
        height: 7pt;
        border: 1.2pt solid #f97316;
        border-radius: 50%;
      }

      .ek-desc {
        font-size: 8pt;
        color: #64748B;
        font-weight: 400;
        line-height: 1.3;
      }
      
      .ek-desc strong {
        color: #334155;
        font-weight: 500;
      }

      /* Radiators & Windows Summary Table Styles */
      .clean-table-container {
        margin: 16pt 0;
      }

      .items-summary-table {
        width: 100%;
        border-collapse: collapse;
        margin: 0;
        background: white;
        box-shadow: 0 1pt 3pt rgba(0, 0, 0, 0.1);
        border: 1pt solid #E5E7EB;
      }

      .items-summary-table thead tr {
        background: #F8FAFC;
        border-bottom: 2pt solid #D1D5DB;
      }

      .items-summary-table th {
        color: #374151;
        font-weight: 600;
        padding: 10pt 6pt;
        text-align: left;
        font-size: 7pt;
        text-transform: uppercase;
        border-right: 1pt solid #E5E7EB;
        letter-spacing: 0.5pt;
      }

      .items-summary-table th:last-child {
        border-right: none;
      }

      .items-summary-table .col-floor { width: 8%; }
      .items-summary-table .col-room { width: 7%; }
      .items-summary-table .col-radiator { width: 10%; }
      .items-summary-table .col-window { width: 9%; }
      .items-summary-table .col-glazing { width: 11%; }
      .items-summary-table .col-gap { width: 7%; }
      .items-summary-table .col-frame { width: 8%; }
      .items-summary-table .col-condition { width: 8%; }
      .items-summary-table .col-draught { width: 8%; }
      .items-summary-table .col-orient { width: 10%; }
      /* Removed columns: door (7%), secondary (8%), vent (7%) */

      .items-summary-table tbody tr {
        border-bottom: 1pt solid #F3F4F6;
      }

      .items-summary-table tbody tr:nth-child(even) {
        background: #FAFBFC;
      }

      .items-summary-table tbody tr:nth-child(odd) {
        background: #FFFFFF;
      }

      .items-summary-table td {
        padding: 8pt 6pt;
        border-right: 1pt solid #F3F4F6;
        vertical-align: top;
        font-size: 7pt;
        line-height: 1.4;
      }

      .items-summary-table td:last-child {
        border-right: none;
      }

      .items-summary-table .floor-location {
        font-weight: 500;
        color: #4B5563;
      }

      .items-summary-table .room-name {
        font-weight: 600;
        color: #1F2937;
      }

      .items-summary-table .item-cell {
        color: #374151;
      }

      .items-summary-table .empty-cell {
        color: #9CA3AF;
        font-style: italic;
      }

      .items-summary-table .single-item {
        font-size: 7pt;
        color: #1F2937;
        line-height: 1.3;
      }

      .items-summary-table .item-type {
        font-size: 6pt;
        color: #6B7280;
        display: block;
        margin-top: 2pt;
      }

      .items-summary-table .multi-item-header {
        font-size: 6.5pt;
        color: #6B7280;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3pt;
        margin-bottom: 4pt;
        padding-bottom: 2pt;
        border-bottom: 0.5pt solid #E5E7EB;
      }

      .items-summary-table .item-row {
        display: flex;
        align-items: baseline;
        margin-bottom: 2pt;
        padding: 2pt 0;
        font-size: 6.5pt;
        line-height: 1.3;
      }

      .items-summary-table .row-num {
        display: inline-block;
        width: 10pt;
        font-weight: 600;
        color: var(--theme-color);
        margin-right: 4pt;
        flex-shrink: 0;
      }

      .items-summary-table .item-spec {
        font-weight: 500;
        color: #1F2937;
        margin-right: 4pt;
        flex-grow: 1;
      }

      .items-summary-table .item-desc {
        font-size: 6pt;
        color: #6B7280;
        flex-grow: 2;
      }

      /* Footer styling */
      .items-summary-table tfoot .totals-row {
        background: #F9FAFB;
        border-top: 2pt solid #D1D5DB;
        font-weight: 600;
        color: #374151;
      }

      .items-summary-table tfoot td {
        padding: 8pt 6pt;
        font-size: 7pt;
      }

      /* Formula & Working Section Styling */
      .calculations-container {
        margin: 16pt 0;
      }

      .floor-calculation-block {
        margin-bottom: 16pt;
        break-inside: avoid;
        page-break-inside: avoid;
        border: 1pt solid #E5E7EB;
        border-radius: 6pt;
        overflow: hidden;
      }

      .floor-header {
        background: var(--theme-color);
        color: white;
        padding: 8pt 12pt;
        font-weight: 600;
        font-size: 9pt;
        letter-spacing: 0.3pt;
      }

      .calculation-content {
        padding: 12pt;
        background: white;
      }

      .calc-item {
        margin-bottom: 6pt;
        display: flex;
        align-items: flex-start;
      }

      .calc-item:last-child {
        margin-bottom: 0;
      }

      .calc-label {
        font-weight: 600;
        color: #374151;
        min-width: 50pt;
        margin-right: 8pt;
        font-size: 8pt;
        flex-shrink: 0;
      }

      .calc-formula {
        color: #1F2937;
        font-size: 9pt;
        font-family: 'Courier New', monospace;
        background: #F9FAFB;
        padding: 4pt 8pt;
        border-radius: 3pt;
        flex-grow: 1;
        word-break: break-word;
        line-height: 1.4;
      }

      /* Table of Contents Styles - Modern Minimalist */
      .toc-container {
        padding: 30pt 40pt;
      }

      .toc-heading {
        font-size: 18pt;
        font-weight: 600;
        color: #1a1a1a;
        margin: 0 0 30pt 0;
        padding-bottom: 12pt;
        border-bottom: 2pt solid #e5e7eb;
        text-transform: uppercase;
        letter-spacing: 1.5pt;
      }

      .toc-grid {
        display: grid;
        grid-template-columns: 1fr;
        gap: 0;
      }

      .toc-item {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        padding: 12pt 0;
        min-height: 36pt;
        background: transparent;
        border-bottom: 1pt solid #f3f4f6;
        transition: all 0.15s ease;
        cursor: pointer;
        text-decoration: none !important;
        color: inherit;
        position: relative;
        width: 100%;
        gap: 12pt;
      }

      .toc-item:hover {
        background: #fafbfc;
        padding-left: 8pt;
        padding-right: 8pt;
        margin-left: -8pt;
        margin-right: -8pt;
      }

      .toc-item:hover .toc-item-number {
        color: var(--theme-color);
        font-weight: 700;
      }

      .toc-item:hover .toc-item-title {
        color: #000000;
      }

      .toc-item-number {
        font-size: 10pt;
        font-weight: 600;
        color: #6b7280;
        min-width: 32pt;
        flex-shrink: 0;
        transition: all 0.15s ease;
      }

      .toc-item-title {
        font-size: 10.5pt;
        font-weight: 500;
        color: #374151;
        line-height: 1.4;
        transition: all 0.15s ease;
        max-width: 350pt;
      }

      .toc-dots {
        flex: 1;
        display: block;
        border-bottom: 1pt dotted #d1d5db;
        height: 1pt;
        margin: 0 12pt;
        min-width: 40pt;
        align-self: flex-end;
        margin-bottom: 4pt;
      }

      .toc-item-page {
        font-size: 9.5pt;
        color: #9ca3af;
        font-weight: 500;
        min-width: 60pt;
        text-align: right;
        font-variant-numeric: tabular-nums;
        flex-shrink: 0;
      }

      /* Custom Fields Styles (from Form Builder) */
      ${getCustomFieldsPDFStyles()}

    </style>
  `;
}

/**
 * Generate page header (reduced height)
 */
export function generateHeader(
  surveyData: RetrofitSurveyData,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  // Combine house number and street address
  const houseNumber = surveyData.initial_questionnaire.houseNumberName || '';
  const street = surveyData.initial_questionnaire.streetAddress || '';
  const address = houseNumber && street 
    ? `${houseNumber} ${street}` 
    : street || houseNumber || 'Property Address';
  const postCode = String(surveyData.initial_questionnaire.postCode || '');
  const fullAddress = postCode ? `${address}, ${postCode}` : address;
  
  const logoDataUrl = options?.logoDataUrl;
  
  return `
    <div class="header">
      <div class="header-content">
        <div class="header-left">
          <p class="property-address">${escapeHtml(fullAddress)}</p>
          <p class="report-type">Retrofit Survey Report</p>
          <p class="page-number">Page ${pageNumber} of ${totalPages}</p>
        </div>
        <div class="header-right">
          ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
        </div>
      </div>
    </div>
  `;
}

/**
 * Generate page footer with company information (2 lines, centered at bottom)
 */
export function generateFooter(options?: RetrofitPDFExportOptions): string {
  const companyName = options?.companyName || '';
  const companyAddress = options?.companyAddress || '';
  const companyEmail = options?.companyEmail || '';
  const companyPhone = options?.companyPhone || '';
  const companyWebsite = options?.companyWebsite || '';
  
  // Build footer items array
  const allItems = [
    companyName,
    companyAddress,
    companyEmail,
    companyPhone,
    companyWebsite
  ].filter(Boolean); // Remove empty strings
  
  if (allItems.length === 0) {
    return ''; // No footer if no company info
  }
  
  // Split items into 2 lines
  // Line 1: Company name and address (or first items)
  // Line 2: Contact info (email, phone, website)
  const line1Items: string[] = [];
  const line2Items: string[] = [];
  
  if (companyName) line1Items.push(companyName);
  if (companyAddress) line1Items.push(companyAddress);
  if (companyEmail) line2Items.push(companyEmail);
  if (companyPhone) line2Items.push(companyPhone);
  if (companyWebsite) line2Items.push(companyWebsite);
  
  // If line2 is empty, split line1 into 2 lines
  if (line2Items.length === 0 && line1Items.length > 1) {
    const mid = Math.ceil(line1Items.length / 2);
    line2Items.push(...line1Items.splice(mid));
  }
  
  const line1Text = line1Items.map(item => escapeHtml(item)).join(' <span class="footer-separator">•</span> ');
  const line2Text = line2Items.map(item => escapeHtml(item)).join(' <span class="footer-separator">•</span> ');
  
  return `
    <div class="footer">
      ${line1Text ? `<div class="footer-line">${line1Text}</div>` : ''}
      ${line2Text ? `<div class="footer-line">${line2Text}</div>` : ''}
    </div>
  `;
}

/**
 * Generate modern minimalist cover section
 */
function generateCoverSection(surveyData: RetrofitSurveyData): string {
  const { initial_questionnaire } = surveyData;
  
  // Format address components - combine house number and street with proper spacing
  const houseNumber = String(initial_questionnaire.houseNumberName || '').trim();
  const street = String(initial_questionnaire.streetAddress || '').trim();
  const streetAddress = houseNumber && street 
    ? `${houseNumber} ${street}` 
    : street || houseNumber || 'Address Not Specified';
  const town = String(initial_questionnaire.town || '').trim();
  const postCode = String(initial_questionnaire.postCode || '').trim();
  const locationText = [town, postCode].filter(Boolean).join(', ');
  
  return `
    <div class="cover-section">
      <div class="cover-title">Property Retrofit Assessment</div>
      
      <div class="cover-address">
        <div class="cover-address-main">${escapeHtml(streetAddress)}</div>
        ${locationText ? `<div class="cover-address-sub">${escapeHtml(locationText)}</div>` : ''}
      </div>
      
      <div class="cover-meta-row">
        <div class="cover-meta-item">
          <div class="cover-meta-label">Survey Date</div>
          <div class="cover-meta-value">${escapeHtml(capitalizeText(initial_questionnaire.surveyDate))}</div>
        </div>
        <div class="cover-meta-item">
          <div class="cover-meta-label">Surveyor</div>
          <div class="cover-meta-value">${escapeHtml(capitalizeText(initial_questionnaire.surveyorName))}</div>
        </div>
        <div class="cover-meta-item">
          <div class="cover-meta-label">Property Type</div>
          <div class="cover-meta-value">${escapeHtml(capitalizeText(initial_questionnaire.dwellingType))}</div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Generate property details section with modern card layout
 */
function generatePropertyDetailsSection(surveyData: RetrofitSurveyData): string {
  const { initial_questionnaire } = surveyData;
  
  return `
    <div class="content-section">
      <div class="section-heading">Property Overview</div>
      
      <div class="property-cards">
        <!-- Customer Information Card -->
        <div class="property-card">
          <div class="property-card-title">Customer Information</div>
          <div class="property-card-content">
            <div class="property-card-row">
              <div class="property-card-label">Name</div>
              <div class="property-card-value">${capitalizeText(initial_questionnaire.customerFullName)}</div>
            </div>
            <div class="property-card-row">
              <div class="property-card-label">Email</div>
              <div class="property-card-value">${initial_questionnaire.customerEmail || 'Not Specified'}</div>
            </div>
            <div class="property-card-row">
              <div class="property-card-label">Mobile</div>
              <div class="property-card-value">${initial_questionnaire.customerMobile || 'Not Specified'}</div>
            </div>
          </div>
        </div>
        
        <!-- Property Characteristics Card -->
        <div class="property-card">
          <div class="property-card-title">Property Characteristics</div>
          <div class="property-card-content">
            <div class="property-card-row">
              <div class="property-card-label">Building Age</div>
              <div class="property-card-value">${capitalizeText(initial_questionnaire.buildingAge)}</div>
            </div>
            <div class="property-card-row">
              <div class="property-card-label">Tenure</div>
              <div class="property-card-value">${capitalizeText(initial_questionnaire.tenure)}</div>
            </div>
            <div class="property-card-row">
              <div class="property-card-label">Orientation</div>
              <div class="property-card-value">${surveyData.property_front?.buildingOrientation || initial_questionnaire.buildingOrientation ? `${surveyData.property_front?.buildingOrientation || initial_questionnaire.buildingOrientation}°` : 'Not Specified'}</div>
            </div>
          </div>
        </div>
        
        <!-- Room Configuration & Building Construction Row -->
        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8pt; margin-bottom: 10pt;">
          <!-- Room Configuration Card (Compact) -->
          <div class="property-card">
            <div class="property-card-title">Room Configuration</div>
            <div class="property-card-content" style="padding: 6pt;">
              <table style="width: 100%; border-collapse: collapse;">
                <tr style="border-bottom: 0.5pt solid #e5e7eb;">
                  <td style="padding: 4pt 6pt; font-size: 8.5pt; color: #6b7280; font-weight: 500;">Habitable</td>
                  <td style="padding: 4pt 6pt; font-size: 8.5pt; color: #6b7280; font-weight: 500;">Heated</td>
                  <td style="padding: 4pt 6pt; font-size: 8.5pt; color: #6b7280; font-weight: 500;">Total</td>
                </tr>
                <tr>
                  <td style="padding: 6pt; font-size: 10pt; color: #1f2937; font-weight: 600; text-align: center;">${surveyData.ventilation_condition?.habitableRooms || initial_questionnaire.habitableRooms || '-'}</td>
                  <td style="padding: 6pt; font-size: 10pt; color: #1f2937; font-weight: 600; text-align: center;">${surveyData.ventilation_condition?.heatedHabitableRooms || initial_questionnaire.heatedHabitableRooms || '-'}</td>
                  <td style="padding: 6pt; font-size: 10pt; color: #1f2937; font-weight: 600; text-align: center;">${surveyData.ventilation_condition?.totalRooms || initial_questionnaire.totalRooms || '-'}</td>
                </tr>
              </table>
            </div>
          </div>
          
          <!-- Building Construction Card -->
          <div class="property-card">
            <div class="property-card-title">Building Construction</div>
            <div class="property-card-content">
              <div class="property-card-row">
                <div class="property-card-label">Build Form</div>
                <div class="property-card-value">${capitalizeText(surveyData.property_front?.buildForm)}</div>
              </div>
              <div class="property-card-row">
                <div class="property-card-label">Roof Type</div>
                <div class="property-card-value">${capitalizeText(surveyData.property_front?.roofType)}</div>
              </div>
              ${surveyData.property_front?.roofCondition ? `
              <div class="property-card-row">
                <div class="property-card-label">Roof Condition</div>
                <div class="property-card-value">${capitalizeText(surveyData.property_front?.roofCondition)}</div>
              </div>
              ` : ''}
              <div class="property-card-row">
                <div class="property-card-label">Wall Construction</div>
                <div class="property-card-value">${(() => {
                  const wallType = surveyData.initial_questionnaire?.majorityWallType || surveyData.property_front?.propertyWallConstructionType || surveyData.property_front?.wallConstruction;
                  return wallType ? getWallTypeLabel(wallType) : 'Not Specified';
                })()}</div>
              </div>
              <div class="property-card-row">
                <div class="property-card-label">Wall Thickness</div>
                <div class="property-card-value">${surveyData.property_front?.wallThickness ? `${surveyData.property_front.wallThickness}mm` : 'Not Specified'}</div>
              </div>
              <div class="property-card-row">
                <div class="property-card-label">Floor Construction</div>
                <div class="property-card-value">${capitalizeText(surveyData.property_front?.floorConstructionType)}</div>
              </div>
              ${surveyData.property_front?.gutteringCondition ? `
              <div class="property-card-row">
                <div class="property-card-label">Guttering Condition</div>
                <div class="property-card-value">${capitalizeText(surveyData.property_front?.gutteringCondition)}</div>
              </div>
              ` : ''}
            </div>
          </div>
        </div>
        
        <!-- Any Other Relevant Information Card (Full Width) -->
        ${surveyData.initial_questionnaire?.additionalNotes && surveyData.initial_questionnaire.additionalNotes.trim() ? `
        <div class="property-card" style="margin-bottom: 10pt;">
          <div class="property-card-title">Any Other Relevant Information</div>
          <div class="property-card-content" style="padding: 10pt;">
            <div style="font-size: 9pt; color: #374151; line-height: 1.5; white-space: pre-wrap; word-wrap: break-word;">${escapeHtml(surveyData.initial_questionnaire.additionalNotes)}</div>
          </div>
        </div>
        ` : ''}
      </div>
      
    </div>
  `;
}

/**
 * Generate Table of Contents pages with clickable links
 * Splits into multiple pages if more than 13 items
 * Returns array of content HTML (without page wrapper)
 */
function generateTableOfContents(
  sectionPages: SectionPageMap,
  surveyData: RetrofitSurveyData,
  options: RetrofitPDFExportOptions
): string[] {
  // Create a map of built-in section IDs to their TOC metadata
  const builtInSectionMap: Record<string, { title: string; key: string }> = {
    'property_overview': { title: 'Property Overview', key: 'propertyOverview' },
    [BUILT_IN_SECTION_IDS.FLOOR_PLAN]: { title: 'Floor Plan', key: 'floorPlan' },
    [BUILT_IN_SECTION_IDS.RADIATORS_WINDOWS]: { title: 'Radiators & Windows Summary', key: 'radiatorsWindows' },
    [BUILT_IN_SECTION_IDS.DIMENSIONS_CALCULATIONS]: { title: 'All Floors Dimensions & Calculations', key: 'dimensionsCalculations' },
    [BUILT_IN_SECTION_IDS.HEATING_SYSTEM]: { title: 'Heating System Assessment', key: 'heatingSystem' },
    [BUILT_IN_SECTION_IDS.MEASURE_SPECIFICATION]: { title: 'Measure Specification', key: 'measureSpecification' },
    [BUILT_IN_SECTION_IDS.PAS2030_QUESTIONS]: { title: 'PAS 2030 Questions', key: 'pas2030Questions' },
    [BUILT_IN_SECTION_IDS.PROPERTY_FRONT]: { title: 'Property Front', key: 'propertyFront' },
    [BUILT_IN_SECTION_IDS.PROPERTY_SIDE]: { title: 'Property Side', key: 'propertySide' },
    [BUILT_IN_SECTION_IDS.PROPERTY_REAR]: { title: 'Property Rear', key: 'propertyRear' },
    [BUILT_IN_SECTION_IDS.CELLAR]: { title: 'Cellar', key: 'cellar' },
    [BUILT_IN_SECTION_IDS.ROOMS]: { title: 'Rooms', key: 'rooms' },
    [BUILT_IN_SECTION_IDS.EXTENSIONS]: { title: 'Extensions', key: 'extensions' },
    [BUILT_IN_SECTION_IDS.METERS_SERVICES]: { title: 'Meters & Services', key: 'metersServices' },
    [BUILT_IN_SECTION_IDS.VENTILATION_CONDITION]: { title: 'Ventilation & Condition', key: 'ventilationCondition' },
    [BUILT_IN_SECTION_IDS.OCCUPANCY]: { title: 'Occupancy Assessment', key: 'occupancy' },
    [BUILT_IN_SECTION_IDS.SITE_ASSESSMENT]: { title: 'Site Assessment', key: 'siteAssessment' },
    [BUILT_IN_SECTION_IDS.SIGNATURES]: { title: 'Signatures & Feedback', key: 'signatures' },
    [BUILT_IN_SECTION_IDS.ADDITIONAL_NOTES]: { title: 'Additional Notes', key: 'additionalNotes' },
  };
  
  // Get the actual page order (merged order respecting custom page positions)
  const pageOrder = getMergedPageOrder(options.formTemplate);
  
  // Get custom pages for lookup
  const customPages = options.formTemplate ? getCustomPagesForPDF(options.formTemplate) : [];
  const customPageMap = new Map(customPages.map(p => [p.id, p]));
  
  // Build sections array following the actual page order
  const sections: Array<{ key: string; number: string; title: string; page: number | null; id: string }> = [];
  
  // Always add Property Overview first (it's always page 1)
  if (sectionPages.propertyOverview) {
    sections.push({
      key: 'propertyOverview',
      number: '', // Will be renumbered below
      title: 'Property Overview',
      page: sectionPages.propertyOverview,
      id: 'property-overview'
    });
  }
  
  // Then follow the page order for remaining sections
  for (const sectionId of pageOrder) {
    // Check if it's a built-in section
    if (builtInSectionMap[sectionId]) {
      const { title, key } = builtInSectionMap[sectionId];
      const page = (sectionPages as any)[key];
      
      if (page !== null && page !== undefined) {
        sections.push({
          key,
          number: '', // Will be renumbered below
          title,
          page,
          id: sectionId
        });
      }
    } 
    // Check if it's a custom page
    else if (customPageMap.has(sectionId)) {
      const customPage = customPageMap.get(sectionId)!;
      const page = sectionPages.customPages?.[sectionId];
      
      if (page !== null && page !== undefined) {
        sections.push({
          key: `custom_${sectionId}`,
          number: '', // Will be renumbered below
          title: customPage.title,
          page,
          id: sectionId
        });
      }
    }
  }
  
  // Renumber all sections sequentially
  const numberedSections = sections.map((section, index) => ({
    ...section,
    number: String(index + 1)
  }));
  
  console.log(`📋 [TOC] Generated ${numberedSections.length} sections in order:`, 
    numberedSections.map(s => `${s.number}. ${s.title} (pg ${s.page})`));

  const ITEMS_PER_PAGE = 13;
  const totalPages = Math.ceil(numberedSections.length / ITEMS_PER_PAGE);
  const contentPages: string[] = [];

  // Split sections into chunks of 13
  for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
    const startIdx = pageIndex * ITEMS_PER_PAGE;
    const endIdx = Math.min(startIdx + ITEMS_PER_PAGE, numberedSections.length);
    const pageSections = numberedSections.slice(startIdx, endIdx);

    const tocItems = pageSections.map(section => `
      <a href="#${section.id}" class="toc-item" style="display: flex; width: 100%; text-decoration: none;">
        <span class="toc-item-number">${section.number}.</span>
        <span class="toc-item-title">${section.title}</span>
        <span class="toc-dots"></span>
        <span class="toc-item-page">Page ${section.page}</span>
      </a>
    `).join('');

    const pageContent = `
      <div class="toc-container">
        <div class="toc-heading">Table of Contents${totalPages > 1 ? ` (${pageIndex + 1}/${totalPages})` : ''}</div>
        <div class="toc-grid">
          ${tocItems}
        </div>
      </div>
    `;

    contentPages.push(pageContent);
  }

  return contentPages;
}

/**
 * Generate initial assessment photos section (Meters page photos)
 * Shows ALL photos with max 4 per page
 * Returns array of page HTML strings
 */
async function generateInitialPhotosPages(surveyData: RetrofitSurveyData, startPage: number, totalPages: number, options?: RetrofitPDFExportOptions): Promise<string[]> {
  const { initial_questionnaire } = surveyData;
  const photos = initial_questionnaire.photos || {};
  
  const photoSections: { label: string; key: string }[] = [
    { label: 'Orientation Image', key: 'orientationImage' },
    { label: 'Gas Meter Earth Bonding', key: 'gasMeterEarthBonding' },
    { label: 'Electric Meter', key: 'electricMeter' },
    { label: 'Consumer Board', key: 'consumerBoard' },
    { label: 'Stop Tap', key: 'stopTap' }
  ];

  // Collect all photos and optimize them
  const allPhotos: { url: string; label: string }[] = [];
  
  for (const section of photoSections) {
    const sectionPhotos = photos[section.key]?.photos || [];
    
    for (const photo of sectionPhotos) {
      const photoUrl = photo.url || photo.localPath || '';
      
      if (photoUrl) {
        try {
          // Optimize photo for PDF (resize and compress with high quality)
          const optimizedUrl = await optimizeImageForPDF(photoUrl, 1600, 1200, 0.90);
          allPhotos.push({
            url: optimizedUrl,
            label: `${section.label}${sectionPhotos.length > 1 ? ` (${sectionPhotos.indexOf(photo) + 1}/${sectionPhotos.length})` : ''}`
          });
        } catch (error) {
          console.warn(`Failed to optimize photo for ${section.label}:`, error);
          // Fallback to original URL if optimization fails
          allPhotos.push({
            url: photoUrl,
            label: `${section.label}${sectionPhotos.length > 1 ? ` (${sectionPhotos.indexOf(photo) + 1}/${sectionPhotos.length})` : ''}`
          });
        }
      }
    }
  }

  if (allPhotos.length === 0) {
    return [`
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage, totalPages, options)}
        <div class="content-section">
          <div class="section-heading">Meters Assessment Photos</div>
          <p class="text-muted">No photos available</p>
        </div>
        ${generateFooter(options)}
      </section>
    `];
  }

  // Split into pages of max 4 photos each
  const pages: string[] = [];
  for (let i = 0; i < allPhotos.length; i += 4) {
    const pagePhotos = allPhotos.slice(i, i + 4);
    const photosHTML = pagePhotos.map(photo => `
      <div class="photo-item">
        <img src="${photo.url}" alt="${photo.label}" />
        <div class="photo-label">${photo.label}</div>
      </div>
    `).join('');

    pages.push(`
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + Math.floor(i / 4), totalPages, options)}
        <div class="content-section">
          <div class="section-heading">Meters Assessment Photos ${allPhotos.length > 4 ? `(${Math.floor(i / 4) + 1}/${Math.ceil(allPhotos.length / 4)})` : ''}</div>
          <div class="photo-grid">
            ${photosHTML}
          </div>
        </div>
        ${generateFooter(options)}
      </section>
    `);
  }

  return pages;
}

/**
 * Generate element key (legend) HTML
 */
function generateElementKeyHTML(): string {
  return `
    <div class="element-key">
      <div class="ek-header">ELEMENT KEY</div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-symbol-line" style="border-top-color:#ff0000"></span>
            <span class="ek-desc"><strong>HLP</strong> - Heat Loss Perimeter</span>
          </div>
          <div class="ek-cell">
            <span class="ek-symbol-line" style="border-top-color:#0000ff"></span>
            <span class="ek-desc"><strong>Party Wall</strong> - Party Wall</span>
          </div>
          <div class="ek-cell">
            <span class="ek-symbol-line" style="border-top-color:#f97316"></span>
            <span class="ek-desc"><strong>Sheltered</strong> - Sheltered Wall</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">RADT</span>
            <span class="ek-desc">- Radiator with TRVs</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">RAD</span>
            <span class="ek-desc">- Radiator</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">H/S</span>
            <span class="ek-desc">- Heat Source</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">PH</span>
            <span class="ek-desc">- Panel Heat</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">FH</span>
            <span class="ek-desc">- Fan Heat</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">ESH</span>
            <span class="ek-desc">- Storage Heat</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">GM</span>
            <span class="ek-desc">- Gas Meter</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">EM</span>
            <span class="ek-desc">- Electric Meter</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">CU</span>
            <span class="ek-desc">- Consumer Unit</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">TNK</span>
            <span class="ek-desc">- Tank</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">FAN</span>
            <span class="ek-desc">- Fan</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">V</span>
            <span class="ek-desc">- Vent</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">T-STAT</span>
            <span class="ek-desc">- Thermostat</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#e6f3ff;">LH</span>
            <span class="ek-desc">- Loft Hatch</span>
          </div>
          <div class="ek-cell">
            <span class="ek-light-led">×</span>
            <span class="ek-desc">- LED Light</span>
          </div>
          <div class="ek-cell">
            <span class="ek-light-standard"></span>
            <span class="ek-desc">- Standard Light</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc" style="color:#ef4444;">SA</strong>
            <span class="ek-desc">- Smoke Alarm</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <strong class="ek-desc">DR</strong>
            <span class="ek-desc">- Dining Room</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">ST</strong>
            <span class="ek-desc">- Study</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">BR</strong>
            <span class="ek-desc">- Bedroom</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">BTH</strong>
            <span class="ek-desc">- Bathroom</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <strong class="ek-desc">LR</strong>
            <span class="ek-desc">- Living Room</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">H</strong>
            <span class="ek-desc">- Hallway</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">L</strong>
            <span class="ek-desc">- Landing</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">K/D</strong>
            <span class="ek-desc">- Kitchen/Diner</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <strong class="ek-desc">K</strong>
            <span class="ek-desc">- Kitchen</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">W/C</strong>
            <span class="ek-desc">- Water Closet</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">CPD</strong>
            <span class="ek-desc">- Cupboard</span>
          </div>
          <div class="ek-cell"></div>
        </div>
      </div>`;
}

/**
 * Get floor name label (Ground Floor, First Floor, etc.)
 */
function getFloorLabel(floorNumber: number): string {
  const floorNames: Record<number, string> = {
    1: 'Ground Floor',
    2: 'First Floor',
    3: 'Second Floor',
    4: 'Third Floor',
    5: 'Fourth Floor',
    6: 'Fifth Floor',
    7: 'Sixth Floor',
    8: 'Seventh Floor',
    9: 'Eighth Floor',
    10: 'Ninth Floor'
  };
  
  return floorNames[floorNumber] || `Floor ${floorNumber}`;
}

/**
 * Check if a floor plan needs full-page layout to avoid stretching
 */
function needsFullPageLayout(imageData: FloorPlanImageData): boolean {
  const A4_WIDTH_MM = 210;
  const AVAILABLE_WIDTH_MM = A4_WIDTH_MM - 10;
  const STANDARD_PAGE_HEIGHT_MM = 230;
  const MM_TO_PX = 3.7795;
  
  const canvasWidthMm = (imageData.canvasWidth / MM_TO_PX);
  const canvasHeightMm = (imageData.canvasHeight / MM_TO_PX);
  
  const standardWidthScale = AVAILABLE_WIDTH_MM / canvasWidthMm;
  const standardHeightScale = STANDARD_PAGE_HEIGHT_MM / canvasHeightMm;
  
  const scaleRatio = standardWidthScale / standardHeightScale;
  return scaleRatio > 1.2 || scaleRatio < 0.8; // 20% aspect ratio tolerance
}

/**
 * Calculate total pages needed for floor plans (accounting for full-page layouts)
 */
function calculateFloorPlanPageCount(floorPlanImages: FloorPlanImageData[]): number {
  let pageCount = 0;
  floorPlanImages.forEach((imageData) => {
    if (needsFullPageLayout(imageData)) {
      pageCount += 2; // Floor plan page + element key page
    } else {
      pageCount += 1; // Standard single page
    }
  });
  return pageCount;
}

/**
 * Generate floor plan pages (one page per floor)
 * Automatically detects if floor plan needs full-page layout to avoid stretching
 */
function generateFloorPlanPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options: RetrofitPDFExportOptions = {}
): string[] {
  const floorPlanImages = options.floorPlanImages || [];
  
  if (floorPlanImages.length === 0) {
    return [];
  }
  
  const pages: string[] = [];
  const floors = surveyData.floor_plan?.linkedFloorPlanData?.siteNotesData?.floors || [];
  
  let currentPage = startPage;
  
  // Generate pages for each floor plan image
  floorPlanImages.forEach((imageData, index) => {
    const floor = floors[index];
    const floorNumber = floor?.floorNumber || (index + 1);
    const floorLabel = getFloorLabel(floorNumber);
    
    const wouldBeStretched = needsFullPageLayout(imageData);
    
    console.log(`📐 Floor ${floorNumber} layout decision:`, {
      canvasDimensions: { widthPx: imageData.canvasWidth, heightPx: imageData.canvasHeight },
      wouldBeStretched,
      layout: wouldBeStretched ? 'FULL-PAGE (2 pages)' : 'STANDARD (1 page)'
    });
    
    if (wouldBeStretched) {
      // Full-page layout: Floor plan on page 1, element key on page 2
      pages.push(`
        <section class="pdf-page">
          ${generateHeader(surveyData, currentPage, totalPages, options)}
          <div class="content-section floorplan-section">
            <h2 class="section-heading">${floorLabel}</h2>
            ${generateBuildingFabricTableHTML(surveyData)}
            <img class="floorplan-image-fullpage" src="${imageData.dataUrl}" alt="${floorLabel}" />
          </div>
          ${generateFooter(options)}
        </section>
      `);
      currentPage++;
      
      // Element key on separate page
      pages.push(`
        <section class="pdf-page">
          ${generateHeader(surveyData, currentPage, totalPages, options)}
          <div class="content-section">
            <h2 class="section-heading">${floorLabel} - Legend</h2>
            ${generateElementKeyHTML()}
          </div>
          ${generateFooter(options)}
        </section>
      `);
      currentPage++;
    } else {
      // Standard layout: Everything on one page
      pages.push(`
        <section class="pdf-page">
          ${generateHeader(surveyData, currentPage, totalPages, options)}
          <div class="content-section floorplan-section">
            <h2 class="section-heading">${floorLabel}</h2>
            ${generateBuildingFabricTableHTML(surveyData)}
            <img class="floorplan-image" src="${imageData.dataUrl}" alt="${floorLabel}" />
            ${generateElementKeyHTML()}
          </div>
          ${generateFooter(options)}
        </section>
      `);
      currentPage++;
    }
  });
  
  return pages;
}

/**
 * Helper function to estimate wall segments for HLP/PW calculations
 */
function estimateWallSegments(totalLength: number, type: string): number[] {
  if (totalLength <= 0) return [];
  const segmentCount = Math.max(2, Math.min(6, Math.ceil(totalLength / 3)));
  const baseSegment = totalLength / segmentCount;
  const segments: number[] = [];
  
  for (let i = 0; i < segmentCount; i++) {
    const variation = (Math.random() - 0.5) * 0.4;
    segments.push(Math.max(0.5, baseSegment + variation));
  }
  
  const actualTotal = segments.reduce((sum: number, seg: number) => sum + seg, 0);
  const scaleFactor = totalLength / actualTotal;
  return segments.map((seg: number) => parseFloat((seg * scaleFactor).toFixed(2)));
}

/**
 * Generate area calculation formula
 */
function generateAreaCalculation(floor: any, floorArea: number, siteNotesData: any, allRooms: any[]): string {
  console.log(`📐 [generateAreaCalculation] Called for floor:`, {
    floorLevel: floor.floorLevel,
    buildingType: floor.buildingType,
    area: floorArea,
    hasRooms: !!floor.rooms,
    roomsCount: floor.rooms?.length || 0,
    rooms: floor.rooms
  });
  
  // PRIORITY 1: Try to get rooms directly from floor.rooms (has calculated length/width from floor plan)
  let floorRooms: any[] = [];
  
  if (floor.rooms && floor.rooms.length > 0) {
    console.log(`📐 [generateAreaCalculation] Using floor.rooms (${floor.rooms.length} rooms) with calculated dimensions`);
    floorRooms = floor.rooms;
  } else {
    // PRIORITY 2: Filter from allRooms by floor ID
    console.log(`📐 [generateAreaCalculation] Filtering from allRooms (floor.id: ${floor.id})`);
    floorRooms = allRooms?.filter((room: any) => room.floor === floor.id) || [];
  }
  
  const validRooms = floorRooms.filter((room: any) => {
    const hasValidName = room.name && room.name.trim() !== '' && !/^Room \d+$/.test(room.name);
    const length = parseFloat(room.length) || 0;
    const width = parseFloat(room.width) || 0;
    const area = parseFloat(room.area) || 0;
    const hasWalls = room.walls && room.walls.length > 0;
    const isValid = hasValidName && ((length > 0 && width > 0) || area > 0 || hasWalls);
    
    if (!isValid && hasValidName) {
      console.log(`📐 [generateAreaCalculation] Room ${room.name} filtered out:`, { length, width, area, hasWalls });
    }
    
    return isValid;
  });

  console.log(`📐 [generateAreaCalculation] Valid rooms: ${validRooms.length}/${floorRooms.length}`, 
    validRooms.map((r: any) => ({ 
      name: r.name, 
      length: r.length, 
      width: r.width, 
      area: r.area,
      hasLength: !!r.length,
      hasWidth: !!r.width,
      lengthType: typeof r.length,
      widthType: typeof r.width
    })));

  if (validRooms.length === 0 && floorArea > 0) {
    console.log(`📐 [generateAreaCalculation] No valid rooms, returning simple total: ${floorArea.toFixed(2)}m²`);
    return `Total: ${floorArea.toFixed(2)}m²`;
  }
  
  if (validRooms.length > 1) {
    const roomCalcs: string[] = [];
    const roomAreas: number[] = [];
    
    validRooms.forEach((room: any) => {
      const length = parseFloat(room.length) || 0;
      const width = parseFloat(room.width) || 0;
      const roomName = room.name || 'Room';
      
      // Always use length × width calculation
      if (length > 0 && width > 0) {
        const calculatedArea = length * width;
        roomCalcs.push(`${length.toFixed(2)}m×${width.toFixed(2)}m=${calculatedArea.toFixed(2)}m²`);
        roomAreas.push(calculatedArea);
      }
    });
    
    if (roomCalcs.length === 0) {
      return floorArea > 0 ? `Total: ${floorArea.toFixed(2)}m²` : '';
    }
    
    // Build area sum: "3.57m² + 2.03m²"
    const areaSum = roomAreas.map(a => `${a.toFixed(2)}m²`).join(' + ');
    const result = `${roomCalcs.join(' + ')} = ${areaSum} = ${floorArea.toFixed(2)}m²`;
    console.log(`📐 [generateAreaCalculation] Multi-room calculation: ${result}`);
    return result;
  }
  
  if (validRooms.length === 1) {
    const room = validRooms[0];
    const length = parseFloat(room.length) || 0;
    const width = parseFloat(room.width) || 0;
    
    // Always use length × width calculation
    if (length > 0 && width > 0) {
      const calculatedArea = length * width;
      const result = `${length.toFixed(2)}m×${width.toFixed(2)}m=${calculatedArea.toFixed(2)}m²`;
      console.log(`📐 [generateAreaCalculation] Single-room calculation: ${result}`);
      return result;
    }
  }
  
  return floorArea > 0 ? `Total: ${floorArea.toFixed(2)}m²` : '';
}

/**
 * Generate main property measurements section with detailed dimensions & calculations
 */
function generateMainPropertyMeasurementsSection(surveyData: RetrofitSurveyData): string {
  const floorPlanData = surveyData.floor_plan?.linkedFloorPlanData;
  const siteNotesData = floorPlanData?.siteNotesData;
  const mainPropertyMeasurements = surveyData.main_property_measurements;
  
  console.log('📊 [PDF] linkedFloorPlanData siteNotesData check:', {
    hasLinkedFloorPlanData: !!floorPlanData,
    hasSiteNotesData: !!siteNotesData,
    siteNotesFloorsCount: siteNotesData?.floors?.length || 0,
    siteNotesFloors: siteNotesData?.floors?.map((f: any) => ({
      id: f.id,
      floorLevel: f.floorLevel,
      buildingType: f.buildingType,
      hasRooms: !!f.rooms,
      hasHighlighterBreakdown: !!f.highlighterBreakdown
    })),
    linkedFloorPlanDataKeys: floorPlanData ? Object.keys(floorPlanData) : []
  });
  
  console.log('📐 [PDF] generateMainPropertyMeasurementsSection called:', {
    hasFloorPlanData: !!floorPlanData,
    floorPlanDataKeys: floorPlanData ? Object.keys(floorPlanData) : [],
    hasSiteNotesData: !!siteNotesData,
    hasSiteNotesFloors: !!(siteNotesData?.floors),
    siteNotesFloorsCount: siteNotesData?.floors?.length || 0,
    hasMainPropertyMeasurements: !!mainPropertyMeasurements,
    hasStoreyMeasurements: !!(mainPropertyMeasurements?.storeyMeasurements),
    storeyMeasurementsCount: mainPropertyMeasurements?.storeyMeasurements?.length || 0,
    storeyMeasurements: mainPropertyMeasurements?.storeyMeasurements,
    mainPropertyMeasurements,
    floorPlanData: floorPlanData ? {
      hlpLength: (floorPlanData as any).hlpLength,
      pwLength: (floorPlanData as any).pwLength,
      totalArea: (floorPlanData as any).totalArea,
      FULL_DATA: floorPlanData  // Show complete structure to find the right key
    } : null
  });
  
  // PRIORITY: Use Main Property Measurements storeyMeasurements as primary source (more reliable)
  const hasStoreyData = mainPropertyMeasurements?.storeyMeasurements && mainPropertyMeasurements.storeyMeasurements.length > 0;
  const hasSiteNotesFloors = siteNotesData?.floors && siteNotesData.floors.length > 0;
  
  // If no floor data from either source, show simple table
  if (!hasStoreyData && !hasSiteNotesFloors) {
    console.log('⚠️ [PDF] No floor data found in either storeyMeasurements or siteNotesData - showing simple table instead of "All Floors - Dimensions & Calculations"');
    return `
      <div class="content-section">
        <div class="section-heading">Main Property Measurements</div>
        <table class="occupancy-table">
          <thead>
            <tr>
              <th>Attribute</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Number of Storeys</td>
              <td>${mainPropertyMeasurements?.numberOfStoreys || 'Not Specified'}</td>
            </tr>
            <tr>
              <td>Has Cellar</td>
              <td>${mainPropertyMeasurements?.hasCellar !== null && mainPropertyMeasurements?.hasCellar !== undefined ? (mainPropertyMeasurements.hasCellar ? 'Yes' : 'No') : 'Not Specified'}</td>
            </tr>
            <tr>
              <td>Total Area (m²)</td>
              <td>${mainPropertyMeasurements?.totalArea || 'Not Specified'}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  }
  
  // Get floor plan rooms and survey room details
  const roomDetails = surveyData.room_details?.rooms || [];
  const allRooms = roomDetails; // For backward compatibility with generateAreaCalculation
  
  // Build detailed floor data - ALWAYS use actual floor plan structure as base
  let floorsData: any[] = [];
  
  // Get all unique floors - PRIORITY: Use freshly calculated rooms from floor plan
  const freshRooms = (floorPlanData as any)?.rooms || [];
  const roomsToAnalyze = freshRooms.length > 0 ? freshRooms : roomDetails;
  const dataSource = freshRooms.length > 0 ? 'floorPlanData.rooms (fresh)' : 'room_details (stored)';
  
  // IMPORTANT: Normalize floor numbers - floor 0 should be treated as floor 1 (Ground Floor)
  const rawFloors: number[] = roomsToAnalyze.map((r: any) => r.floor || 1);  // Default to 1 if undefined
  const uniqueFloors: number[] = Array.from(new Set(rawFloors))
    .map(f => f === 0 ? 1 : f)  // Convert floor 0 to floor 1
    .sort((a, b) => a - b);
  
  console.log(`📊 [PDF] Building floor data from ${dataSource}:`, {
    rawFloors: Array.from(new Set(rawFloors)).sort((a, b) => a - b),
    uniqueFloors,
    totalRooms: roomsToAnalyze.length,
    hasStoreyData,
    hasSiteNotesFloors,
    roomsByFloor: roomsToAnalyze.reduce((acc: any, r: any) => {
      const floor = r.floor || 0;
      acc[floor] = (acc[floor] || 0) + 1;
      return acc;
    }, {})
  });
  
  if (uniqueFloors.length > 0) {
    // Log complete siteNotesData.floors structure FIRST
    if (hasSiteNotesFloors) {
      console.log(`🔍 [PDF] siteNotesData.floors count: ${siteNotesData!.floors!.length}`);
      siteNotesData!.floors!.forEach((floor: any, idx: number) => {
        console.log(`🔍 [PDF] siteNotesData.floors[${idx}] (id: ${floor.id}):`, {
          floorLevel: floor.floorLevel,
          buildingType: floor.buildingType,
          floorArea: floor.floorArea,
          hlp: floor.hlp,
          pw: floor.pw,
          hasRooms: !!(floor.rooms),
          roomsCount: floor.rooms?.length || 0,
          roomsSample: floor.rooms?.[0] ? {
            name: floor.rooms[0].name,
            length: floor.rooms[0].length,
            width: floor.rooms[0].width,
            area: floor.rooms[0].area
          } : null,
          hasHighlighterBreakdown: !!(floor.highlighterBreakdown),
          hlpSegments: floor.highlighterBreakdown?.hlpSegments,
          pwSegments: floor.highlighterBreakdown?.pwSegments,
          allKeys: Object.keys(floor)
        });
      });
    }
    
    // Build floor data from actual floors in the floor plan
    floorsData = uniqueFloors.map((floorNumber: number) => {
      // Calculate area - PRIORITY: Use freshly calculated rooms from floor plan (most accurate)
      let floorArea = 0;
      
      // Use the rooms we already determined (freshRooms or roomDetails)
      if (freshRooms.length > 0) {
        // Use floor plan calculated rooms (these are fresh from calculateRooms)
        const roomsOnFloor = freshRooms.filter((r: any) => {
          const roomFloor = (r.floor === 0 || r.floor === undefined) ? 1 : r.floor;
          return roomFloor === floorNumber;
        });
        floorArea = roomsOnFloor.reduce((sum: number, r: any) => sum + (parseFloat(r.area) || 0), 0);
        console.log(`📊 [PDF] Floor ${floorNumber}: Using freshly calculated floor plan rooms (${roomsOnFloor.length} rooms): ${floorArea.toFixed(2)}m²`);
      } else {
        // Fallback: calculate from room_details (older stored values)
        // IMPORTANT: Normalize floor numbers when filtering (floor 0 = floor 1)
        const roomsOnFloor = roomDetails.filter((r: any) => {
          const roomFloor = (r.floor === 0 || r.floor === undefined) ? 1 : r.floor;
          return roomFloor === floorNumber;
        });
        floorArea = roomsOnFloor.reduce((sum: number, r: any) => sum + (parseFloat(r.area) || 0), 0);
        console.log(`📊 [PDF] Floor ${floorNumber}: Fallback to room_details (${roomsOnFloor.length} rooms): ${floorArea.toFixed(2)}m²`);
      }
      
      // Try to get height from storeyMeasurements first (floor number is 1-based, array is 0-based)
      let height = '—';
      if (hasStoreyData) {
        const storeyIndex = floorNumber - 1;
        const storey = mainPropertyMeasurements!.storeyMeasurements![storeyIndex];
        if (storey?.height) {
          const heightStr = storey.height.toString().replace(/[^0-9.]/g, '');
          const heightNum = parseFloat(heightStr);
          height = !isNaN(heightNum) && heightNum > 0 ? heightNum.toFixed(2) : '—';
        }
      }
      
      // Try to get HLP/PW from multiple sources
      let hlp = 0;
      let pw = 0;
      let floorLevelName = '';
      
      // Source 1: Try siteNotesData first
      if (hasSiteNotesFloors) {
        const siteNotesFloor = siteNotesData!.floors!.find((f: any) => f.id === floorNumber);
        if (siteNotesFloor) {
          hlp = parseFloat(siteNotesFloor.hlp) || 0;
          pw = parseFloat(siteNotesFloor.pw) || 0;
          floorLevelName = siteNotesFloor.floorLevel || '';
          // Also use height from siteNotes if not found in storeyMeasurements
          if (height === '—' && siteNotesFloor.height) {
            height = siteNotesFloor.height;
          }
        }
      }
      
      // Source 2: Try floor_plan object (top-level) which has auto-calculated values
      if (uniqueFloors.length === 1 && surveyData.floor_plan) {
        if (!hlp && (surveyData.floor_plan as any).hlpLength) {
          hlp = parseFloat((surveyData.floor_plan as any).hlpLength) || 0;
          console.log(`📊 [PDF] Using HLP from floor_plan for floor ${floorNumber}: ${hlp}m`);
        }
        if (!pw && (surveyData.floor_plan as any).pwLength) {
          pw = parseFloat((surveyData.floor_plan as any).pwLength) || 0;
          console.log(`📊 [PDF] Using PW from floor_plan for floor ${floorNumber}: ${pw}m`);
        }
      }
      
      // Source 3: If it's a single floor and floorPlanData has overall HLP/PW, use that
      if (uniqueFloors.length === 1 && floorPlanData) {
        if (!hlp && (floorPlanData as any).hlpLength) {
          hlp = parseFloat((floorPlanData as any).hlpLength) || 0;
          console.log(`📊 [PDF] Using overall HLP from floorPlanData for floor ${floorNumber}: ${hlp}m`);
        }
        if (!pw && (floorPlanData as any).pwLength) {
          pw = parseFloat((floorPlanData as any).pwLength) || 0;
          console.log(`📊 [PDF] Using overall PW from floorPlanData for floor ${floorNumber}: ${pw}m`);
        }
      }
      
      // Determine floor name
      if (!floorLevelName) {
        if (hasStoreyData) {
          const storeyIndex = floorNumber - 1;
          floorLevelName = mainPropertyMeasurements!.storeyMeasurements![storeyIndex]?.name || '';
        }
      }
      
      // Default floor names if still not set
      if (!floorLevelName) {
        if (floorNumber === 1) floorLevelName = 'Ground';
        else if (floorNumber === 2) floorLevelName = 'First';
        else if (floorNumber === 3) floorLevelName = 'Second';
        else floorLevelName = `Floor ${floorNumber}`;
      }
      
      // Get buildingType, rooms, and highlighterBreakdown from siteNotesData if available
      let buildingType = 'main building';
      let rooms: any[] | undefined;
      let highlighterBreakdown: any | undefined;
      
      if (hasSiteNotesFloors) {
        const siteNotesFloor = siteNotesData!.floors!.find((f: any) => f.id === floorNumber);
        if (siteNotesFloor) {
          if (siteNotesFloor.buildingType) {
            buildingType = siteNotesFloor.buildingType;
          }
          // CRITICAL: Pass through rooms array with calculated dimensions
          if (siteNotesFloor.rooms && siteNotesFloor.rooms.length > 0) {
            rooms = siteNotesFloor.rooms;
            console.log(`📊 [PDF] Floor ${floorNumber}: Passing through ${siteNotesFloor.rooms.length} rooms from siteNotesData`);
          }
          // CRITICAL: Pass through highlighterBreakdown for real wall segments
          if (siteNotesFloor.highlighterBreakdown) {
            highlighterBreakdown = siteNotesFloor.highlighterBreakdown;
            console.log(`📊 [PDF] Floor ${floorNumber}: Passing through highlighterBreakdown:`, highlighterBreakdown);
          }
        }
      }
      
      return {
        id: floorNumber,  // Add ID for matching in generateAreaCalculation
        floorLevel: floorLevelName.charAt(0).toUpperCase() + floorLevelName.slice(1),
        buildingType,
        area: floorArea,
        height,
        hlp,
        pw,
        rooms,  // Include rooms with calculated dimensions
        highlighterBreakdown  // Include wall segments
      };
    });
    
    // IMPORTANT: Add extension floors from siteNotesData that weren't included above (extensions without rooms)
    if (hasSiteNotesFloors) {
      const extensionFloors = siteNotesData!.floors!.filter((f: any) => 
        f.buildingType && f.buildingType.toLowerCase().includes('extension') && 
        !floorsData.some(fd => fd.floorLevel.toLowerCase() === (f.floorLevel || `Floor ${f.id}`).toLowerCase())
      );
      
      if (extensionFloors.length > 0) {
        console.log(`📊 [PDF] Adding ${extensionFloors.length} extension floor(s) from siteNotesData:`, extensionFloors.map((f: any) => f.floorLevel || f.id));
        extensionFloors.forEach((floor: any) => {
          floorsData.push({
            id: floor.id,
            floorLevel: (floor.floorLevel || `Floor ${floor.id}`).charAt(0).toUpperCase() + (floor.floorLevel || `Floor ${floor.id}`).slice(1),
            buildingType: floor.buildingType,
            area: parseFloat(floor.floorArea) || 0,
            height: floor.height || '—',
            hlp: parseFloat(floor.hlp) || 0,
            pw: parseFloat(floor.pw) || 0,
            rooms: floor.rooms,  // Include rooms with calculated dimensions
            highlighterBreakdown: floor.highlighterBreakdown  // Include wall segments
          });
        });
      }
    }
    
    // IMPORTANT: Add extensions from surveyData.extensions.existingExtensions
    console.log('🔍 [PDF] Extension Processing Debug:', {
      hasExtensions: !!surveyData.extensions,
      hasExistingExtensions: !!surveyData.extensions?.existingExtensions,
      extensionsCount: surveyData.extensions?.existingExtensions?.length || 0,
      hasSiteNotesData: !!siteNotesData,
      hasSiteNotesFloors: hasSiteNotesFloors,
      siteNotesFloorsCount: siteNotesData?.floors?.length || 0,
      siteNotesFloors: siteNotesData?.floors?.map((f: any) => ({
        id: f.id,
        floorLevel: f.floorLevel,
        buildingType: f.buildingType,
        hasRooms: !!f.rooms,
        roomsCount: f.rooms?.length || 0,
        hasHighlighterBreakdown: !!f.highlighterBreakdown
      }))
    });
    
    if (surveyData.extensions?.existingExtensions && Array.isArray(surveyData.extensions.existingExtensions)) {
      const existingExtensions = surveyData.extensions.existingExtensions;
      if (existingExtensions.length > 0) {
        console.log(`📊 [PDF] Adding ${existingExtensions.length} extension(s) from surveyData.extensions:`, existingExtensions);
        existingExtensions.forEach((ext: any, index: number) => {
          const extName = ext.type || `EXT ${index + 1}`;
          
          // Try to find matching floor data from siteNotesData for room/highlighter data
          let extensionRooms: any = undefined;
          let extensionHighlighterBreakdown: any = undefined;
          
          if (hasSiteNotesFloors) {
            // Look for a floor that matches this extension by building type
            // Extensions are stored with buildingType like "ext 1", "ext 2", "extension"
            const matchingFloor = siteNotesData!.floors!.find((f: any) => {
              const buildingType = f.buildingType?.toLowerCase() || '';
              const searchName = extName.toLowerCase();
              
              // Check if building type contains "ext" or "extension"
              const isExtension = buildingType.includes('ext') || buildingType === 'extension';
              
              // Try various matching patterns:
              // 1. Exact match: "ext 1" === "ext 1"
              // 2. Contains match: "ext 1" contains "ext 1"
              // 3. Number match: both contain same number (e.g., "1" in "ext 1" and "EXT 1")
              const exactMatch = buildingType === searchName;
              const containsMatch = buildingType.includes(searchName.replace(/\s/g, ' '));
              const numberInExt = buildingType.match(/\d+/)?.[0];
              const numberInSearch = searchName.match(/\d+/)?.[0];
              const numberMatch = numberInExt && numberInSearch && numberInExt === numberInSearch;
              
              return isExtension && (exactMatch || containsMatch || numberMatch);
            });
            
            if (matchingFloor) {
              extensionRooms = matchingFloor.rooms;
              extensionHighlighterBreakdown = matchingFloor.highlighterBreakdown;
              console.log(`✅ [PDF] Found matching siteNotesData floor for "${extName}":`, {
                matchedFloor: matchingFloor.buildingType,
                hasRooms: !!extensionRooms,
                roomsCount: extensionRooms?.length || 0,
                rooms: extensionRooms,
                hasHighlighterBreakdown: !!extensionHighlighterBreakdown,
                highlighterBreakdown: extensionHighlighterBreakdown
              });
            } else {
              console.warn(`⚠️  [PDF] No matching siteNotesData floor found for "${extName}". Available floors:`, 
                siteNotesData!.floors!.map((f: any) => f.buildingType));
            }
          }
          
          floorsData.push({
            id: `ext-${index + 1}`,
            floorLevel: extName,
            buildingType: 'extension',
            area: parseFloat(ext.area) || 0,
            height: ext.height ? parseFloat(ext.height).toFixed(2) : '—',
            hlp: parseFloat(ext.hlp) || 0,
            pw: parseFloat(ext.pw) || 0,
            rooms: extensionRooms,
            highlighterBreakdown: extensionHighlighterBreakdown
          });
        });
      }
    }
    
    console.log('📊 [PDF] Built floor data from room_details + enhanced with measurements:', floorsData);
  } else if (hasSiteNotesFloors) {
    // FALLBACK: Use siteNotesData.floors if no room_details floors found
    console.log('📊 [PDF] Using siteNotesData.floors as complete fallback');
    floorsData = siteNotesData!.floors!.map((floor: any) => {
      const floorLevel = floor.floorLevel || `Floor ${floor.id}`;
      const area = parseFloat(floor.floorArea) || 0;
      const hlp = parseFloat(floor.hlp) || 0;
      const pw = parseFloat(floor.pw) || 0;
      const height = floor.height || '—';
      
      return {
        id: floor.id,
        floorLevel: floorLevel.charAt(0).toUpperCase() + floorLevel.slice(1),
        buildingType: floor.buildingType || 'main building',
        area,
        height,
        hlp,
        pw,
        rooms: floor.rooms,  // Include rooms with calculated dimensions
        highlighterBreakdown: floor.highlighterBreakdown  // Include wall segments
      };
    });
    
    // Add extensions from surveyData.extensions.existingExtensions
    if (surveyData.extensions?.existingExtensions && Array.isArray(surveyData.extensions.existingExtensions)) {
      const existingExtensions = surveyData.extensions.existingExtensions;
      if (existingExtensions.length > 0) {
        console.log(`📊 [PDF] (Fallback) Adding ${existingExtensions.length} extension(s) from surveyData.extensions:`, existingExtensions);
        existingExtensions.forEach((ext: any, index: number) => {
          const extName = ext.type || `EXT ${index + 1}`;
          
          // Try to find matching floor data from siteNotesData for room/highlighter data
          let extensionRooms: any = undefined;
          let extensionHighlighterBreakdown: any = undefined;
          
          // In fallback mode, we're already using siteNotesData.floors
          const matchingFloor = siteNotesData!.floors!.find((f: any) => {
            const buildingType = f.buildingType?.toLowerCase() || '';
            const searchName = extName.toLowerCase();
            
            // Check if building type contains "ext" or "extension"
            const isExtension = buildingType.includes('ext') || buildingType === 'extension';
            
            // Try various matching patterns
            const exactMatch = buildingType === searchName;
            const containsMatch = buildingType.includes(searchName.replace(/\s/g, ' '));
            const numberInExt = buildingType.match(/\d+/)?.[0];
            const numberInSearch = searchName.match(/\d+/)?.[0];
            const numberMatch = numberInExt && numberInSearch && numberInExt === numberInSearch;
            
            return isExtension && (exactMatch || containsMatch || numberMatch);
          });
          
          if (matchingFloor) {
            extensionRooms = matchingFloor.rooms;
            extensionHighlighterBreakdown = matchingFloor.highlighterBreakdown;
            console.log(`✅ [PDF] (Fallback) Found matching siteNotesData floor for "${extName}":`, {
              matchedFloor: matchingFloor.buildingType,
              hasRooms: !!extensionRooms,
              roomsCount: extensionRooms?.length || 0,
              rooms: extensionRooms,
              hasHighlighterBreakdown: !!extensionHighlighterBreakdown,
              highlighterBreakdown: extensionHighlighterBreakdown
            });
          } else {
            console.warn(`⚠️  [PDF] (Fallback) No matching siteNotesData floor found for "${extName}". Available floors:`, 
              siteNotesData!.floors!.map((f: any) => f.buildingType));
          }
          
          floorsData.push({
            id: `ext-${index + 1}`,
            floorLevel: extName,
            buildingType: 'extension',
            area: parseFloat(ext.area) || 0,
            height: ext.height ? parseFloat(ext.height).toFixed(2) : '—',
            hlp: parseFloat(ext.hlp) || 0,
            pw: parseFloat(ext.pw) || 0,
            rooms: extensionRooms,
            highlighterBreakdown: extensionHighlighterBreakdown
          });
        });
      }
    }
  }
  
  // Calculate totals
  const totalArea = floorsData.reduce((sum: number, f: any) => sum + f.area, 0);
  
  console.log('✅ [PDF] Generating "All Floors - Dimensions & Calculations" table:', {
    floorsCount: floorsData.length,
    floorsData,
    totalArea,
    heightsPresent: floorsData.map((f: any) => ({ floor: f.floorLevel, height: f.height }))
  });
  
  return `
    <div class="content-section">
      <div class="section-heading">All Floors - Dimensions & Calculations</div>
      
      <div class="modern-table-container" style="background: #FFFFFF; border-radius: 12pt; overflow: hidden; box-shadow: 0 1pt 3pt rgba(0,0,0,0.1); margin: 12pt 0;">
        <table class="modern-calc-table" style="width: 100%; border-collapse: collapse;">
          <thead>
            <tr style="background: linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%);">
              <th style="padding: 10pt 12pt; text-align: left; font-weight: 600; font-size: 10pt; color: white; border-bottom: 2pt solid #FF6B00;">Floor Level</th>
              <th style="padding: 10pt 12pt; text-align: center; font-weight: 600; font-size: 10pt; color: white; border-bottom: 2pt solid #FF6B00;">Area (m²)</th>
              <th style="padding: 10pt 12pt; text-align: center; font-weight: 600; font-size: 10pt; color: white; border-bottom: 2pt solid #FF6B00;">Height (m)</th>
              <th style="padding: 10pt 12pt; text-align: center; font-weight: 600; font-size: 10pt; color: white; border-bottom: 2pt solid #FF6B00;">HLP (m)</th>
              <th style="padding: 10pt 12pt; text-align: center; font-weight: 600; font-size: 10pt; color: white; border-bottom: 2pt solid #FF6B00;">PWL (m)</th>
            </tr>
          </thead>
          <tbody>
            ${floorsData.map((floor: any, index: number) => {
              const isMainBuilding = floor.buildingType === 'main building';
              const bgColor = index % 2 === 0 ? '#F9FAFB' : '#FFFFFF';
              return `
                <tr style="background: ${bgColor};">
                  <td style="padding: 8pt 12pt;">
                    <span style="display: inline-block; padding: 4pt 10pt; background: ${isMainBuilding ? '#E0E7FF' : '#FEE2E2'}; color: ${isMainBuilding ? '#3730A3' : '#991B1B'}; border-radius: 6pt; font-weight: 500; font-size: 8.5pt;">
                      ${floor.floorLevel}${!isMainBuilding ? ` - ${floor.buildingType}` : ''}
                    </span>
                  </td>
                  <td style="padding: 8pt 12pt; text-align: center; font-weight: 500; color: #1F2937;">${floor.area > 0 ? floor.area.toFixed(2) : '—'}</td>
                  <td style="padding: 8pt 12pt; text-align: center; font-weight: 500; color: #1F2937;">${floor.height !== '—' ? (typeof floor.height === 'string' ? floor.height : floor.height.toFixed(2) + ' m') : '—'}</td>
                  <td style="padding: 8pt 12pt; text-align: center; font-weight: 500; color: #1F2937;">${floor.hlp > 0 ? floor.hlp.toFixed(2) : '—'}</td>
                  <td style="padding: 8pt 12pt; text-align: center; font-weight: 500; color: #1F2937;">${floor.pw > 0 ? floor.pw.toFixed(2) : '—'}</td>
                </tr>
              `;
            }).join('')}
            <tr style="background: linear-gradient(to right, #FFF7ED, #FFEDD5); border-top: 2pt solid #FF8C00;">
              <td style="padding: 10pt 12pt; font-weight: 700; color: #1F2937; font-size: 9.5pt;">Total (${floorsData.length} floor${floorsData.length > 1 ? 's' : ''})</td>
              <td style="padding: 10pt 12pt; text-align: center; font-weight: 700; color: #C2410C; font-size: 9.5pt;">${totalArea.toFixed(2)} m²</td>
              <td style="padding: 10pt 12pt; text-align: center; font-weight: 500; color: #6B7280;">—</td>
              <td style="padding: 10pt 12pt; text-align: center; font-weight: 500; color: #6B7280;">—</td>
              <td style="padding: 10pt 12pt; text-align: center; font-weight: 500; color: #6B7280;">—</td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <!-- Formula & Working Section (generate whenever we have floor data) -->
      ${floorsData.length > 0 ? `
      <div class="content-section" style="margin-top: 16pt;">
        <div class="section-heading">Formula & Working - All Floors</div>
        ${floorsData.map((floor: any, index: number) => {
          const floorArea = floor.area || 0;
          const hlp = floor.hlp || 0;
          const pw = floor.pw || 0;
          
          if (floorArea <= 0 && hlp <= 0 && pw <= 0) return '';
          
          const floorLevel = floor.floorLevel || `Floor ${index + 1}`;
          const floorName = floorLevel.charAt(0).toUpperCase() + floorLevel.slice(1);
          const buildingType = floor.buildingType === 'main building' ? 'Main Building' : (floor.buildingType || 'Main Building');
          
          // Try to find matching site notes floor for detailed calculation
          // For extensions, match by both floorLevel and buildingType
          const siteNotesFloor = hasSiteNotesFloors ? siteNotesData!.floors!.find((f: any) => {
            const levelMatch = f.floorLevel?.toLowerCase() === floorLevel.toLowerCase();
            const typeMatch = (f.buildingType === buildingType) || 
                             (buildingType.toLowerCase().includes('extension') && f.buildingType?.toLowerCase().includes('extension'));
            return levelMatch && typeMatch;
          }) : null;
          
          console.log(`🔍 [Formula & Working] Floor ${index + 1} (${floorName}):`, {
            hasSiteNotesFloor: !!siteNotesFloor,
            hasRooms: !!(siteNotesFloor?.rooms),
            roomsCount: siteNotesFloor?.rooms?.length || 0,
            hasHighlighterBreakdown: !!(siteNotesFloor?.highlighterBreakdown),
            hlpSegmentsCount: siteNotesFloor?.highlighterBreakdown?.hlpSegments?.length || 0,
            pwSegmentsCount: siteNotesFloor?.highlighterBreakdown?.pwSegments?.length || 0,
            siteNotesFloor: siteNotesFloor ? {
              id: siteNotesFloor.id,
              floorLevel: siteNotesFloor.floorLevel,
              buildingType: siteNotesFloor.buildingType,
              highlighterBreakdown: siteNotesFloor.highlighterBreakdown
            } : null
          });
          
          // Area calculation with room-by-room breakdown
          // Use floor.rooms (from floorsData) if available, otherwise fall back to siteNotesFloor
          const floorWithRooms = floor.rooms && floor.rooms.length > 0 ? floor : siteNotesFloor;
          const areaCalc = floorWithRooms ? generateAreaCalculation(floorWithRooms, floorArea, siteNotesData, allRooms) : `Total: ${floorArea.toFixed(2)}m²`;
          
          console.log(`📊 [Area Calc] ${floorName}:`, {
            hasFloorRooms: !!(floor.rooms && floor.rooms.length > 0),
            hasSiteNotesFloor: !!siteNotesFloor,
            usingFloorWithRooms: !!floorWithRooms,
            roomsCount: floorWithRooms?.rooms?.length || 0
          });
          
          // HLP calculation - use highlighterBreakdown from floor object if available, otherwise estimate
          let hlpCalc = '';
          if (hlp > 0) {
            // Check floor.highlighterBreakdown first (from floorsData), then fall back to siteNotesFloor
            const breakdown = floor.highlighterBreakdown || siteNotesFloor?.highlighterBreakdown;
            if (breakdown?.hlpSegments && breakdown.hlpSegments.length > 0) {
              const segments = breakdown.hlpSegments.map((s: number) => `${s.toFixed(2)}m`);
              hlpCalc = `${segments.join(' + ')} = ${hlp.toFixed(2)}m`;
              console.log(`✅ [Formula & Working] Using REAL HLP segments for ${floorName}:`, segments);
            } else {
              // Fallback to estimated segments
              const hlpSegments = estimateWallSegments(hlp, 'HLP');
              hlpCalc = `${hlpSegments.map((s: number) => `${s.toFixed(2)}m`).join(' + ')} = ${hlp.toFixed(2)}m`;
              console.log(`⚠️ [Formula & Working] Using ESTIMATED HLP segments for ${floorName} (no highlighterBreakdown):`, hlpSegments);
            }
          }
          
          // PW calculation - use highlighterBreakdown from floor object if available, otherwise estimate
          let pwCalc = '';
          if (pw > 0) {
            // Check floor.highlighterBreakdown first (from floorsData), then fall back to siteNotesFloor
            const breakdown = floor.highlighterBreakdown || siteNotesFloor?.highlighterBreakdown;
            if (breakdown?.pwSegments && breakdown.pwSegments.length > 0) {
              const segments = breakdown.pwSegments.map((s: number) => `${s.toFixed(2)}m`);
              pwCalc = `${segments.join(' + ')} = ${pw.toFixed(2)}m`;
              console.log(`✅ [Formula & Working] Using REAL PW segments for ${floorName}:`, segments);
            } else {
              // Fallback to estimated segments
              const pwSegments = estimateWallSegments(pw, 'PW');
              pwCalc = `${pwSegments.map((s: number) => `${s.toFixed(2)}m`).join(' + ')} = ${pw.toFixed(2)}m`;
              console.log(`⚠️ [Formula & Working] Using ESTIMATED PW segments for ${floorName} (no highlighterBreakdown):`, pwSegments);
            }
          }
          
          return `
            <div style="background: linear-gradient(135deg, #FF8C00 0%, #FF6B00 100%); padding: 8pt 12pt; margin: 12pt 0 8pt 0; border-radius: 6pt;">
              <span style="color: white; font-weight: 600; font-size: 10pt;">${floorName} - ${buildingType}</span>
            </div>
            <div style="padding: 8pt 12pt; background: #F9FAFB; border-radius: 6pt; margin-bottom: 12pt;">
              ${areaCalc ? `
                <div style="margin-bottom: 6pt;">
                  <span style="font-weight: 600; color: #1F2937; min-width: 50pt; display: inline-block;">Area:</span>
                  <span style="font-family: 'Courier New', monospace; font-size: 9pt; color: #374151;">${areaCalc}</span>
                </div>
              ` : ''}
              ${hlpCalc ? `
                <div style="margin-bottom: 6pt;">
                  <span style="font-weight: 600; color: #1F2937; min-width: 50pt; display: inline-block;">HLP:</span>
                  <span style="font-family: 'Courier New', monospace; font-size: 9pt; color: #374151;">${hlpCalc}</span>
                </div>
              ` : ''}
              ${pwCalc ? `
                <div>
                  <span style="font-weight: 600; color: #1F2937; min-width: 50pt; display: inline-block;">PW:</span>
                  <span style="font-family: 'Courier New', monospace; font-size: 9pt; color: #374151;">${pwCalc}</span>
                </div>
              ` : ''}
            </div>
          `;
        }).join('')}
      </div>
      ` : ''}
    </div>
  `;
}

/**
 * Generate occupancy assessment section
 */
function generateOccupancyAssessmentSection(surveyData: RetrofitSurveyData): string {
  const { occupancy_assessment } = surveyData;

  return `
    <div class="content-section">
      <div class="section-heading">Occupancy Assessment</div>
      <table class="occupancy-table">
        <thead>
          <tr>
            <th>Attribute</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Number of Occupants</td>
            <td>${occupancy_assessment.numberOfOccupants || 'Not Specified'}</td>
          </tr>
          <tr>
            <td>Number of Children</td>
            <td>${occupancy_assessment.numberOfChildren || 'Not Specified'}</td>
          </tr>
          <tr>
            <td>Number of Disabled</td>
            <td>${occupancy_assessment.numberOfDisabled || 'Not Specified'}</td>
          </tr>
          <tr>
            <td>Number of Elderly</td>
            <td>${occupancy_assessment.numberOfElderly || 'Not Specified'}</td>
          </tr>
          <tr>
            <td>Single Bed Spaces</td>
            <td>${occupancy_assessment.singleBedSpaces || 'Not Specified'}</td>
          </tr>
          <tr>
            <td>Double Bed Spaces</td>
            <td>${occupancy_assessment.doubleBedSpaces || 'Not Specified'}</td>
          </tr>
        </tbody>
      </table>
      ${occupancy_assessment.notes ? `
        <div class="mt-2">
          <strong>Additional Notes:</strong>
          <div class="text-sm mt-2" style="padding: 8pt; background: #f9fafb; border-left: 3pt solid var(--theme-color); border-radius: 4pt;">
            ${occupancy_assessment.notes}
          </div>
        </div>
      ` : ''}
    </div>
  `;
}

/**
 * Generate site assessment section with full questions across 2 pages
 * Returns array of page HTML strings
 */
function generateSiteAssessmentPages(surveyData: RetrofitSurveyData): string[] {
  const { site_assessment } = surveyData;
  const pages: string[] = [];
  
  // Helper to format boolean values
  const formatBoolean = (value: boolean | null | undefined): string => {
    if (value === null || value === undefined) return 'Not Specified';
    return value ? 'Yes' : 'No';
  };

  // Build array of all fields with their FULL question text from the form
  const allFields: Array<{ label: string; value: string; section: string }> = [];
  
  // Risk Assessment Section (26 questions)
  if (site_assessment?.drillingConcerns !== undefined && site_assessment?.drillingConcerns !== null) allFields.push({ section: 'Risk Assessment', label: 'Are there any drilling concerns?', value: formatBoolean(site_assessment.drillingConcerns) });
  if (site_assessment?.aggressivePeoplePresent !== undefined && site_assessment?.aggressivePeoplePresent !== null) allFields.push({ section: 'Risk Assessment', label: 'Aggressive people present?', value: formatBoolean(site_assessment.aggressivePeoplePresent) });
  if (site_assessment?.rcd !== undefined && site_assessment?.rcd !== null) allFields.push({ section: 'Risk Assessment', label: 'RCD?', value: formatBoolean(site_assessment.rcd) });
  if (site_assessment?.loftBoarding !== undefined && site_assessment?.loftBoarding !== null) allFields.push({ section: 'Risk Assessment', label: 'Loft boarding?', value: formatBoolean(site_assessment.loftBoarding) });
  if (site_assessment?.petsIdentified !== undefined && site_assessment?.petsIdentified !== null) allFields.push({ section: 'Risk Assessment', label: 'Spoken to customers to identify the presence of any pets', value: formatBoolean(site_assessment.petsIdentified) });
  if (site_assessment?.environmentalImpactAssessed !== undefined && site_assessment?.environmentalImpactAssessed !== null) allFields.push({ section: 'Risk Assessment', label: 'Reviewed any environmental impact assessments associated', value: formatBoolean(site_assessment.environmentalImpactAssessed) });
  if (site_assessment?.batsNestingBirdsPresent !== undefined && site_assessment?.batsNestingBirdsPresent !== null) allFields.push({ section: 'Risk Assessment', label: 'Checked for presence of bats, nesting birds?', value: formatBoolean(site_assessment.batsNestingBirdsPresent) });
  if (site_assessment?.fishProtected !== undefined && site_assessment?.fishProtected !== null) allFields.push({ section: 'Risk Assessment', label: 'Protected fish in ponds from dust, insulation media or other hazardous substances', value: formatBoolean(site_assessment.fishProtected) });
  if (site_assessment?.hazardsForIndividuals !== undefined && site_assessment?.hazardsForIndividuals !== null) allFields.push({ section: 'Risk Assessment', label: 'Any hazards that may affect this category of individuals?', value: formatBoolean(site_assessment.hazardsForIndividuals) });
  if (site_assessment?.knotweedPresent !== undefined && site_assessment?.knotweedPresent !== null) allFields.push({ section: 'Risk Assessment', label: 'Japanese Knotweed or other invasive species present?', value: formatBoolean(site_assessment.knotweedPresent) });
  if (site_assessment?.protectedSpeciesInLoft !== undefined && site_assessment?.protectedSpeciesInLoft !== null) allFields.push({ section: 'Risk Assessment', label: 'Protected species in loft?', value: formatBoolean(site_assessment.protectedSpeciesInLoft) });
  if (site_assessment?.finlockGutters !== undefined && site_assessment?.finlockGutters !== null) allFields.push({ section: 'Risk Assessment', label: 'Does the property have finlock gutters?', value: formatBoolean(site_assessment.finlockGutters) });
  if (site_assessment?.accessAroundProperty !== undefined && site_assessment?.accessAroundProperty !== null) allFields.push({ section: 'Risk Assessment', label: 'Access from road / around property?', value: formatBoolean(site_assessment.accessAroundProperty) });
  if (site_assessment?.verminEvidence !== undefined && site_assessment?.verminEvidence !== null) allFields.push({ section: 'Risk Assessment', label: 'Is there any evidence of vermin?', value: formatBoolean(site_assessment.verminEvidence) });
  if (site_assessment?.protectedWildlifeEvidence !== undefined && site_assessment?.protectedWildlifeEvidence !== null) allFields.push({ section: 'Risk Assessment', label: 'Is there any evidence of protected wildlife (ie bats)?', value: formatBoolean(site_assessment.protectedWildlifeEvidence) });
  if (site_assessment?.scaffoldingErected !== undefined && site_assessment?.scaffoldingErected !== null) allFields.push({ section: 'Risk Assessment', label: 'Scaffolding erected?', value: formatBoolean(site_assessment.scaffoldingErected) });
  if (site_assessment?.additionalLightingRequired !== undefined && site_assessment?.additionalLightingRequired !== null) allFields.push({ section: 'Risk Assessment', label: 'Are there any additional lighting requirements needed at point of work?', value: formatBoolean(site_assessment.additionalLightingRequired) });
  if (site_assessment?.electricalPowerAvailable !== undefined && site_assessment?.electricalPowerAvailable !== null) allFields.push({ section: 'Risk Assessment', label: 'Is an electrical power supply available?', value: formatBoolean(site_assessment.electricalPowerAvailable) });
  if (site_assessment?.gasSupplyRisk !== undefined && site_assessment?.gasSupplyRisk !== null) allFields.push({ section: 'Risk Assessment', label: 'Any aspects of existing gas supply and distribution that pose a risk?', value: formatBoolean(site_assessment.gasSupplyRisk) });
  if (site_assessment?.animalSignsOnApproach !== undefined && site_assessment?.animalSignsOnApproach !== null) allFields.push({ section: 'Risk Assessment', label: 'Checked for signs of animals on approaching property?', value: formatBoolean(site_assessment.animalSignsOnApproach) });
  if (site_assessment?.foulWaterPointsAvailable !== undefined && site_assessment?.foulWaterPointsAvailable !== null) allFields.push({ section: 'Risk Assessment', label: 'Are there foul water points available to drain system?', value: formatBoolean(site_assessment.foulWaterPointsAvailable) });
  if (site_assessment?.pathsDrivewaysGoodOrder !== undefined && site_assessment?.pathsDrivewaysGoodOrder !== null) allFields.push({ section: 'Risk Assessment', label: 'Are the surrounding paths/driveways in good order?', value: formatBoolean(site_assessment.pathsDrivewaysGoodOrder) });
  if (site_assessment?.goodWorkingEnvironment !== undefined && site_assessment?.goodWorkingEnvironment !== null) allFields.push({ section: 'Risk Assessment', label: 'Does the property allow for a good working environment?', value: formatBoolean(site_assessment.goodWorkingEnvironment) });
  if (site_assessment?.overheadCablesPresent !== undefined && site_assessment?.overheadCablesPresent !== null) allFields.push({ section: 'Risk Assessment', label: 'Overhead electricity cables present?', value: formatBoolean(site_assessment.overheadCablesPresent) });
  if (site_assessment?.hasGarage !== undefined && site_assessment?.hasGarage !== null) allFields.push({ section: 'Risk Assessment', label: 'Does the property have a garage?', value: formatBoolean(site_assessment.hasGarage) });
  if (site_assessment?.hasPorch !== undefined && site_assessment?.hasPorch !== null) allFields.push({ section: 'Risk Assessment', label: 'Does the property have a porch?', value: formatBoolean(site_assessment.hasPorch) });
  if (site_assessment?.hasOutsideTap !== undefined && site_assessment?.hasOutsideTap !== null) allFields.push({ section: 'Risk Assessment', label: 'Does the property have an outside tap?', value: formatBoolean(site_assessment.hasOutsideTap) });
  
  // Working at Heights (12 questions)
  if (site_assessment?.conservatoryLadderRequired !== undefined && site_assessment?.conservatoryLadderRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Conservatory ladder required', value: formatBoolean(site_assessment.conservatoryLadderRequired) });
  if (site_assessment?.loftWalkBoardsRequired !== undefined && site_assessment?.loftWalkBoardsRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Loft walk boards required', value: formatBoolean(site_assessment.loftWalkBoardsRequired) });
  if (site_assessment?.loftGuardrailsRequired !== undefined && site_assessment?.loftGuardrailsRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Loft guardrails required', value: formatBoolean(site_assessment.loftGuardrailsRequired) });
  if (site_assessment?.loftLightingRequired !== undefined && site_assessment?.loftLightingRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Loft lighting required', value: formatBoolean(site_assessment.loftLightingRequired) });
  if (site_assessment?.specialistScaffoldingRequired !== undefined && site_assessment?.specialistScaffoldingRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Specialist scaffolding required', value: formatBoolean(site_assessment.specialistScaffoldingRequired) });
  if (site_assessment?.ladderRequired !== undefined && site_assessment?.ladderRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Ladder required', value: formatBoolean(site_assessment.ladderRequired) });
  if (site_assessment?.ladderMatsRequired !== undefined && site_assessment?.ladderMatsRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Ladder mats required', value: formatBoolean(site_assessment.ladderMatsRequired) });
  if (site_assessment?.catLadderRequired !== undefined && site_assessment?.catLadderRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Cat ladder required', value: formatBoolean(site_assessment.catLadderRequired) });
  if (site_assessment?.tetraAndRunningLineRequired !== undefined && site_assessment?.tetraAndRunningLineRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Tetra and running line required', value: formatBoolean(site_assessment.tetraAndRunningLineRequired) });
  if (site_assessment?.tetraHarnessRequired !== undefined && site_assessment?.tetraHarnessRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Tetra harness required', value: formatBoolean(site_assessment.tetraHarnessRequired) });
  if (site_assessment?.flatRoofLanyardRequired !== undefined && site_assessment?.flatRoofLanyardRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Flat roof lanyard required', value: formatBoolean(site_assessment.flatRoofLanyardRequired) });
  if (site_assessment?.flatRoofProtectionRequired !== undefined && site_assessment?.flatRoofProtectionRequired !== null) allFields.push({ section: 'Working at Heights', label: 'Flat roof protection required', value: formatBoolean(site_assessment.flatRoofProtectionRequired) });
  
  // Asbestos (5 questions)
  if (site_assessment?.existingAsbestosReports !== undefined && site_assessment?.existingAsbestosReports !== null) allFields.push({ section: 'Asbestos', label: 'Does the client have any existing asbestos reports / documents?', value: formatBoolean(site_assessment.existingAsbestosReports) });
  if (site_assessment?.knownAsbestos !== undefined && site_assessment?.knownAsbestos !== null) allFields.push({ section: 'Asbestos', label: 'Has the client been asked if there is any known asbestos?', value: formatBoolean(site_assessment.knownAsbestos) });
  if (site_assessment?.asbestosIndicated !== undefined && site_assessment?.asbestosIndicated !== null) allFields.push({ section: 'Asbestos', label: 'Has the client indicated there may be asbestos within the property?', value: formatBoolean(site_assessment.asbestosIndicated) });
  if (site_assessment?.asbestosSuspected !== undefined && site_assessment?.asbestosSuspected !== null) allFields.push({ section: 'Asbestos', label: 'Do you or the client have any reason to suspect asbestos may be present?', value: formatBoolean(site_assessment.asbestosSuspected) });
  if (site_assessment?.asbestosPresent !== undefined && site_assessment?.asbestosPresent !== null) allFields.push({ section: 'Asbestos', label: 'Is there asbestos present?', value: formatBoolean(site_assessment.asbestosPresent) });
  
  // Health and Safety (5 questions)
  if (site_assessment?.worksCompleteStandardsInformed !== undefined && site_assessment?.worksCompleteStandardsInformed !== null) allFields.push({ section: 'Health and Safety', label: 'I have informed the customer that the works are complete to the relevant standards', value: formatBoolean(site_assessment.worksCompleteStandardsInformed) });
  if (site_assessment?.glovesWipedDown !== undefined && site_assessment?.glovesWipedDown !== null) allFields.push({ section: 'Health and Safety', label: 'I have wiped down and operational rubber gloves, surfaces, tools and instruments used within the premises', value: formatBoolean(site_assessment.glovesWipedDown) });
  if (site_assessment?.wasteDisposedCorrectly !== undefined && site_assessment?.wasteDisposedCorrectly !== null) allFields.push({ section: 'Health and Safety', label: 'I have removed and placed into a plastic bag, any wiped, disposable gloves and overalls used, whilst trying not to cross contaminate onto existing clothes or persons', value: formatBoolean(site_assessment.wasteDisposedCorrectly) });
  if (site_assessment?.handsWashed !== undefined && site_assessment?.handsWashed !== null) allFields.push({ section: 'Health and Safety', label: 'I have ensured that I have cleaned / washed my hands and where possible / appropriate any reusable PPE lie safety glasses), using soap and water, antibacterial wipes, or used suitable hand sanitiser on my hands before travelling to the next job', value: formatBoolean(site_assessment.handsWashed) });
  if (site_assessment?.dailyWasteDisposed !== undefined && site_assessment?.dailyWasteDisposed !== null) allFields.push({ section: 'Health and Safety', label: 'I have, at the end of each day, and following the appropriate local waste regulations, disposed of any bagged waste in line with existing business processes and / or Government guidance', value: formatBoolean(site_assessment.dailyWasteDisposed) });
  
  console.log(`📋 [Site Assessment] Collected ${allFields.length} field(s) from site_assessment data`);
  
  if (allFields.length === 0) {
    console.warn(`⚠️ [Site Assessment] No fields found - returning empty page`);
    // No data - return single empty page
    return [`
      <div class="content-section">
        <div class="section-heading">Site Assessment</div>
        <p style="color: #64748b; font-style: italic;">No site assessment data available</p>
      </div>
    `];
  }
  
  console.log(`✅ [Site Assessment] Generating pages for ${allFields.length} field(s)`);
  
  // Split fields into 3 pages (approximately 16 fields per page for better spacing)
  const fieldsPerPage = Math.ceil(allFields.length / 3);
  const page1Fields = allFields.slice(0, fieldsPerPage);
  const page2Fields = allFields.slice(fieldsPerPage, fieldsPerPage * 2);
  const page3Fields = allFields.slice(fieldsPerPage * 2);
  
  // Generate page 1
  const page1Rows: string[] = [];
  let currentSection = '';
  for (const field of page1Fields) {
    if (field.section !== currentSection) {
      currentSection = field.section;
      page1Rows.push(`<tr><td colspan="2" style="background: #f3f4f6; font-weight: 600; padding: 8pt 6pt; color: #374151; border-top: 2pt solid #d1d5db;">${currentSection}</td></tr>`);
    }
    page1Rows.push(`<tr><td style="width: 70%;">${field.label}</td><td style="width: 30%; text-align: center; font-weight: 600;">${field.value}</td></tr>`);
  }
  
  pages.push(`
    <div class="content-section">
      <div class="section-heading">Site Assessment (Page 1 of 3)</div>
      <table class="occupancy-table">
        <thead>
          <tr>
            <th style="width: 70%;">Question</th>
            <th style="width: 30%;">Response</th>
          </tr>
        </thead>
        <tbody>
          ${page1Rows.join('\n')}
        </tbody>
      </table>
    </div>
  `);
  
  // Generate page 2
  const page2Rows: string[] = [];
  currentSection = '';
  for (const field of page2Fields) {
    if (field.section !== currentSection) {
      currentSection = field.section;
      page2Rows.push(`<tr><td colspan="2" style="background: #f3f4f6; font-weight: 600; padding: 8pt 6pt; color: #374151; border-top: 2pt solid #d1d5db;">${currentSection}</td></tr>`);
    }
    page2Rows.push(`<tr><td style="width: 70%;">${field.label}</td><td style="width: 30%; text-align: center; font-weight: 600;">${field.value}</td></tr>`);
  }
  
  pages.push(`
    <div class="content-section">
      <div class="section-heading">Site Assessment (Page 2 of 3)</div>
      <table class="occupancy-table">
        <thead>
          <tr>
            <th style="width: 70%;">Question</th>
            <th style="width: 30%;">Response</th>
          </tr>
        </thead>
        <tbody>
          ${page2Rows.join('\n')}
        </tbody>
      </table>
    </div>
  `);
  
  // Generate page 3
  const page3Rows: string[] = [];
  currentSection = '';
  for (const field of page3Fields) {
    if (field.section !== currentSection) {
      currentSection = field.section;
      page3Rows.push(`<tr><td colspan="2" style="background: #f3f4f6; font-weight: 600; padding: 8pt 6pt; color: #374151; border-top: 2pt solid #d1d5db;">${currentSection}</td></tr>`);
    }
    page3Rows.push(`<tr><td style="width: 70%;">${field.label}</td><td style="width: 30%; text-align: center; font-weight: 600;">${field.value}</td></tr>`);
  }
  
  pages.push(`
    <div class="content-section">
      <div class="section-heading">Site Assessment (Page 3 of 3)</div>
      <table class="occupancy-table">
        <thead>
          <tr>
            <th style="width: 70%;">Question</th>
            <th style="width: 30%;">Response</th>
          </tr>
        </thead>
        <tbody>
          ${page3Rows.join('\n')}
        </tbody>
      </table>
      ${site_assessment?.notes ? `
        <div class="mt-2">
          <strong>Additional Notes:</strong>
          <div class="text-sm mt-2" style="padding: 8pt; background: #f9fafb; border-left: 3pt solid var(--theme-color); border-radius: 4pt;">
            ${site_assessment.notes}
          </div>
        </div>
      ` : ''}
    </div>
  `);
  
  return pages;
}

/**
 * Generate Pre-Installation Assessment Questions Page (Page 1 of 4 Signatures)
 * Shows only the pre-installation assessment questions
 */
function generatePreInstallationQuestionsPage(surveyData: RetrofitSurveyData): string {
  const signatures = surveyData.signatures;
  
  if (!signatures) {
    return `
      <div class="content-section">
        <div class="section-heading">Survey Sign Off & Feedback</div>
        <p style="color: #64748b; font-style: italic;">No assessment data available</p>
      </div>
    `;
  }

  const renderYesNoQuestion = (question: string, answer: boolean | null | undefined) => {
    const answerText = answer === true ? 'YES' : answer === false ? 'NO' : 'Not Answered';
    const answerColor = answer === true ? '#3b82f6' : answer === false ? '#6b7280' : '#9ca3af';
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; margin-bottom: 10px; background: #f9fafb;">
        <p style="font-size: 10px; color: #374151; margin-bottom: 6px; line-height: 1.4;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${question}
        </p>
        <div style="display: inline-block; padding: 4px 12px; background: ${answerColor}; color: white; border-radius: 4px; font-size: 10px; font-weight: 600;">
          ${answerText}
        </div>
      </div>
    `;
  };

  const hasPreSignatureQuestions = signatures.asbestosChecksUndertaken !== undefined ||
    signatures.servicesAffectedByInstallation !== undefined ||
    signatures.potentialRiskToServices !== undefined ||
    signatures.installationInLineWithDesign !== undefined ||
    signatures.methodStatementSuitable !== undefined ||
    signatures.protectedSpeciesDisturbance !== undefined ||
    signatures.customerUnderstandsWork !== undefined ||
    signatures.customerAwareOfDisruption !== undefined ||
    signatures.customerHasRequests !== undefined ||
    signatures.siteAccessArrangementsAdequate !== undefined ||
    signatures.otherInstallationInfo !== undefined ||
    signatures.potentialMoistureBuildUp !== undefined;

  return `
    <div class="content-section">
      <div class="section-heading">Survey Sign Off & Feedback</div>
      
      ${hasPreSignatureQuestions ? `
      <!-- Pre-Installation Assessment -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #7c3aed; font-size: 16px; font-weight: 600; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #7c3aed;">
          Pre-Installation Assessment
        </h3>
        ${renderYesNoQuestion('Have you determined if relevant checks have been undertaken to determine if materials containing asbestos are present?', signatures.asbestosChecksUndertaken)}
        ${renderYesNoQuestion('Will the functionality and/or safety of installed services (gas, electricity, water, telecommunications etc.) be affected in any way as a result of the installation?', signatures.servicesAffectedByInstallation)}
        ${renderYesNoQuestion('Is there any potential risk in relation to the ongoing performance of installed services? (If YES liaison with the Retrofit Designer and / or Retrofit Coordinator will need to take place).', signatures.potentialRiskToServices)}
        ${renderYesNoQuestion('From the findings of this building inspection, is the installation able to be carried out in line with the full Design Specification?', signatures.installationInLineWithDesign)}
        ${renderYesNoQuestion('From the findings of this building inspection, is the Method Statement suitable and complete?', signatures.methodStatementSuitable)}
        ${renderYesNoQuestion('Is there any evidence that any form of protected species would be disturbed by the installation process?', signatures.protectedSpeciesDisturbance)}
      </div>
      ` : `
        <p style="color: #64748b; font-style: italic;">No pre-installation assessment questions available</p>
      `}
      
      <div style="margin-top: 24px; padding: 14px; background: #fffbeb; border-left: 4px solid #f59e0b; border-radius: 4px;">
        <p style="font-size: 11px; color: #92400e; line-height: 1.6;">
          <strong>Note:</strong> PAS 2030 General questions are on the next page, followed by customer and surveyor signatures.
        </p>
      </div>
    </div>
  `;
}

/**
 * Generate PAS 2030 General Questions Page (Page 2 of 4 Signatures)
 * Shows only the PAS 2030 general questions
 */
function generatePAS2030QuestionsPage(surveyData: RetrofitSurveyData): string {
  const signatures = surveyData.signatures;
  
  if (!signatures) {
    return `
      <div class="content-section">
        <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
        <p style="color: #64748b; font-style: italic;">No assessment data available</p>
      </div>
    `;
  }

  const renderYesNoQuestion = (question: string, answer: boolean | null | undefined) => {
    const answerText = answer === true ? 'YES' : answer === false ? 'NO' : 'Not Answered';
    const answerColor = answer === true ? '#3b82f6' : answer === false ? '#6b7280' : '#9ca3af';
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px; margin-bottom: 14px; background: #f9fafb;">
        <p style="font-size: 11px; color: #374151; margin-bottom: 8px; line-height: 1.5;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${question}
        </p>
        <div style="display: inline-block; padding: 5px 14px; background: ${answerColor}; color: white; border-radius: 4px; font-size: 10px; font-weight: 600;">
          ${answerText}
        </div>
      </div>
    `;
  };

  const hasPAS2030Questions = signatures.customerUnderstandsWork !== undefined ||
    signatures.customerAwareOfDisruption !== undefined ||
    signatures.customerHasRequests !== undefined ||
    signatures.siteAccessArrangementsAdequate !== undefined ||
    signatures.otherInstallationInfo !== undefined ||
    signatures.potentialMoistureBuildUp !== undefined;

  return `
    <div class="content-section">
      <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
      
      ${hasPAS2030Questions ? `
      <!-- PAS 2030 General -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #6366f1; font-size: 16px; font-weight: 600; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #6366f1;">
          PAS 2030 General
        </h3>
        ${renderYesNoQuestion('Does the customer understand the nature and extent of the work?', signatures.customerUnderstandsWork)}
        ${renderYesNoQuestion('Have they been made aware of any disruption that may be caused (eg rubbish disposal and the siting of skips)?', signatures.customerAwareOfDisruption)}
        ${renderYesNoQuestion('Does the customer have any particular requests or requirements?', signatures.customerHasRequests)}
        ${renderYesNoQuestion('Are the arrangements for site access and the storage of installation materials adequate and appropriate?', signatures.siteAccessArrangementsAdequate)}
        ${renderYesNoQuestion('Is there any other information which would help ensure the smooth running of the installation process?', signatures.otherInstallationInfo)}
        ${renderYesNoQuestion('Is there any potential for moisture build up as a result of the installation?', signatures.potentialMoistureBuildUp)}
      </div>
      ` : `
        <p style="color: #64748b; font-style: italic;">No PAS 2030 general questions available</p>
      `}
      
      <div style="margin-top: 24px; padding: 14px; background: #fffbeb; border-left: 4px solid #f59e0b; border-radius: 4px;">
        <p style="font-size: 11px; color: #92400e; line-height: 1.6;">
          <strong>Note:</strong> Customer and surveyor signatures are on the next pages.
        </p>
      </div>
    </div>
  `;
}

/**
 * Generate Customer Signatures Page (Page 3 of 4 Signatures)
 * Shows customer sign-off and feedback only
 */
function generateCustomerSignaturesPage(surveyData: RetrofitSurveyData): string {
  const signatures = surveyData.signatures;
  
  if (!signatures || !signatures.customer) {
    return `
      <div class="content-section">
        <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
        <p style="color: #64748b; font-style: italic;">No customer signature data available</p>
      </div>
    `;
  }

  const customerStatements = [
    { text: 'I can confirm that the Retrofit Assessor has completed the survey in my property and I am happy with the way he has explained me before the survey.', signature: signatures.customer?.statement1, date: signatures.customer?.statement1Date },
    { text: 'I understand that the result of the survey will be provided by the Office via Retrofit Advisor and I can decide from there if I am happy with the installation.', signature: signatures.customer?.statement2, date: signatures.customer?.statement2Date },
    { text: 'I do understand that the Retrofit Assessment completed was for the OFGEM scheme and all my data will be stored and shared with OFGEM scheme providers only for the scheme purpose', signature: signatures.customer?.statement3, date: signatures.customer?.statement3Date },
    { text: 'I can confirm that I am the homeowner/tenant staying in the property and all the information provided by me for the scheme eligibility is true and no fraudulent documents has been provided by me to the installers', signature: signatures.customer?.statement4, date: signatures.customer?.statement4Date }
  ];

  const renderSignatureBox = (statement: string, signature?: string, date?: string) => {
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; margin-bottom: 16px; background: #ffffff;">
        <p style="font-size: 11px; color: #374151; margin-bottom: 10px; line-height: 1.5;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${statement}
        </p>
        ${signature ? `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 10px;">
            <div style="flex: 1;">
              <img src="${signature}" alt="Signature" style="max-height: 70px; max-width: 220px; border: 1px solid #d1d5db; border-radius: 4px; padding: 4px; background: #f9fafb;" />
            </div>
            <div style="font-size: 11px; color: #6b7280; margin-left: 12px; white-space: nowrap;">
              Date: ${date || 'N/A'}
            </div>
          </div>
        ` : `
          <div style="color: #9ca3af; font-style: italic; font-size: 10px; margin-top: 8px;">
            No signature provided
          </div>
        `}
      </div>
    `;
  };

  return `
    <div class="content-section">
      <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
      
      <!-- Customer Sign Off -->
      <div style="margin-bottom: 24px;">
        <h3 style="color: #1e40af; font-size: 16px; font-weight: 600; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #1e40af;">
          Customer Sign Off
        </h3>
        ${customerStatements.map(stmt => renderSignatureBox(stmt.text, stmt.signature, stmt.date)).join('')}
      </div>

      <!-- Feedback Section -->
      ${signatures.feedback && (signatures.feedback.rating || signatures.feedback.comments) ? `
        <div style="margin-bottom: 20px; padding: 14px; background: #f0f9ff; border-left: 4px solid #0ea5e9; border-radius: 4px;">
          <h3 style="color: #0369a1; font-size: 14px; font-weight: 600; margin-bottom: 10px;">
            Survey Feedback
          </h3>
          ${signatures.feedback.rating ? `
            <div style="margin-bottom: 10px;">
              <span style="font-size: 11px; color: #374151; font-weight: 500;">Rating: </span>
              <span style="font-size: 14px; color: #0ea5e9; font-weight: 600;">
                ${'★'.repeat(signatures.feedback.rating)}${'☆'.repeat(5 - signatures.feedback.rating)}
                (${signatures.feedback.rating}/5)
              </span>
            </div>
          ` : ''}
          ${signatures.feedback.comments ? `
            <div>
              <span style="font-size: 11px; color: #374151; font-weight: 500; display: block; margin-bottom: 6px;">Comments:</span>
              <div style="font-size: 10px; color: #475569; padding: 10px; background: #ffffff; border-radius: 4px; line-height: 1.5;">
                ${signatures.feedback.comments}
              </div>
            </div>
          ` : ''}
        </div>
      ` : ''}
      
      <div style="margin-top: 24px; padding: 14px; background: #fffbeb; border-left: 4px solid #f59e0b; border-radius: 4px;">
        <p style="font-size: 11px; color: #92400e; line-height: 1.6;">
          <strong>Note:</strong> Surveyor signatures are on the next page.
        </p>
      </div>
    </div>
  `;
}

/**
 * Generate Surveyor Signatures Page (Page 4 of 4 Signatures)
 * Shows surveyor sign-off only
 */
function generateSurveyorSignaturesPage(surveyData: RetrofitSurveyData): string {
  const signatures = surveyData.signatures;
  
  if (!signatures || !signatures.surveyor) {
    return `
      <div class="content-section">
        <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
        <p style="color: #64748b; font-style: italic;">No surveyor signature data available</p>
      </div>
    `;
  }

  const surveyorStatements = [
    { text: 'I can confirm that I have collected all evidence as per the Retrofit Assessment scheme', signature: signatures.surveyor?.statement1, date: signatures.surveyor?.statement1Date },
    { text: 'I can confirm that all the data collected by me will follow GDPR rules and regulations as per Eco Approach Ltd\'s standard', signature: signatures.surveyor?.statement2, date: signatures.surveyor?.statement2Date },
    { text: 'I have informed the customer that measures to be fitted in the property will be processed by the head office and will get back to you as per PAS 2035 Process.', signature: signatures.surveyor?.statement3, date: signatures.surveyor?.statement3Date },
    { text: 'I can confirm that I have ensured customer property and GDPR details are well taken care while doing my job.', signature: signatures.surveyor?.statement4, date: signatures.surveyor?.statement4Date },
    { text: 'I can confirm that my survey will be subject to compliance check and if they compliance is not approved I will not be getting paid for the job.', signature: signatures.surveyor?.statement5, date: signatures.surveyor?.statement5Date }
  ];

  const renderSignatureBox = (statement: string, signature?: string, date?: string) => {
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 14px; margin-bottom: 16px; background: #ffffff;">
        <p style="font-size: 11px; color: #374151; margin-bottom: 10px; line-height: 1.5;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${statement}
        </p>
        ${signature ? `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 10px;">
            <div style="flex: 1;">
              <img src="${signature}" alt="Signature" style="max-height: 70px; max-width: 220px; border: 1px solid #d1d5db; border-radius: 4px; padding: 4px; background: #f9fafb;" />
            </div>
            <div style="font-size: 11px; color: #6b7280; margin-left: 12px; white-space: nowrap;">
              Date: ${date || 'N/A'}
            </div>
          </div>
        ` : `
          <div style="color: #9ca3af; font-style: italic; font-size: 10px; margin-top: 8px;">
            No signature provided
          </div>
        `}
      </div>
    `;
  };

  return `
    <div class="content-section">
      <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
      
      <!-- Surveyor Sign Off -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #059669; font-size: 16px; font-weight: 600; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #059669;">
          Surveyor Sign Off
        </h3>
        ${surveyorStatements.map(stmt => renderSignatureBox(stmt.text, stmt.signature, stmt.date)).join('')}
      </div>
    </div>
  `;
}

/**
 * OLD FUNCTION - KEPT FOR BACKWARD COMPATIBILITY
 * Generate Signatures-Only Page (Page 2 of Signatures)
 * Shows customer sign-off, surveyor sign-off, and feedback only
 */
function generateSignaturesOnlyPage(surveyData: RetrofitSurveyData): string {
  const signatures = surveyData.signatures;
  
  if (!signatures || (!signatures.customer && !signatures.surveyor && !signatures.feedback)) {
    return `
      <div class="content-section">
        <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
        <p style="color: #64748b; font-style: italic;">No signature or feedback data available</p>
      </div>
    `;
  }

  const customerStatements = [
    { text: 'I can confirm that the Retrofit Assessor has completed the survey in my property and I am happy with the way he has explained me before the survey.', signature: signatures.customer?.statement1, date: signatures.customer?.statement1Date },
    { text: 'I understand that the result of the survey will be provided by the Office via Retrofit Advisor and I can decide from there if I am happy with the installation.', signature: signatures.customer?.statement2, date: signatures.customer?.statement2Date },
    { text: 'I do understand that the Retrofit Assessment completed was for the OFGEM scheme and all my data will be stored and shared with OFGEM scheme providers only for the scheme purpose', signature: signatures.customer?.statement3, date: signatures.customer?.statement3Date },
    { text: 'I can confirm that I am the homeowner/tenant staying in the property and all the information provided by me for the scheme eligibility is true and no fraudulent documents has been provided by me to the installers', signature: signatures.customer?.statement4, date: signatures.customer?.statement4Date }
  ];

  const surveyorStatements = [
    { text: 'I can confirm that I have collected all evidence as per the Retrofit Assessment scheme', signature: signatures.surveyor?.statement1, date: signatures.surveyor?.statement1Date },
    { text: 'I can confirm that all the data collected by me will follow GDPR rules and regulations as per Eco Approach Ltd\'s standard', signature: signatures.surveyor?.statement2, date: signatures.surveyor?.statement2Date },
    { text: 'I have informed the customer that measures to be fitted in the property will be processed by the head office and will get back to you as per PAS 2035 Process.', signature: signatures.surveyor?.statement3, date: signatures.surveyor?.statement3Date },
    { text: 'I can confirm that I have ensured customer property and GDPR details are well taken care while doing my job.', signature: signatures.surveyor?.statement4, date: signatures.surveyor?.statement4Date },
    { text: 'I can confirm that my survey will be subject to compliance check and if they compliance is not approved I will not be getting paid for the job.', signature: signatures.surveyor?.statement5, date: signatures.surveyor?.statement5Date }
  ];

  const renderSignatureBox = (statement: string, signature?: string, date?: string) => {
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-bottom: 12px; background: #ffffff;">
        <p style="font-size: 11px; color: #374151; margin-bottom: 8px; line-height: 1.4;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${statement}
        </p>
        ${signature ? `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 8px;">
            <div style="flex: 1;">
              <img src="${signature}" alt="Signature" style="max-height: 60px; max-width: 200px; border: 1px solid #d1d5db; border-radius: 4px; padding: 4px; background: #f9fafb;" />
            </div>
            <div style="font-size: 10px; color: #6b7280; margin-left: 12px;">
              Date: ${date || 'N/A'}
            </div>
          </div>
        ` : `
          <div style="color: #9ca3af; font-style: italic; font-size: 10px; margin-top: 8px;">
            No signature provided
          </div>
        `}
      </div>
    `;
  };

  return `
    <div class="content-section">
      <div class="section-heading">Survey Sign Off & Feedback (Continued)</div>
      
      <!-- Customer Sign Off -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; font-size: 14px; font-weight: 600; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #1e40af;">
          Customer Sign Off
        </h3>
        ${customerStatements.map(stmt => renderSignatureBox(stmt.text, stmt.signature, stmt.date)).join('')}
      </div>

      <!-- Feedback Section -->
      ${signatures.feedback && (signatures.feedback.rating || signatures.feedback.comments) ? `
        <div style="margin-bottom: 20px; padding: 12px; background: #f0f9ff; border-left: 4px solid #0ea5e9; border-radius: 4px;">
          <h3 style="color: #0369a1; font-size: 13px; font-weight: 600; margin-bottom: 8px;">
            Survey Feedback
          </h3>
          ${signatures.feedback.rating ? `
            <div style="margin-bottom: 8px;">
              <span style="font-size: 11px; color: #374151; font-weight: 500;">Rating: </span>
              <span style="font-size: 13px; color: #0ea5e9; font-weight: 600;">
                ${'★'.repeat(signatures.feedback.rating)}${'☆'.repeat(5 - signatures.feedback.rating)}
                (${signatures.feedback.rating}/5)
              </span>
            </div>
          ` : ''}
          ${signatures.feedback.comments ? `
            <div>
              <span style="font-size: 11px; color: #374151; font-weight: 500; display: block; margin-bottom: 4px;">Comments:</span>
              <div style="font-size: 10px; color: #475569; padding: 8px; background: #ffffff; border-radius: 4px; line-height: 1.4;">
                ${signatures.feedback.comments}
              </div>
            </div>
          ` : ''}
        </div>
      ` : ''}

      <!-- Surveyor Sign Off -->
      <div style="margin-top: 20px;">
        <h3 style="color: #059669; font-size: 14px; font-weight: 600; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #059669;">
          Surveyor Sign Off
        </h3>
        ${surveyorStatements.map(stmt => renderSignatureBox(stmt.text, stmt.signature, stmt.date)).join('')}
      </div>
    </div>
  `;
}

/**
 * Generate Signatures & Feedback page
 * Shows customer sign-off, surveyor sign-off, and feedback
 */
function generateSignaturesPage(surveyData: RetrofitSurveyData): string {
  const signatures = surveyData.signatures;
  
  if (!signatures || (!signatures.customer && !signatures.surveyor && !signatures.feedback)) {
    return `
      <div class="content-section">
        <div class="section-heading">Survey Sign Off & Feedback</div>
        <p style="color: #64748b; font-style: italic;">No signature or feedback data available</p>
      </div>
    `;
  }

  const customerStatements = [
    { text: 'I can confirm that the Retrofit Assessor has completed the survey in my property and I am happy with the way he has explained me before the survey.', signature: signatures.customer?.statement1, date: signatures.customer?.statement1Date },
    { text: 'I understand that the result of the survey will be provided by the Office via Retrofit Advisor and I can decide from there if I am happy with the installation.', signature: signatures.customer?.statement2, date: signatures.customer?.statement2Date },
    { text: 'I do understand that the Retrofit Assessment completed was for the OFGEM scheme and all my data will be stored and shared with OFGEM scheme providers only for the scheme purpose', signature: signatures.customer?.statement3, date: signatures.customer?.statement3Date },
    { text: 'I can confirm that I am the homeowner/tenant staying in the property and all the information provided by me for the scheme eligibility is true and no fraudulent documents has been provided by me to the installers', signature: signatures.customer?.statement4, date: signatures.customer?.statement4Date }
  ];

  const surveyorStatements = [
    { text: 'I can confirm that I have collected all evidence as per the Retrofit Assessment scheme', signature: signatures.surveyor?.statement1, date: signatures.surveyor?.statement1Date },
    { text: 'I can confirm that all the data collected by me will follow GDPR rules and regulations as per Eco Approach Ltd\'s standard', signature: signatures.surveyor?.statement2, date: signatures.surveyor?.statement2Date },
    { text: 'I have informed the customer that measures to be fitted in the property will be processed by the head office and will get back to you as per PAS 2035 Process.', signature: signatures.surveyor?.statement3, date: signatures.surveyor?.statement3Date },
    { text: 'I can confirm that I have ensured customer property and GDPR details are well taken care while doing my job.', signature: signatures.surveyor?.statement4, date: signatures.surveyor?.statement4Date },
    { text: 'I can confirm that my survey will be subject to compliance check and if they compliance is not approved I will not be getting paid for the job.', signature: signatures.surveyor?.statement5, date: signatures.surveyor?.statement5Date }
  ];

  const renderSignatureBox = (statement: string, signature?: string, date?: string) => {
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px; margin-bottom: 12px; background: #ffffff;">
        <p style="font-size: 11px; color: #374151; margin-bottom: 8px; line-height: 1.4;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${statement}
        </p>
        ${signature ? `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-top: 8px;">
            <div style="flex: 1;">
              <img src="${signature}" alt="Signature" style="max-height: 60px; max-width: 200px; border: 1px solid #d1d5db; border-radius: 4px; padding: 4px; background: #f9fafb;" />
            </div>
            <div style="font-size: 10px; color: #6b7280; margin-left: 12px;">
              Date: ${date || 'N/A'}
            </div>
          </div>
        ` : `
          <div style="color: #9ca3af; font-style: italic; font-size: 10px; margin-top: 8px;">
            No signature provided
          </div>
        `}
      </div>
    `;
  };

  const renderYesNoQuestion = (question: string, answer: boolean | null | undefined) => {
    const answerText = answer === true ? 'YES' : answer === false ? 'NO' : 'Not Answered';
    const answerColor = answer === true ? '#3b82f6' : answer === false ? '#6b7280' : '#9ca3af';
    return `
      <div style="border: 1px solid #e5e7eb; border-radius: 6px; padding: 10px; margin-bottom: 10px; background: #f9fafb;">
        <p style="font-size: 10px; color: #374151; margin-bottom: 6px; line-height: 1.4;">
          <span style="color: #ef4444; font-weight: bold;">* </span>${question}
        </p>
        <div style="display: inline-block; padding: 4px 12px; background: ${answerColor}; color: white; border-radius: 4px; font-size: 10px; font-weight: 600;">
          ${answerText}
        </div>
      </div>
    `;
  };

  const hasPreSignatureQuestions = signatures.asbestosChecksUndertaken !== undefined ||
    signatures.servicesAffectedByInstallation !== undefined ||
    signatures.potentialRiskToServices !== undefined ||
    signatures.installationInLineWithDesign !== undefined ||
    signatures.methodStatementSuitable !== undefined ||
    signatures.protectedSpeciesDisturbance !== undefined ||
    signatures.customerUnderstandsWork !== undefined ||
    signatures.customerAwareOfDisruption !== undefined ||
    signatures.customerHasRequests !== undefined ||
    signatures.siteAccessArrangementsAdequate !== undefined ||
    signatures.otherInstallationInfo !== undefined ||
    signatures.potentialMoistureBuildUp !== undefined;

  return `
    <div class="content-section">
      <div class="section-heading">Survey Sign Off & Feedback</div>
      
      ${hasPreSignatureQuestions ? `
      <!-- Pre-Installation Assessment -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #7c3aed; font-size: 14px; font-weight: 600; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #7c3aed;">
          Pre-Installation Assessment
        </h3>
        ${renderYesNoQuestion('Have you determined if relevant checks have been undertaken to determine if materials containing asbestos are present?', signatures.asbestosChecksUndertaken)}
        ${renderYesNoQuestion('Will the functionality and/or safety of installed services (gas, electricity, water, telecommunications etc.) be affected in any way as a result of the installation?', signatures.servicesAffectedByInstallation)}
        ${renderYesNoQuestion('Is there any potential risk in relation to the ongoing performance of installed services? (If YES liaison with the Retrofit Designer and / or Retrofit Coordinator will need to take place).', signatures.potentialRiskToServices)}
        ${renderYesNoQuestion('From the findings of this building inspection, is the installation able to be carried out in line with the full Design Specification?', signatures.installationInLineWithDesign)}
        ${renderYesNoQuestion('From the findings of this building inspection, is the Method Statement suitable and complete?', signatures.methodStatementSuitable)}
        ${renderYesNoQuestion('Is there any evidence that any form of protected species would be disturbed by the installation process?', signatures.protectedSpeciesDisturbance)}
      </div>

      <!-- PAS 2030 General -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #6366f1; font-size: 14px; font-weight: 600; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #6366f1;">
          PAS 2030 General
        </h3>
        ${renderYesNoQuestion('Does the customer understand the nature and extent of the work?', signatures.customerUnderstandsWork)}
        ${renderYesNoQuestion('Have they been made aware of any disruption that may be caused (eg rubbish disposal and the siting of skips)?', signatures.customerAwareOfDisruption)}
        ${renderYesNoQuestion('Does the customer have any particular requests or requirements?', signatures.customerHasRequests)}
        ${renderYesNoQuestion('Are the arrangements for site access and the storage of installation materials adequate and appropriate?', signatures.siteAccessArrangementsAdequate)}
        ${renderYesNoQuestion('Is there any other information which would help ensure the smooth running of the installation process?', signatures.otherInstallationInfo)}
        ${renderYesNoQuestion('Is there any potential for moisture build up as a result of the installation?', signatures.potentialMoistureBuildUp)}
      </div>
      ` : ''}
      
      <!-- Customer Sign Off -->
      <div style="margin-bottom: 20px;">
        <h3 style="color: #1e40af; font-size: 14px; font-weight: 600; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #1e40af;">
          Customer Sign Off
        </h3>
        ${customerStatements.map(stmt => renderSignatureBox(stmt.text, stmt.signature, stmt.date)).join('')}
      </div>

      <!-- Feedback Section -->
      ${signatures.feedback && (signatures.feedback.rating || signatures.feedback.comments) ? `
        <div style="margin-bottom: 20px; padding: 12px; background: #f0f9ff; border-left: 4px solid #0ea5e9; border-radius: 4px;">
          <h3 style="color: #0369a1; font-size: 13px; font-weight: 600; margin-bottom: 8px;">
            Survey Feedback
          </h3>
          ${signatures.feedback.rating ? `
            <div style="margin-bottom: 8px;">
              <span style="font-size: 11px; color: #374151; font-weight: 500;">Rating: </span>
              <span style="font-size: 13px; color: #0ea5e9; font-weight: 600;">
                ${'★'.repeat(signatures.feedback.rating)}${'☆'.repeat(5 - signatures.feedback.rating)}
                (${signatures.feedback.rating}/5)
              </span>
            </div>
          ` : ''}
          ${signatures.feedback.comments ? `
            <div>
              <span style="font-size: 11px; color: #374151; font-weight: 500; display: block; margin-bottom: 4px;">Comments:</span>
              <div style="font-size: 10px; color: #475569; padding: 8px; background: #ffffff; border-radius: 4px; line-height: 1.4;">
                ${signatures.feedback.comments}
              </div>
            </div>
          ` : ''}
        </div>
      ` : ''}

      <!-- Surveyor Sign Off -->
      <div style="margin-top: 20px;">
        <h3 style="color: #059669; font-size: 14px; font-weight: 600; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 2px solid #059669;">
          Surveyor Sign Off
        </h3>
        ${surveyorStatements.map(stmt => renderSignatureBox(stmt.text, stmt.signature, stmt.date)).join('')}
      </div>
    </div>
  `;
}

/**
 * Generate heating system section with photos
 * Shows ALL photos with max 4 per page
 * Returns array of page HTML strings
 */
async function generateHeatingSystemPages(surveyData: RetrofitSurveyData, startPage: number, totalPages: number, options?: RetrofitPDFExportOptions): Promise<string[]> {
  const { heating_system } = surveyData;
  const photos = heating_system?.photos || {};
  
  // Collect all heating system photos
  const allPhotos: { url: string; label: string }[] = [];
  
  const photoTypes = [
    { key: 'currentBoilerImage', label: 'Current Boiler' },
    { key: 'boilerDataBadgeImage', label: 'Boiler Data Badge' },
    { key: 'fluePipeImage', label: 'Flue Pipe' },
    { key: 'condensatePipeImage', label: 'Condensate Pipe' },
    { key: 'oilLpgTankImage', label: 'Oil/LPG Tank' },
    { key: 'renewablesImage', label: 'Renewables System' },
    { key: 'cylinderWaterHeatingImage', label: 'Cylinder Water Heating' },
    { key: 'preExistingHeatingControlsImages', label: 'Heating Controls' },
    { key: 'bypassRadiatorImage', label: 'Bypass Radiator' }
  ];

  for (const photoType of photoTypes) {
    const photoArray = photos[photoType.key] || [];
    const photosToProcess = Array.isArray(photoArray) ? photoArray : [];
    
    for (const photo of photosToProcess) {
      const photoUrl = photo.url || photo.localPath || photo.dataUrl || '';
      if (photoUrl) {
        try {
          const optimizedUrl = await optimizeImageForPDF(photoUrl, 1600, 1200, 0.90);
          allPhotos.push({ url: optimizedUrl, label: photoType.label });
        } catch (error) {
          allPhotos.push({ url: photoUrl, label: photoType.label });
        }
      }
    }
  }

  // Build heating system data rows (compact 2-column layout)
  const heatingSystemRows = [
    { label: 'Main Heating Source', value: heating_system?.mainHeatingSource ? capitalizeText(heating_system.mainHeatingSource) : null },
    { label: 'Boiler Brand/Model', value: heating_system?.boilerBrand || null },
    { label: 'Model Qualifier', value: heating_system?.modelQualifier || null },
    { label: 'Boiler Type', value: heating_system?.boilerType ? capitalizeText(heating_system.boilerType) : null },
    { label: 'Fuel Type', value: heating_system?.fuelType ? capitalizeText(heating_system.fuelType) : null },
    { label: 'SAP Winter Seasonal Efficiency (%)', value: heating_system?.sapWinterEfficiency ? `${heating_system.sapWinterEfficiency}%` : null },
    { label: 'Age of Boiler', value: heating_system?.boilerAge ? `${heating_system.boilerAge} years` : null },
    { label: 'Year of Commissioning', value: heating_system?.commissionYear || null },
    { label: 'Serial Number', value: heating_system?.serialNumber || null },
    { label: 'Boiler Location', value: heating_system?.boilerLocation ? capitalizeText(heating_system.boilerLocation) : null },
    { label: 'Boiler Position', value: heating_system?.boilerPosition ? capitalizeText(heating_system.boilerPosition) : null },
    { label: 'Heating System Condition', value: heating_system?.heatingSystemCondition ? capitalizeText(heating_system.heatingSystemCondition) : null },
    { label: 'Condensate Pipe Present', value: heating_system?.hasCondensatePipe !== undefined && heating_system?.hasCondensatePipe !== null ? (heating_system.hasCondensatePipe ? 'Yes' : 'No') : null },
    { label: 'Oil/LPG Tank Present', value: heating_system?.hasOilLpgTank !== undefined && heating_system?.hasOilLpgTank !== null ? (heating_system.hasOilLpgTank ? 'Yes' : 'No') : null },
    { label: 'Renewables Present', value: heating_system?.hasRenewables !== undefined && heating_system?.hasRenewables !== null ? (heating_system.hasRenewables ? 'Yes' : 'No') : null },
    { label: 'Cylinder Water Heating', value: heating_system?.hasCylinderWaterHeating !== undefined && heating_system?.hasCylinderWaterHeating !== null ? (heating_system.hasCylinderWaterHeating ? 'Yes' : 'No') : null },
    { label: 'Other Boilers Present', value: heating_system?.hasOtherBoilers !== undefined && heating_system?.hasOtherBoilers !== null ? (heating_system.hasOtherBoilers ? 'Yes' : 'No') : null },
    { label: 'Other Boilers Details', value: heating_system?.otherBoilersDetails || null }
  ].filter(row => row.value !== null);

  // Calculate heating element counts from room details
  const heatingElementCounts = {
    'radiator-with-trv': 0,
    'radiator-without-trv': 0,
    'panel-heater': 0,
    'fan-heater': 0,
    'electric-storage-heater': 0,
    'high-heat-retention': 0
  };

  if (surveyData?.room_details?.rooms) {
    surveyData.room_details.rooms.forEach((room: any) => {
      if (room.radiatorDetails && Array.isArray(room.radiatorDetails)) {
        room.radiatorDetails.forEach((rad: any) => {
          const type = (rad.type || '').toLowerCase();
          
          if (!type || type === 'radiator' || type.includes('radiator')) {
            if (rad.hasTRV === true) {
              heatingElementCounts['radiator-with-trv']++;
            } else {
              heatingElementCounts['radiator-without-trv']++;
            }
          } else if (type.includes('panel')) {
            heatingElementCounts['panel-heater']++;
          } else if (type.includes('fan')) {
            heatingElementCounts['fan-heater']++;
          } else if (type.includes('storage')) {
            heatingElementCounts['electric-storage-heater']++;
          } else if (type.includes('retention')) {
            heatingElementCounts['high-heat-retention']++;
          }
        });
      }
    });
  }

  const totalRadiators = heatingElementCounts['radiator-with-trv'] + heatingElementCounts['radiator-without-trv'];
  const totalOther = heatingElementCounts['panel-heater'] + heatingElementCounts['fan-heater'] + heatingElementCounts['electric-storage-heater'] + heatingElementCounts['high-heat-retention'];
  const totalAll = totalRadiators + totalOther;

  // Build heating controls data rows
  const heatingControlsRows = [
    { label: 'Is there heating controls?', value: heating_system?.fullSetFunctioningControls !== undefined && heating_system?.fullSetFunctioningControls !== null ? (heating_system.fullSetFunctioningControls ? 'Yes' : 'No') : null },
    { label: 'Does the system incorporate a bypass radiator?', value: heating_system?.systemHasBypassRadiator !== undefined && heating_system?.systemHasBypassRadiator !== null ? (heating_system.systemHasBypassRadiator ? 'Yes' : 'No') : null },
    // Heating elements from room details
    { label: 'Radiators (with TRV)', value: heatingElementCounts['radiator-with-trv'].toString() },
    { label: 'Radiators (no TRV)', value: heatingElementCounts['radiator-without-trv'].toString() },
    { label: 'Panel Heaters', value: heatingElementCounts['panel-heater'].toString() },
    { label: 'Fan Heaters', value: heatingElementCounts['fan-heater'].toString() },
    { label: 'Electric Storage', value: heatingElementCounts['electric-storage-heater'].toString() },
    { label: 'High Heat Retention', value: heatingElementCounts['high-heat-retention'].toString() },
    { label: 'Total Radiators', value: totalRadiators.toString(), highlight: true },
    { label: 'Total All Heating Elements', value: totalAll.toString(), highlight: true }
  ].filter(row => row.value !== null);

  // Generate compact 2-column table HTML
  let heatingSystemTableHTML = '';
  for (let i = 0; i < heatingSystemRows.length; i += 2) {
    const leftRow = heatingSystemRows[i];
    const rightRow = heatingSystemRows[i + 1];
    heatingSystemTableHTML += `
      <tr>
        <td style="font-weight: 600;">${leftRow.label}</td>
        <td>${leftRow.value}</td>
        ${rightRow ? `<td style="font-weight: 600;">${rightRow.label}</td><td>${rightRow.value}</td>` : '<td colspan="2"></td>'}
      </tr>
    `;
  }

  // Generate heating controls table HTML
  let heatingControlsTableHTML = '';
  if (heatingControlsRows.length > 0) {
    for (const row of heatingControlsRows) {
      const rowStyle = row.highlight ? ' style="background-color: #f0f9ff; font-weight: 600;"' : '';
      heatingControlsTableHTML += `<tr${rowStyle}><td>${row.label}</td><td>${row.value}</td></tr>`;
    }
  }

  // First page always shows the tables
  const tableHTML = `
    <div class="content-section">
      <div class="section-heading">Heating System Assessment</div>
      
      <table class="occupancy-table" style="table-layout: fixed;">
        <tbody>
          ${heatingSystemTableHTML}
        </tbody>
      </table>

      ${heatingControlsRows.length > 0 ? `
        <div class="subsection-heading" style="margin-top: 16pt;">Heating Controls Survey</div>
        <table class="occupancy-table">
          <thead>
            <tr>
              <th>Question</th>
              <th style="width: 120pt;">Response</th>
            </tr>
          </thead>
          <tbody>
            ${heatingControlsTableHTML}
          </tbody>
        </table>
      ` : ''}

      ${heating_system?.notes ? `
        <div class="mt-2">
          <strong>Additional Notes:</strong>
          <div class="text-sm mt-2" style="padding: 8pt; background: #f9fafb; border-left: 3pt solid var(--theme-color); border-radius: 4pt;">
            ${heating_system.notes}
          </div>
        </div>
      ` : ''}
    </div>
  `;

  const pages: string[] = [
    `
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage, totalPages, options)}
        ${tableHTML}
        ${generateFooter(options)}
      </section>
    `
  ];

  // Generate photo pages with 6 photos per page (matching room photos layout)
  if (allPhotos.length > 0) {
    const photosPerPage = 6;
    for (let i = 0; i < allPhotos.length; i += photosPerPage) {
      const pagePhotos = allPhotos.slice(i, i + photosPerPage);
      const photosHTML = pagePhotos.map(photo => `
        <div class="property-photo-wrapper">
          <div class="property-photo-item">
            <img src="${photo.url}" alt="${photo.label}" />
          </div>
          <div class="property-photo-label">${photo.label}</div>
        </div>
      `).join('');

      const pageNumber = Math.floor(i / photosPerPage) + 1;
      const totalPhotoPages = Math.ceil(allPhotos.length / photosPerPage);

      pages.push(`
        <section class="pdf-page">
          ${generateHeader(surveyData, startPage + pageNumber, totalPages, options)}
          <div class="content-section">
            <div class="property-section">
              <div class="property-section-header">
                <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
                  <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M3 3v18h18"></path>
                    <path d="M18 9l-5 5"></path>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                </div>
                <h3><span class="section-parent">Heating System:</span> Photos${totalPhotoPages > 1 ? ` (${pageNumber}/${totalPhotoPages})` : ''}</h3>
              </div>
              <div class="property-photo-grid">${photosHTML}</div>
            </div>
          </div>
          ${generateFooter(options)}
        </section>
      `);
    }
  }
  return pages;
}

/**
 * Generate Loft photo pages
 * Shows all Loft photos with max 6 per page
 * Returns array of page HTML strings
 */
async function generateLoftPhotoPages(surveyData: RetrofitSurveyData, startPage: number, totalPages: number, options?: RetrofitPDFExportOptions): Promise<string[]> {
  const measure_specification = surveyData.measure_specification;
  if (!measure_specification) return [];
  
  // Collect all Loft photos
  const allPhotos: { url: string; label: string }[] = [];
  
  const photoSections = [
    { key: 'mainPropertyInsulationDepthEvidence', label: 'Insulation Depth Evidence' },
    { key: 'mainLoftHatchEvidence', label: 'Loft Hatch Evidence' },
    { key: 'mainPropertyRoofPitchEvidence', label: 'Roof Pitch Evidence' },
    { key: 'mainLoftAreaImages', label: 'Loft Area Images' },
    { key: 'apexMeasurementEvidence', label: 'Apex Measurement Evidence' },
    { key: 'partyWallEvidence', label: 'Party Wall Evidence' },
    { key: 'downlightImage', label: 'Downlight Images' },
    { key: 'coldWaterTankPipeworkEvidence', label: 'Cold Water Tank/Pipework' },
    { key: 'roofVentsImage', label: 'Roof Vents Images' }
  ];

  for (const section of photoSections) {
    const photoArray = (measure_specification as any)[section.key] || [];
    const photosToProcess = Array.isArray(photoArray) ? photoArray : [];
    
    for (const photo of photosToProcess) {
      const photoUrl = photo.url || photo.localPath || '';
      if (photoUrl) {
        try {
          const optimizedUrl = await optimizeImageForPDF(photoUrl, 1600, 1200, 0.90);
          allPhotos.push({ 
            url: optimizedUrl, 
            label: `${section.label}${photosToProcess.length > 1 ? ` (${photosToProcess.indexOf(photo) + 1}/${photosToProcess.length})` : ''}` 
          });
        } catch (error) {
          allPhotos.push({ 
            url: photoUrl, 
            label: `${section.label}${photosToProcess.length > 1 ? ` (${photosToProcess.indexOf(photo) + 1}/${photosToProcess.length})` : ''}` 
          });
        }
      }
    }
  }

  if (allPhotos.length === 0) return [];

  const pages: string[] = [];
  
  // Generate photo pages with 6 photos per page
  const photosPerPage = 6;
  for (let i = 0; i < allPhotos.length; i += photosPerPage) {
    const pagePhotos = allPhotos.slice(i, i + photosPerPage);
    const photosHTML = pagePhotos.map(photo => `
      <div class="property-photo-wrapper">
        <div class="property-photo-item">
          <img src="${photo.url}" alt="${photo.label}" />
        </div>
        <div class="property-photo-label">${photo.label}</div>
      </div>
    `).join('');

    const pageNumber = Math.floor(i / photosPerPage);
    const totalPhotoPages = Math.ceil(allPhotos.length / photosPerPage);

    pages.push(`
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + pageNumber, totalPages, options)}
        <div class="content-section">
          <div class="property-section">
            <div class="property-section-header">
              <div class="property-icon-container" style="background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);">
                <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 3v18h18"></path>
                  <path d="M18 9l-5 5"></path>
                  <circle cx="9" cy="9" r="2"></circle>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                </svg>
              </div>
              <h3><span class="section-parent">Loft Survey:</span> Photo Evidence${totalPhotoPages > 1 ? ` (${pageNumber + 1}/${totalPhotoPages})` : ''}</h3>
            </div>
            <div class="property-photo-grid">${photosHTML}</div>
          </div>
        </div>
        ${generateFooter(options)}
      </section>
    `);
  }

  return pages;
}

/**
 * Generate RIR photo pages
 * Shows all RIR photos with max 6 per page
 * Returns array of page HTML strings
 */
async function generateRIRPhotoPages(surveyData: RetrofitSurveyData, startPage: number, totalPages: number, options?: RetrofitPDFExportOptions): Promise<string[]> {
  const measure_specification = surveyData.measure_specification;
  if (!measure_specification) return [];
  
  // Collect all RIR photos
  const allPhotos: { url: string; label: string }[] = [];
  
  const photoSections = [
    { key: 'rirExteriorElevations', label: 'Exterior Elevations' },
    { key: 'rirInternalRoomPhotos', label: 'Internal Room Photos' },
    { key: 'rirFlatCeilingInsulation', label: 'Flat Ceiling & Insulation' },
    { key: 'rirSlopingCeilingInsulation', label: 'Sloping Ceiling & Insulation' },
    { key: 'rirStudWallsInsulation', label: 'Stud Walls & Insulation' },
    { key: 'rirDormerPhotos', label: 'Dormer Photos' }
  ];

  for (const section of photoSections) {
    const photoArray = (measure_specification as any)[section.key] || [];
    const photosToProcess = Array.isArray(photoArray) ? photoArray : [];
    
    for (const photo of photosToProcess) {
      const photoUrl = photo.url || photo.localPath || '';
      if (photoUrl) {
        try {
          const optimizedUrl = await optimizeImageForPDF(photoUrl, 1600, 1200, 0.90);
          allPhotos.push({ 
            url: optimizedUrl, 
            label: `${section.label}${photosToProcess.length > 1 ? ` (${photosToProcess.indexOf(photo) + 1}/${photosToProcess.length})` : ''}` 
          });
        } catch (error) {
          allPhotos.push({ 
            url: photoUrl, 
            label: `${section.label}${photosToProcess.length > 1 ? ` (${photosToProcess.indexOf(photo) + 1}/${photosToProcess.length})` : ''}` 
          });
        }
      }
    }
  }

  if (allPhotos.length === 0) return [];

  const pages: string[] = [];
  
  // Generate photo pages with 6 photos per page
  const photosPerPage = 6;
  for (let i = 0; i < allPhotos.length; i += photosPerPage) {
    const pagePhotos = allPhotos.slice(i, i + photosPerPage);
    const photosHTML = pagePhotos.map(photo => `
      <div class="property-photo-wrapper">
        <div class="property-photo-item">
          <img src="${photo.url}" alt="${photo.label}" />
        </div>
        <div class="property-photo-label">${photo.label}</div>
      </div>
    `).join('');

    const pageNumber = Math.floor(i / photosPerPage);
    const totalPhotoPages = Math.ceil(allPhotos.length / photosPerPage);

    pages.push(`
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + pageNumber, totalPages, options)}
        <div class="content-section">
          <div class="property-section">
            <div class="property-section-header">
              <div class="property-icon-container" style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);">
                <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 3v18h18"></path>
                  <path d="M18 9l-5 5"></path>
                  <circle cx="9" cy="9" r="2"></circle>
                  <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                </svg>
              </div>
              <h3><span class="section-parent">Room in Roof (RIR):</span> Photo Evidence${totalPhotoPages > 1 ? ` (${pageNumber + 1}/${totalPhotoPages})` : ''}</h3>
            </div>
            <div class="property-photo-grid">${photosHTML}</div>
          </div>
        </div>
        ${generateFooter(options)}
      </section>
    `);
  }

  return pages;
}

/**
 * Generate measure specification section with integrated wall insulation assessment
 */
async function generateMeasureSpecificationSection(surveyData: RetrofitSurveyData, elevationImages?: ElevationImageData[]): Promise<string> {
  const measure_specification = surveyData.measure_specification;
  
  console.log('🔍 [PDF] Measure Specification Check:', {
    hasMeasureSpec: !!measure_specification,
    hasSuitableMeasures: !!measure_specification?.suitableMeasures,
    measuresCount: measure_specification?.suitableMeasures?.length,
    measures: measure_specification?.suitableMeasures
  });
  
  // Check if any measures are selected
  if (!measure_specification?.suitableMeasures || measure_specification.suitableMeasures.length === 0) {
    console.log('⚠️ [PDF] No suitable measures found, skipping section');
    return '';
  }
  
  console.log('✅ [PDF] Checking Measure Specification section with measures:', measure_specification.suitableMeasures);
  
  // Check if Wall Insulation has actual content to display
  const hasWallInsulationContent = measure_specification.suitableMeasures.includes('Wall Insulation') 
    && measure_specification.wallInsulationType 
    && elevationImages 
    && elevationImages.length > 0;
  
  // Only generate section if there's actual content to display
  // (Loft, RIR, and Heating System are on separate pages)
  if (!hasWallInsulationContent) {
    console.log('⚠️ [PDF] No displayable content in Measure Specification section (other measures have separate pages)');
    return '';
  }
  
  let content = `<div class="content-section">
    <div class="section-heading">Measure Specification</div>`;
  
  // Wall Insulation Assessment (integrated into this page)
  if (hasWallInsulationContent) {
    const calc = measure_specification.wallInsulationGlobalCalculations;
    const wallInsulationType = measure_specification.wallInsulationType;
    const elevationsCount = measure_specification.wallInsulationElevationsCount || elevationImages.length;
    const dwellingType = surveyData.initial_questionnaire?.dwellingType || 'House';
    
    content += `
    <div style="margin-top: 16pt; page-break-inside: avoid;">
      <div class="subsection-heading" style="margin-bottom: 16pt;">${capitalizeText(wallInsulationType)} Wall Insulation Assessment</div>
      
      <!-- Property and Calculations Grid -->
      <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 12pt; margin-bottom: 16pt;">
        
        <!-- LEFT: PROPERTY -->
        <div style="background: white; padding: 10pt; border-radius: 6pt; border: 1pt solid #e5e7eb;">
          <div style="font-size: 8pt; font-weight: 700; color: #64748b; margin-bottom: 8pt; text-transform: uppercase; letter-spacing: 0.5pt;">Property</div>
          <table style="width: 100%; border-collapse: collapse;">
            <tbody>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">Type</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 600; color: #374151; text-align: right;">${dwellingType}</td>
              </tr>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">Insulation</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 600; color: #374151; text-align: right;">${capitalizeText(wallInsulationType)}</td>
              </tr>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">Elevations</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 600; color: #374151; text-align: right;">${elevationsCount}</td>
              </tr>
            </tbody>
          </table>
        </div>
        
        <!-- RIGHT: CALCULATIONS -->
        <div style="background: white; padding: 10pt; border-radius: 6pt; border: 1pt solid #e5e7eb;">
          <div style="font-size: 8pt; font-weight: 700; color: #64748b; margin-bottom: 8pt; text-transform: uppercase; letter-spacing: 0.5pt;">Calculations</div>
          ${calc ? `
          <table style="width: 100%; border-collapse: collapse;">
            <tbody>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">Total Area</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 700; color: #3b82f6; text-align: right;">${calc.totalDwellingArea.toFixed(2)}m²</td>
              </tr>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">Untreatable</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 700; color: #ef4444; text-align: right;">${calc.totalUntreatableArea.toFixed(2)}m²</td>
              </tr>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">Treatable</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 700; color: #22c55e; text-align: right;">${calc.totalTreatableArea.toFixed(2)}m²</td>
              </tr>
              <tr>
                <td style="padding: 4pt 0; font-size: 8pt; color: #64748b;">PQPT %</td>
                <td style="padding: 4pt 0; font-size: 8pt; font-weight: 700; color: #6366f1; text-align: right;">${calc.poptPercentage.toFixed(2)}%</td>
              </tr>
            </tbody>
          </table>
          ` : `<div style="text-align: center; color: #94a3b8; font-size: 7pt; padding: 12pt;">No calculations available</div>`}
        </div>
      </div>
    `;
    
    // Add first 2 elevations only (to avoid page stretching)
    const elevationsForThisPage = elevationImages.slice(0, 2);
    elevationsForThisPage.forEach((elevationImg, index) => {
      content += generateWallInsulationElevationCard(elevationImg, surveyData, index > 0);
    });
    
    content += `</div>`;
  }
  
  content += `</div>`;
  
  return content;
}

/**
 * Generate PAS 2030 Measures Survey Questions Page
 * Dynamically shows questions based on selected measures in measure_specification
 */
function generatePAS2030MeasuresQuestionsPage(surveyData: RetrofitSurveyData): string {
  const measure_specification = surveyData.measure_specification;
  
  // Check if any measures are selected that require PAS 2030 questions
  if (!measure_specification?.suitableMeasures || measure_specification.suitableMeasures.length === 0) {
    return '';
  }
  
  const answers = measure_specification.pas2030Questions || {};
  
  // Define question sets for each measure type
  const heatingSystemQuestions = [
    { id: 'buildingFabric', text: 'Is the condition of the building fabric satisfactory in relation to the proposed work?', type: 'yes-no' },
    { id: 'electricalInstallation', text: 'Is the condition of the existing electrical installation satisfactory in relation to the proposed work?', type: 'yes-no' },
    { id: 'buildingRegs', text: 'Will the installation work result in non-compliance with the Building Regulations in relation to workmanship; materials; structural stability; fire safety; resistance to moisture; heat-producing appliances; conservation of fuel and power', type: 'yes-no' },
    { id: 'gasSafety', text: 'Will the installation work result in non-compliance with relevant gas safety regulations?', type: 'yes-no' },
    { id: 'manufacturer', text: 'Will the proposed installation be compliant with any requirements stated by the boiler manufacturer?', type: 'yes-no' },
    { id: 'plumeNuisance', text: 'Will/might the proposed installation result in a plume nuisance situation?', type: 'yes-no' },
    { id: 'condensate', text: 'Are any special condensate disposal arrangements required?', type: 'yes-no' },
    { id: 'hardWater', text: 'Is the dwelling located in a hard water area (above 200 ppm)?', type: 'yes-no' },
    { id: 'waterConditioning', text: 'Are water conditioning arrangements required in relation to the water hardness?', type: 'yes-no' }
  ];
  
  // IWI (Internal Wall Insulation) Questions
  const iwiQuestions = [
    { id: 'preDesignSurvey', text: 'A full and detailed pre-design building survey has been undertaken by a competent person, prior to the retrofit design being undertaken.', type: 'yes-no' },
    { id: 'thermalCalculations', text: 'Thermal performance calculations', type: 'yes-no' },
    { id: 'condensationRisk', text: 'Condensation risk analysis', type: 'yes-no' },
    { id: 'ventilationReqs', text: 'Ventilation requirements and standard/bespoke drawing details', type: 'yes-no' },
    { id: 'systemComponents', text: 'The main components of the system including the fixing type/method, the insulation type and thickness, the reinforcing coat and type of reinforcement and the finish', type: 'yes-no' },
    { id: 'interfaces', text: 'The proposed details for the main interfaces', type: 'yes-no' },
    { id: 'practical', text: 'The installation to the retrofit design is practical and achievable given the particular IWI system chosen for the project and the specific dwelling construction, site conditions and other EEMs planned for the property', type: 'yes-no' },
    { id: 'buildingRegs', text: 'Will the installation result in non-compliance with the Building Regulations e.g. in relation to workmanship; materials; structural ability; fire safety', type: 'yes-no' },
    { id: 'moistureResistance', text: 'Will the installation provide resistance to moisture.', type: 'yes-no' },
    { id: 'thermalBridging', text: 'Will the installation result in avoidable thermal bridging', type: 'yes-no' },
    { id: 'combustionAppliances', text: 'Will the installation result in the unsafe operation of combustion appliances', type: 'yes-no' },
    { id: 'ventilationDucts', text: 'Will the installation compromise the functionality of existing ventilation ducts/systems', type: 'yes-no' },
    { id: 'services', text: 'Will the installation compromise the functionality and/or safety of existing services (gas, electric, water, telephone, etc.)', type: 'yes-no' },
    { id: 'supplierCompliance', text: 'Will the installation result in the proposed installation being non-compliant with any requirements of the EEM supplier or of the retrofit design.', type: 'yes-no' },
    { id: 'substrateCondition', text: 'Confirmation that the condition of the substrate is suitable for the works to commence and where all or any of the substrate does not fulfil the requirements for installation, preparation of proposals for adaptations to be made or additional preparation undertaken that will be necessary in order that works can commence.', type: 'yes-no' },
    { id: 'nonComplianceConfirmation', text: 'Confirmation all instances of potential non-compliance identified in the pre-installation building inspection shall be documented and referred to the Retrofit Coordinator for resolution. Any design adjustments, special adaptations and/or additional preparation requirements shall be confirmed as acceptable in writing, by the system supplier and/or the Retrofit Coordinator.', type: 'yes-no' },
    { id: 'designAmendment', text: 'Confirmation the retrofit design documentation shall be amended to include any specified changes to the installation, the installation method statement modified accordingly and the pre-installation building inspection records updated to provide documentary evidence that the intended modified installation will address all the issues identified in the pre-installation building inspection and meets the requirements of all parties.', type: 'yes-no' }
  ];
  
  // EWI (External Wall Insulation) Questions
  const ewiQuestions = [
    { id: 'preDesignSurvey', text: 'A full and detailed pre-design building survey has been undertaken by a competent person, prior to the retrofit design being undertaken.', type: 'yes-no' },
    { id: 'thermalCalculations', text: 'Thermal performance calculations', type: 'yes-no' },
    { id: 'condensationRisk', text: 'Condensation risk analysis', type: 'yes-no' },
    { id: 'ventilationReqs', text: 'Ventilation requirements and standard/bespoke drawing details', type: 'yes-no' },
    { id: 'systemComponents', text: 'The main components of the system including the fixing type/method, the insulation type and thickness, the reinforcing coat and type of reinforcement and the finish', type: 'yes-no' },
    { id: 'interfaces', text: 'The proposed details for the main interfaces', type: 'yes-no' },
    { id: 'practical', text: 'The installation to the retrofit design is practical and achievable given the particular EWI system chosen for the project and the specific dwelling construction, site conditions and other EEMs planned for the property', type: 'yes-no' },
    { id: 'buildingRegs', text: 'Will the installation result in non-compliance with the Building Regulations e.g. in relation to workmanship; materials; structural ability; fire safety', type: 'yes-no' },
    { id: 'moistureResistance', text: 'Will the installation provide resistance to moisture.', type: 'yes-no' },
    { id: 'thermalBridging', text: 'Will the installation result in avoidable thermal bridging', type: 'yes-no' },
    { id: 'combustionAppliances', text: 'Will the installation result in the unsafe operation of combustion appliances', type: 'yes-no' },
    { id: 'ventilationDucts', text: 'Will the installation compromise the functionality of existing ventilation ducts/systems', type: 'yes-no' },
    { id: 'services', text: 'Will the installation compromise the functionality and/or safety of existing services (gas, electric, water, telephone, etc.)', type: 'yes-no' },
    { id: 'supplierCompliance', text: 'Will the installation result in the proposed installation being non-compliant with any requirements of the EEM supplier or of the retrofit design.', type: 'yes-no' },
    { id: 'substrateCondition', text: 'Confirmation that the condition of the substrate is suitable for the works to commence and where all or any of the substrate does not fulfil the requirements for installation, preparation of proposals for adaptations to be made or additional preparation undertaken that will be necessary in order that works can commence.', type: 'yes-no' },
    { id: 'nonComplianceConfirmation', text: 'Confirmation all instances of potential non-compliance identified in the pre-installation building inspection shall be documented and referred to the Retrofit Coordinator for resolution. Any design adjustments, special adaptations and/or additional preparation requirements shall be confirmed as acceptable in writing, by the system supplier and/or the Retrofit Coordinator.', type: 'yes-no' },
    { id: 'designAmendment', text: 'Confirmation the retrofit design documentation shall be amended to include any specified changes to the installation, the installation method statement modified accordingly and the pre-installation building inspection records updated to provide documentary evidence that the intended modified installation will address all the issues identified in the pre-installation building inspection and meets the requirements of all parties.', type: 'yes-no' },
    { id: 'cavityWallConfirmation', text: 'Confirmation that the EWI is not being installed over walls of cavity construction that have had the cavities removed and not replaced, or have unfilled cavities. If they are to be so, that we have received confirmation from the Retrofit Designer that these cavities can be sealed in accordance with the specifications and construction details provided.', type: 'yes-no' }
  ];
  
  // CWI (Cavity Wall Insulation) Questions
  const cwiQuestions = [
    { id: 'combustionAppliances', text: 'Will the work result in the unsafe operation of combustion appliances?', type: 'yes-no' },
    { id: 'ventilationDucts', text: 'Will the work compromise the functionality of existing ventilation ducts/systems?', type: 'yes-no' },
    { id: 'services', text: 'Will the work compromise the functionality and/or safety of existing services? (gas, electric, water, telephone, etc.)', type: 'yes-no' },
    { id: 'supplierReqs', text: 'Will the proposed installation be non-compliant with any requirements stated by the supplier?', type: 'yes-no' },
    { id: 'buildingStructure', text: 'Is the type and condition of the building structure suitable for the works to commence?', type: 'yes-no' },
    { id: 'cavityExtent', text: 'What is the extent of the cavity to be filled?', type: 'text' },
    { id: 'siteLayout', text: 'Will the site layout or conditions impair the execution of the works?', type: 'yes-no' }
  ];
  
  const rirQuestions = [
    { id: 'roofVentilation', text: 'The ventilation to spaces within the roof void and not included in the Room-in-Roof space, are checked and assessed to ensure that adequate ventilation is provided and maintained', type: 'yes-no' },
    { id: 'thermalBridging', text: 'Confirmation that there is no requirement for thermal bridging to be addressed at the ridge or other connections with the main structure.', type: 'yes-no' }
  ];
  
  // Determine which questions to show based on selected measures
  const showHeating = measure_specification.suitableMeasures.includes('Heating System');
  const showWallInsulation = measure_specification.suitableMeasures.includes('Wall Insulation');
  const showRIR = measure_specification.suitableMeasures.includes('RIR insulation');
  
  // If no applicable measures, return empty
  if (!showHeating && !showWallInsulation && !showRIR) {
    return '';
  }
  
  let content = '<div class="content-section">';
  content += '<div class="section-heading">PAS 2030 Survey Questions</div>';
  content += '<div style="color: #64748b; font-size: 9pt; margin-bottom: 16pt;">Pre-installation assessment questions based on selected measures</div>';
  
  // Helper function to render a question
  const renderQuestion = (question: any, sectionAnswers: Record<string, boolean | string>) => {
    const answer = sectionAnswers?.[question.id];
    let displayAnswer = '—';
    
    if (answer !== undefined && answer !== null) {
      if (question.type === 'yes-no') {
        displayAnswer = answer === true || answer === 'yes' ? '<span style="color: #10b981; font-weight: 600;">YES</span>' : '<span style="color: #ef4444; font-weight: 600;">NO</span>';
      } else if (question.type === 'text') {
        displayAnswer = String(answer);
      }
    }
    
    return `
      <div style="display: grid; grid-template-columns: 1fr auto; gap: 12pt; padding: 8pt; border-bottom: 1pt solid #e5e7eb;">
        <div style="font-size: 9pt; color: #374151;">${question.text}</div>
        <div style="font-size: 9pt; text-align: right; white-space: nowrap;">${displayAnswer}</div>
      </div>
    `;
  };
  
  // Heating System Questions
  if (showHeating) {
    content += `
      <div style="margin-bottom: 20pt; page-break-inside: avoid;">
        <div style="font-weight: 600; font-size: 11pt; color: #1e293b; margin-bottom: 8pt; padding: 6pt 8pt; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; border-radius: 4pt;">
          PAS 2030 - Boiler / Heating System
        </div>
        <div style="border: 1pt solid #e5e7eb; border-radius: 4pt; background: white;">
          ${heatingSystemQuestions.map(q => renderQuestion(q, answers.heatingSystem || {})).join('')}
        </div>
      </div>
    `;
  }
  
  // Wall Insulation Questions (IWI, EWI, or CWI)
  if (showWallInsulation) {
    const wallType = measure_specification.wallInsulationType || 'Wall Insulation';
    const wallTypeLower = wallType.toLowerCase();
    
    // Determine which question set to use based on insulation type
    let questions: Array<{ id: string; text: string; type: string }> = [];
    let answerKey: 'iwi' | 'ewi' | 'cwi' | 'wallInsulation' = 'wallInsulation';
    
    if (wallTypeLower.includes('iwi') || wallTypeLower.includes('internal')) {
      questions = iwiQuestions;
      answerKey = 'iwi';
    } else if (wallTypeLower.includes('ewi') || wallTypeLower.includes('external')) {
      questions = ewiQuestions;
      answerKey = 'ewi';
    } else if (wallTypeLower.includes('cwi') || wallTypeLower.includes('cavity')) {
      questions = cwiQuestions;
      answerKey = 'cwi';
    } else {
      // Default to IWI if type is unclear
      questions = iwiQuestions;
      answerKey = 'wallInsulation';
    }
    
    content += `
      <div style="margin-bottom: 20pt; page-break-inside: avoid;">
        <div style="font-weight: 600; font-size: 11pt; color: #1e293b; margin-bottom: 8pt; padding: 6pt 8pt; background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%); color: white; border-radius: 4pt;">
          PAS 2030 - ${capitalizeText(wallType)}
        </div>
        <div style="border: 1pt solid #e5e7eb; border-radius: 4pt; background: white;">
          ${questions.map((q: any) => renderQuestion(q, (answers as any)[answerKey] || {})).join('')}
        </div>
      </div>
    `;
  }
  
  // RIR Questions
  if (showRIR) {
    content += `
      <div style="margin-bottom: 20pt; page-break-inside: avoid;">
        <div style="font-weight: 600; font-size: 11pt; color: #1e293b; margin-bottom: 8pt; padding: 6pt 8pt; background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); color: white; border-radius: 4pt;">
          PAS 2030 - RIR (Room in Roof)
        </div>
        <div style="border: 1pt solid #e5e7eb; border-radius: 4pt; background: white;">
          ${rirQuestions.map(q => renderQuestion(q, answers.rirInsulation || {})).join('')}
        </div>
      </div>
    `;
  }
  
  content += '</div>';
  
  return content;
}

/**
 * Generate Heating System section as a separate page (from measure specification)
 */
function generateHeatingSystemMeasureSection(surveyData: RetrofitSurveyData): string {
  const measure_specification = surveyData.measure_specification;
  const heating_system = surveyData.heating_system;
  
  // Check if Heating System is selected in suitable measures
  if (!measure_specification?.suitableMeasures?.includes('Heating System')) {
    return '';
  }
  
  console.log('📋 [PDF] Generating Heating System section as separate page (from measure spec)');
  
  let content = `
  <div class="content-section">
    <div class="subsection-heading">Heating System Measure</div>
    
    <table class="occupancy-table" style="margin-top: 8pt;">
      <tbody>
        ${heating_system?.mainHeatingSource ? `<tr><td style="font-weight: 600;">Main Heating Source</td><td>${capitalizeText(heating_system.mainHeatingSource)}</td></tr>` : ''}
        ${heating_system?.boilerManufacturer ? `<tr><td style="font-weight: 600;">Boiler Manufacturer</td><td>${capitalizeText(heating_system.boilerManufacturer)}</td></tr>` : ''}
        ${heating_system?.boilerBrand ? `<tr><td style="font-weight: 600;">Boiler Brand/Model</td><td>${heating_system.boilerBrand}</td></tr>` : ''}
        ${heating_system?.boilerType ? `<tr><td style="font-weight: 600;">Boiler Type</td><td>${capitalizeText(heating_system.boilerType)}</td></tr>` : ''}
        ${heating_system?.fuelType ? `<tr><td style="font-weight: 600;">Fuel Type</td><td>${capitalizeText(heating_system.fuelType)}</td></tr>` : ''}
        ${heating_system?.sapWinterEfficiency ? `<tr><td style="font-weight: 600;">SAP Winter Efficiency</td><td>${heating_system.sapWinterEfficiency}%</td></tr>` : ''}
        ${heating_system?.boilerAge ? `<tr><td style="font-weight: 600;">Age of Boiler</td><td>${heating_system.boilerAge} years</td></tr>` : ''}
        ${heating_system?.heatingSystemCondition ? `<tr><td style="font-weight: 600;">System Condition</td><td>${capitalizeText(heating_system.heatingSystemCondition)}</td></tr>` : ''}
      </tbody>
    </table>
    
    ${heating_system?.notes ? `
      <div style="margin-top: 12pt; padding: 10pt; background: #fef3c7; border-left: 3pt solid #f59e0b; border-radius: 4pt;">
        <strong style="font-size: 8pt; color: #92400e;">Additional Notes:</strong>
        <div style="font-size: 8pt; color: #78350f; margin-top: 6pt;">${heating_system.notes}</div>
      </div>
    ` : ''}
  </div>`;
  
  return content;
}

/**
 * Generate Loft Insulation section as a separate page
 */
function generateLoftInsulationSection(surveyData: RetrofitSurveyData): string {
  const measure_specification = surveyData.measure_specification;
  
  // Check if Loft insulation is selected
  if (!measure_specification?.suitableMeasures?.includes('Loft insulation')) {
    return '';
  }
  
  console.log('📋 [PDF] Generating Loft Insulation section as separate page');
  
  let content = `
  <div class="content-section">
    <div class="subsection-heading">Loft Insulation Assessment</div>
    
    <table class="occupancy-table" style="margin-top: 8pt;">
      <tbody>
        ${(measure_specification as any).mainPropertyRoofType ? `<tr><td style="font-weight: 600;">Main Property Roof Type</td><td>${(measure_specification as any).mainPropertyRoofType}</td></tr>` : ''}
        ${(measure_specification as any).loftNeedsInsulating !== undefined && (measure_specification as any).loftNeedsInsulating !== null ? `<tr><td style="font-weight: 600;">Loft Needs Insulating (min 270mm)</td><td>${(measure_specification as any).loftNeedsInsulating ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).loftHasExistingInsulation !== undefined && (measure_specification as any).loftHasExistingInsulation !== null ? `<tr><td style="font-weight: 600;">Existing Insulation Present</td><td>${(measure_specification as any).loftHasExistingInsulation ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).mainLoftDepthOfInsulation ? `<tr><td style="font-weight: 600;">Depth of Insulation</td><td>${(measure_specification as any).mainLoftDepthOfInsulation} mm</td></tr>` : ''}
        ${(measure_specification as any).accessToAllAreasOfLoft !== undefined && (measure_specification as any).accessToAllAreasOfLoft !== null ? `<tr><td style="font-weight: 600;">Access to All Areas of Loft</td><td>${(measure_specification as any).accessToAllAreasOfLoft ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).inaccessibleAreaM2 ? `<tr><td style="font-weight: 600;">Inaccessible Area</td><td>${(measure_specification as any).inaccessibleAreaM2} m²</td></tr>` : ''}
        ${(measure_specification as any).loftHatchAlreadyPresent !== undefined && (measure_specification as any).loftHatchAlreadyPresent !== null ? `<tr><td style="font-weight: 600;">Loft Hatch Already Present</td><td>${(measure_specification as any).loftHatchAlreadyPresent ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).isThereALoftLadder !== undefined && (measure_specification as any).isThereALoftLadder !== null ? `<tr><td style="font-weight: 600;">Loft Ladder Present</td><td>${(measure_specification as any).isThereALoftLadder ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).isTheLoftHatchAlreadyInsulated !== undefined && (measure_specification as any).isTheLoftHatchAlreadyInsulated !== null ? `<tr><td style="font-weight: 600;">Loft Hatch Already Insulated</td><td>${(measure_specification as any).isTheLoftHatchAlreadyInsulated ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).isTheLoftHatchDraughtProofed !== undefined && (measure_specification as any).isTheLoftHatchDraughtProofed !== null ? `<tr><td style="font-weight: 600;">Loft Hatch Draught Proofed</td><td>${(measure_specification as any).isTheLoftHatchDraughtProofed ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).loftHatchLengthMM ? `<tr><td style="font-weight: 600;">Loft Hatch Length</td><td>${(measure_specification as any).loftHatchLengthMM} mm</td></tr>` : ''}
        ${(measure_specification as any).loftHatchWidthMM ? `<tr><td style="font-weight: 600;">Loft Hatch Width</td><td>${(measure_specification as any).loftHatchWidthMM} mm</td></tr>` : ''}
        ${(measure_specification as any).mainPropertyLoftLength ? `<tr><td style="font-weight: 600;">Loft Length</td><td>${(measure_specification as any).mainPropertyLoftLength} m</td></tr>` : ''}
        ${(measure_specification as any).mainPropertyLoftWidth ? `<tr><td style="font-weight: 600;">Loft Width</td><td>${(measure_specification as any).mainPropertyLoftWidth} m</td></tr>` : ''}
        ${(measure_specification as any).mainPropertyLoftHeight ? `<tr><td style="font-weight: 600;">Loft Height</td><td>${(measure_specification as any).mainPropertyLoftHeight} m</td></tr>` : ''}
        ${(measure_specification as any).mainPropertyRoofPitch ? `<tr><td style="font-weight: 600;">Roof Pitch</td><td>${(measure_specification as any).mainPropertyRoofPitch}°</td></tr>` : ''}
        ${measure_specification.walkboardsRequired !== undefined && measure_specification.walkboardsRequired !== null ? `<tr><td style="font-weight: 600;">Walkboards Required</td><td>${measure_specification.walkboardsRequired ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).tenantsBelongingsToBeRemoved !== undefined && (measure_specification as any).tenantsBelongingsToBeRemoved !== null ? `<tr><td style="font-weight: 600;">Occupier Belongings to be Removed</td><td>${(measure_specification as any).tenantsBelongingsToBeRemoved ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).explainedToOccupier !== undefined && (measure_specification as any).explainedToOccupier !== null ? `<tr><td style="font-weight: 600;">Explained to Occupier</td><td>${(measure_specification as any).explainedToOccupier ? 'Yes' : 'No'}</td></tr>` : ''}
        ${measure_specification.loftDownlightsPresent !== undefined && measure_specification.loftDownlightsPresent !== null ? `<tr><td style="font-weight: 600;">Loft Downlights Present</td><td>${measure_specification.loftDownlightsPresent ? 'Yes' : 'No'}</td></tr>` : ''}
        ${measure_specification.downlightCoversRequired !== undefined && measure_specification.downlightCoversRequired !== null ? `<tr><td style="font-weight: 600;">Downlight Covers Required</td><td>${measure_specification.downlightCoversRequired ? 'Yes' : 'No'}</td></tr>` : ''}
        ${measure_specification.coldWaterTankPresent !== undefined && measure_specification.coldWaterTankPresent !== null ? `<tr><td style="font-weight: 600;">Cold Water Tank Present</td><td>${measure_specification.coldWaterTankPresent ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).pipeworkPresent !== undefined && (measure_specification as any).pipeworkPresent !== null ? `<tr><td style="font-weight: 600;">Pipework Present</td><td>${(measure_specification as any).pipeworkPresent ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).coldWaterTankPipeworkNeedInsulation !== undefined && (measure_specification as any).coldWaterTankPipeworkNeedInsulation !== null ? `<tr><td style="font-weight: 600;">Cold Water Tank/Pipework Need Insulation</td><td>${(measure_specification as any).coldWaterTankPipeworkNeedInsulation ? 'Yes' : 'No'}</td></tr>` : ''}
        ${measure_specification.roofVentsPresent !== undefined && measure_specification.roofVentsPresent !== null ? `<tr><td style="font-weight: 600;">Roof Vents Present</td><td>${measure_specification.roofVentsPresent ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).verticalFlueInLoft !== undefined && (measure_specification as any).verticalFlueInLoft !== null ? `<tr><td style="font-weight: 600;">Vertical Flue in Loft</td><td>${(measure_specification as any).verticalFlueInLoft ? 'Yes' : 'No'}</td></tr>` : ''}
        ${(measure_specification as any).highCurrentCablesPresent !== undefined && (measure_specification as any).highCurrentCablesPresent !== null ? `<tr><td style="font-weight: 600;">High Current Cables Present</td><td>${(measure_specification as any).highCurrentCablesPresent ? 'Yes' : 'No'}</td></tr>` : ''}
      </tbody>
    </table>
  </div>`;
  
  return content;
}

/**
 * Generate RIR Insulation section as a separate page
 */
async function generateRIRInsulationSection(surveyData: RetrofitSurveyData): Promise<string> {
  const measure_specification = surveyData.measure_specification;
  
  // Check if RIR insulation is selected and has data
  if (!measure_specification?.suitableMeasures?.includes('RIR insulation') || !measure_specification.roomInRoofData) {
    return '';
  }
  
  console.log('📋 [PDF] Generating RIR Insulation section as separate page');
  const rirData = measure_specification.roomInRoofData;
  const hasDiagram = !!rirData.diagramImage;
  const hasMeasurements = rirData.measurements && rirData.measurements.length > 0;
  
  console.log('📋 [PDF] RIR Data:', {
    hasDiagram,
    diagramLength: rirData.diagramImage?.length,
    measurementsCount: rirData.measurements?.length,
    insulationType: rirData.insulationType,
    ventilationType: rirData.ventilationType,
    diagramType: rirData.diagramType,
    diagramImagePrefix: rirData.diagramImage?.substring(0, 50)
  });
  
  let content = `
  <div class="content-section">
    <div class="subsection-heading">Room in Roof (RIR) Insulation Assessment</div>
  `;
  
  // Display RIR diagram if available (optimized size for better space usage)
  if (hasDiagram && rirData.diagramImage) {
    console.log('✅ [PDF] Including RIR diagram in PDF');
    
    // Optimize diagram image for PDF rendering (convert to WebP and compress)
    let optimizedDiagramUrl = rirData.diagramImage;
    try {
      optimizedDiagramUrl = await optimizeImageForPDF(rirData.diagramImage, 1600, 1200, 0.95);
      console.log('📐 [PDF] RIR diagram optimized:', {
        originalSize: rirData.diagramImage.length,
        optimizedSize: optimizedDiagramUrl.length,
        reduction: `${Math.round((1 - optimizedDiagramUrl.length / rirData.diagramImage.length) * 100)}%`
      });
    } catch (error) {
      console.warn('⚠️ [PDF] Failed to optimize RIR diagram, using original:', error);
    }
    
    console.log('📄 [PDF] Adding RIR diagram to PDF HTML:', {
      urlLength: optimizedDiagramUrl.length,
      urlPreview: optimizedDiagramUrl.substring(0, 50),
      diagramType: rirData.diagramType
    });
    
    content += `
    <div style="margin: 8pt 0; text-align: center; page-break-inside: avoid;">
      <img src="${optimizedDiagramUrl}" style="max-width: 100%; max-height: 400pt; display: block; margin: 0 auto;" />
      <p style="font-size: 6pt; color: #64748b; margin-top: 2pt;">RIR Insulation Diagram - ${rirData.diagramType === 'with-dormer' ? 'With Dormer' : 'Standard'}</p>
    </div>`;
  } else {
    console.warn('⚠️ [PDF] No RIR diagram image available');
  }
  
  // Display measurements table if available
  if (hasMeasurements) {
    const visibleMeasurements = rirData.measurements.filter((m: any) => m.visible !== false);
    
    if (visibleMeasurements.length > 0) {
      content += `
    <div style="margin-top: 8pt; page-break-inside: avoid;">
      <h4 style="font-size: 8pt; font-weight: 600; color: #374151; margin-bottom: 4pt;">Area Details</h4>
      <table style="width: 100%; border-collapse: collapse; font-size: 7pt;">
        <thead>
          <tr style="background: #f8fafc;">
            <th style="padding: 3pt 4pt; text-align: left; font-weight: 600; border-bottom: 1.5pt solid #e5e7eb;">Area</th>
            <th style="padding: 3pt 4pt; text-align: right; font-weight: 600; border-bottom: 1.5pt solid #e5e7eb;">Length (m)</th>
            <th style="padding: 3pt 4pt; text-align: right; font-weight: 600; border-bottom: 1.5pt solid #e5e7eb;">Height (m)</th>
            <th style="padding: 3pt 4pt; text-align: left; font-weight: 600; border-bottom: 1.5pt solid #e5e7eb;">Insulation</th>
            <th style="padding: 3pt 4pt; text-align: left; font-weight: 600; border-bottom: 1.5pt solid #e5e7eb;">Type</th>
          </tr>
        </thead>
        <tbody>`;
      
      visibleMeasurements.forEach((m: any, index: number) => {
        const bgColor = index % 2 === 0 ? '#ffffff' : '#f8fafc';
        content += `
          <tr style="background: ${bgColor};">
            <td style="padding: 3pt 4pt; border-bottom: 0.5pt solid #e5e7eb;">${m.label}</td>
            <td style="padding: 3pt 4pt; text-align: right; border-bottom: 0.5pt solid #e5e7eb;">${m.length || '—'}</td>
            <td style="padding: 3pt 4pt; text-align: right; border-bottom: 0.5pt solid #e5e7eb;">${m.height || '—'}</td>
            <td style="padding: 3pt 4pt; border-bottom: 0.5pt solid #e5e7eb;">${m.insulation || '—'}</td>
            <td style="padding: 3pt 4pt; border-bottom: 0.5pt solid #e5e7eb;">${m.insulationType || '—'}</td>
          </tr>`;
      });
      
      content += `
        </tbody>
      </table>
    </div>`;
    }
  }
  
  content += `
  </div>`;
  
  return content;
}

/**
 * Generate continuation page for additional wall insulation elevations
 * Shows 2 elevations per page to avoid stretching
 */
function generateWallInsulationContinuationPage(
  elevationImages: ElevationImageData[],
  surveyData: RetrofitSurveyData
): string {
  let content = `
    <div class="content-section">
      <div class="section-heading">Wall Insulation Assessment (Continued)</div>
  `;
  
  // Add elevation cards (max 2 per page)
  elevationImages.forEach((elevationImg, index) => {
    content += generateWallInsulationElevationCard(elevationImg, surveyData, index > 0);
  });
  
  content += `</div>`;
  return content;
}

/**
 * Generate wall insulation elevation card for a single elevation
 * Compact card format with diagram + element table side-by-side
 */
function generateWallInsulationElevationCard(
  elevationImg: ElevationImageData,
  surveyData: RetrofitSurveyData,
  addTopMargin: boolean = false
): string {
  const { elevationType, elevationData, dataUrl, canvasWidth, canvasHeight } = elevationImg;
  const wallInsulationType = surveyData.measure_specification?.wallInsulationType || 'Unknown';
  const { doors, windows, airVents } = elevationData.elements;
  
  // Calculate sizing for diagram (compact size for two-column layout)
  const DIAGRAM_WIDTH_MM = 75; // Left column for diagram (smaller for compactness)
  const MM_TO_PX = 3.7795;
  
  const canvasWidthMm = canvasWidth / MM_TO_PX;
  const canvasHeightMm = canvasHeight / MM_TO_PX;
  const scale = DIAGRAM_WIDTH_MM / canvasWidthMm;
  const displayWidthMm = canvasWidthMm * scale;
  const displayHeightMm = canvasHeightMm * scale;
  
  // Build element rows combining all types
  const allElements = [
    ...doors.map(d => ({ ...d, type: 'door', color: '#d97706' })),
    ...windows.map(w => ({ ...w, type: 'window', color: '#1e40af' })),
  ].sort((a, b) => {
    // Sort by type then ID
    if (a.type !== b.type) return a.type === 'door' ? -1 : 1;
    return a.id.localeCompare(b.id);
  });
  
  return `
    <!-- Elevation Card -->
    <div style="${addTopMargin ? 'margin-top: 12pt;' : ''} background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); padding: 10pt; border-radius: 6pt; border: 1pt solid #e2e8f0;">
      
      <!-- Elevation Header with Inline Summary -->
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8pt;">
        <!-- Left: Name + Total Area -->
        <div style="display: flex; align-items: center; gap: 6pt;">
          <div style="font-size: 9pt; font-weight: 700; color: #1e293b; text-transform: capitalize;">${elevationType}</div>
          <div style="font-size: 8pt; font-weight: 600; color: #3b82f6;">${elevationData.totalArea.toFixed(2)}m²</div>
        </div>
        
        <!-- Center: Insulation + Treatable + Untreatable -->
        <div style="display: flex; align-items: center; gap: 6pt; font-size: 7pt;">
          <div style="display: flex; align-items: center; gap: 3pt;">
            <span style="color: #64748b;">Type:</span>
            <span style="font-weight: 600; color: #374151;">${capitalizeText(wallInsulationType)}</span>
          </div>
          <div style="width: 1pt; height: 10pt; background: #d1d5db;"></div>
          <div style="display: flex; align-items: center; gap: 3pt;">
            <span style="color: #64748b;">Treatable:</span>
            <span style="font-weight: 700; color: #22c55e;">${elevationData.treatableArea.toFixed(2)}m²</span>
          </div>
          <div style="width: 1pt; height: 10pt; background: #d1d5db;"></div>
          <div style="display: flex; align-items: center; gap: 3pt;">
            <span style="color: #64748b;">Untreatable:</span>
            <span style="font-weight: 700; color: #ef4444;">${elevationData.untreatableArea.toFixed(2)}m²</span>
          </div>
        </div>
        
        <!-- Right: Badges -->
        <div style="display: flex; gap: 4pt;">
          ${doors.length > 0 ? `<span style="padding: 3pt 6pt; background: #fed7aa; color: #92400e; border-radius: 3pt; font-size: 7pt; font-weight: 600;">${doors.length}D</span>` : ''}
          ${windows.length > 0 ? `<span style="padding: 3pt 6pt; background: #bfdbfe; color: #1e40af; border-radius: 3pt; font-size: 7pt; font-weight: 600;">${windows.length}W</span>` : ''}
        </div>
      </div>
      
      <!-- Two Column Layout: Diagram (left) + Element Table (right) -->
      <div style="display: grid; grid-template-columns: 82mm 98mm; gap: 8pt;">
        
        <!-- LEFT: Elevation Diagram -->
        <div style="background: white; padding: 8pt; border-radius: 6pt; border: 1pt solid #e5e7eb;">
          <img 
            src="${dataUrl}" 
            style="width: ${displayWidthMm}mm; height: auto; display: block; margin: 0 auto;" 
            alt="${elevationType} Elevation Diagram"
          />
        </div>
        
        <!-- RIGHT: Element Data Table -->
        <div style="background: white; border-radius: 6pt; border: 1pt solid #e5e7eb; overflow: hidden;">
          <table style="width: 100%; border-collapse: collapse;">
            <thead>
              <tr style="background: #f9fafb;">
                <th style="padding: 4pt 6pt; text-align: left; font-size: 7pt; color: #64748b; border-bottom: 1pt solid #e5e7eb;">ID</th>
                <th style="padding: 4pt 6pt; text-align: right; font-size: 7pt; color: #64748b; border-bottom: 1pt solid #e5e7eb;">H</th>
                <th style="padding: 4pt 6pt; text-align: right; font-size: 7pt; color: #64748b; border-bottom: 1pt solid #e5e7eb;">W</th>
                <th style="padding: 4pt 6pt; text-align: right; font-size: 7pt; color: #64748b; border-bottom: 1pt solid #e5e7eb;">Area</th>
              </tr>
            </thead>
            <tbody>
              ${allElements.map((element, idx) => `
                <tr style="${idx % 2 === 0 ? 'background: #fafafa;' : 'background: white;'}">
                  <td style="padding: 3pt 6pt; font-size: 7.5pt; font-weight: 600; color: ${element.color};">${element.id}</td>
                  <td style="padding: 3pt 6pt; text-align: right; font-size: 7.5pt; color: #374151;">${element.height.toFixed(2)}</td>
                  <td style="padding: 3pt 6pt; text-align: right; font-size: 7.5pt; color: #374151;">${element.width.toFixed(2)}</td>
                  <td style="padding: 3pt 6pt; text-align: right; font-size: 7.5pt; font-weight: 600; color: #374151;">${element.area.toFixed(2)}</td>
                </tr>
              `).join('')}
              ${allElements.length === 0 ? '<tr><td colspan="4" style="padding: 12pt; text-align: center; color: #94a3b8; font-size: 7pt;">No elements placed</td></tr>' : ''}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  `;
}

/**
 * Generate final notes section
 */
function generateFinalNotesSection(surveyData: RetrofitSurveyData): string {
  const notes = surveyData.initial_questionnaire?.additionalNotes;
  
  if (!notes || notes.trim().length === 0) {
    return '';
  }
  
  return `
    <div class="content-section">
      <div class="section-heading">Additional Notes</div>
      <div style="margin-top: 12pt; padding: 12pt; background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%); border-left: 4pt solid #f59e0b; border-radius: 8pt;">
        <div style="font-size: 8.5pt; color: #451a03; line-height: 1.6; white-space: pre-wrap;">${escapeHtml(notes)}</div>
      </div>
    </div>
  `;
}

/**
 * Helper function to escape HTML special characters
 */
function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Helper function to capitalize text properly (Title Case)
 */
function capitalizeText(text: string | number | boolean | null | undefined): string {
  if (text === null || text === undefined) return 'Not Specified';
  if (text === true) return 'Yes';
  if (text === false) return 'No';
  
  const str = String(text).trim();
  if (!str || str.toLowerCase() === 'not specified') return 'Not Specified';
  
  return str
    .replace(/[-_]/g, ' ') // Replace dashes and underscores with spaces
    .split(' ')
    .map(word => {
      if (word.length === 0) return '';
      // Keep pure numbers as-is (e.g., "123" in "123 Main Street")
      if (/^\d+$/.test(word)) {
        return word;
      }
      // Handle special cases for ordinals (1st, 2nd, 3rd, etc.)
      if (/^\d+(st|nd|rd|th)$/i.test(word)) {
        return word.charAt(0) + word.slice(1).toLowerCase();
      }
      // Handle hyphens within words
      if (word.includes('-')) {
        return word.split('-').map(part => 
          part.length > 0 ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase() : ''
        ).join('-');
      }
      // Standard title case
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim();
}

/**
 * Helper function to format building fabric text
 */
function formatBuildingFabricText(text: string): string {
  return capitalizeText(text);
}

/**
 * Map internal wall type codes to human-readable labels
 */
function mapWallTypeCodeToLabel(code?: string): string | undefined {
  if (!code) return undefined;
  const map: Record<string, string> = {
    SG: 'Stone (granite or whinstone)',
    SS: 'Stone (sandstone or limestone)',
    SO: 'Solid Brick',
    CO: 'Cob',
    CA: 'Cavity',
    TI: 'Timber Frame',
    SY: 'System Build',
    CW: 'Curtain Wall',
  };
  return map[code.toUpperCase()];
}

/**
 * Map RdSAP roof type codes to human terms
 */
function mapRoofTypeCodeToLabel(code?: string): string | undefined {
  if (!code) return undefined;
  const c = code.toUpperCase();
  switch (c) {
    case 'PA':
    case 'PN':
      return 'Pitched';
    case 'PS':
      return 'Sloping Ceiling';
    case 'PT':
      return 'Thatch';
    case 'F':
      return 'Flat';
    case 'S':
      return 'Same Dwelling Above';
    case 'A':
      return 'Another Dwelling Above';
    case 'NR':
      return 'Non-residential Above';
    default:
      return undefined;
  }
}

/**
 * Generate building fabric table HTML matching offline export
 */
function generateBuildingFabricTableHTML(surveyData: RetrofitSurveyData): string {
  // Extract building fabric data from property_front
  const propertyFront = surveyData.property_front || {};
  
  // Get wall type from initial questionnaire first (majority external wall type), 
  // then fall back to property_front if not available
  const wallTypeRaw = surveyData.initial_questionnaire?.majorityWallType || 
                       propertyFront.propertyWallConstructionType || 
                       undefined;
  const wallThicknessRaw = propertyFront.wallThickness;
  
  // Get roof type from property_front
  const roofTypeRaw = propertyFront.roofType;
  
  // Get floor type from property_front
  const floorTypeRaw = propertyFront.floorConstructionType || 'Not specified';
  
  // Format and clean up text
  const wallTypeLabelFromCode = mapWallTypeCodeToLabel(wallTypeRaw);
  const formattedWallType = wallTypeLabelFromCode
    ? wallTypeLabelFromCode
    : wallTypeRaw
      ? formatBuildingFabricText(wallTypeRaw)
      : 'Not Specified';

  const formattedWallCombined = formattedWallType + (wallThicknessRaw ? ` (${wallThicknessRaw}mm)` : '');

  const formattedRoofType = roofTypeRaw
    ? formatBuildingFabricText(roofTypeRaw)
    : 'Not Specified';

  const formattedFloorType = floorTypeRaw
    ? formatBuildingFabricText(floorTypeRaw)
    : 'Not Specified';

  return `
    <div class="building-fabric-container" style="margin: 8pt 0 12pt 0; position: relative;">
      <table class="building-fabric-table" style="
        width: 65%;
        margin: 0 auto;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: 6pt;
        overflow: hidden;
        border: 0.5pt solid rgba(0, 0, 0, 0.08);
        border-bottom: 1.5pt solid rgba(0, 0, 0, 0.12);
        border-right: 1pt solid rgba(0, 0, 0, 0.1);
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      ">
        <thead>
          <tr style="background: linear-gradient(135deg, #1E293B 0%, #334155 100%); border-radius: 6pt 6pt 0 0;">
            <th style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 8pt;
              font-weight: 500;
              border: none;
              width: 33.33%;
              border-top-left-radius: 6pt;
            ">.</th>
            <th style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 8pt;
              font-weight: 500;
              border: none;
              width: 33.33%;
            ">.</th>
            <th style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 8pt;
              font-weight: 500;
              border: none;
              width: 33.33%;
              border-top-right-radius: 6pt;
            ">.</th>
          </tr>
        </thead>
        <tbody>
          <tr style="background: #F8FAFC;">
            <td style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 7.5pt;
              border: 1pt solid #F1F5F9;
            ">.</td>
            <td style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 7.5pt;
              border: 1pt solid #F1F5F9;
            ">.</td>
            <td style="
              padding: 2pt 6pt;
              text-align: center;
              color: transparent;
              font-size: 7.5pt;
              border: 1pt solid #F1F5F9;
            ">.</td>
          </tr>
        </tbody>
      </table>
      
      <!-- Independently positioned text content -->
      <div style="position: absolute; top: -1pt; left: 17.5%; width: 21.67%; text-align: center; color: white; font-size: 8pt; font-weight: 500; display: table-cell; vertical-align: middle; height: 16pt;">Wall Type</div>
      <div style="position: absolute; top: -1pt; left: 39.17%; width: 21.67%; text-align: center; color: white; font-size: 8pt; font-weight: 500; display: table-cell; vertical-align: middle; height: 16pt;">Roof Type</div>
      <div style="position: absolute; top: -1pt; left: 60.83%; width: 21.67%; text-align: center; color: white; font-size: 8pt; font-weight: 500; display: table-cell; vertical-align: middle; height: 16pt;">Floor Type</div>
      
      <div style="position: absolute; top: 16pt; left: 17.5%; width: 21.67%; text-align: center; color: #1E293B; font-size: 7.5pt; display: table-cell; vertical-align: middle; height: 14pt;">${formattedWallCombined}</div>
      <div style="position: absolute; top: 16pt; left: 39.17%; width: 21.67%; text-align: center; color: #1E293B; font-size: 7.5pt; display: table-cell; vertical-align: middle; height: 14pt;">${formattedRoofType}</div>
      <div style="position: absolute; top: 16pt; left: 60.83%; width: 21.67%; text-align: center; color: #1E293B; font-size: 7.5pt; display: table-cell; vertical-align: middle; height: 14pt;">${formattedFloorType}</div>
    </div>
  `;
}

/**
 * Generate floor plan section with image and room details
 */
function generateFloorPlanSection(
  surveyData: RetrofitSurveyData,
  floorPlanImage: string,
  floorNumber: number,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  const floorName = floorNumber === 1 ? 'Ground Floor' : 
                     floorNumber === 2 ? 'First Floor' :
                     floorNumber === 3 ? 'Second Floor' :
                     `Floor ${floorNumber}`;
  
  // Get rooms for this floor
  const floorRooms = surveyData.room_details.rooms.filter(room => room.floor === floorNumber);
  
  // Element key - identical to offline floor plan export
  const elementKey = `
    <div class="element-key">
      <div class="ek-header">ELEMENT KEY</div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-symbol-line" style="border-top-color:#ff0000"></span>
            <span class="ek-desc"><strong>HLP</strong> - Heat Loss Perimeter</span>
          </div>
          <div class="ek-cell">
            <span class="ek-symbol-line" style="border-top-color:#0000ff"></span>
            <span class="ek-desc"><strong>Party Wall</strong> - Party Wall</span>
          </div>
          <div class="ek-cell">
            <span class="ek-symbol-line" style="border-top-color:#f97316"></span>
            <span class="ek-desc"><strong>Sheltered</strong> - Sheltered Wall</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">RADT</span>
            <span class="ek-desc">- Radiator with TRVs</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">RAD</span>
            <span class="ek-desc">- Radiator</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">H/S</span>
            <span class="ek-desc">- Heat Source</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">PH</span>
            <span class="ek-desc">- Panel Heat</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">FH</span>
            <span class="ek-desc">- Fan Heat</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#fde047;">ESH</span>
            <span class="ek-desc">- Storage Heat</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">GM</span>
            <span class="ek-desc">- Gas Meter</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">EM</span>
            <span class="ek-desc">- Electric Meter</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">CU</span>
            <span class="ek-desc">- Consumer Unit</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">TNK</span>
            <span class="ek-desc">- Tank</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">FAN</span>
            <span class="ek-desc">- Fan</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">V</span>
            <span class="ek-desc">- Vent</span>
          </div>
          <div class="ek-cell">
            <span class="ek-badge" style="background:#bbf7d0;">T-STAT</span>
            <span class="ek-desc">- Thermostat</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <span class="ek-badge" style="background:#e6f3ff;">LH</span>
            <span class="ek-desc">- Loft Hatch</span>
          </div>
          <div class="ek-cell">
            <span class="ek-light-led">×</span>
            <span class="ek-desc">- LED Light</span>
          </div>
          <div class="ek-cell">
            <span class="ek-light-standard"></span>
            <span class="ek-desc">- Standard Light</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc" style="color:#ef4444;">SA</strong>
            <span class="ek-desc">- Smoke Alarm</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <strong class="ek-desc">DR</strong>
            <span class="ek-desc">- Dining Room</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">ST</strong>
            <span class="ek-desc">- Study</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">BR</strong>
            <span class="ek-desc">- Bedroom</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">BTH</strong>
            <span class="ek-desc">- Bathroom</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <strong class="ek-desc">LR</strong>
            <span class="ek-desc">- Living Room</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">H</strong>
            <span class="ek-desc">- Hallway</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">L</strong>
            <span class="ek-desc">- Landing</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">K/D</strong>
            <span class="ek-desc">- Kitchen/Diner</span>
          </div>
        </div>
        
        <div class="ek-row">
          <div class="ek-cell">
            <strong class="ek-desc">K</strong>
            <span class="ek-desc">- Kitchen</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">W/C</strong>
            <span class="ek-desc">- Water Closet</span>
          </div>
          <div class="ek-cell">
            <strong class="ek-desc">CPD</strong>
            <span class="ek-desc">- Cupboard</span>
          </div>
          <div class="ek-cell"></div>
        </div>
      </div>
  `;

  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      
      <div class="content-section">
        <div class="section-heading">${floorName} Plan</div>
        
        ${generateBuildingFabricTableHTML(surveyData)}
        
        <img src="${floorPlanImage}" alt="${floorName}" class="floorplan-image" />
        
        ${elementKey}
      </div>
    </section>
  `;
}

/**
 * Generate Radiators & Windows Summary Section
 * Extracts data from floor plan elements and creates a comprehensive summary table
 */
function generateRadiatorsWindowsSummarySection(
  surveyData: RetrofitSurveyData,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  console.log('🔧 Generating radiators & windows summary section...');
  
  const floorPlanData = surveyData.floor_plan?.linkedFloorPlanData;
  
  if (!floorPlanData || !floorPlanData.elements || floorPlanData.elements.length === 0) {
    console.log('⚠️ No floor plan elements found for radiators & windows summary');
    return '';
  }

  // Extract elements
  const radiatorElements = floorPlanData.elements.filter((el: any) => el.type === 'radiator');
  const windowElements = floorPlanData.elements.filter((el: any) => el.type === 'window' || el.type === 'bayWindow');
  
  console.log(`📊 Found ${radiatorElements.length} radiators and ${windowElements.length} windows`);

  if (radiatorElements.length === 0 && windowElements.length === 0) {
    console.log('⚠️ No radiators or windows to summarize');
    return '';
  }

  // Get room labels from floor plan data
  const roomCustomLabels = floorPlanData.roomCustomLabels || {};
  
  // Get rooms data for spatial matching
  const rooms = floorPlanData.rooms || [];
  
  console.log('🏷️ Available room custom labels:', roomCustomLabels);
  console.log('🏠 Available rooms for spatial matching:', rooms.length);
  console.log('🔍 Sample radiator:', radiatorElements[0]);
  console.log('🔍 Sample window:', windowElements[0]);
  
  // Helper function to find which room contains a point
  const findRoomForElement = (element: any): { roomId: string; roomName: string } | null => {
    const position = element.position;
    if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
      console.warn('⚠️ Element missing position:', element);
      return null;
    }

    // Check each room to see if element is inside
    for (const room of rooms) {
      if (!room.walls || room.walls.length < 3) continue;
      
      // Simple point-in-polygon test using ray casting
      const walls = room.walls;
      let inside = false;
      
      for (let i = 0, j = walls.length - 1; i < walls.length; j = i++) {
        const xi = walls[i].start.x, yi = walls[i].start.y;
        const xj = walls[j].start.x, yj = walls[j].start.y;
        
        const intersect = ((yi > position.y) !== (yj > position.y))
          && (position.x < (xj - xi) * (position.y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
      }
      
      if (inside) {
        const roomId = room.stableId || room.id;
        const roomName = roomCustomLabels[roomId] || roomId;
        return { roomId, roomName };
      }
    }
    
    return null;
  };
  
  // Get room details from survey data
  const surveyRooms = surveyData.room_details?.rooms || [];
  const roomDetailsMap: Map<string, any> = new Map();
  surveyRooms.forEach((room: any) => {
    if (room.id) {
      roomDetailsMap.set(room.id, room);
    }
  });
  
  // Group elements by floor and room
  const roomDataMap: Map<string, {
    floor: number;
    roomId: string;
    roomName: string;
    radiators: any[];
    windows: any[];
    doorPresent?: boolean | null;
    doorUndercutSize?: string;
    hasSecondaryHeating?: boolean | null;
    ventilationType?: string;
  }> = new Map();

  // Process radiators
  radiatorElements.forEach((radiator: any) => {
    const floor = radiator.floor || 1;
    
    // Try to find room from element's roomId field first, then use spatial matching
    let roomId = radiator.roomId || radiator.stableId || radiator.room;
    let roomName = roomId ? (roomCustomLabels[roomId] || roomId) : null;
    
    // If no roomId or room not found, use spatial matching
    if (!roomName || roomName === roomId) {
      const roomMatch = findRoomForElement(radiator);
      if (roomMatch) {
        roomId = roomMatch.roomId;
        roomName = roomMatch.roomName;
        console.log(`🔥 Spatially matched radiator to room: ${roomName}`);
      } else {
        roomId = 'unknown';
        roomName = 'Unknown Room';
        console.warn('⚠️ Could not match radiator to any room');
      }
    }
    
    const key = `${floor}-${roomId}`;
    
    if (!roomDataMap.has(key)) {
      const roomDetails = roomDetailsMap.get(roomId);
      roomDataMap.set(key, { 
        floor, 
        roomId, 
        roomName, 
        radiators: [], 
        windows: [],
        doorPresent: roomDetails?.doorPresent ?? roomDetails?.doorUndercutsPresent,
        doorUndercutSize: roomDetails?.doorUndercutSize || '',
        hasSecondaryHeating: roomDetails?.hasSecondaryHeating,
        ventilationType: roomDetails?.ventilationType
      });
    }
    
    roomDataMap.get(key)!.radiators.push({
      width: radiator.width ? Math.round(radiator.width).toString() : '—',
      height: radiator.height ? Math.round(radiator.height).toString() : '—',
      type: radiator.radiatorType || 'Not specified'
    });
  });

  // Process windows
  windowElements.forEach((window: any) => {
    const floor = window.floor || 1;
    
    // Try to find room from element's roomId field first, then use spatial matching
    let roomId = window.roomId || window.stableId || window.room;
    let roomName = roomId ? (roomCustomLabels[roomId] || roomId) : null;
    
    // If no roomId or room not found, use spatial matching
    if (!roomName || roomName === roomId) {
      const roomMatch = findRoomForElement(window);
      if (roomMatch) {
        roomId = roomMatch.roomId;
        roomName = roomMatch.roomName;
        console.log(`🪟 Spatially matched window to room: ${roomName}`);
      } else {
        roomId = 'unknown';
        roomName = 'Unknown Room';
        console.warn('⚠️ Could not match window to any room');
      }
    }
    
    const key = `${floor}-${roomId}`;
    
    if (!roomDataMap.has(key)) {
      const roomDetails = roomDetailsMap.get(roomId);
      roomDataMap.set(key, { 
        floor, 
        roomId, 
        roomName, 
        radiators: [], 
        windows: [],
        doorPresent: roomDetails?.doorPresent ?? roomDetails?.doorUndercutsPresent,
        doorUndercutSize: roomDetails?.doorUndercutSize || '',
        hasSecondaryHeating: roomDetails?.hasSecondaryHeating,
        ventilationType: roomDetails?.ventilationType
      });
    }
    
    roomDataMap.get(key)!.windows.push({
      width: window.width ? (window.width / 1000).toFixed(2) : '—',
      height: window.height ? (window.height / 1000).toFixed(2) : '—',
      orientation: window.orientation || 'Not specified',
      trickleVents: typeof window.trickleVents === 'number' ? window.trickleVents : (window.trickleVents === true || window.trickleVents === 'true' || window.trickleVents === 'Yes' ? 1 : 0),
      glazingType: window.glazingType || 'Not specified',
      glazingGap: window.glazingGap || 'Not specified',
      frameMaterial: window.windowType || window.frameMaterial || 'Not specified',
      condition: window.condition || 'Not specified',
      draughtProofed: window.isDraughtProofed === true || window.isDraughtProofed === 'true' || window.isDraughtProofed === 'Yes',
      windowCategory: window.windowCategory || 'Not specified'
    });
  });

  // Generate table rows
  let tableRows = '';
  let totalRadiators = 0;
  let totalWindows = 0;
  
  // Sort by floor and room name
  const sortedEntries = Array.from(roomDataMap.entries()).sort((a, b) => {
    const [, dataA] = a;
    const [, dataB] = b;
    if (dataA.floor !== dataB.floor) return dataA.floor - dataB.floor;
    return dataA.roomName.localeCompare(dataB.roomName);
  });

  sortedEntries.forEach(([key, data]) => {
    const { floor, roomName, radiators, windows, doorPresent, doorUndercutSize, hasSecondaryHeating, ventilationType } = data;
    
    if (radiators.length === 0 && windows.length === 0) return;
    
    totalRadiators += radiators.length;
    totalWindows += windows.length;

    const floorName = floor === 1 ? 'Ground Floor' : `${floor - 1}${getOrdinalSuffix(floor - 1)} Floor`;

    // Format radiator info
    let radiatorInfo = '<span class="empty-cell">—</span>';
    if (radiators.length > 0) {
      if (radiators.length === 1) {
        radiatorInfo = `<div class="single-item">${radiators[0].width}×${radiators[0].height}<br><span class="item-type">${radiators[0].type}</span></div>`;
      } else {
        radiatorInfo = `<div class="multi-item-header">${radiators.length} radiators</div>` +
          radiators.map((r: any, index: number) => 
            `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${r.width}×${r.height}</span><span class="item-desc">${r.type}</span></div>`
          ).join('');
      }
    }

    // Format window info
    let windowDimensions = '<span class="empty-cell">—</span>';
    let windowGlazingType = '<span class="empty-cell">—</span>';
    let windowGlazingGap = '<span class="empty-cell">—</span>';
    let windowFrameMaterial = '<span class="empty-cell">—</span>';
    let windowCondition = '<span class="empty-cell">—</span>';
    let windowDraughtProofed = '<span class="empty-cell">—</span>';
    let windowOrientation = '<span class="empty-cell">—</span>';

    if (windows.length > 0) {
      if (windows.length === 1) {
        windowDimensions = `<div class="single-item">${windows[0].width}×${windows[0].height}</div>`;
        windowGlazingType = `<div class="single-item">${abbreviateGlazingType(windows[0].glazingType)}</div>`;
        windowGlazingGap = `<div class="single-item">${abbreviateGap(windows[0].glazingGap)}</div>`;
        windowFrameMaterial = `<div class="single-item">${formatFrameMaterial(windows[0].frameMaterial)}</div>`;
        windowCondition = `<div class="single-item">${windows[0].condition ? windows[0].condition.charAt(0).toUpperCase() + windows[0].condition.slice(1) : '—'}</div>`;
        windowDraughtProofed = `<div class="single-item">${windows[0].draughtProofed ? 'Yes' : 'No'}</div>`;
        windowOrientation = `<div class="single-item">${formatOrientation(windows[0].orientation)}</div>`;
      } else {
        const header = `<div class="multi-item-header">${windows.length} windows</div>`;
        windowDimensions = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${w.width}×${w.height}</span></div>`
        ).join('');
        windowGlazingType = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${abbreviateGlazingType(w.glazingType)}</span></div>`
        ).join('');
        windowGlazingGap = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${abbreviateGap(w.glazingGap)}</span></div>`
        ).join('');
        windowFrameMaterial = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${formatFrameMaterial(w.frameMaterial)}</span></div>`
        ).join('');
        windowCondition = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${w.condition ? w.condition.charAt(0).toUpperCase() + w.condition.slice(1) : '—'}</span></div>`
        ).join('');
        windowDraughtProofed = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${w.draughtProofed ? 'Yes' : 'No'}</span></div>`
        ).join('');
        windowOrientation = header + windows.map((w: any, index: number) => 
          `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${formatOrientation(w.orientation)}</span></div>`
        ).join('');
      }
    }

    // Format additional room info
    let doorInfo = '<span class="empty-cell">—</span>';
    if (doorPresent === true) {
      doorInfo = doorUndercutSize ? `Yes (${doorUndercutSize})` : 'Yes';
    } else if (doorPresent === false) {
      doorInfo = 'No';
    } else if (doorPresent === null) {
      doorInfo = 'N/A';
    }
    
    const secondaryHeatingInfo = hasSecondaryHeating !== undefined && hasSecondaryHeating !== null 
      ? (hasSecondaryHeating ? 'Yes' : 'No') 
      : '<span class="empty-cell">—</span>';
    const ventilationInfo = ventilationType && ventilationType !== 'none' 
      ? ventilationType 
      : '<span class="empty-cell">—</span>';
    
    tableRows += `
      <tr>
        <td class="floor-location">${floorName}</td>
        <td class="room-name">${roomName}</td>
        <td class="item-cell">${radiatorInfo}</td>
        <td class="item-cell">${windowDimensions}</td>
        <td class="item-cell">${windowGlazingType}</td>
        <td class="item-cell">${windowGlazingGap}</td>
        <td class="item-cell">${windowFrameMaterial}</td>
        <td class="item-cell">${windowCondition}</td>
        <td class="item-cell">${windowDraughtProofed}</td>
        <td class="item-cell">${windowOrientation}</td>
      </tr>
    `;
  });

  if (!tableRows) {
    console.log('⚠️ No valid room data for radiators & windows summary');
    return '';
  }

  console.log(`✅ Generated summary with ${totalRadiators} radiators and ${totalWindows} windows`);

  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      
      <div class="content-section">
        <h2 class="section-heading">RADIATORS & WINDOWS SUMMARY</h2>
        
        <div class="clean-table-container">
          <table class="items-summary-table">
            <thead>
              <tr>
                <th class="col-floor">FLOOR</th>
                <th class="col-room">ROOM</th>
                <th class="col-radiator">RADIATORS<br/><span style="font-size: 6pt; font-weight: 500;">(W×H mm)</span></th>
                <th class="col-window">WINDOW SIZE<br/><span style="font-size: 6pt; font-weight: 500;">(W×L m)</span></th>
                <th class="col-glazing">GLAZING<br/><span style="font-size: 6pt; font-weight: 500;">TYPE</span></th>
                <th class="col-gap">GAP<br/><span style="font-size: 6pt; font-weight: 500;">(mm)</span></th>
                <th class="col-frame">FRAME</th>
                <th class="col-condition">COND.</th>
                <th class="col-draught">DRAUGHT<br/><span style="font-size: 6pt; font-weight: 500;">PROOF</span></th>
                <th class="col-orient">ORIENT.</th>
              </tr>
            </thead>
            <tbody>
              ${tableRows}
            </tbody>
            <tfoot>
              <tr class="totals-row">
                <td colspan="2">Summary</td>
                <td>${totalRadiators} total</td>
                <td colspan="7">${totalWindows} windows across all rooms</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
      ${generateFooter(options)}
    </section>
  `;
}

/**
 * Helper: Get ordinal suffix for floor numbers
 */
function getOrdinalSuffix(num: number): string {
  const j = num % 10;
  const k = num % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
}

/**
 * Helper: Abbreviate glazing type for table readability
 */
function abbreviateGlazingType(glazingType: string): string {
  if (!glazingType || glazingType === 'Not specified') return '—';
  
  const abbreviations: { [key: string]: string } = {
    'Single glazing': 'Single',
    'Double pre 2002': 'Double <2002',
    'Double between 2002 and 2021': 'Double 2002-21',
    'Double post or during 2022': 'Double ≥2022',
    'Double with unknown install date': 'Double (Unknown)',
    'Secondary glazing': 'Secondary',
    'Triple pre 2002': 'Triple <2002',
    'Triple between 2002 and 2021': 'Triple 2002-21',
    'Triple post or during 2022': 'Triple ≥2022',
    'Triple with unknown install date': 'Triple (Unknown)',
    'Single glazing, known data': 'Single (Known)',
    'Double glazing, known data': 'Double (Known)',
    'Triple glazing, known data': 'Triple (Known)',
    'Double with known install date': 'Double (Known)',
    'Single glazed': 'Single',
    'Triple glazed': 'Triple',
    'Not specified': '—'
  };
  
  return abbreviations[glazingType] || glazingType.substring(0, 12) + (glazingType.length > 12 ? '...' : '');
}

/**
 * Helper: Abbreviate gap values for table readability
 */
function abbreviateGap(gap: string): string {
  if (!gap || gap === 'Not specified') return '—';
  
  const abbreviations: { [key: string]: string } = {
    '16 mm or more': '≥16mm',
    '12 mm to 15 mm': '12-15mm',
    '6 mm to 11 mm': '6-11mm',
    'Less than 6 mm': '<6mm',
    'Not specified': '—'
  };
  
  return abbreviations[gap] || gap.replace(' mm', 'mm').replace(' or more', '+');
}

/**
 * Helper: Format frame material for proper capitalization
 */
function formatFrameMaterial(frameMaterial: string): string {
  if (!frameMaterial || frameMaterial === 'Not specified') return 'Not specified';
  
  switch (frameMaterial.toLowerCase()) {
    case 'pvc':
      return 'PVC';
    case 'wood':
      return 'Wood';
    case 'metal':
      return 'Metal';
    case 'composite':
      return 'Composite';
    default:
      return frameMaterial.charAt(0).toUpperCase() + frameMaterial.slice(1).toLowerCase();
  }
}

/**
 * Helper: Format orientation for proper capitalization
 */
function formatOrientation(orientation: string): string {
  if (!orientation || orientation === 'Not specified' || orientation === 'Unknown') return 'Not specified';
  
  switch (orientation.toLowerCase()) {
    case 'n':
    case 'north':
      return 'North';
    case 's':
    case 'south':
      return 'South';
    case 'e':
    case 'east':
      return 'East';
    case 'w':
    case 'west':
      return 'West';
    case 'ne':
    case 'northeast':
    case 'north-east':
      return 'Northeast';
    case 'nw':
    case 'northwest':
    case 'north-west':
      return 'Northwest';
    case 'se':
    case 'southeast':
    case 'south-east':
      return 'Southeast';
    case 'sw':
    case 'southwest':
    case 'south-west':
      return 'Southwest';
    default:
      return orientation.charAt(0).toUpperCase() + orientation.slice(1).toLowerCase();
  }
}

/**
 * Check if property front data exists
 */
function hasPropertyFrontData(propertyFront: any): boolean {
  if (!propertyFront) return false;
  
  // Check if any meaningful data exists
  const hasBasicData = propertyFront.buildForm || 
    propertyFront.externalWallType || 
    propertyFront.wallConstruction ||
    propertyFront.roofType ||
    propertyFront.additionalNotes;
  
  const hasPhotos = (propertyFront.detachmentType && propertyFront.detachmentType.length > 0) ||
    (propertyFront.frontElevationImage && propertyFront.frontElevationImage.length > 0) ||
    (propertyFront.roofTypeEvidenceImages && propertyFront.roofTypeEvidenceImages.length > 0) ||
    (propertyFront.gutteringDrainageImages && propertyFront.gutteringDrainageImages.length > 0) ||
    (propertyFront.wallTypeEvidenceImage && propertyFront.wallTypeEvidenceImage.length > 0) ||
    (propertyFront.wallThicknessImages && propertyFront.wallThicknessImages.length > 0) ||
    (propertyFront.wallThicknessFromDoor && propertyFront.wallThicknessFromDoor.length > 0) ||
    (propertyFront.wallThicknessFromWindow && propertyFront.wallThicknessFromWindow.length > 0);
  
  return !!(hasBasicData || hasPhotos);
}

/**
 * Calculate how many pages Property Front section needs
 */
async function calculatePropertyFrontPages(surveyData: RetrofitSurveyData): Promise<number> {
  if (!surveyData.property_front || !hasPropertyFrontData(surveyData.property_front)) {
    return 0;
  }

  const data = surveyData.property_front;
  
  // Helper to count photos
  const getPhotoCount = (photoArray: any[] | any): number => {
    if (!photoArray) return 0;
    if (Array.isArray(photoArray)) return photoArray.length;
    if (typeof photoArray === 'object') return 1;
    return 0;
  };

  const totalPhotos = 
    getPhotoCount(data.frontElevationImage) +
    getPhotoCount(data.roofTypeEvidenceImages) +
    getPhotoCount(data.gutteringDrainageImages) +
    getPhotoCount(data.wallTypeEvidenceImage) +
    getPhotoCount(data.wallThicknessFromDoor || data.wallThicknessImages) +
    getPhotoCount(data.wallThicknessFromWindow);

  // Conservative estimate: Each photo section gets its own page
  // Count how many sections have photos
  let photoSections = 0;
  if (getPhotoCount(data.detachmentType) > 0) photoSections++;
  if (getPhotoCount(data.frontElevationImage) > 0) photoSections++;
  if (getPhotoCount(data.roofTypeEvidenceImages) > 0) photoSections++;
  if (getPhotoCount(data.gutteringDrainageImages) > 0) photoSections++;
  if (getPhotoCount(data.wallTypeEvidenceImage) > 0) photoSections++;
  if (getPhotoCount(data.wallThicknessFromDoor || data.wallThicknessImages) > 0) photoSections++;
  if (getPhotoCount(data.wallThicknessFromWindow) > 0) photoSections++;
  
  // 1 intro page + 1 page per photo section
  return photoSections > 0 ? 1 + photoSections : 1;
}

/**
 * Generate Property Front Summary Page
 * Consolidates all property front data in a compact, easy-to-read format
 */
function generatePropertyFrontSummary(
  surveyData: RetrofitSurveyData,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  const data = surveyData.property_front;
  if (!data || !hasPropertyFrontData(data)) {
    return '';
  }

  const summaryItems = [
    { label: 'Build Form', value: data.buildForm },
    // Add flat-specific fields if property is a flat
    ...(surveyData.initial_questionnaire?.dwellingType === 'flat' ? [
      { label: 'Floor Number', value: surveyData.initial_questionnaire?.flatFloorNumber },
      { label: 'Corridor Heating', value: surveyData.initial_questionnaire?.corridorHeating ? capitalizeText(surveyData.initial_questionnaire.corridorHeating) : null },
    ] : []),
    { label: 'Wall Construction', value: data.propertyWallConstructionType || data.wallConstruction },
    { label: 'Wall Thickness', value: data.wallThickness ? `${data.wallThickness}mm` : null },
    { label: 'External Wall Type', value: data.externalWallType ? (mapWallTypeCodeToLabel(data.externalWallType) || data.externalWallType) : null },
    { label: 'External Wall Condition', value: data.externalWallCondition },
    { label: 'Wall Defects', value: data.hasWallDefects !== null && data.hasWallDefects !== undefined ? (data.hasWallDefects ? 'Yes' : 'No') : null },
    { label: 'Wall Insulated', value: data.isWallInsulated !== null && data.isWallInsulated !== undefined ? (data.isWallInsulated ? 'Yes' : 'No') : null },
    { label: 'Roof Type', value: data.roofType },
    { label: 'Roof Condition', value: data.roofCondition },
    { label: 'External Roof Defects', value: (data as any).hasExternalRoofDefects !== null && (data as any).hasExternalRoofDefects !== undefined ? ((data as any).hasExternalRoofDefects ? 'Yes' : 'No') : null },
    { label: 'Floor Construction', value: data.floorConstructionType },
    { label: 'Adequate Sub-floor Ventilation', value: data.adequateSubFloorVentilation !== null && data.adequateSubFloorVentilation !== undefined ? (data.adequateSubFloorVentilation ? 'Yes' : 'No') : null },
    { label: 'Evidence of Floor Dampness', value: data.evidenceOfFloorDampness !== null && data.evidenceOfFloorDampness !== undefined ? (data.evidenceOfFloorDampness ? 'Yes' : 'No') : null },
    { label: 'DPC Present', value: data.dpcPresent !== null && data.dpcPresent !== undefined ? (data.dpcPresent ? 'Yes' : 'No') : null },
    { label: 'DPC Visible Above Ground', value: data.dpcVisible !== null && data.dpcVisible !== undefined ? (data.dpcVisible ? 'Yes' : 'No') : null },
    { label: 'Guttering Condition', value: data.gutteringCondition },
    { label: 'Drainage Type', value: (data as any).drainageType },
    { label: 'Building Orientation', value: data.buildingOrientation ? `${data.buildingOrientation}°` : null },
  ].filter(item => item.value); // Remove empty values

  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      <div class="content-section">
        <div class="section-heading">Property Front - Summary</div>
        <div class="summary-grid">
          ${summaryItems.map(item => `
            <div class="summary-card">
              <div class="summary-card-label">${item.label}</div>
              <div class="summary-card-value">${capitalizeText(item.value)}</div>
            </div>
          `).join('')}
        </div>
        ${data.additionalNotes ? `
          <div class="summary-section-title">Additional Notes</div>
          <div style="padding: 8pt 10pt; background: #f8fafc; border-radius: 6pt; font-size: 8.5pt; color: #475569;">
            ${escapeHtml(data.additionalNotes)}
          </div>
        ` : ''}
      </div>
      ${generateFooter(options)}
    </section>
  `;
}

/**
 * Generate Property Elevation Summary Page (for rear, left, right)
 * Consolidates all property elevation data in a compact format
 */
function generatePropertyElevationSummary(
  surveyData: RetrofitSurveyData,
  elevationType: 'rear' | 'left' | 'right',
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  const data = elevationType === 'rear' ? surveyData.property_rear :
                elevationType === 'left' ? surveyData.property_left :
                surveyData.property_right;
  
  if (!data) return '';

  const elevationTitle = elevationType.charAt(0).toUpperCase() + elevationType.slice(1);
  
  const summaryItems = [
    { label: 'External Wall Type', value: data.externalWallType ? (mapWallTypeCodeToLabel(data.externalWallType) || data.externalWallType) : null },
    { label: 'External Wall Condition', value: data.externalWallCondition },
    { label: 'Wall Defects', value: data.hasWallDefects !== null ? (data.hasWallDefects ? 'Yes' : 'No') : null },
    { label: 'Wall Insulated', value: data.isWallInsulated !== null ? (data.isWallInsulated ? 'Yes' : 'No') : null },
    { label: 'DPC Present', value: (data as any).dpcPresent !== null && (data as any).dpcPresent !== undefined ? ((data as any).dpcPresent ? 'Yes' : 'No') : null },
    { label: 'DPC Visible Above Ground', value: (data as any).dpcVisible !== null && (data as any).dpcVisible !== undefined ? ((data as any).dpcVisible ? 'Yes' : 'No') : null },
  ];

  // Add rear-specific fields
  if (elevationType === 'rear') {
    const hasBackyard = (data as any).hasBackyard;
    summaryItems.push(
      { label: 'Has Backyard', value: hasBackyard !== null && hasBackyard !== undefined ? (hasBackyard ? 'Yes' : 'No') : null }
    );
  }

  const filteredItems = summaryItems.filter(item => item.value);

  if (filteredItems.length === 0) return '';

  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      <div class="content-section">
        <div class="section-heading">Property ${elevationTitle} - Summary</div>
        <div class="summary-grid">
          ${filteredItems.map(item => `
            <div class="summary-card">
              <div class="summary-card-label">${item.label}</div>
              <div class="summary-card-value">${capitalizeText(item.value)}</div>
            </div>
          `).join('')}
        </div>
        ${data.additionalNotes ? `
          <div class="summary-section-title">Additional Notes</div>
          <div style="padding: 8pt 10pt; background: #f8fafc; border-radius: 6pt; font-size: 8.5pt; color: #475569;">
            ${escapeHtml(data.additionalNotes)}
          </div>
        ` : ''}
      </div>
      ${generateFooter(options)}
    </section>
  `;
}

/**
 * Generate Property Front Pages (multi-page support)
 * Returns array of page HTML strings
 */
async function generatePropertyFrontPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): Promise<string[]> {
  const data = surveyData.property_front;
  if (!data || !hasPropertyFrontData(data)) {
    return [];
  }

  console.log('🏠 Generating Property Front pages...');

  const pages: string[] = [];

  // Helper to get photo URLs
  const getPhotoUrls = (photoArray: any[] | any): string[] => {
    if (!photoArray) return [];
    if (Array.isArray(photoArray)) {
      return photoArray.map(p => p.url || p.dataUrl || '').filter(Boolean);
    }
    if (typeof photoArray === 'object') {
      return [photoArray.url || photoArray.dataUrl || ''].filter(Boolean);
    }
    return [];
  };

  // Optimize photos for PDF
  const detachmentPhotos = await Promise.all(
    getPhotoUrls(data.detachmentType).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  
  // For flats: get corridor/stairway images from initial questionnaire
  const corridorPhotos = surveyData.initial_questionnaire?.dwellingType === 'flat' 
    ? await Promise.all(
        getPhotoUrls(surveyData.initial_questionnaire?.photos?.corridorStairwayImages?.photos).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
      )
    : [];
  
  // Orientation photos are captured in Property Front form, not Initial Questionnaire
  const orientationPhotos = await Promise.all(
    getPhotoUrls(data.orientationImage).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const frontElevationPhotos = await Promise.all(
    getPhotoUrls(data.frontElevationImage).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const roofTypePhotos = await Promise.all(
    getPhotoUrls(data.roofTypeEvidenceImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const externalRoofDefectsPhotos = await Promise.all(
    getPhotoUrls(data.externalRoofDefectsImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const gutteringPhotos = await Promise.all(
    getPhotoUrls(data.gutteringDrainageImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const wallTypePhotos = await Promise.all(
    getPhotoUrls(data.wallTypeEvidenceImage).slice(0, 2).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const wallThicknessFromDoorPhotos = await Promise.all(
    getPhotoUrls(data.wallThicknessFromDoor || data.wallThicknessImages).slice(0, 2).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const wallThicknessFromWindowPhotos = await Promise.all(
    getPhotoUrls(data.wallThicknessFromWindow).slice(0, 2).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const wallDefectPhotos = await Promise.all(
    getPhotoUrls(data.wallDefectImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const floorConstructionPhotos = await Promise.all(
    getPhotoUrls(data.floorConstructionEvidenceImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const dpcEvidencePhotos = await Promise.all(
    getPhotoUrls(data.dpcEvidenceImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );

  // Helper to format values - use capitalizeText for proper capitalization
  const formatValue = (value: any): string => {
    return capitalizeText(value);
  };

  // Helper specifically for external wall types to map codes to labels
  const formatExternalWallType = (value: any): string => {
    return mapWallTypeCodeToLabel(value) || capitalizeText(value);
  };

  // Build questions with photos
  const sections = [];

  // Get orientation from property_front or initial_questionnaire
  const buildingOrientation = (data as any).buildingOrientation || surveyData.initial_questionnaire?.buildingOrientation;

  // Build Form, Detachment & Orientation - with pagination
  if (detachmentPhotos.length > 0 || corridorPhotos.length > 0 || orientationPhotos.length > 0 || data.buildForm || data.exposedSide || buildingOrientation) {
    // Combine all photos with their labels
    const allPhotos: Array<{ url: string; label: string; type: string }> = [];
    
    detachmentPhotos.forEach((url, idx) => {
      allPhotos.push({
        url,
        label: 'Detachment Type',
        type: 'detachment'
      });
    });
    
    corridorPhotos.forEach((url, idx) => {
      allPhotos.push({
        url,
        label: 'Corridor/Stairway',
        type: 'corridor'
      });
    });
    
    orientationPhotos.forEach((url, idx) => {
      allPhotos.push({
        url,
        label: 'Building Orientation',
        type: 'orientation'
      });
    });

    // Split photos into chunks of 6 (3 columns x 2 rows)
    const photoChunks = [];
    for (let i = 0; i < allPhotos.length; i += 6) {
      photoChunks.push(allPhotos.slice(i, i + 6));
    }

    // If we have photos, create pages for them
    if (photoChunks.length > 0) {
      photoChunks.forEach((chunk, chunkIndex) => {
        const isFirstChunk = chunkIndex === 0;
        const totalChunks = photoChunks.length;
        
        sections.push(`
          <div class="property-section">
            <div class="property-section-header">
              <div class="property-icon-container">
                <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                  <polyline points="9 22 9 12 15 12 15 22"></polyline>
                </svg>
              </div>
              <h3><span class="section-parent">Property Front:</span> Building Form, Detachment & Orientation${totalChunks > 1 ? ` (${chunkIndex + 1}/${totalChunks})` : ''}</h3>
            </div>
            ${isFirstChunk && (data.buildForm || data.exposedSide || buildingOrientation) ? `
              <div class="property-qa-grid">
                ${data.buildForm ? `
                  <div class="property-qa-item">
                    <div class="property-question">Build Form</div>
                    <div class="property-answer">${formatValue(data.buildForm)}</div>
                  </div>
                ` : ''}
                ${data.exposedSide ? `
                  <div class="property-qa-item">
                    <div class="property-question">Exposed Side</div>
                    <div class="property-answer">${formatValue(data.exposedSide)}</div>
                  </div>
                ` : ''}
                ${buildingOrientation ? `
                  <div class="property-qa-item">
                    <div class="property-question">Building Orientation</div>
                    <div class="property-answer">${buildingOrientation}°</div>
                  </div>
                ` : ''}
              </div>
            ` : ''}
            <div class="property-photo-grid">
              ${chunk.map((photo, idx) => {
                const globalIdx = chunkIndex * 6 + idx;
                return `
                  <div class="property-photo-wrapper">
                    <div class="property-photo-item">
                      <img src="${photo.url}" alt="${photo.label} ${globalIdx + 1}" />
                    </div>
                    <div class="property-photo-label">${photo.label}${allPhotos.length > 1 ? ` (${globalIdx + 1}/${allPhotos.length})` : ''}</div>
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `);
      });
    } else if (data.buildForm || data.exposedSide || buildingOrientation) {
      // No photos, just show the metadata
      sections.push(`
        <div class="property-section">
          <div class="property-section-header">
            <div class="property-icon-container">
              <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <h3><span class="section-parent">Property Front:</span> Building Form, Detachment & Orientation</h3>
          </div>
          <div class="property-qa-grid">
            ${data.buildForm ? `
              <div class="property-qa-item">
                <div class="property-question">Build Form</div>
                <div class="property-answer">${formatValue(data.buildForm)}</div>
              </div>
            ` : ''}
            ${data.exposedSide ? `
              <div class="property-qa-item">
                <div class="property-question">Exposed Side</div>
                <div class="property-answer">${formatValue(data.exposedSide)}</div>
              </div>
            ` : ''}
            ${buildingOrientation ? `
              <div class="property-qa-item">
                <div class="property-question">Building Orientation</div>
                <div class="property-answer">${buildingOrientation}°</div>
              </div>
            ` : ''}
          </div>
        </div>
      `);
    }
  }

  // Front Elevation with Photos
  if (frontElevationPhotos.length > 0 || data.externalWallCondition || data.hasWallDefects !== undefined) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: var(--theme-color);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Front Elevation</h3>
        </div>
        ${data.externalWallCondition || data.hasWallDefects !== undefined ? `
          <div class="property-qa-grid">
            ${data.externalWallCondition ? `
              <div class="property-qa-item">
                <div class="property-question">Wall Condition</div>
                <div class="property-answer">${formatValue(data.externalWallCondition)}</div>
              </div>
            ` : ''}
            ${data.hasWallDefects !== undefined ? `
              <div class="property-qa-item">
                <div class="property-question">Wall Defects Present</div>
                <div class="property-answer">${formatValue(data.hasWallDefects)}</div>
              </div>
            ` : ''}
          </div>
        ` : ''}
        ${frontElevationPhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${frontElevationPhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Front Elevation ${idx + 1}" />
                </div>
                <div class="property-photo-label">Front Elevation ${frontElevationPhotos.length > 1 ? `(${idx + 1}/${frontElevationPhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  }

  // Wall Construction with Photos
  if (wallTypePhotos.length > 0 || data.externalWallType || data.isWallInsulated !== undefined) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <line x1="12" y1="4" x2="12" y2="20"></line>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Wall Construction</h3>
        </div>
        <div class="property-qa-grid">
          ${data.externalWallType ? `
            <div class="property-qa-item">
              <div class="property-question">External Wall Type</div>
              <div class="property-answer">${formatExternalWallType(data.externalWallType)}</div>
            </div>
          ` : ''}
          ${data.wallThickness ? `
            <div class="property-qa-item">
              <div class="property-question">Wall Thickness</div>
              <div class="property-answer">${formatValue(data.wallThickness)}</div>
            </div>
          ` : ''}
          ${data.isWallInsulated !== undefined && data.isWallInsulated !== null ? `
            <div class="property-qa-item">
              <div class="property-question">Wall Insulated</div>
              <div class="property-answer">${formatValue(data.isWallInsulated)}</div>
            </div>
          ` : ''}
        </div>
        ${(() => {
          // Combine all wall construction photos into a single array with labels
          const allWallPhotos = [
            ...wallTypePhotos.map((url, idx) => ({ 
              url, 
              label: `Wall Construction Evidence ${wallTypePhotos.length > 1 ? `(${idx + 1}/${wallTypePhotos.length})` : ''}` 
            })),
            ...wallThicknessFromDoorPhotos.map((url, idx) => ({ 
              url, 
              label: `Wall Thickness from Door ${wallThicknessFromDoorPhotos.length > 1 ? `(${idx + 1}/${wallThicknessFromDoorPhotos.length})` : ''}` 
            })),
            ...wallThicknessFromWindowPhotos.map((url, idx) => ({ 
              url, 
              label: `Wall Thickness from Window ${wallThicknessFromWindowPhotos.length > 1 ? `(${idx + 1}/${wallThicknessFromWindowPhotos.length})` : ''}` 
            }))
          ];
          
          if (allWallPhotos.length === 0) return '';
          
          return `
            <div class="property-photo-grid">
              ${allWallPhotos.map((photo, idx) => `
                <div class="property-photo-wrapper">
                  <div class="property-photo-item">
                    <img src="${photo.url}" alt="Wall Construction ${idx + 1}" />
                  </div>
                  <div class="property-photo-label">${photo.label}</div>
                </div>
              `).join('')}
            </div>
          `;
        })()}
      </div>
    `);
  }

  // Roof Type with Photos
  if (roofTypePhotos.length > 0 || data.roofType || data.roofCondition) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Roof Construction</h3>
        </div>
        ${roofTypePhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${roofTypePhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Roof Type ${idx + 1}" />
                </div>
                <div class="property-photo-label">Roof Type Evidence ${roofTypePhotos.length > 1 ? `(${idx + 1}/${roofTypePhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
        ${data.roofType || data.roofCondition ? `
          <div class="property-qa-grid">
            ${data.roofType ? `
              <div class="property-qa-item">
                <div class="property-question">Roof Type</div>
                <div class="property-answer">${formatValue(data.roofType)}</div>
              </div>
            ` : ''}
            ${data.roofCondition ? `
              <div class="property-qa-item">
                <div class="property-question">Roof Condition</div>
                <div class="property-answer">${capitalizeText(data.roofCondition)}</div>
              </div>
            ` : ''}
            ${data.hasExternalRoofDefects !== null && data.hasExternalRoofDefects !== undefined ? `
              <div class="property-qa-item">
                <div class="property-question">External Roof Defects</div>
                <div class="property-answer">${data.hasExternalRoofDefects ? 'Yes' : 'No'}</div>
              </div>
            ` : ''}
          </div>
        ` : ''}
      </div>
    `);
  }

  // External Roof Defects Photos
  if (externalRoofDefectsPhotos.length > 0) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> External Roof Defects</h3>
        </div>
        <div class="property-photo-grid">
          ${externalRoofDefectsPhotos.map((photoUrl, idx) => `
            <div class="property-photo-wrapper">
              <div class="property-photo-item">
                <img src="${photoUrl}" alt="Roof Defect ${idx + 1}" />
              </div>
              <div class="property-photo-label">Roof Defect ${externalRoofDefectsPhotos.length > 1 ? `(${idx + 1}/${externalRoofDefectsPhotos.length})` : ''}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `);
  }

  // Guttering & Drainage with Photos
  if (gutteringPhotos.length > 0 || data.gutteringCondition) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #047857 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Guttering & Drainage</h3>
        </div>
        ${data.gutteringCondition ? `
          <div class="property-qa-grid">
            <div class="property-qa-item">
              <div class="property-question">Guttering & Drainage Condition</div>
              <div class="property-answer">${capitalizeText(data.gutteringCondition)}</div>
            </div>
          </div>
        ` : ''}
        ${gutteringPhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${gutteringPhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Guttering ${idx + 1}" />
                </div>
                <div class="property-photo-label">Guttering & Drainage ${gutteringPhotos.length > 1 ? `(${idx + 1}/${gutteringPhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  }

  // Wall Defects with Photos
  if (wallDefectPhotos.length > 0 || data.hasWallDefects) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Wall Defects</h3>
        </div>
        ${data.hasWallDefects !== undefined && data.hasWallDefects !== null ? `
          <div class="property-qa-grid">
            <div class="property-qa-item">
              <div class="property-question">Additional Visible Wall Defects</div>
              <div class="property-answer">${data.hasWallDefects ? 'Yes' : 'No'}</div>
            </div>
          </div>
        ` : ''}
        ${wallDefectPhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${wallDefectPhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Wall Defect ${idx + 1}" />
                </div>
                <div class="property-photo-label">Wall Defect Evidence ${wallDefectPhotos.length > 1 ? `(${idx + 1}/${wallDefectPhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  }

  // Floor Construction
  if (floorConstructionPhotos.length > 0 || data.floorConstructionType || data.adequateSubFloorVentilation !== undefined || data.evidenceOfFloorDampness !== undefined) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="14" width="20" height="6" rx="1"></rect>
              <path d="M2 14V6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"></path>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Floor Construction</h3>
        </div>
        <div class="property-qa-grid">
          ${data.floorConstructionType ? `
            <div class="property-qa-item">
              <div class="property-question">Floor Construction Type</div>
              <div class="property-answer">${formatValue(data.floorConstructionType)}</div>
            </div>
          ` : ''}
          ${data.adequateSubFloorVentilation !== undefined && data.adequateSubFloorVentilation !== null ? `
            <div class="property-qa-item">
              <div class="property-question">Adequate Sub-Floor Ventilation</div>
              <div class="property-answer">${formatValue(data.adequateSubFloorVentilation)}</div>
            </div>
          ` : ''}
          ${data.evidenceOfFloorDampness !== undefined && data.evidenceOfFloorDampness !== null ? `
            <div class="property-qa-item">
              <div class="property-question">Evidence of Floor Dampness</div>
              <div class="property-answer">${formatValue(data.evidenceOfFloorDampness)}</div>
            </div>
          ` : ''}
        </div>
        ${floorConstructionPhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${floorConstructionPhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Floor Construction ${idx + 1}" />
                </div>
                <div class="property-photo-label">Floor Construction Evidence ${floorConstructionPhotos.length > 1 ? `(${idx + 1}/${floorConstructionPhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  }

  // DPC (Damp Proof Course)
  if (dpcEvidencePhotos.length > 0 || data.dpcPresent !== undefined || data.dpcVisible !== undefined) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Damp Proof Course (DPC)</h3>
        </div>
        <div class="property-qa-grid">
          ${data.dpcPresent !== undefined && data.dpcPresent !== null ? `
            <div class="property-qa-item">
              <div class="property-question">Is there DPC Present?</div>
              <div class="property-answer">${data.dpcPresent ? 'Yes' : 'No'}</div>
            </div>
          ` : ''}
          ${data.dpcVisible !== undefined && data.dpcVisible !== null ? `
            <div class="property-qa-item">
              <div class="property-question">Is the DPC visible above ground level?</div>
              <div class="property-answer">${data.dpcVisible ? 'Yes' : 'No'}</div>
            </div>
          ` : ''}
        </div>
        ${dpcEvidencePhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${dpcEvidencePhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="DPC Evidence ${idx + 1}" />
                </div>
                <div class="property-photo-label">DPC Evidence ${dpcEvidencePhotos.length > 1 ? `(${idx + 1}/${dpcEvidencePhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  }

  // Additional Notes
  if (data.additionalNotes) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #64748b 0%, #475569 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </div>
          <h3><span class="section-parent">Property Front:</span> Additional Notes</h3>
        </div>
        <div class="property-notes-content">
          ${data.additionalNotes}
        </div>
      </div>
    `);
  }

  // Split content into pages with intelligent spacing
  const sectionsPerPage: string[][] = [];
  let currentPageSections: string[] = [];
  let currentPagePhotoCount = 0;
  let firstPage = true;

  // Helper to estimate section size
  const estimateSectionSize = (section: string): { photos: number; hasQA: boolean; hasNotes: boolean } => {
    const photos = (section.match(/property-photo-wrapper/g) || []).length;
    const hasQA = section.includes('property-qa-grid');
    const hasNotes = section.includes('property-notes-content');
    return { photos, hasQA, hasNotes };
  };

  // Intelligent page breaking based on content density - CONSERVATIVE APPROACH
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const sectionSize = estimateSectionSize(section);
    
    // Very conservative overcrowding detection
    const wouldOvercrowd = 
      // If current page already has photos and this section has photos, break
      (currentPagePhotoCount > 0 && sectionSize.photos > 0 && !firstPage) ||
      // Max 3 photos on first page (one section with 3 photos in grid)
      (currentPagePhotoCount + sectionSize.photos > 3 && firstPage) ||
      // If current page has 2+ sections and this section has photos, break
      (currentPageSections.length >= 2 && sectionSize.photos > 0);

    if (wouldOvercrowd && currentPageSections.length > 1) {
      // Start new page
      sectionsPerPage.push([...currentPageSections]);
      currentPageSections = [];
      currentPagePhotoCount = 0;
      firstPage = false;
    }

    // Add section to current page
    currentPageSections.push(section);
    currentPagePhotoCount += sectionSize.photos;

    // Force page break after ANY section with photos (one photo section per page max)
    if (sectionSize.photos > 0 && i < sections.length - 1) {
      sectionsPerPage.push([...currentPageSections]);
      currentPageSections = [];
      currentPagePhotoCount = 0;
      firstPage = false;
    }
  }

  // Add remaining sections to last page
  if (currentPageSections.length > 0) {
    sectionsPerPage.push(currentPageSections);
  }

  // Generate page HTML
  sectionsPerPage.forEach((pageSections, pageIndex) => {
    const pageHTML = `
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + pageIndex, totalPages, options)}
        <div class="content-section">
          ${pageSections.join('')}
        </div>
        ${generateFooter(options)}
      </section>
    `;
    pages.push(pageHTML);
  });

  console.log(`✅ Generated ${pages.length} Property Front page(s)`);
  return pages;
}

/**
 * Generate Cellar Pages (multi-page support with 6 photos per page)
 * Returns array of page HTML strings
 */
async function generateCellarPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): Promise<string[]> {
  const cellarData = surveyData.main_property_measurements;
  if (!cellarData || cellarData.hasCellar !== true) {
    return [];
  }

  const cellarLength = (cellarData as any).cellarLength || 0;
  const cellarWidth = (cellarData as any).cellarWidth || 0;
  const cellarArea = (cellarData as any).cellarArea || '0.00';
  const cellarPhotos = (cellarData as any).cellarPhotos || [];

  console.log('🏠 Generating Cellar pages...', {
    length: cellarLength,
    width: cellarWidth,
    area: cellarArea,
    photoCount: cellarPhotos.length
  });

  const pages: string[] = [];

  // Helper to get photo URLs
  const getPhotoUrls = (photoArray: any[]): string[] => {
    if (!photoArray || !Array.isArray(photoArray)) return [];
    return photoArray.map(p => p.fileName || p.url || p.dataUrl || '').filter(Boolean);
  };

  // Optimize photos for PDF
  const optimizedPhotos = await Promise.all(
    getPhotoUrls(cellarPhotos).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );

  // Build main details section
  const detailsSection = `
    <div class="property-section">
      <div class="property-section-header">
        <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
          <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
          </svg>
        </div>
        <h3>Cellar Measurements</h3>
      </div>
      <div class="property-qa-grid">
        <div class="property-qa-item">
          <div class="property-question">Length</div>
          <div class="property-answer">${cellarLength} m</div>
        </div>
        <div class="property-qa-item">
          <div class="property-question">Width</div>
          <div class="property-answer">${cellarWidth} m</div>
        </div>
        <div class="property-qa-item">
          <div class="property-question">Area</div>
          <div class="property-answer">${cellarArea} m²</div>
        </div>
      </div>
    </div>
  `;

  // Build photo sections (6 photos per page)
  const photoSections: string[] = [];
  if (optimizedPhotos.length > 0) {
    // Split photos into chunks of 6
    const photoChunks = [];
    for (let i = 0; i < optimizedPhotos.length; i += 6) {
      photoChunks.push(optimizedPhotos.slice(i, i + 6));
    }

    // Create a section for each chunk
    photoChunks.forEach((chunk, chunkIndex) => {
      const isFirstChunk = chunkIndex === 0;
      const totalChunks = photoChunks.length;
      
      photoSections.push(`
        <div class="property-section">
          <div class="property-section-header">
            <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
              <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
            </div>
            <h3>Cellar Images${totalChunks > 1 ? ` (${chunkIndex + 1}/${totalChunks})` : ''}</h3>
          </div>
          <div class="property-photo-grid">
            ${chunk.map((photoUrl, idx) => {
              const globalIdx = chunkIndex * 6 + idx;
              const photo = cellarPhotos[globalIdx];
              return `
                <div class="property-photo-wrapper">
                  <div class="property-photo-item">
                    <img src="${photoUrl}" alt="Cellar photo ${globalIdx + 1}" />
                  </div>
                  <div class="property-photo-label">
                    ${photo?.note || `Cellar Image ${globalIdx + 1}`}
                    ${optimizedPhotos.length > 1 ? ` (${globalIdx + 1}/${optimizedPhotos.length})` : ''}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `);
    });
  }

  // Combine sections and paginate
  const allSections = [detailsSection, ...photoSections];
  
  // Split sections into pages - details on first page, then 1 photo section per page
  const sectionsPerPage: string[][] = [];
  let currentPageSections: string[] = [];
  let isFirstPage = true;

  allSections.forEach((section, index) => {
    const isDetailsSection = index === 0;
    const isPhotoSection = index > 0;

    if (isFirstPage) {
      // First page: details + first photo section (if exists)
      if (isDetailsSection) {
        currentPageSections.push(section);
      } else if (isPhotoSection && currentPageSections.length === 1) {
        // Add first photo section to first page with details
        currentPageSections.push(section);
        sectionsPerPage.push([...currentPageSections]);
        currentPageSections = [];
        isFirstPage = false;
      }
    } else {
      // Subsequent pages: 1 photo section per page
      if (isPhotoSection) {
        sectionsPerPage.push([section]);
      }
    }
  });

  // Handle case where we only have details, no photos
  if (currentPageSections.length > 0) {
    sectionsPerPage.push(currentPageSections);
  }

  // Generate page HTML
  sectionsPerPage.forEach((pageSections, pageIndex) => {
    const pageHTML = `
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + pageIndex, totalPages, options)}
        <div class="content-section">
          ${pageSections.join('')}
        </div>
        ${generateFooter(options)}
      </section>
    `;
    pages.push(pageHTML);
  });

  console.log(`✅ Generated ${pages.length} Cellar page(s)`);
  return pages;
}

/**
 * Calculate how many pages Cellar section needs
 */
async function calculateCellarPages(surveyData: RetrofitSurveyData): Promise<number> {
  const cellarData = surveyData.main_property_measurements;
  if (!cellarData || cellarData.hasCellar !== true) {
    return 0;
  }

  const cellarPhotos = (cellarData as any).cellarPhotos || [];
  
  if (cellarPhotos.length === 0) {
    // Just details, no photos - 1 page
    return 1;
  }

  // First page: details + first 6 photos
  // Additional pages: 6 photos per page
  const remainingPhotos = Math.max(0, cellarPhotos.length - 6);
  const additionalPages = Math.ceil(remainingPhotos / 6);
  
  return 1 + additionalPages;
}

/**
 * Check if cellar data exists
 */
function hasCellarData(surveyData: RetrofitSurveyData): boolean {
  const cellarData = surveyData.main_property_measurements;
  if (!cellarData || cellarData.hasCellar !== true) {
    return false;
  }
  
  const hasBasicData = (cellarData as any).cellarLength || (cellarData as any).cellarWidth;
  const hasPhotos = (cellarData as any).cellarPhotos && (cellarData as any).cellarPhotos.length > 0;
  
  return !!(hasBasicData || hasPhotos);
}

/**
 * Check if property rear data exists
 */
function hasPropertyRearData(propertyRear: any): boolean {
  if (!propertyRear) return false;
  
  const hasBasicData = propertyRear.externalWallType || 
    propertyRear.wallConstruction ||
    propertyRear.additionalNotes;
  
  const hasPhotos = (propertyRear.rearElevationImage && propertyRear.rearElevationImage.length > 0) ||
    (propertyRear.wallDefectImages && propertyRear.wallDefectImages.length > 0) ||
    (propertyRear.roofTypeEvidenceImages && propertyRear.roofTypeEvidenceImages.length > 0) ||
    (propertyRear.rearBackyardImages && propertyRear.rearBackyardImages.length > 0) ||
    (propertyRear.gutteringDrainageImages && propertyRear.gutteringDrainageImages.length > 0) ||
    (propertyRear.wallTypeEvidenceImage && propertyRear.wallTypeEvidenceImage.length > 0);
  
  return !!(hasBasicData || hasPhotos);
}

/**
 * Check if property side data exists (left or right)
 */
function hasPropertySideData(propertySide: any): boolean {
  if (!propertySide) return false;
  
  const hasBasicData = propertySide.externalWallType || 
    propertySide.wallConstruction ||
    propertySide.exposureLevel ||
    propertySide.additionalNotes;
  
  const hasPhotos = (propertySide.leftElevationImage && propertySide.leftElevationImage.length > 0) ||
    (propertySide.rightElevationImage && propertySide.rightElevationImage.length > 0) ||
    (propertySide.wallDefectImages && propertySide.wallDefectImages.length > 0) ||
    (propertySide.roofTypeEvidenceImages && propertySide.roofTypeEvidenceImages.length > 0) ||
    (propertySide.leftSideImages && propertySide.leftSideImages.length > 0) ||
    (propertySide.rightSideImages && propertySide.rightSideImages.length > 0) ||
    (propertySide.gutteringDrainageImages && propertySide.gutteringDrainageImages.length > 0) ||
    (propertySide.wallTypeEvidenceImage && propertySide.wallTypeEvidenceImage.length > 0);
  
  return !!(hasBasicData || hasPhotos);
}

/**
 * Calculate pages for property elevation sections
 */
async function calculatePropertyElevationPages(propertyData: any): Promise<number> {
  if (!propertyData) return 0;
  
  const getPhotoCount = (photoArray: any[] | any): number => {
    if (!photoArray) return 0;
    if (Array.isArray(photoArray)) return photoArray.length;
    if (typeof photoArray === 'object') return 1;
    return 0;
  };

  let photoSections = 0;
  const photoFields = [
    'rearElevationImage', 'leftElevationImage', 'rightElevationImage',
    'wallDefectImages', 'roofTypeEvidenceImages', 
    'rearBackyardImages', 'leftSideImages', 'rightSideImages',
    'gutteringDrainageImages', 'wallTypeEvidenceImage'
  ];
  
  photoFields.forEach(field => {
    if (getPhotoCount(propertyData[field]) > 0) photoSections++;
  });
  
  return photoSections > 0 ? 1 + photoSections : 1;
}

/**
 * Generate Property Elevation Pages (Rear, Left, Right)
 * Returns array of page HTML strings
 */
async function generatePropertyElevationPages(
  surveyData: RetrofitSurveyData,
  elevationType: 'rear' | 'left' | 'right',
  startPage: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): Promise<string[]> {
  const dataKey = `property_${elevationType}` as keyof RetrofitSurveyData;
  const data = surveyData[dataKey] as any;
  
  if (!data) return [];
  
  // Check if data exists based on elevation type
  const hasData = elevationType === 'rear' ? hasPropertyRearData(data) : hasPropertySideData(data);
  if (!hasData) return [];

  console.log(`🏠 Generating Property ${elevationType.charAt(0).toUpperCase() + elevationType.slice(1)} pages...`);

  const pages: string[] = [];
  
  const getPhotoUrls = (photoArray: any[] | any): string[] => {
    if (!photoArray) return [];
    if (Array.isArray(photoArray)) {
      return photoArray.map(p => p.url || p.dataUrl || '').filter(Boolean);
    }
    if (typeof photoArray === 'object') {
      return [photoArray.url || photoArray.dataUrl || ''].filter(Boolean);
    }
    return [];
  };

  // Optimize photos for PDF
  const elevationPhotos = await Promise.all(
    getPhotoUrls(data[`${elevationType}ElevationImage`]).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const wallDefectPhotos = await Promise.all(
    getPhotoUrls(data.wallDefectImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const roofTypePhotos = await Promise.all(
    getPhotoUrls(data.roofTypeEvidenceImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const gutteringPhotos = await Promise.all(
    getPhotoUrls(data.gutteringDrainageImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  const wallTypePhotos = await Promise.all(
    getPhotoUrls(data.wallTypeEvidenceImage).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
  );
  
  // Special photos for rear (backyard) or sides
  let specialPhotos: string[] = [];
  if (elevationType === 'rear') {
    // Only load backyard images if hasBackyard is true
    if (data.hasBackyard === true) {
      specialPhotos = await Promise.all(
        getPhotoUrls(data.rearBackyardImages).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
      );
    }
  } else {
    specialPhotos = await Promise.all(
      getPhotoUrls(data[`${elevationType}SideImages`]).map(url => optimizeImageForPDF(url, 1600, 1200, 0.90))
    );
  }

  // Helper to format values - use capitalizeText for proper capitalization
  const formatValue = (value: any): string => {
    return capitalizeText(value);
  };

  // Helper specifically for external wall types to map codes to labels
  const formatExternalWallType = (value: any): string => {
    return mapWallTypeCodeToLabel(value) || capitalizeText(value);
  };

  const sections = [];
  const elevationTitle = elevationType.charAt(0).toUpperCase() + elevationType.slice(1);

  // Elevation Images
  if (elevationPhotos.length > 0 || data.externalWallCondition || data.hasWallDefects !== undefined || data.exposureLevel) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: var(--theme-color);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
          </div>
          <h3><span class="section-parent">Property ${elevationTitle}:</span> Elevation</h3>
        </div>
        ${data.externalWallCondition || data.hasWallDefects !== undefined || data.exposureLevel ? `
          <div class="property-qa-grid">
            ${data.externalWallCondition ? `
              <div class="property-qa-item">
                <div class="property-question">Wall Condition</div>
                <div class="property-answer">${formatValue(data.externalWallCondition)}</div>
              </div>
            ` : ''}
            ${data.hasWallDefects !== undefined ? `
              <div class="property-qa-item">
                <div class="property-question">Wall Defects Present</div>
                <div class="property-answer">${formatValue(data.hasWallDefects)}</div>
              </div>
            ` : ''}
            ${data.exposureLevel ? `
              <div class="property-qa-item">
                <div class="property-question">Exposure Level</div>
                <div class="property-answer">${formatValue(data.exposureLevel)}</div>
              </div>
            ` : ''}
          </div>
        ` : ''}
        ${elevationPhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${elevationPhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="${elevationTitle} Elevation ${idx + 1}" />
                </div>
                <div class="property-photo-label">${elevationTitle} Elevation ${elevationPhotos.length > 1 ? `(${idx + 1}/${elevationPhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  }

  // Wall Defect Images
  if (wallDefectPhotos.length > 0) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </div>
          <h3>Wall Defects</h3>
        </div>
        <div class="property-photo-grid">
          ${wallDefectPhotos.map((photoUrl, idx) => `
            <div class="property-photo-wrapper">
              <div class="property-photo-item">
                <img src="${photoUrl}" alt="Wall Defect ${idx + 1}" />
              </div>
              <div class="property-photo-label">Wall Defect ${wallDefectPhotos.length > 1 ? `(${idx + 1}/${wallDefectPhotos.length})` : ''}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `);
  }

  // Backyard Status and Photos (for rear elevation only)
  if (elevationType === 'rear' && data.hasBackyard !== undefined && data.hasBackyard !== null) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #047857 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            </svg>
          </div>
          <h3>Backyard</h3>
        </div>
        <div class="property-qa-grid">
          <div class="property-qa-item">
            <div class="property-question">Has Backyard</div>
            <div class="property-answer">${data.hasBackyard ? 'Yes' : 'No'}</div>
          </div>
        </div>
        ${specialPhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${specialPhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Backyard ${idx + 1}" />
                </div>
                <div class="property-photo-label">Backyard ${specialPhotos.length > 1 ? `(${idx + 1}/${specialPhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
      </div>
    `);
  } else if (elevationType !== 'rear' && specialPhotos.length > 0) {
    // Side views for left/right elevations
    const specialLabel = `${elevationTitle} Side View`;
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #047857 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            </svg>
          </div>
          <h3>${specialLabel}</h3>
        </div>
        <div class="property-photo-grid">
          ${specialPhotos.map((photoUrl, idx) => `
            <div class="property-photo-wrapper">
              <div class="property-photo-item">
                <img src="${photoUrl}" alt="${specialLabel} ${idx + 1}" />
              </div>
              <div class="property-photo-label">${specialLabel} ${specialPhotos.length > 1 ? `(${idx + 1}/${specialPhotos.length})` : ''}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `);
  }

  // Roof Type Photos
  if (roofTypePhotos.length > 0) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
            </svg>
          </div>
          <h3>Roof Construction</h3>
        </div>
        <div class="property-photo-grid">
          ${roofTypePhotos.map((photoUrl, idx) => `
            <div class="property-photo-wrapper">
              <div class="property-photo-item">
                <img src="${photoUrl}" alt="Roof Type ${idx + 1}" />
              </div>
              <div class="property-photo-label">Roof Type Evidence ${roofTypePhotos.length > 1 ? `(${idx + 1}/${roofTypePhotos.length})` : ''}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `);
  }

  // Guttering Photos
  if (gutteringPhotos.length > 0) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #047857 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"></path>
            </svg>
          </div>
          <h3>Guttering & Drainage</h3>
        </div>
        <div class="property-photo-grid">
          ${gutteringPhotos.map((photoUrl, idx) => `
            <div class="property-photo-wrapper">
              <div class="property-photo-item">
                <img src="${photoUrl}" alt="Guttering ${idx + 1}" />
              </div>
              <div class="property-photo-label">Guttering & Drainage ${gutteringPhotos.length > 1 ? `(${idx + 1}/${gutteringPhotos.length})` : ''}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `);
  }

  // Wall Construction Photos
  if (wallTypePhotos.length > 0 || data.externalWallType || data.isWallInsulated !== undefined) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="4" width="20" height="16" rx="2"></rect>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <line x1="12" y1="4" x2="12" y2="20"></line>
            </svg>
          </div>
          <h3><span class="section-parent">Property ${elevationTitle}:</span> Wall Construction</h3>
        </div>
        ${wallTypePhotos.length > 0 ? `
          <div class="property-photo-grid">
            ${wallTypePhotos.map((photoUrl, idx) => `
              <div class="property-photo-wrapper">
                <div class="property-photo-item">
                  <img src="${photoUrl}" alt="Wall Construction ${idx + 1}" />
                </div>
                <div class="property-photo-label">Wall Construction Evidence ${wallTypePhotos.length > 1 ? `(${idx + 1}/${wallTypePhotos.length})` : ''}</div>
              </div>
            `).join('')}
          </div>
        ` : ''}
        <div class="property-qa-grid">
          ${data.externalWallType ? `
            <div class="property-qa-item">
              <div class="property-question">External Wall Type</div>
              <div class="property-answer">${formatExternalWallType(data.externalWallType)}</div>
            </div>
          ` : ''}
          ${data.isWallInsulated !== undefined && data.isWallInsulated !== null ? `
            <div class="property-qa-item">
              <div class="property-question">Wall Insulated</div>
              <div class="property-answer">${formatValue(data.isWallInsulated)}</div>
            </div>
          ` : ''}
        </div>
      </div>
    `);
  }

  // Additional Notes
  if (data.additionalNotes) {
    sections.push(`
      <div class="property-section">
        <div class="property-section-header">
          <div class="property-icon-container" style="background: linear-gradient(135deg, #64748b 0%, #475569 100%);">
            <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
            </svg>
          </div>
          <h3><span class="section-parent">Property ${elevationTitle}:</span> Additional Notes</h3>
        </div>
        <div class="property-notes-content">
          ${data.additionalNotes}
        </div>
      </div>
    `);
  }

  // Split content into pages
  const sectionsPerPage: string[][] = [];
  let currentPageSections: string[] = [];
  let currentPagePhotoCount = 0;
  let firstPage = true;

  const estimateSectionSize = (section: string): { photos: number; hasQA: boolean; hasNotes: boolean } => {
    const photos = (section.match(/property-photo-wrapper/g) || []).length;
    const hasQA = section.includes('property-qa-grid');
    const hasNotes = section.includes('property-notes-content');
    return { photos, hasQA, hasNotes };
  };

  // Intelligent page breaking
  for (let i = 0; i < sections.length; i++) {
    const section = sections[i];
    const sectionSize = estimateSectionSize(section);
    
    const wouldOvercrowd = 
      (currentPagePhotoCount > 0 && sectionSize.photos > 0 && !firstPage) ||
      (currentPagePhotoCount + sectionSize.photos > 3 && firstPage) ||
      (currentPageSections.length >= 2 && sectionSize.photos > 0);

    if (wouldOvercrowd && currentPageSections.length > 1) {
      sectionsPerPage.push([...currentPageSections]);
      currentPageSections = [];
      currentPagePhotoCount = 0;
      firstPage = false;
    }

    currentPageSections.push(section);
    currentPagePhotoCount += sectionSize.photos;

    if (sectionSize.photos > 0 && i < sections.length - 1) {
      sectionsPerPage.push([...currentPageSections]);
      currentPageSections = [];
      currentPagePhotoCount = 0;
      firstPage = false;
    }
  }

  if (currentPageSections.length > 0) {
    sectionsPerPage.push(currentPageSections);
  }

  // Generate page HTML
  sectionsPerPage.forEach((pageSections, pageIndex) => {
    const pageHTML = `
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + pageIndex, totalPages, options)}
        <div class="content-section">
          ${pageSections.join('')}
        </div>
        ${generateFooter(options)}
      </section>
    `;
    pages.push(pageHTML);
  });

  console.log(`✅ Generated ${pages.length} Property ${elevationTitle} page(s)`);
  return pages;
}

/**
 * Check if meters and services data exists
 */
function hasMetersServicesData(metersData: any): boolean {
  console.log('🔍 hasMetersServicesData: checking data', {
    exists: !!metersData,
    hasGasMeter: metersData?.hasGasMeter,
    hasElectricMeter: metersData?.hasElectricMeter,
    hasConsumerBoard: metersData?.hasConsumerBoard,
    hasStopTap: metersData?.hasStopTap,
    hasPhotosObject: !!metersData?.photos,
    photoKeys: metersData?.photos ? Object.keys(metersData.photos) : []
  });
  
  if (!metersData) return false;
  
  // Check if any meaningful data exists
  const hasFields = metersData.hasGasMeter !== undefined ||
                    metersData.hasElectricMeter !== undefined ||
                    metersData.hasConsumerBoard !== undefined ||
                    metersData.hasStopTap !== undefined;
  
  const gasPhotos = (metersData.photos?.gasMeterEarthBonding?.photos?.length || 0);
  const electricPhotos = (metersData.photos?.electricMeter?.photos?.length || 0);
  const consumerPhotos = (metersData.photos?.consumerBoard?.photos?.length || 0);
  const stopTapPhotos = (metersData.photos?.stopTap?.photos?.length || 0);
  
  console.log('🔍 hasMetersServicesData: photo counts', {
    gasPhotos,
    electricPhotos,
    consumerPhotos,
    stopTapPhotos
  });
  
  const hasPhotos = metersData.photos && (
    gasPhotos > 0 ||
    electricPhotos > 0 ||
    consumerPhotos > 0 ||
    stopTapPhotos > 0
  );
  
  console.log('🔍 hasMetersServicesData: result', {
    hasFields,
    hasPhotos,
    returning: hasFields || hasPhotos
  });
  
  return hasFields || hasPhotos;
}

/**
 * Calculate how many pages Meters & Services section needs
 */
async function calculateMetersServicesPages(surveyData: RetrofitSurveyData): Promise<number> {
  if (!surveyData.initial_questionnaire || !hasMetersServicesData(surveyData.initial_questionnaire)) {
    return 0;
  }
  
  const data = surveyData.initial_questionnaire;
  let pageCount = 0;
  
  // Count photos
  const photoCount = 
    (data.photos?.gasMeterEarthBonding?.photos?.length || 0) +
    (data.photos?.electricMeter?.photos?.length || 0) +
    (data.photos?.consumerBoard?.photos?.length || 0) +
    (data.photos?.stopTap?.photos?.length || 0);
  
  // Only count photo pages (6 per page, matching Property Front) - summary is counted separately in totalPages
  if (photoCount > 0) {
    pageCount = Math.ceil(photoCount / 6);
  }
  
  return pageCount;
}

/**
 * Generate Meters & Services Summary Page
 */
function generateMetersServicesSummary(
  surveyData: RetrofitSurveyData,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  const data = surveyData.initial_questionnaire;
  if (!data || !hasMetersServicesData(data)) {
    return '';
  }
  
  const summaryItems: Array<{label: string; value: string; location?: string}> = [];
  
  if (data.hasGasMeter !== undefined) {
    summaryItems.push({
      label: 'Gas Meter Present',
      value: data.hasGasMeter ? 'Yes' : 'No',
      location: data.hasGasMeter && data.gasMeterLocation ? data.gasMeterLocation : undefined
    });
  }
  
  if (data.hasElectricMeter !== undefined) {
    summaryItems.push({
      label: 'Electric Meter Present',
      value: data.hasElectricMeter ? 'Yes' : 'No',
      location: data.hasElectricMeter && data.electricMeterLocation ? data.electricMeterLocation : undefined
    });
  }
  
  if (data.hasConsumerBoard !== undefined) {
    summaryItems.push({
      label: 'Consumer Board Present',
      value: data.hasConsumerBoard ? 'Yes' : 'No',
      location: data.hasConsumerBoard && data.consumerBoardLocation ? data.consumerBoardLocation : undefined
    });
  }
  
  if (data.hasStopTap !== undefined) {
    summaryItems.push({
      label: 'Stop Tap Present',
      value: data.hasStopTap ? 'Yes' : 'No',
      location: data.hasStopTap && data.stopTapLocation ? data.stopTapLocation : undefined
    });
  }
  
  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      <div class="content-section">
        <div class="section-heading">Meters & Services - Summary</div>
        <div class="summary-grid">
          ${summaryItems.map(item => `
            <div class="summary-card">
              <div class="summary-label">${item.label}</div>
              <div class="summary-value">${item.value}</div>
              ${item.location ? `<div style="font-size: 8pt; color: #64748b; margin-top: 4pt; font-style: italic;">📍 ${item.location}</div>` : ''}
            </div>
          `).join('')}
        </div>
      </div>
    </section>
  `;
}

/**
 * Generate Meters & Services Pages (multi-page support for photos)
 */
async function generateMetersServicesPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): Promise<string[]> {
  console.log('🔍 METERS DEBUG: generateMetersServicesPages called');
  
  const data = surveyData.initial_questionnaire;
  console.log('🔍 METERS DEBUG: initial_questionnaire exists?', !!data);
  
  if (!data) {
    console.log('❌ METERS DEBUG: No initial_questionnaire data found!');
    return [];
  }
  
  const hasData = hasMetersServicesData(data);
  console.log('🔍 METERS DEBUG: hasMetersServicesData =', hasData);
  
  if (!hasData) {
    console.log('❌ METERS DEBUG: hasMetersServicesData returned false!');
    return [];
  }
  
  console.log('⚡ Generating Meters & Services pages...');
  console.log('📊 Meters data structure:', {
    hasGasMeter: data.hasGasMeter,
    hasElectricMeter: data.hasElectricMeter,
    hasConsumerBoard: data.hasConsumerBoard,
    hasStopTap: data.hasStopTap,
    photosKeys: Object.keys(data.photos || {}),
    gasMeterPhotos: data.photos?.gasMeterEarthBonding?.photos?.length,
    electricMeterPhotos: data.photos?.electricMeter?.photos?.length,
    consumerBoardPhotos: data.photos?.consumerBoard?.photos?.length,
    stopTapPhotos: data.photos?.stopTap?.photos?.length
  });
  
  const pages: string[] = [];
  const photos = data.photos || {};
  
  // Collect all photos
  const allPhotos: Array<{url: string; title: string}> = [];
  
  if (data.hasGasMeter && photos.gasMeterEarthBonding?.photos) {
    console.log(`🔍 Processing ${photos.gasMeterEarthBonding.photos.length} gas meter photos...`);
    photos.gasMeterEarthBonding.photos.forEach((photo: any, idx: number) => {
      if (photo.url || photo.localPath) {
        allPhotos.push({
          url: photo.url || photo.localPath,
          title: 'Gas Meter Earth Bonding'
        });
        console.log(`  ✅ Added gas meter photo ${idx + 1}`);
      }
    });
  }
  
  if (data.hasElectricMeter && photos.electricMeter?.photos) {
    photos.electricMeter.photos.forEach((photo: any) => {
      if (photo.url || photo.localPath) {
        allPhotos.push({
          url: photo.url || photo.localPath,
          title: 'Electric Meter'
        });
      }
    });
  }
  
  if (data.hasConsumerBoard && photos.consumerBoard?.photos) {
    photos.consumerBoard.photos.forEach((photo: any) => {
      if (photo.url || photo.localPath) {
        allPhotos.push({
          url: photo.url || photo.localPath,
          title: 'Consumer Board'
        });
      }
    });
  }
  
  if (data.hasStopTap && photos.stopTap?.photos) {
    photos.stopTap.photos.forEach((photo: any) => {
      if (photo.url || photo.localPath) {
        allPhotos.push({
          url: photo.url || photo.localPath,
          title: 'Stop Tap'
        });
      }
    });
  }
  
  // Optimize all photos for consistent sizing and quality (parallel like room photos)
  console.log(`📸 Optimizing ${allPhotos.length} Meters & Services photos...`);
  console.log(`📋 Photo URLs to optimize:`, allPhotos.map(p => ({ title: p.title, url: p.url?.substring(0, 60) })));
  
  let optimizedUrls: Array<{url: string; title: string}> = [];
  
  try {
    console.log('⏳ Starting Promise.all for photo optimization...');
    optimizedUrls = await Promise.all(
      allPhotos.map(async (photo, idx) => {
        try {
          console.log(`    📷 Photo ${idx + 1}/${allPhotos.length}: ${photo.title} - ${photo.url?.substring(0, 100)}...`);
          const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
          console.log(`  ✅ Optimized: ${photo.title}`);
          return {
            url: optimizedUrl,
            title: photo.title
          };
        } catch (error) {
          console.error(`  ❌ Error optimizing ${photo.title}:`, error);
          // Fallback to original URL if optimization fails
          return photo;
        }
      })
    );
    console.log('✅ Promise.all completed successfully');
  } catch (error) {
    console.error('❌ FATAL: Promise.all failed completely:', error);
    // Use original photos if optimization completely fails
    optimizedUrls = allPhotos;
  }
  
  console.log(`✅ Successfully processed ${optimizedUrls.length} photos (${allPhotos.length} total)`);
  
  // Split photos into pages (6 per page, matching Property Front)
  const photosPerPage = 6;
  const photoChunks: Array<Array<{url: string; title: string}>> = [];
  
  for (let i = 0; i < optimizedUrls.length; i += photosPerPage) {
    photoChunks.push(optimizedUrls.slice(i, i + photosPerPage));
  }
  
  console.log(`📦 Split into ${photoChunks.length} page(s) with ${photosPerPage} photos per page`);
  photoChunks.forEach((chunk, idx) => {
    console.log(`  Page ${idx + 1}: ${chunk.length} photos - ${chunk.map(p => p.title).join(', ')}`);
  });
  
  // Generate photo pages
  photoChunks.forEach((photoChunk, chunkIndex) => {
    console.log(`📄 Generating Meters page ${chunkIndex + 1} with ${photoChunk.length} photos`);
    photoChunk.forEach((photo, idx) => {
      console.log(`  🖼️ Photo ${idx + 1}: ${photo.title}`);
      console.log(`     URL type: ${photo.url.startsWith('data:') ? 'Base64' : 'External'}`);
      console.log(`     URL length: ${photo.url.length} chars`);
    });
    
    // Build photo grid HTML
    const photoGridHTML = photoChunk.map((photo, photoIdx) => `
      <div class="property-photo-wrapper">
        <div class="property-photo-item">
          <img src="${photo.url}" alt="${photo.title}" />
        </div>
        <div class="property-photo-label">${photo.title}</div>
      </div>`).join('');
    
    const pageHTML = `
      <section class="pdf-page">
        ${generateHeader(surveyData, startPage + chunkIndex, totalPages, options)}
        <div class="content-section">
          <div class="property-section">
            <div class="property-section-header">
              <div class="property-icon-container" style="background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);">
                <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
                </svg>
              </div>
              <h3><span class="section-parent">Meters & Services:</span> Photos${chunkIndex > 0 ? ` (Page ${chunkIndex + 1})` : ''}</h3>
            </div>
            <div class="property-photo-grid">${photoGridHTML}</div>
          </div>
        </div>
        ${generateFooter(options)}
      </section>
    `;
    pages.push(pageHTML);
  });
  
  console.log(`✅ Generated ${pages.length} Meters & Services page(s)`);
  return pages;
}

/**
 * Check if ventilation & condition data exists
 */
function hasVentilationConditionData(ventData: any): boolean {
  console.log('🔍 hasVentilationConditionData: checking data', {
    exists: !!ventData,
    naturalVentilation: ventData?.naturalVentilation,
    mechanicalVentilation: ventData?.mechanicalVentilation,
    airTightnessAssessment: ventData?.airTightnessAssessment,
    moistureIssues: ventData?.moistureIssues,
    condensationProblems: ventData?.condensationProblems,
    ventilationEffectiveness: ventData?.ventilationEffectiveness,
    notes: ventData?.notes,
    hasPhotos: !!ventData?.photos
  });
  
  // Always show if the object exists (like other sections)
  // The section will display with whatever data is available
  if (!ventData) {
    console.log('🔍 hasVentilationConditionData: no data object, returning false');
    return false;
  }
  
  console.log('🔍 hasVentilationConditionData: data object exists, returning true');
  return true;
}

/**
 * Calculate how many pages Ventilation & Condition section needs
 */
async function calculateVentilationConditionPages(surveyData: RetrofitSurveyData): Promise<number> {
  if (!surveyData.ventilation_condition || !hasVentilationConditionData(surveyData.ventilation_condition)) {
    return 0;
  }
  
  const data = surveyData.ventilation_condition;
  let pageCount = 1; // Summary page
  
  // Count photo pages if photos exist
  const dampnessCount = (data.dampnessImages?.length || 0);
  const roofDefectCount = (data.roofDefectImages?.length || 0);
  const wallDefectCount = (data.wallDefectImages?.length || 0);
  const externalDoorsCount = (data.externalDoorsImages?.length || 0);
  const frontDoorOpenCount = (data.frontDoorOpenImage?.length || 0);
  const windowGlazingThicknessCount = (data.windowGlazingThicknessImage?.length || 0);
  const totalPhotoCount = dampnessCount + externalDoorsCount + frontDoorOpenCount + windowGlazingThicknessCount;
  
  if (totalPhotoCount > 0) {
    pageCount += Math.ceil(totalPhotoCount / 6); // 6 photos per page
  }
  
  return pageCount;
}

/**
 * Generate Ventilation & Condition Pages
 */
async function generateVentilationConditionPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): Promise<string[]> {
  console.log('🌬️ VENTILATION DEBUG: generateVentilationConditionPages called');
  
  const data = surveyData.ventilation_condition;
  console.log('🌬️ VENTILATION DEBUG: data exists?', !!data);
  
  if (!data) {
    console.log('❌ VENTILATION DEBUG: No ventilation_condition data found!');
    return [];
  }
  
  const hasData = hasVentilationConditionData(data);
  console.log('🌬️ VENTILATION DEBUG: hasVentilationConditionData =', hasData);
  
  if (!hasData) {
    console.log('❌ VENTILATION DEBUG: hasVentilationConditionData returned false!');
    return [];
  }
  
  console.log('⚡ Generating Ventilation & Condition pages...');
  const pages: string[] = [];
  
  // Helper function to map old internal wall condition values to new ones
  const mapInternalWallCondition = (value: string | undefined): string => {
    if (!value) return '';
    const normalized = value.toLowerCase().trim();
    
    // Map old values to new values
    const conditionMap: Record<string, string> = {
      'sound': 'Good',
      'minor-cracks': 'Average',
      'minor cracks': 'Average',
      'major-cracks': 'Poor',
      'major cracks': 'Poor',
      'damp-staining': 'Poor',
      'damp staining': 'Poor',
      // New values
      'good': 'Good',
      'average': 'Average',
      'poor': 'Poor'
    };
    
    return conditionMap[normalized] || capitalizeText(value);
  };
  
  // Generate structured summary page with separate sections
  const summaryHTML = `
    <section class="pdf-page">
      ${generateHeader(surveyData, startPage, totalPages, options)}
      <div class="content-section">
        <div class="property-section">
          <div class="property-section-header">
            <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);">
              <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </div>
            <h3><span class="section-parent">Ventilation & Condition:</span> Summary</h3>
          </div>
          
          <!-- Two-column grid for compact layout -->
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10pt; margin-bottom: 12pt;">
            <!-- Room Counts -->
            ${(data.habitableRooms || data.heatedHabitableRooms || data.totalRooms) ? `
              <div>
                <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                  🏠 Room Counts
                </h4>
                <table class="occupancy-table">
                  <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
                  <tbody>
                    ${data.habitableRooms ? `<tr><td>Habitable Rooms</td><td>${data.habitableRooms}</td></tr>` : ''}
                    ${data.heatedHabitableRooms ? `<tr><td>Heated Habitable Rooms</td><td>${data.heatedHabitableRooms}</td></tr>` : ''}
                    ${data.totalRooms ? `<tr><td>Total Rooms</td><td>${data.totalRooms}</td></tr>` : ''}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            <!-- Internal Walls (swapped to reduce PDF space) -->
            ${(data.internalWallCondition || data.evidenceOfDamp !== undefined) ? `
              <div>
                <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                  🧱 Internal Walls
                </h4>
                <table class="occupancy-table">
                  <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
                  <tbody>
                    ${data.internalWallCondition ? `<tr><td>Internal Wall Condition</td><td>${mapInternalWallCondition(data.internalWallCondition)}</td></tr>` : ''}
                    ${data.evidenceOfDamp !== undefined && data.evidenceOfDamp !== null ? `<tr><td>Evidence of Dampness</td><td>${data.evidenceOfDamp ? 'Yes' : 'No'}</td></tr>` : ''}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            <!-- Doors -->
            ${(data.numberOfExternalDoors || data.externalDoorsCondition || data.externalDoorsType || data.draughtProofingOnExternalDoor || data.doorUndercutsPresent !== undefined || data.roomsWithNoDoorUndercuts) ? `
              <div>
                <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                  🚪 Doors
                </h4>
                <table class="occupancy-table">
                  <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
                  <tbody>
                    ${data.numberOfExternalDoors ? `<tr><td>Number of External Doors</td><td>${data.numberOfExternalDoors}</td></tr>` : ''}
                    ${data.externalDoorsCondition ? `<tr><td>External Doors Condition</td><td>${capitalizeText(data.externalDoorsCondition)}</td></tr>` : ''}
                    ${data.externalDoorsType ? `<tr><td>External Doors Type</td><td>${data.externalDoorsType.toUpperCase()}</td></tr>` : ''}
                    ${data.draughtProofingOnExternalDoor ? `<tr><td>Draught Proofing on External Door</td><td>${capitalizeText(data.draughtProofingOnExternalDoor)}</td></tr>` : ''}
                    ${data.doorUndercutsPresent !== undefined && data.doorUndercutsPresent !== null ? `<tr><td>Door Undercuts Present</td><td>${data.doorUndercutsPresent ? 'Yes' : 'No'}</td></tr>` : ''}
                    ${data.roomsWithNoDoorUndercuts ? `<tr><td>Rooms with No Door Undercuts</td><td>${data.roomsWithNoDoorUndercuts}</td></tr>` : ''}
                  </tbody>
                </table>
              </div>
            ` : ''}
            
            <!-- Windows (swapped with Internal Walls) -->
            ${(data.windowFramesCondition || data.draughtProofingCondition || data.windowFrameType || data.numberOfWindows || data.numberOfTrickleVents || data.windowGlazingType || data.windowGlazingMeasurement) ? `
              <div>
                <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                  🪟 Windows
                </h4>
                <table class="occupancy-table">
                  <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
                  <tbody>
                    ${data.windowFramesCondition ? `<tr><td>Window Frames Condition</td><td>${capitalizeText(data.windowFramesCondition)}</td></tr>` : ''}
                    ${data.draughtProofingCondition ? `<tr><td>Draught Proofing Condition</td><td>${capitalizeText(data.draughtProofingCondition)}</td></tr>` : ''}
                    ${data.windowFrameType ? `<tr><td>Window Frame Type</td><td>${data.windowFrameType.toUpperCase()}</td></tr>` : ''}
                    ${data.numberOfWindows ? `<tr><td>Number of Windows</td><td>${data.numberOfWindows}</td></tr>` : ''}
                    ${data.numberOfTrickleVents ? `<tr><td>Number of Trickle Vents</td><td>${data.numberOfTrickleVents}</td></tr>` : ''}
                    ${data.windowGlazingType ? `<tr><td>Window Glazing Type</td><td>${capitalizeText(data.windowGlazingType.replace(/_/g, ' '))}</td></tr>` : ''}
                    ${data.windowGlazingMeasurement ? `<tr><td>Window Glazing Measurement</td><td>${data.windowGlazingMeasurement}</td></tr>` : ''}
                  </tbody>
                </table>
              </div>
            ` : ''}
          </div>
          
          <!-- Ventilation Fans (identical to survey form) -->
          ${(data.numberOfWetRooms || data.numberOfVentilationFans || data.roomsMissingVentilationFans || (data.ventilationTypePresent && data.ventilationTypePresent.length > 0)) ? `
            <div style="margin-bottom: 12pt;">
              <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                💨 Ventilation Fans
              </h4>
              <table class="occupancy-table">
                <thead><tr><th>Attribute</th><th>Value</th></tr></thead>
                <tbody>
                  ${data.numberOfWetRooms ? `<tr><td>Number of Wet Rooms</td><td>${data.numberOfWetRooms}</td></tr>` : ''}
                  ${data.numberOfVentilationFans ? `<tr><td>Number of Ventilation Fans</td><td>${data.numberOfVentilationFans}</td></tr>` : ''}
                  ${data.roomsMissingVentilationFans ? `<tr><td>Rooms Missing Ventilation Fans</td><td>${data.roomsMissingVentilationFans}</td></tr>` : ''}
                </tbody>
              </table>
              ${data.ventilationTypeCounts ? `
                <div style="margin-top: 8pt;">
                  <h5 style="color: #1e293b; font-size: 9pt; font-weight: 600; margin-bottom: 4pt;">Ventilation Fan Types</h5>
                  <table class="occupancy-table">
                    <thead><tr><th>Type</th><th>Count</th></tr></thead>
                    <tbody>
                      <tr><td>Decentralised Mechanical Ventilation</td><td style="text-align: center;">${data.ventilationTypeCounts.decentralised_mechanical || 0}</td></tr>
                      <tr><td>Intermittent Ventilation Fan</td><td style="text-align: center;">${data.ventilationTypeCounts.intermittent || 0}</td></tr>
                      <tr><td>Centralised Mechanical Ventilation Fans</td><td style="text-align: center;">${data.ventilationTypeCounts.centralised_mechanical || 0}</td></tr>
                      <tr><td>Other Ventilation Fans</td><td style="text-align: center;">${data.ventilationTypeCounts.other || 0}</td></tr>
                      <tr style="background: #eff6ff; font-weight: 600;"><td>Total</td><td style="text-align: center; color: #1e40af;">${(data.ventilationTypeCounts.decentralised_mechanical || 0) + (data.ventilationTypeCounts.intermittent || 0) + (data.ventilationTypeCounts.centralised_mechanical || 0) + (data.ventilationTypeCounts.other || 0)}</td></tr>
                    </tbody>
                  </table>
                </div>
              ` : ''}
            </div>
          ` : ''}
          
          
          ${data.notes ? `
            <div class="mt-2">
              <strong>Additional Notes:</strong>
              <div class="text-sm mt-2" style="padding: 8pt; background: #f9fafb; border-left: 3pt solid var(--theme-color); border-radius: 4pt;">
                ${data.notes}
              </div>
            </div>
          ` : ''}
        </div>
      </div>
      ${generateFooter(options)}
    </section>
  `;
  
  pages.push(summaryHTML);
  
  // Generate photo pages for dampness images specifically
  const dampnessPhotos: Array<{url: string; title: string}> = [];
  
  // Collect dampness images directly from data (not nested in photos object)
  if ((data as any).dampnessImages && Array.isArray((data as any).dampnessImages)) {
    (data as any).dampnessImages.forEach((photo: any) => {
      if (photo.url || photo.localPath || photo.dataUrl) {
        dampnessPhotos.push({
          url: photo.url || photo.localPath || photo.dataUrl,
          title: 'Internal Wall Dampness'
        });
      }
    });
  }
  
  // Collect door photo types
  const doorPhotos: Array<{url: string; title: string}> = [];
  
  // External doors images (showing draught proofing)
  if ((data as any).externalDoorsImages && Array.isArray((data as any).externalDoorsImages)) {
    (data as any).externalDoorsImages.forEach((photo: any) => {
      if (photo.url || photo.localPath || photo.dataUrl) {
        doorPhotos.push({
          url: photo.url || photo.localPath || photo.dataUrl,
          title: 'External Doors (Showing Draught Proofing)'
        });
      }
    });
  }
  
  // Front door open image
  if ((data as any).frontDoorOpenImage && Array.isArray((data as any).frontDoorOpenImage)) {
    (data as any).frontDoorOpenImage.forEach((photo: any) => {
      if (photo.url || photo.localPath || photo.dataUrl) {
        doorPhotos.push({
          url: photo.url || photo.localPath || photo.dataUrl,
          title: 'Front Door Open'
        });
      }
    });
  }
  
  // Window glazing thickness images
  const windowPhotos: Array<{url: string; title: string}> = [];
  if ((data as any).windowGlazingThicknessImage && Array.isArray((data as any).windowGlazingThicknessImage)) {
    (data as any).windowGlazingThicknessImage.forEach((photo: any) => {
      if (photo.url || photo.localPath || photo.dataUrl) {
        windowPhotos.push({
          url: photo.url || photo.localPath || photo.dataUrl,
          title: 'Window Glazing Thickness'
        });
      }
    });
  }
  
  // Process dampness photos first (priority display)
  if (dampnessPhotos.length > 0) {
    console.log(`📸 Optimizing ${dampnessPhotos.length} Internal Dampness photos...`);
    
    let optimizedDampnessUrls: Array<{url: string; title: string}> = [];
    
    try {
      optimizedDampnessUrls = await Promise.all(
        dampnessPhotos.map(async (photo) => {
          try {
            const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
            return { url: optimizedUrl, title: photo.title };
          } catch (error) {
            console.error(`Error optimizing ${photo.title}:`, error);
            return photo;
          }
        })
      );
    } catch (error) {
      console.error('Error optimizing dampness photos:', error);
      optimizedDampnessUrls = dampnessPhotos;
    }
    
    // Split photos into pages (6 per page)
    const photosPerPage = 6;
    const dampnessPhotoChunks: Array<{url: string; title: string}>[] = [];
    
    for (let i = 0; i < optimizedDampnessUrls.length; i += photosPerPage) {
      dampnessPhotoChunks.push(optimizedDampnessUrls.slice(i, i + photosPerPage));
    }
    
    // Generate dampness photo pages
    dampnessPhotoChunks.forEach((photoChunk, chunkIndex) => {
      const photoGridHTML = photoChunk.map((photo) => `
    <div class="property-photo-wrapper">
      <div class="property-photo-item">
        <img src="${photo.url}" alt="${photo.title}" />
      </div>
      <div class="property-photo-label">${photo.title}</div>
    </div>`).join('');
      
      const pageHTML = `
    <section class="pdf-page">
      ${generateHeader(surveyData, startPage + 1 + chunkIndex, totalPages, options)}
      <div class="content-section">
        <div class="property-section">
          <div class="property-section-header">
            <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);">
              <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </div>
            <h3><span class="section-parent">Ventilation & Condition:</span> Internal Wall Dampness${chunkIndex > 0 ? ` (Page ${chunkIndex + 1})` : ''}</h3>
          </div>
          <div class="property-photo-grid">${photoGridHTML}</div>
        </div>
      </div>
      ${generateFooter(options)}
    </section>
  `;
      pages.push(pageHTML);
    });
  }
  
  // Process door photos
  if (doorPhotos.length > 0) {
    console.log(`📸 Optimizing ${doorPhotos.length} door photos...`);
    
    let optimizedOtherUrls: Array<{url: string; title: string}> = [];
    
    try {
      optimizedOtherUrls = await Promise.all(
        doorPhotos.map(async (photo) => {
          try {
            const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
            return { url: optimizedUrl, title: photo.title };
          } catch (error) {
            console.error(`Error optimizing ${photo.title}:`, error);
            return photo;
          }
        })
      );
    } catch (error) {
      console.error('Error optimizing ventilation photos:', error);
      optimizedOtherUrls = doorPhotos;
    }
    
    // Split photos into pages (6 per page)
    const photosPerPage = 6;
    const otherPhotoChunks: Array<{url: string; title: string}>[] = [];
    
    for (let i = 0; i < optimizedOtherUrls.length; i += photosPerPage) {
      otherPhotoChunks.push(optimizedOtherUrls.slice(i, i + photosPerPage));
    }
    
    // Generate other photo pages
    const dampnessPagesCount = Math.ceil(dampnessPhotos.length / 6);
    otherPhotoChunks.forEach((photoChunk, chunkIndex) => {
      const photoGridHTML = photoChunk.map((photo) => `
    <div class="property-photo-wrapper">
      <div class="property-photo-item">
        <img src="${photo.url}" alt="${photo.title}" />
      </div>
      <div class="property-photo-label">${photo.title}</div>
    </div>`).join('');
      
      const pageHTML = `
    <section class="pdf-page">
      ${generateHeader(surveyData, startPage + 1 + dampnessPagesCount + chunkIndex, totalPages, options)}
      <div class="content-section">
        <div class="property-section">
          <div class="property-section-header">
            <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);">
              <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </div>
            <h3><span class="section-parent">Ventilation & Condition:</span> Door Images${chunkIndex > 0 ? ` (Page ${chunkIndex + 1})` : ''}</h3>
          </div>
          <div class="property-photo-grid">${photoGridHTML}</div>
        </div>
      </div>
      ${generateFooter(options)}
    </section>
  `;
      pages.push(pageHTML);
    });
  }
  
  // Process window glazing thickness photos
  if (windowPhotos.length > 0) {
    console.log(`📸 Optimizing ${windowPhotos.length} window glazing thickness photos...`);
    
    let optimizedWindowUrls: Array<{url: string; title: string}> = [];
    
    try {
      optimizedWindowUrls = await Promise.all(
        windowPhotos.map(async (photo) => {
          try {
            const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
            return { url: optimizedUrl, title: photo.title };
          } catch (error) {
            console.error(`Error optimizing ${photo.title}:`, error);
            return photo;
          }
        })
      );
    } catch (error) {
      console.error('Error optimizing window photos:', error);
      optimizedWindowUrls = windowPhotos;
    }
    
    // Split photos into pages (6 per page)
    const photosPerPage = 6;
    const windowPhotoChunks: Array<{url: string; title: string}>[] = [];
    
    for (let i = 0; i < optimizedWindowUrls.length; i += photosPerPage) {
      windowPhotoChunks.push(optimizedWindowUrls.slice(i, i + photosPerPage));
    }
    
    // Generate window photo pages
    const dampnessPagesCount = Math.ceil(dampnessPhotos.length / 6);
    const doorPagesCount = Math.ceil(doorPhotos.length / 6);
    windowPhotoChunks.forEach((photoChunk, chunkIndex) => {
      const photoGridHTML = photoChunk.map((photo) => `
    <div class="property-photo-wrapper">
      <div class="property-photo-item">
        <img src="${photo.url}" alt="${photo.title}" />
      </div>
      <div class="property-photo-label">${photo.title}</div>
    </div>`).join('');
      
      const pageHTML = `
    <section class="pdf-page">
      ${generateHeader(surveyData, startPage + 1 + dampnessPagesCount + doorPagesCount + chunkIndex, totalPages, options)}
      <div class="content-section">
        <div class="property-section">
          <div class="property-section-header">
            <div class="property-icon-container" style="background: linear-gradient(135deg, #10b981 0%, #059669 100%);">
              <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
              </svg>
            </div>
            <h3><span class="section-parent">Ventilation & Condition:</span> Window Glazing Thickness${chunkIndex > 0 ? ` (Page ${chunkIndex + 1})` : ''}</h3>
          </div>
          <div class="property-photo-grid">${photoGridHTML}</div>
        </div>
      </div>
      ${generateFooter(options)}
    </section>
  `;
      pages.push(pageHTML);
    });
  }
  
  console.log(`✅ Generated ${pages.length} Ventilation & Condition page(s)`);
  return pages;
}

/**
 * Calculate pages needed for room details section
 */
async function calculateRoomDetailsPages(surveyData: RetrofitSurveyData): Promise<number> {
  const rooms = surveyData.room_details?.rooms || [];
  if (rooms.length === 0) return 0;
  
  // Group rooms by floor to count floor header pages
  const roomsByFloor = new Map<number, number>();
  rooms.forEach(room => {
    const floor = room.floor || 1;
    roomsByFloor.set(floor, (roomsByFloor.get(floor) || 0) + 1);
  });
  
  // Count total photos across all rooms
  let totalPhotos = 0;
  rooms.forEach(room => {
    totalPhotos += (room.photos?.length || 0);
  });
  
  // Page calculation:
  // - 1 header page per floor
  // - 1 info page per room
  // - Photo pages (max 6 photos per page)
  let pageCount = roomsByFloor.size; // One header page per floor
  pageCount += rooms.length; // One page per room for info
  pageCount += Math.ceil(totalPhotos / 6); // Additional pages for photos (6 per page)
  
  return pageCount;
}

/**
 * Generate Room Details Pages with photos
 * Returns array of page HTML strings
 */
async function generateRoomDetailsPages(
  surveyData: RetrofitSurveyData,
  startPage: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): Promise<string[]> {
  console.log('🔍 ROOM DETAILS DEBUG - Survey Data Structure:', {
    hasRoomDetails: !!surveyData.room_details,
    roomDetailsKeys: surveyData.room_details ? Object.keys(surveyData.room_details) : [],
    hasRooms: !!surveyData.room_details?.rooms,
    roomCount: surveyData.room_details?.rooms?.length || 0
  });
  
  const rooms = surveyData.room_details?.rooms || [];
  
  if (rooms.length === 0) {
    console.warn('⚠️ No rooms found in surveyData.room_details.rooms');
    return [];
  }

  console.log(`🏠 Generating Room Details pages for ${rooms.length} rooms...`);

  const pages: string[] = [];
  
  // Helper to convert photos to categorized array with labels
  const getCategorizedPhotos = (photos: any): Array<{ url: string; label: string; category: string }> => {
    if (!photos) return [];
    
    const categorizedPhotos: Array<{ url: string; label: string; category: string }> = [];
    
    // If already an array of strings (URLs) - uncategorized photos
    if (Array.isArray(photos)) {
      photos.forEach((p, idx) => {
        let url = '';
        if (typeof p === 'string') url = p;
        else if (p && typeof p === 'object') url = p.url || p.dataUrl || p.uri || '';
        
        if (url) {
          categorizedPhotos.push({
            url,
            label: `Photo ${idx + 1}`,
            category: 'general'
          });
        }
      });
      return categorizedPhotos;
    }
    
    // If it's an object with photo properties (like wallImages, radiatorImages, etc.)
    if (typeof photos === 'object') {
      // Map of category keys to human-readable labels
      // Based on actual keys used in RoomSurvey component
      const categoryLabels: Record<string, string> = {
        // Wall photos
        'wallImages': 'Wall',
        'wall': 'Wall',
        // Radiator photos (special case - array of objects)
        'radiatorImages': 'Radiator',
        'radiator': 'Radiator',
        // Heating system photos
        'secondaryHeatingImages': 'Heating System',
        'heatingImages': 'Heating System',
        'heating': 'Heating System',
        // Window photos
        'multipleWindowImages': 'Window',
        'windowImages': 'Window',
        'window': 'Window',
        // Lighting photos
        'lightingImages': 'Lighting',
        'lighting': 'Lighting',
        // Alarm photos
        'alarmImages': 'Alarm',
        'alarm': 'Alarm',
        // Door photos
        'doorUnderCutsImage': 'Door Undercut',
        'doorImages': 'Door',
        'door': 'Door',
        // Ventilation photos
        'ventilationFansImage': 'Ventilation Fan',
        'ventilationImages': 'Ventilation',
        'ventilation': 'Ventilation',
        // Floor to ceiling height measurement
        'fullHeightImage': 'Floor-Ceiling Height',
        'heightImage': 'Height Measurement',
        'ceilingHeightImage': 'Ceiling Height',
        // General categories
        'ceilingImages': 'Ceiling',
        'ceiling': 'Ceiling',
        'floorImages': 'Floor',
        'floor': 'Floor',
        'defectImages': 'Defect',
        'defect': 'Defect',
        'generalImages': 'General',
        'general': 'General',
        'other': 'Other'
      };
      
      Object.entries(photos).forEach(([key, value]: [string, any]) => {
        const categoryLabel = categoryLabels[key] || 'General';
        
        // Special handling for radiatorImages (array of objects with radiatorId and images)
        if (key === 'radiatorImages' && Array.isArray(value)) {
          value.forEach((radiatorEntry: any) => {
            if (radiatorEntry && radiatorEntry.images && Array.isArray(radiatorEntry.images)) {
              const radiatorNum = radiatorEntry.radiatorId?.replace('radiator-', '') || '?';
              radiatorEntry.images.forEach((photo: any, idx: number) => {
                let url = '';
                if (typeof photo === 'string') url = photo;
                else if (photo && typeof photo === 'object') url = photo.url || photo.dataUrl || photo.uri;
                
                if (url) {
                  const totalPhotos = radiatorEntry.images.length;
                  categorizedPhotos.push({
                    url,
                    label: totalPhotos > 1 
                      ? `Radiator ${radiatorNum} (${idx + 1}/${totalPhotos})` 
                      : `Radiator ${radiatorNum}`,
                    category: 'radiatorImages'
                  });
                }
              });
            }
          });
        }
        // Handle regular nested arrays (wallImages: Array, lightingImages: Array, etc.)
        else if (Array.isArray(value)) {
          value.forEach((item: any, idx: number) => {
            let url = '';
            if (typeof item === 'string') url = item;
            else if (item && typeof item === 'object') url = item.url || item.dataUrl || item.uri;
            
            if (url) {
              categorizedPhotos.push({
                url,
                label: value.length > 1 ? `${categoryLabel} (${idx + 1}/${value.length})` : categoryLabel,
                category: key
              });
            }
          });
        } 
        // Handle direct photo objects
        else if (typeof value === 'string') {
          categorizedPhotos.push({
            url: value,
            label: categoryLabel,
            category: key
          });
        } else if (value && typeof value === 'object') {
          const url = value.url || value.dataUrl || value.uri;
          if (url) {
            categorizedPhotos.push({
              url,
              label: categoryLabel,
              category: key
            });
          }
        }
      });
    }
    
    return categorizedPhotos;
  };
  
  // Helper to convert photos to array of URLs (for backward compatibility)
  const getPhotoUrls = (photos: any): string[] => {
    return getCategorizedPhotos(photos).map(p => p.url);
  };
  
  // Helper to format values
  const formatValue = (value: any): string => {
    if (value === undefined || value === null || value === '') return 'Not specified';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') return capitalizeText(value);
    if (typeof value === 'number') return value.toString();
    return String(value);
  };

  console.log('📊 Room Data Sample:', rooms.slice(0, 2).map(r => ({
    id: r.id,
    name: r.name,
    floor: r.floor,
    photosType: typeof r.photos,
    photosIsArray: Array.isArray(r.photos),
    photosKeys: r.photos && typeof r.photos === 'object' ? Object.keys(r.photos) : [],
    photoUrls: getPhotoUrls(r.photos)
  })));

  // Optimize all room photos upfront with category labels preserved
  const optimizedRoomPhotos = new Map<string, Array<{ url: string; label: string; category: string }>>();
  console.log('📸 Starting photo optimization for all rooms...');
  
  // Calculate total photos for progress tracking
  const totalPhotosCount = rooms.reduce((sum, room) => {
    const categorizedPhotos = getCategorizedPhotos(room.photos);
    return sum + categorizedPhotos.length;
  }, 0);
  
  let processedPhotos = 0;
  
  for (let roomIndex = 0; roomIndex < rooms.length; roomIndex++) {
    const room = rooms[roomIndex];
    // First log the raw photo data structure
    console.log(`🔍 Room "${room.name}" (ID: ${room.id}) - RAW PHOTOS:`, {
      hasPhotos: !!room.photos,
      photosType: typeof room.photos,
      photosIsArray: Array.isArray(room.photos),
      photosKeys: room.photos && typeof room.photos === 'object' ? Object.keys(room.photos) : [],
      photosValues: room.photos && typeof room.photos === 'object' ? Object.values(room.photos).slice(0, 2) : [],
      rawPhotos: room.photos
    });
    
    const categorizedPhotos = getCategorizedPhotos(room.photos);
    
    console.log(`🔍 Room "${room.name}" (ID: ${room.id}) - EXTRACTED:`, {
      extractedPhotos: categorizedPhotos.length,
      categories: [...new Set(categorizedPhotos.map(p => p.category))],
      sampleLabels: categorizedPhotos.slice(0, 3).map(p => p.label)
    });
    
    if (categorizedPhotos.length > 0) {
      try {
        console.log(`  ⏳ Optimizing ${categorizedPhotos.length} photos for room "${room.name}"...`);
        const optimizedWithLabels = await Promise.all(
          categorizedPhotos.map(async (photo, idx) => {
            console.log(`    📷 Photo ${idx + 1}/${categorizedPhotos.length} [${photo.label}]: ${photo.url?.substring(0, 100)}...`);
            const optimizedUrl = await optimizeImageForPDF(photo.url, 1600, 1200, 0.90);
            
            // Update progress for each photo (50-70% range for photo optimization)
            processedPhotos++;
            if (totalPhotosCount > 0 && options?.onProgress) {
              const photoProgress = 50 + (20 * (processedPhotos / totalPhotosCount));
              options.onProgress(Math.round(photoProgress), `Optimizing photos: ${processedPhotos}/${totalPhotosCount}...`);
            }
            
            return {
              url: optimizedUrl,
              label: photo.label,
              category: photo.category
            };
          })
        );
        optimizedRoomPhotos.set(room.id, optimizedWithLabels);
        console.log(`  ✅ Successfully optimized ${optimizedWithLabels.length} photos for room "${room.name}"`);
      } catch (error) {
        console.error(`  ❌ Error optimizing photos for room "${room.name}":`, error);
      }
    } else {
      console.log(`  ℹ️ No photos to optimize for room "${room.name}"`);
    }
  }
  
  console.log(`📸 Photo optimization complete. Rooms with photos: ${optimizedRoomPhotos.size}`);
  
  if (options?.onProgress) {
    options.onProgress(70, 'Generating room pages...');
  }

  // Group rooms by floor for organized presentation
  const roomsByFloor = new Map<number, typeof rooms>();
  rooms.forEach(room => {
    const floor = room.floor || 1;
    if (!roomsByFloor.has(floor)) {
      roomsByFloor.set(floor, []);
    }
    roomsByFloor.get(floor)!.push(room);
  });

  // Generate pages for each floor
  for (const [floorNum, floorRooms] of Array.from(roomsByFloor.entries()).sort((a, b) => a[0] - b[0])) {
    const floorLabel = floorNum === 1 ? 'Ground Floor' : 
                      floorNum === 2 ? 'First Floor' :
                      floorNum === 3 ? 'Second Floor' :
                      `Floor ${floorNum}`;

    // Generate pages for each room on this floor
    for (const room of floorRooms) {
      const roomPhotos = optimizedRoomPhotos.get(room.id) || [];
      
      // Build content sections for intelligent pagination
      const radiatorDetails = (room as any).radiatorDetails || [];
      const windowDetails = (room as any).windowDetails || [];
      
      // Estimate content items (rough pagination logic)
      const hasBasicInfo = !!(room.area || room.width || room.length || room.height);
      const hasCharacteristics = !!((room as any).roomType || (room as any).heatingStatus || room.ventilationType || room.condition);
      const hasNotes = !!(room.notes && room.notes.trim());
      
      // Compact table pagination - fit more on first page
      // With table format, we can fit several items on one page
      // Page 1: Dimensions + Characteristics + all radiators + up to 3 windows
      // Page 2: Remaining windows (if more than 3)
      const radiatorsPage1 = radiatorDetails; // All radiators on first page (compact table)
      const radiatorsPage2: any[] = [];    // No radiators on page 2
      const windowsPage1: any[] = windowDetails.slice(0, 3); // Up to 3 windows on first page
      const windowsPage2 = windowDetails.slice(3); // Remaining windows on continuation
      
      console.log(`📄 Generating page for room "${room.name}":`, {
        id: room.id,
        floor: room.floor,
        hasArea: !!room.area,
        hasUsage: !!room.usage,
        hasNotes: !!room.notes,
        photoCount: roomPhotos.length,
        roomData: {
          area: room.area,
          width: room.width,
          length: room.length,
          height: room.height,
          usage: room.usage,
          heatingStatus: (room as any).heatingStatus,
          ventilationType: room.ventilationType,
          condition: room.condition
        }
      });

      // Helper function to generate radiator HTML (compact table format)
      const generateRadiatorHTML = (radiators: any[]) => radiators.length > 0 ? `
        <div class="property-section" style="margin-top: 12pt;">
          <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt;">
            🔥 Heating System (${radiatorDetails.length})
          </h4>
          <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
            <thead>
              <tr style="background: #f3f4f6; border-bottom: 1pt solid #d1d5db;">
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">#</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Width</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Height</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Type</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">TRV</th>
              </tr>
            </thead>
            <tbody>
              ${radiators.map((rad: any) => {
                const actualIdx = radiatorDetails.indexOf(rad);
                return `
                <tr style="border-bottom: 1pt solid #e5e7eb;">
                  <td style="padding: 4pt 6pt; color: #374151;">${actualIdx + 1}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${rad.width ? rad.width + ' mm' : '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${rad.height ? rad.height + ' mm' : '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${
                    rad.type === 'radiator' ? 'Radiator' :
                    rad.type === 'panel-heater' ? 'Panel Heater' :
                    rad.type === 'fan-heater' ? 'Fan Heater' :
                    rad.type === 'electric-storage-heater' ? 'Electric Storage Heater' :
                    rad.type === 'high-heat-retention' ? 'High Heat Retention' : 
                    rad.type || '—'
                  }</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${
                    rad.hasTRV !== undefined && rad.hasTRV !== null && (rad.type === 'radiator' || !rad.type) 
                      ? (rad.hasTRV ? 'Yes' : 'No')
                      : '—'
                  }</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>
        </div>
      ` : '';
      
      // Helper function to generate window HTML (compact table format)
      const generateWindowHTML = (windows: any[]) => windows.length > 0 ? `
        <div class="property-section" style="margin-top: 12pt;">
          <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt;">
            🪟 Windows (${windowDetails.length})
          </h4>
          <table style="width: 100%; border-collapse: collapse; font-size: 9pt;">
            <thead>
              <tr style="background: #f3f4f6; border-bottom: 1pt solid #d1d5db;">
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">#</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Width</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Height</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Orient.</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Frame</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Cond.</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Glazing</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">Gap</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">TV</th>
                <th style="padding: 4pt 6pt; text-align: left; font-weight: 600; color: #6b7280;">DP</th>
              </tr>
            </thead>
            <tbody>
              ${windows.map((win: any) => {
                const actualIdx = windowDetails.indexOf(win);
                return `
                <tr style="border-bottom: 1pt solid #e5e7eb;">
                  <td style="padding: 4pt 6pt; color: #374151;">${actualIdx + 1}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.width ? win.width + ' m' : '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.height ? win.height + ' m' : '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.orientation || '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.windowType || '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.condition ? win.condition.charAt(0).toUpperCase() + win.condition.slice(1) : '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.glazingType || '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${win.glazingGap || '—'}</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${
                    typeof win.trickleVents === 'number' 
                      ? win.trickleVents
                      : (win.trickleVents ? 'Yes' : '—')
                  }</td>
                  <td style="padding: 4pt 6pt; color: #374151;">${
                    win.isDraughtProofed !== undefined && win.isDraughtProofed !== null 
                      ? (win.isDraughtProofed ? 'Yes' : 'No')
                      : '—'
                  }</td>
                </tr>
              `}).join('')}
            </tbody>
          </table>
        </div>
      ` : '';

      // Room information page 1
      const roomInfoPage = `
        <section class="pdf-page">
          ${generateHeader(surveyData, startPage + pages.length, totalPages, options)}
          <div class="content-section">
            <div class="property-section">
              <div class="property-section-header">
                <div class="property-icon-container" style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);">
                  <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                    <line x1="8" y1="6" x2="16" y2="6"></line>
                    <line x1="8" y1="10" x2="16" y2="10"></line>
                    <line x1="8" y1="14" x2="16" y2="14"></line>
                    <line x1="8" y1="18" x2="16" y2="18"></line>
                  </svg>
                </div>
                <h3><span class="section-parent">Room:</span> ${formatValue(room.name)}${room.abbreviation ? ` (${room.abbreviation})` : ''}</h3>
              </div>

              <!-- Room Dimensions -->
              ${room.area || room.width || room.length || room.height ? `
                <div class="property-section" style="margin-top: 8pt;">
                  <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                    📐 Dimensions
                  </h4>
                  <div class="property-qa-grid">
                    ${room.area ? `
                      <div class="property-qa-item">
                        <div class="property-question">Floor Area</div>
                        <div class="property-answer">${room.area.toFixed(2)} m²</div>
                      </div>
                    ` : ''}
                    ${room.width ? `
                      <div class="property-qa-item">
                        <div class="property-question">Width</div>
                        <div class="property-answer">${room.width.toFixed(2)} m</div>
                      </div>
                    ` : ''}
                    ${room.length ? `
                      <div class="property-qa-item">
                        <div class="property-question">Length</div>
                        <div class="property-answer">${room.length.toFixed(2)} m</div>
                      </div>
                    ` : ''}
                    ${room.height ? `
                      <div class="property-qa-item">
                        <div class="property-question">Height</div>
                        <div class="property-answer">${room.height.toFixed(2)} m</div>
                      </div>
                    ` : ''}
                  </div>
                </div>
              ` : ''}

              <!-- Room Characteristics -->
              <div class="property-section" style="margin-top: 8pt;">
                <h4 style="color: #1e293b; font-size: 10pt; font-weight: 600; margin-bottom: 6pt; padding-bottom: 3pt; border-bottom: 1.5pt solid #e5e7eb;">
                  🏷️ Characteristics
                </h4>
                <div class="property-qa-grid">
                  ${(room as any).roomType ? `
                    <div class="property-qa-item">
                      <div class="property-question">Room Type</div>
                      <div class="property-answer">${formatValue((room as any).roomType)}</div>
                    </div>
                  ` : ''}
                  ${(room as any).heatingStatus ? `
                    <div class="property-qa-item">
                      <div class="property-question">Heating Status</div>
                      <div class="property-answer">${formatValue((room as any).heatingStatus)}</div>
                    </div>
                  ` : ''}
                  ${(() => {
                    // Extract heating types from radiatorDetails
                    const heatingTypes = radiatorDetails
                      .map((rad: any) => {
                        if (rad.type === 'radiator') return 'Radiator';
                        if (rad.type === 'panel-heater') return 'Panel Heater';
                        if (rad.type === 'fan-heater') return 'Fan Heater';
                        if (rad.type === 'electric-storage-heater') return 'Electric Storage Heater';
                        if (rad.type === 'high-heat-retention') return 'High Heat Retention';
                        return rad.type;
                      })
                      .filter((type: any) => type);
                    
                    // Get unique types
                    const uniqueTypes = [...new Set(heatingTypes)];
                    
                    if (uniqueTypes.length > 0) {
                      return `
                        <div class="property-qa-item">
                          <div class="property-question">Heating Type</div>
                          <div class="property-answer">${uniqueTypes.join(', ')}</div>
                        </div>
                      `;
                    }
                    return '';
                  })()}
                  ${room.ventilationType ? `
                    <div class="property-qa-item">
                      <div class="property-question">Ventilation Type</div>
                      <div class="property-answer">${formatValue(room.ventilationType)}</div>
                    </div>
                  ` : ''}
                  ${room.condition ? `
                    <div class="property-qa-item">
                      <div class="property-question">Condition</div>
                      <div class="property-answer">${formatValue(room.condition)}</div>
                    </div>
                  ` : ''}
                  ${(room as any).doorPresent !== undefined && (room as any).doorPresent !== null ? `
                    <div class="property-qa-item">
                      <div class="property-question">Door Present</div>
                      <div class="property-answer">${
                        (room as any).doorPresent === true 
                          ? ((room as any).doorUndercutSize ? `Yes (${(room as any).doorUndercutSize})` : 'Yes')
                          : (room as any).doorPresent === false 
                            ? 'No' 
                            : 'N/A'
                      }</div>
                    </div>
                  ` : (room as any).doorUndercutsPresent !== undefined && (room as any).doorUndercutsPresent !== null ? `
                    <div class="property-qa-item">
                      <div class="property-question">Door Present</div>
                      <div class="property-answer">${(room as any).doorUndercutsPresent ? 'Yes' : 'No'}</div>
                    </div>
                  ` : ''}
                  ${(room as any).hasSecondaryHeating !== undefined && (room as any).hasSecondaryHeating !== null ? `
                    <div class="property-qa-item">
                      <div class="property-question">Secondary Heating</div>
                      <div class="property-answer">${(room as any).hasSecondaryHeating ? 'Yes' : 'No'}</div>
                    </div>
                  ` : ''}
                  ${(room as any).ledLightsCount !== undefined || (room as any).nonLedLightsCount !== undefined ? `
                    <div class="property-qa-item">
                      <div class="property-question">LED Lights</div>
                      <div class="property-answer">${(room as any).ledLightsCount || 0}</div>
                    </div>
                  ` : ''}
                  ${(room as any).ledLightsCount !== undefined || (room as any).nonLedLightsCount !== undefined ? `
                    <div class="property-qa-item">
                      <div class="property-question">Non-LED Lights</div>
                      <div class="property-answer">${(room as any).nonLedLightsCount || 0}</div>
                    </div>
                  ` : ''}
                  ${(room as any).smokeAlarmsCount !== undefined && (room as any).smokeAlarmsCount > 0 ? `
                    <div class="property-qa-item">
                      <div class="property-question">Smoke Alarms</div>
                      <div class="property-answer">${(room as any).smokeAlarmsCount}</div>
                    </div>
                  ` : ''}
                  ${(room as any).carbonMonoxideAlarmsCount !== undefined && (room as any).carbonMonoxideAlarmsCount > 0 ? `
                    <div class="property-qa-item">
                      <div class="property-question">CO Alarms</div>
                      <div class="property-answer">${(room as any).carbonMonoxideAlarmsCount}</div>
                    </div>
                  ` : ''}
                  ${(room as any).heatAlarmsCount !== undefined && (room as any).heatAlarmsCount > 0 ? `
                    <div class="property-qa-item">
                      <div class="property-question">Heat Alarms</div>
                      <div class="property-answer">${(room as any).heatAlarmsCount}</div>
                    </div>
                  ` : ''}
                </div>
              </div>

              <!-- Radiators and Windows on Page 1 -->
              ${generateRadiatorHTML(radiatorsPage1)}
              ${generateWindowHTML(windowsPage1)}

              <!-- Room Notes -->
              ${room.notes && room.notes.trim() ? `
                <div class="property-section" style="margin-top: 12pt;">
                  <h4 style="color: #1e293b; font-size: 11pt; font-weight: 600; margin-bottom: 8pt; padding-bottom: 4pt; border-bottom: 1.5pt solid #e5e7eb;">
                    📝 Notes
                  </h4>
                  <div class="property-notes-content">
                    ${room.notes}
                  </div>
                </div>
              ` : ''}
            </div>
          </div>
          ${generateFooter(options)}
        </section>
      `;
      pages.push(roomInfoPage);

      // Room details continuation page (if needed - typically just for additional windows)
      const needsContinuationPage = windowsPage2.length > 0;
      if (needsContinuationPage) {
        const continuationPage = `
          <section class="pdf-page">
            ${generateHeader(surveyData, startPage + pages.length, totalPages, options)}
            <div class="content-section">
              <div class="property-section">
                <div class="property-section-header">
                  <div class="property-icon-container" style="background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);">
                    <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect>
                      <line x1="8" y1="6" x2="16" y2="6"></line>
                      <line x1="8" y1="10" x2="16" y2="10"></line>
                      <line x1="8" y1="14" x2="16" y2="14"></line>
                      <line x1="8" y1="18" x2="16" y2="18"></line>
                    </svg>
                  </div>
                  <h3><span class="section-parent">Room:</span> ${formatValue(room.name)} (Continued)</h3>
                </div>

                ${generateWindowHTML(windowsPage2)}
              </div>
            </div>
            ${generateFooter(options)}
          </section>
        `;
        pages.push(continuationPage);
      }

      // Room photos pages (max 6 photos per page)
      if (roomPhotos.length > 0) {
        for (let photoIndex = 0; photoIndex < roomPhotos.length; photoIndex += 6) {
          const pagePhotos = roomPhotos.slice(photoIndex, photoIndex + 6);
          const photoPage = `
            <section class="pdf-page">
              ${generateHeader(surveyData, startPage + pages.length, totalPages, options)}
              <div class="content-section">
                <div class="property-section">
                  <div class="property-section-header">
                    <div class="property-icon-container" style="background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%);">
                      <svg class="property-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                        <circle cx="8.5" cy="8.5" r="1.5"></circle>
                        <polyline points="21 15 16 10 5 21"></polyline>
                      </svg>
                    </div>
                    <h3><span class="section-parent">Room Photos:</span> ${formatValue(room.name)}</h3>
                  </div>
                  <div class="property-photo-grid">
                    ${pagePhotos.map((photo, idx) => `
                      <div class="property-photo-wrapper">
                        <div class="property-photo-item">
                          <img src="${photo.url}" alt="${room.name} ${photo.label}" />
                        </div>
                        <div class="property-photo-label">${room.name} - ${photo.label}</div>
                      </div>
                    `).join('')}
                  </div>
                </div>
              </div>
              ${generateFooter(options)}
            </section>
          `;
          pages.push(photoPage);
        }
      }
    }
  }

  console.log(`✅ Generated ${pages.length} Room Details page(s) for ${rooms.length} room(s)`);
  console.log('📊 Room Details Generation Summary:', {
    totalRooms: rooms.length,
    totalPages: pages.length,
    floors: Array.from(roomsByFloor.keys()).sort(),
    roomsPerFloor: Array.from(roomsByFloor.entries()).map(([floor, rooms]) => ({
      floor,
      count: rooms.length,
      roomNames: rooms.map(r => r.name)
    })),
    roomsWithPhotos: Array.from(optimizedRoomPhotos.entries()).map(([id, photos]) => ({
      roomId: id,
      roomName: rooms.find(r => r.id === id)?.name,
      photoCount: photos.length
    })),
    samplePageHTML: pages[0]?.substring(0, 500) + '...'
  });
  
  return pages;
}

/**
 * Generate Formula & Working Section
 * Shows detailed calculations for floor areas, HLP (Heat Loss Perimeter), and PW (Party Wall)
 */
function generateFormulaWorkingSection(
  surveyData: RetrofitSurveyData,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  console.log('📐 Generating formula & working section...');
  
  const floorPlanData = surveyData.floor_plan?.linkedFloorPlanData;
  const siteNotesData = floorPlanData?.siteNotesData;
  
  if (!floorPlanData || !floorPlanData.rooms || floorPlanData.rooms.length === 0) {
    console.log('⚠️ No floor plan data found for formula & working');
    return '';
  }

  // Check for multi-floor data from site notes (preferred) or detect from room floor numbers
  if (siteNotesData?.floors && siteNotesData.floors.length > 0) {
    console.log(`📊 Multi-floor data detected: ${siteNotesData.floors.length} floors from siteNotesData`);
    return generateMultiFloorFormulaWorkingSection(surveyData, siteNotesData, floorPlanData, pageNumber, totalPages, options);
  }
  
  // Detect multi-floor from rooms' floor property
  const rooms = floorPlanData.rooms || [];
  const uniqueFloors = new Set(rooms.map((r: any) => r.floor || 1));
  
  console.log('🔍 Formula & Working: Room floor analysis:', {
    totalRooms: rooms.length,
    uniqueFloors: Array.from(uniqueFloors),
    roomFloors: rooms.map((r: any) => ({ id: r.id || r.stableId, name: r.name, floor: r.floor || 'MISSING' }))
  });
  
  if (uniqueFloors.size > 1) {
    console.log(`📊 Multi-floor detected from rooms: ${uniqueFloors.size} floors (${Array.from(uniqueFloors).sort().join(', ')})`);
    // Build synthetic siteNotesData structure from rooms and walls
    const syntheticSiteNotesData = buildSyntheticFloorsData(floorPlanData, surveyData);
    return generateMultiFloorFormulaWorkingSection(surveyData, syntheticSiteNotesData, floorPlanData, pageNumber, totalPages, options);
  }

  console.log(`📊 Single floor data detected: Processing ${rooms.length} rooms on floor 1`);
  // Build synthetic siteNotesData structure for single floor to use multi-floor generation
  const syntheticSiteNotesData = buildSyntheticFloorsData(floorPlanData, surveyData);
  return generateMultiFloorFormulaWorkingSection(surveyData, syntheticSiteNotesData, floorPlanData, pageNumber, totalPages, options);
}

/**
 * Build synthetic siteNotesData structure from floor plan rooms and walls
 */
function buildSyntheticFloorsData(floorPlanData: any, surveyData: RetrofitSurveyData): any {
  const rooms = floorPlanData.rooms || [];
  const walls = floorPlanData.currentWalls || [];
  const allFloorData = floorPlanData.allFloorData || {};
  const roomCustomLabels = floorPlanData.roomCustomLabels || {};
  
  // Group rooms by floor
  const floorMap = new Map<number, any[]>();
  rooms.forEach((room: any) => {
    const floor = room.floor || 1;
    if (!floorMap.has(floor)) {
      floorMap.set(floor, []);
    }
    floorMap.get(floor)!.push(room);
  });
  
  // Build floor data structure
  const floors: any[] = [];
  Array.from(floorMap.entries())
    .sort(([a], [b]) => a - b)
    .forEach(([floorNum, floorRooms]) => {
      // Calculate metrics for this floor
      const floorData = allFloorData[floorNum];
      const floorMetrics = floorData?.floorMetrics || {};
      
      // Calculate total area from rooms and build room breakdown
      const totalArea = floorRooms.reduce((sum, room) => sum + (room.area || 0), 0);
      
      // Build area breakdown with room calculations from room_details
      const areaBreakdown = floorRooms.map((room: any) => {
        const roomId = room.stableId || room.id;
        // Try to get room details with width and length
        const roomDetail = surveyData.room_details?.rooms?.find((r: any) => r.id === roomId);
        
        return {
          name: room.name,
          width: roomDetail?.width || 0,
          length: roomDetail?.length || 0,
          area: room.area || 0
        };
      });
      
      // Get HLP and PW from walls
      const floorWalls = walls.filter((w: any) => (w.floor || 1) === floorNum);
      const hlpData = getHLPData(floorWalls, floorNum);
      const pwData = getPWData(floorWalls, floorNum);
      
      // IMPORTANT: For single-floor properties, prefer pre-calculated values from floor_plan
      // If wall calculations return 0, use the saved values from live calculations panel
      let finalHLP = hlpData.length;
      let finalPW = pwData.length;
      let hlpSegments = hlpData.segments;
      let pwSegments = pwData.segments;
      
      if (floors.length === 0 && floorNum === 1) { // First floor being processed for single-floor property
        const savedHLP = surveyData.floor_plan?.hlpLength;
        const savedPW = surveyData.floor_plan?.pwLength;
        
        if (finalHLP === 0 && savedHLP && savedHLP > 0) {
          finalHLP = savedHLP;
          hlpSegments = estimateWallSegments(finalHLP, 'HLP');
          console.log(`📊 [FORMULA] Using saved HLP from floor_plan: ${finalHLP}m (wall calc was 0)`);
        }
        
        if (finalPW === 0 && savedPW && savedPW > 0) {
          finalPW = savedPW;
          pwSegments = estimateWallSegments(finalPW, 'PW');
          console.log(`📊 [FORMULA] Using saved PW from floor_plan: ${finalPW}m (wall calc was 0)`);
        }
      }
      
      const floorName = floorNum === 1 ? 'ground' : 
                       floorNum === 2 ? 'first' :
                       floorNum === 3 ? 'second' :
                       `floor ${floorNum}`;
      
      floors.push({
        id: floorNum,
        floorLevel: floorName,
        buildingType: 'main building',
        floorArea: totalArea.toFixed(2),
        hlp: finalHLP.toFixed(2),
        pw: finalPW.toFixed(2),
        areaBreakdown: areaBreakdown,
        highlighterBreakdown: {
          hlpSegments: hlpSegments,
          pwSegments: pwSegments
        }
      });
    });
  
  console.log(`🏗️ Built synthetic floor data: ${floors.length} floors`);
  floors.forEach(f => console.log(`  Floor ${f.id} (${f.floorLevel}): ${f.floorArea}m², HLP ${f.hlp}m, PW ${f.pw}m`));
  
  return { floors };
}

/**
 * Generate Formula & Working for multi-floor data from site notes
 */
function generateMultiFloorFormulaWorkingSection(
  surveyData: RetrofitSurveyData,
  siteNotesData: any,
  floorPlanData: any,
  pageNumber: number,
  totalPages: number,
  options?: RetrofitPDFExportOptions
): string {
  console.log('📐 Generating multi-floor formula & working section...');
  
  const allFloorData = floorPlanData?.allFloorData || {};
  const allFloorPlans = floorPlanData?.allFloorPlans || {};
  
  console.log(`📊 allFloorData keys:`, Object.keys(allFloorData));
  console.log(`📊 allFloorPlans keys:`, Object.keys(allFloorPlans));
  console.log(`📊 siteNotesData floors:`, siteNotesData.floors.length);
  console.log(`📊 siteNotesData.floors structure:`, siteNotesData.floors.map((f: any) => ({
    id: f.id,
    floorLevel: f.floorLevel,
    floorArea: f.floorArea,
    hlp: f.hlp,
    pw: f.pw,
    hasBreakdown: !!f.highlighterBreakdown
  })));

  //Generate calculations for each floor from site notes
  const calculationSections = siteNotesData.floors.map((floor: any) => {
    const floorNumber = floor.id;
    const floorArea = parseFloat(floor.floorArea) || 0;
    const hlp = parseFloat(floor.hlp) || 0;
    const pw = parseFloat(floor.pw) || 0;

    console.log(`🔍 Processing floor ${floorNumber}:`, { floorArea, hlp, pw, willSkip: (floorArea <= 0 && hlp <= 0 && pw <= 0) });

    if (floorArea <= 0 && hlp <= 0 && pw <= 0) return '';

    const floorName = floor.floorLevel === 'ground' ? 'Ground Floor' : 
                     floor.floorLevel === 'first' ? 'First Floor' :
                     floor.floorLevel === 'second' ? 'Second Floor' :
                     `Floor ${floorNumber}`;
    const buildingType = floor.buildingType === 'main building' ? 'Main Building' : 
                        (floor.buildingType || '').split(' ').map((w: string) => 
                          w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

    // Use highlighter breakdown if available, otherwise show totals
    const hlpSegments = floor.highlighterBreakdown?.hlpSegments || [];
    const pwSegments = floor.highlighterBreakdown?.pwSegments || [];
    const areaBreakdown = floor.areaBreakdown || [];

    // Build area calculation with room breakdown
    let areaCalc = '';
    if (areaBreakdown.length > 0) {
      const roomCalcs = areaBreakdown.map((room: any) => {
        if (room.width > 0 && room.length > 0) {
          return `${room.name}: ${room.width.toFixed(2)}m × ${room.length.toFixed(2)}m = ${room.area.toFixed(2)}m²`;
        } else {
          return `${room.name}: ${room.area.toFixed(2)}m²`;
        }
      });
      
      // Add the addition formula
      const areaValues = areaBreakdown.map((room: any) => room.area.toFixed(2));
      const additionFormula = areaValues.join('m² + ') + `m² = ${floorArea.toFixed(2)}m²`;
      
      areaCalc = roomCalcs.join('<br>') + `<br><br>${additionFormula}`;
    } else {
      areaCalc = `${floorArea.toFixed(2)}m²`;
    }
    
    const hlpCalc = hlpSegments.length > 0 
      ? `${hlpSegments.map((s: number) => `${s.toFixed(2)}m`).join(' + ')} = ${hlp.toFixed(2)}m`
      : `${hlp.toFixed(2)}m`;
    const pwCalc = pwSegments.length > 0
      ? `${pwSegments.map((s: number) => `${s.toFixed(2)}m`).join(' + ')} = ${pw.toFixed(2)}m`
      : pw > 0 ? `${pw.toFixed(2)}m` : '0.00m (No party walls)';

    return `
      <div class="floor-calculation-block">
        <div class="floor-header">${floorName} - ${buildingType}</div>
        <div class="calculation-content">
          <div class="calc-item">
            <div class="calc-label">Area:</div>
            <div class="calc-formula">${areaCalc}</div>
          </div>
          <div class="calc-item">
            <div class="calc-label">HLP:</div>
            <div class="calc-formula">${hlpCalc}</div>
          </div>
          <div class="calc-item">
            <div class="calc-label">PW:</div>
            <div class="calc-formula">${pwCalc}</div>
          </div>
        </div>
      </div>
    `;
  }).filter((section: string) => section !== '');

  if (calculationSections.length === 0) {
    console.log('⚠️ No calculations to display');
    return '';
  }

  console.log(`✅ Generated ${calculationSections.length} floor calculation(s)`);

  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, options)}
      
      <div class="content-section">
        <div class="section-heading">Formula & Working - All Floors</div>
        <div class="calculations-container">
          ${calculationSections.join('')}
        </div>
      </div>
      ${generateFooter(options)}
    </section>
  `;
}

// Helper function to get HLP walls and total length (for single-floor logic)
function getHLPData(walls: any[], floorNumber: number): { length: number; segments: number[] } {
  const floorWalls = walls.filter(w => (w.floor || 1) === floorNumber);
  if (floorWalls.length === 0) return { length: 0, segments: [] };

  const segments: number[] = [];
  
  floorWalls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // Calculate from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach((section: any) => {
        if (section.type === 'HLP' || section.type === 'SW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / PIXELS_PER_METER;
          console.log(`  🔍 HLP/SW section: wallLength=${wallPixelLength.toFixed(0)}px, range=${section.startPercent}-${section.endPercent}, sectionLength=${sectionLengthMeters.toFixed(2)}m`);
          segments.push(sectionLengthMeters);
        }
      });
    } else if (wall.type === 'HLP' || wall.type === 'SW') {
      // Calculate from whole wall
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      console.log(`  🔍 HLP/SW whole wall: ${wallPixelLength.toFixed(0)}px = ${(wallPixelLength / PIXELS_PER_METER).toFixed(2)}m`);
      segments.push(wallPixelLength / PIXELS_PER_METER);
    }
  });
  
  // Calculate total by summing the segments to ensure formula matches the answer
  const totalLength = segments.reduce((sum, seg) => sum + seg, 0);
  console.log(`📐 Calculated HLP for floor ${floorNumber}: ${totalLength.toFixed(2)}m from ${segments.length} segments`);
  console.log(`📐 HLP segments:`, segments.map(s => `${s.toFixed(2)}m`).join(', '));
  console.log(`📐 HLP segment sum verification: ${segments.map(s => s.toFixed(2)).join(' + ')} = ${totalLength.toFixed(2)}`);
  return { length: totalLength, segments };
}

// Helper function to get PW walls and total length (for single-floor logic)
function getPWData(walls: any[], floorNumber: number): { length: number; segments: number[] } {
  const floorWalls = walls.filter(w => (w.floor || 1) === floorNumber);
  if (floorWalls.length === 0) return { length: 0, segments: [] };

  const segments: number[] = [];
  
  floorWalls.forEach(wall => {
    if (wall.sections && wall.sections.length > 0) {
      // Calculate from wall sections
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      wall.sections.forEach((section: any) => {
        if (section.type === 'PW') {
          const sectionPixelLength = wallPixelLength * (section.endPercent - section.startPercent);
          const sectionLengthMeters = sectionPixelLength / PIXELS_PER_METER;
          segments.push(sectionLengthMeters);
        }
      });
    } else if (wall.type === 'PW') {
      // Calculate from whole wall
      const wallPixelLength = Math.hypot(wall.end.x - wall.start.x, wall.end.y - wall.start.y);
      segments.push(wallPixelLength / PIXELS_PER_METER);
    }
  });
  
  // Calculate total by summing the segments to ensure formula matches the answer
  const totalLength = segments.reduce((sum, seg) => sum + seg, 0);
  console.log(`📐 Calculated PW for floor ${floorNumber}: ${totalLength.toFixed(2)}m from ${segments.length} segments`);
  console.log(`📐 PW segments:`, segments.map(s => `${s.toFixed(2)}m`).join(', '));
  console.log(`📐 PW segment sum verification: ${segments.map(s => s.toFixed(2)).join(' + ')} = ${totalLength.toFixed(2)}`);
  return { length: totalLength, segments };
}

// NOTE: Single-floor Formula & Working generation is currently disabled
// The main generateFormulaWorkingSection function now only supports multi-floor via siteNotesData
// If needed, single-floor support can be re-implemented by uncommenting and fixing the code below

/* ORPHANED SINGLE-FLOOR CODE - COMMENTED OUT FOR NOW
  // Group rooms and get metrics by floor
  const floorMap = new Map<number, {
    rooms: any[];
    totalArea: number;
    totalHLP: number;
    totalPW: number;
    hlpSegments?: number[];
    pwSegments?: number[];
  }>();

  rooms.forEach((room: any) => {
    const floor = room.floor || 1;
    const roomId = room.stableId || room.id;
    const roomName = roomCustomLabels[roomId] || roomId;
    const area = room.area || 0;

    if (!floorMap.has(floor)) {
      // Get floor metrics from allFloorData if available
      const floorData = allFloorData[floor];
      const floorMetrics = floorData?.floorMetrics || { hlpLength: 0, pwLength: 0, totalArea: 0 };
      
      // Calculate HLP and PW from walls if not available in stored metrics
      let hlpData = { length: 0, segments: [] as number[] };
      let pwData = { length: 0, segments: [] as number[] };
      
      if (currentWalls.length > 0) {
        // Always recalculate from walls to ensure accuracy
        hlpData = getHLPData(currentWalls, floor);
        pwData = getPWData(currentWalls, floor);
      }
      
      floorMap.set(floor, { 
        rooms: [], 
        totalArea: floorMetrics.totalArea || 0,
        totalHLP: hlpData.length,
        totalPW: pwData.length,
        hlpSegments: hlpData.segments,
        pwSegments: pwData.segments
      } as any);
    }

    const floorData = floorMap.get(floor)!;
    
    floorData.rooms.push({
      name: roomName,
      area,
      width: room.width || (area > 0 ? estimateRectangleDimensions(area).width : 0),
      length: room.length || (area > 0 ? estimateRectangleDimensions(area).height : 0)
    });
  });

  // After processing all rooms, recalculate totalArea from rooms if needed
  floorMap.forEach((floorData, floor) => {
    if (floorData.totalArea === 0 || floorData.rooms.length > 0) {
      // Sum up room areas
      const calculatedArea = floorData.rooms.reduce((sum, room) => sum + (room.area || 0), 0);
      if (calculatedArea > 0) {
        floorData.totalArea = calculatedArea;
        console.log(`📐 Floor ${floor} total area from rooms: ${calculatedArea.toFixed(2)}m²`);
      }
    }
  });

  // Generate calculation sections for each floor
  const calculationSections: string[] = [];

  Array.from(floorMap.entries())
    .sort(([a], [b]) => a - b) // Sort by floor number
    .forEach(([floorNum, floorData]) => {
      const floorName = floorNum === 1 ? 'Ground Floor' : `${floorNum - 1}${getOrdinalSuffix(floorNum - 1)} Floor`;
      
      // Generate area calculation
      let areaCalc = '';
      if (floorData.rooms.length === 1) {
        const room = floorData.rooms[0];
        if (room.width > 0 && room.length > 0) {
          areaCalc = `${room.width.toFixed(2)}m × ${room.length.toFixed(2)}m = ${floorData.totalArea.toFixed(2)}m²`;
        } else {
          areaCalc = `${floorData.totalArea.toFixed(2)}m²`;
        }
      } else if (floorData.rooms.length > 1) {
        const roomCalcs = floorData.rooms.map((room: any) => {
          if (room.width > 0 && room.length > 0) {
            return `${room.width.toFixed(2)}m×${room.length.toFixed(2)}m=${room.area.toFixed(2)}m²`;
          }
          return `${room.area.toFixed(2)}m²`;
        });
        areaCalc = `${roomCalcs.join(' + ')} = ${floorData.totalArea.toFixed(2)}m²`;
      } else {
        areaCalc = `${floorData.totalArea.toFixed(2)}m²`;
      }

      // Generate HLP calculation with actual wall segments
      let hlpCalc = '';
      if (floorData.totalHLP > 0) {
        const segments = floorData.hlpSegments || [];
        if (segments.length > 0) {
          hlpCalc = `${segments.map(s => `${s.toFixed(2)}m`).join(' + ')} = ${floorData.totalHLP.toFixed(2)}m`;
        } else {
          // Fallback to estimated segments if actual not available
          const estimatedSegments = estimateWallSegments(floorData.totalHLP);
          hlpCalc = `${estimatedSegments.map(s => `${s.toFixed(2)}m`).join(' + ')} = ${floorData.totalHLP.toFixed(2)}m`;
        }
      } else {
        hlpCalc = '0.00m (No exterior walls detected)';
      }

      // Generate PW calculation with actual wall segments
      let pwCalc = '';
      if (floorData.totalPW > 0) {
        const segments = floorData.pwSegments || [];
        if (segments.length > 0) {
          pwCalc = `${segments.map(s => `${s.toFixed(2)}m`).join(' + ')} = ${floorData.totalPW.toFixed(2)}m`;
        } else {
          // Fallback to just showing total
          pwCalc = `${floorData.totalPW.toFixed(2)}m`;
        }
      } else {
        pwCalc = '0.00m (No party walls)';
      }

      calculationSections.push(`
        <div class="floor-calculation-block">
          <div class="floor-header">${floorName} - Main Building</div>
          <div class="calculation-content">
            <div class="calc-item">
              <div class="calc-label">Area:</div>
              <div class="calc-formula">${areaCalc || 'Not available'}</div>
            </div>
            <div class="calc-item">
              <div class="calc-label">HLP:</div>
              <div class="calc-formula">${hlpCalc || 'Not calculated'}</div>
            </div>
            <div class="calc-item">
              <div class="calc-label">PW:</div>
              <div class="calc-formula">${pwCalc || 'Not calculated'}</div>
            </div>
          </div>
        </div>
      `);
    });

  if (calculationSections.length === 0) {
    console.log('⚠️ No calculations to display');
    return '';
  }

  console.log(`✅ Generated ${calculationSections.length} floor calculation(s)`);

  return `
    <section class="pdf-page">
      ${generateHeader(surveyData, pageNumber, totalPages, logoDataUrl)}
      
      <div class="content-section">
        <div class="section-heading">Formula & Working - All Floors</div>
        <div class="calculations-container">
          ${calculationSections.join('')}
        </div>
      </div>
    </section>
  `;
}
*/
